import { getToken } from '../../services/tokenStore';
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import { getTrialSchemaImageRejectionReason } from '../../utils/trialSchemaImageQualityCheck';
import * as Yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  FormControlLabel,
  Divider,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormGroup,
  Tooltip,
  Stack,
} from '@mui/material';
import { Remove as RemoveIcon, CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Add as AddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import axiosInstance from '../../config/axios';
import API_ENDPOINTS from '../../config/api';
import { generateOfficialTitlesFromIntake } from '../../utils/officialTitleGeneration';
import {
  loadDocumentValidationState,
  buildPersistedValidationState,
  saveDocumentValidationCache,
  clearDocumentValidationCache,
  isDocumentValidationCachePassing,
  resolveValidationStatusFromMap,
  flattenFileStatusMap,
} from '../../utils/documentValidationCache';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import clinicalIntakeService, { formatClinicalIntakeForLLM } from '../../services/clinicalIntake.service';
import {
  BRIEF_DESCRIPTION_OF_TRIAL_POPULATION,
  GENERAL_DESCRIPTION_OF_TRIAL_POPULATION,
  LEGACY_PATIENT_POPULATION,
  LEGACY_SHORT_DESCRIPTION,
  STUDY_PARTS_POPULATION_FIELD_LABELS,
  STUDY_PARTS_POPULATION_FIELD_KEYS,
  backfillStudyPartsCommitteesFromRegulatory,
  migrateStudyPartsPopulationKeys,
  resolveCommitteesForIntake,
} from '../../constants/clinicalIntakeStudyPartsPopulationFields';
import {
  buildStudyPartCohortColumns,
  buildStudyPartOnlyColumns,
  buildTreatmentTableColumns,
  distributeRegimensAcrossParts,
  getStudyPartDisplayName,
  hasConfiguredTrialPartCount,
  isMultiPartTrial,
  effectiveStudyPartSlotCount,
  parseDeclaredStudyPartCount,
  isStudyPartCohortKey,
} from '../../constants/studyPartsNaming';
import {
  applyFullMergedNotesToCohort,
  applyMergedNotesToCohort,
  applyMergedNotesToPartDirect,
  getFullMergedNotesForCohort,
  getMergedNotesForCohort,
  getMergedNotesForPartDirect,
  isEmptyStudyPartNote,
  joinTreatmentAdditionalInformationLive,
  splitTreatmentAdditionalInformationLive,
} from '../../constants/studyPartNotesMerge';
import {
  buildTrialSchemaFromLegacy,
  formatRegimenColumnHeader,
  normalizeTrialSchemaConfiguration,
  syncLegacyFieldsFromTrialSchema,
} from '../../constants/trialSchemaConfiguration';
import TrialSchemaConfigurationSection from './TrialSchemaConfigurationSection';
import TemplateLanguageFieldsTable from './TemplateLanguageFieldsTable';
import PatientLibrarySection54Panel from './PatientLibrarySection54Panel';
import PatientLibrarySection7111Panel from './PatientLibrarySection7111Panel';
import TemporaryDiscontinuationSection712Panel from './TemporaryDiscontinuationSection712Panel';
import RechallengeSection713Panel from './RechallengeSection713Panel';
import CptTemplateTextField, { CptColorLegend } from './CptTemplateTextField';
import {
  ADVERSE_EVENTS_TEMPLATE_LANGUAGE_ROWS,
  DISCONTINUATION_TEMPLATE_LANGUAGE_ROWS,
  DISCONTINUATION_TEMPLATE_FIELD_KEYS,
  ADVERSE_EVENTS_TEMPLATE_FIELD_KEYS,
  POPULATION_TEMPLATE_LANGUAGE_ROWS,
  ALL_TEMPLATE_LANGUAGE_FIELD_KEYS,
} from '../../constants/templateLanguageFieldRows';
import { generateContent } from '../../services/AIService';
import { templateLanguageUpdatedBase } from '../../data/templateLanguageUpdatedBase';
import { TEMPLATE_INTAKE_FIELD_META } from '../../data/templateIntakeCheckboxFieldMap';
import {
  assemblePatientLibrarySection54,
  isPatientLibraryDeterministicField,
  DEFAULT_PATIENT_LIBRARY_DECISIONS,
} from '../../utils/patientLibrarySection54';
import {
  assemblePatientLibrarySection7111,
  DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
} from '../../utils/patientLibrarySection7111';
import {
  assembleTemporaryDiscontinuationSection712,
  DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
  isTemporaryDiscontinuationDeterministicField,
} from '../../utils/temporaryDiscontinuationSection712';
import {
  assembleRechallengeSection713,
  DEFAULT_RECHALLENGE_DECISIONS,
  isRechallengeDeterministicField,
} from '../../utils/rechallengeSection713';
import { CLINICAL_INTAKE_FIELD_HOVER_INFO } from '../../data/clinicalIntakeFieldHoverInfo';
import {
  buildClinicalIntakeFieldGuidance,
  buildAdminTrialSchemaFieldGuidance,
} from '../../utils/buildClinicalIntakeFieldGuidance';
import { useProject } from '../../context/ProjectContext';
import {
  getProjectIdentityFields,
  projectListPrimaryLabel,
  resolveProjectSponsorName,
  resolveProjectSponsorProtocolId,
  resolveProjectTrialAcronym,
  PROJECT_IDENTITY_SOURCE_HINT,
} from '../../utils/projectIdentityFields';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleOfActivities from '../tables/ScheduleOfActivities';
import scheduleOfActivitiesService from '../../services/scheduleOfActivities.service';
import documentEditorService from '../../services/documentEditor.service';
import { mergeAppendicesIntoSection12Output } from '../../protocol/constants/sectionData';
import templateLanguageService from '../../services/templateLanguage.service';
import { buildCptTemplateLlmPromptSections } from '../../utils/cptTemplateConventions';
import {
  cptHtmlToPlainText,
  sanitizeCptTemplateHtml,
  coerceCptTemplateHtml,
  shouldUseCptColoredOutput,
} from '../../utils/cptTemplateHtml';
import { stripHtmlToPlainText } from '../../utils/stripHtmlToPlainText';
import { normalizeTemplateLanguagePlainText } from '../../utils/normalizeTemplateLanguagePlainText';
import { stripLeadingDuplicateHeading } from '../../utils/stripLeadingDuplicateHeading';
import { applyGrammarGuardsToHtml } from '../../utils/grammarGuards';
import {
  getLifestyleParentDisplayValueLive,
  getPrepHandlingParentDisplayValueLive,
  inferLifestyleSubfieldEnables,
  inferPrepHandlingSubfieldEnables,
  inferPrimaryAnalysisSubfieldEnables,
  inferRescueSubfieldEnables,
  inferSecondaryAnalysisSubfieldEnables,
  normalizeStudyPartsParentSubFields,
  normalizeTreatmentTableParentSubFields,
} from '../../utils/studyTableParentSubFields';
import ScrollToTop from '../common/ScrollToTop';
import { useAuth } from '../../context/AuthContext';
import {
  runSourceDocumentJob,
  SOURCE_DOCUMENT_JOB_TYPES,
} from '../../services/sourceDocumentJob.service';

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

/** Protocol Intake Form header navigation (A–E). */
const PROTOCOL_INTAKE_NAV = [
  { id: 'trial-identifier', label: 'A Trial Identifier', accordionKey: 'trialIdentifier' },
  { id: 'trial-schema', label: 'B Trial Schema', accordionKey: 'trialSchema' },
  { id: 'trial-design', label: 'C Trial Design', accordionKey: 'trialDesign' },
  { id: 'arguments', label: 'D Arguments', accordionKey: 'arguments' },
  { id: 'soa', label: 'E SOA', accordionKey: null },
];

const PROTOCOL_SECTION_SCROLL_MARGIN = '88px';

/** Consistent black styling for M11 section banners, accordion titles, and table headers. */
const M11_SECTION_TITLE_COLOR = '#212121';
const M11_SECTION_BANNER_SX = {
  mt: 1,
  mb: 1,
  px: 1.5,
  py: 1,
  borderRadius: 1,
  backgroundColor: '#f5f5f5',
  color: M11_SECTION_TITLE_COLOR,
  fontWeight: 800,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  border: '1px solid #ddd',
};
const M11_SECTION_TITLE_ROW_STYLE = {
  border: '1px solid #ddd',
  padding: '16px',
  backgroundColor: '#f5f5f5',
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: '16px',
  color: M11_SECTION_TITLE_COLOR,
  borderBottom: '2px solid #ddd',
};
const M11_ACCORDION_TITLE_SX = { color: M11_SECTION_TITLE_COLOR, fontWeight: 'bold' };

/**
 * Split table-row labels into display title + optional "View guidance" hint.
 * Only treat trailing "(…)" as guidance when it mentions guidance — not plurals like "Estimand(s)".
 */
function splitFieldLabelGuidance(rawLabel) {
  const label = String(rawLabel || '');
  const hintStart = label.lastIndexOf('(');
  if (hintStart <= 0 || !label.endsWith(')')) {
    return { shortLabel: label, hintText: '', hasHint: false };
  }
  const hintText = label.slice(hintStart + 1, -1).trim();
  if (!/guidance/i.test(hintText)) {
    return { shortLabel: label, hintText: '', hasHint: false };
  }
  return {
    shortLabel: label.slice(0, hintStart).trim(),
    hintText,
    hasHint: true,
  };
}

/** Long-running AI routes (document validation can take several minutes on large PDFs). */
const AI_LONG_REQUEST_TIMEOUT_MS = 600000; // 10 minutes

// Configure PDF.js worker. See PdfJsHighlightViewer.jsx for why this isn't
// `new URL('pdfjs-dist/...', import.meta.url)`: react-scripts' Babel pass
// over bundled .mjs files injects absolute-filesystem-path helper imports
// that break once emitted as a standalone asset. scripts/copy-pdf-worker.js
// (postinstall) copies the worker into public/ untouched instead.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

// Helper component to trigger auto-save on Formik value changes.
// (React hooks must not be called inside Formik render callbacks.)
const AutoSaveWatcher = ({ enabled, values, onAutoSave }) => {
  useEffect(() => {
    if (!enabled) return;
    onAutoSave(values);
  }, [enabled, values, onAutoSave]);

  return null;
};

const STUDY_DESIGN_KEYS = [
  'single_arm',
  'multi_arm_randomized',
  'multi_arm_non_randomized',
  'crossover',
  'multi_part_study',
  'single_part_multi_cohort',
  'multi_part_multi_cohort',
  'platform_umbrella_basket',
  'adaptive',
];

const STUDY_DESIGN_CONFLICTS = {
  // Arm structure: choose at most one of these
  single_arm: ['multi_arm_randomized', 'multi_arm_non_randomized'],
  multi_arm_randomized: ['single_arm', 'multi_arm_non_randomized'],
  multi_arm_non_randomized: ['single_arm', 'multi_arm_randomized'],

  // Part/cohort structure (allow multi_part_study + multi_part_multi_cohort together)
  single_part_multi_cohort: ['multi_part_study', 'multi_part_multi_cohort'],
  multi_part_study: ['single_part_multi_cohort'],
  multi_part_multi_cohort: ['single_part_multi_cohort'],
};

const STUDY_DESIGN_IMPLIES = {
  // If a multi-part multi-cohort study is selected, multi_part_study must be true
  multi_part_multi_cohort: { multi_part_study: true },
};

// All Gemini/AI calls go through the backend (see API_ENDPOINTS.ai.*) — the
// client must never hold a Gemini API key: any REACT_APP_* env var is inlined
// into the built JS bundle and is readable by anyone who loads the app.

// Add background information prompts
const BACKGROUND_PROMPTS = {
  disease_epidemiology: (values) => `Reference Specific I/E for disease condition: ${values.study_overview?.disease_indication || 'Not specified'}, Overall primary disease condition: ${values.study_overview?.therapeutic_area || 'Not specified'}, Disease Background Documents: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}, to create a brief outline of what background information (example disease prevalence could be used in protocol background section and do a web search to find relevant information online and list the information here with appropriate citations.).Limit your response only to only disease background.`,
  
  current_standard_of_care: (values) => `Reference Specific I/E for disease condition: ${values.study_overview?.disease_indication || 'Not specified'}, Disease Background Documents: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}, control arm description: ${values.study_treatments?.control_regimen || 'Not specified'}, Control Arm-Related Documents: ${values.study_overview?.control_method || 'Not specified'} to create a brief outline of current standard of care and their outcomes for proposed patient population use web search to find relevant information and list the relevant resources with appropriate citations. Limit your response to the current standard of care and their outcomes and avoid providing additional context.`,
  
  outcomes_current_soc: (values) => `Reference Specific I/E for disease condition: ${values.study_overview?.disease_indication || 'Not specified'}, Disease Background Documents: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}, control arm description: ${values.study_treatments?.control_regimen || 'Not specified'}, Control Arm-Related Documents: ${values.study_overview?.control_method || 'Not specified'} to create a brief outline of what are the outcomes with current standard of care for proposed patient population that could be used in protocol background section and do a web search to find relevant information online and list the information here with appropriate citations`,
  
  benefits_investigational: (values) => `Reference Primary Investigational Product (IP) Sources: ${values.study_treatments?.regimen_arm_1 || 'Not specified'}, Supporting Product Documents: ${values.study_overview?.therapeutic_area || 'Not specified'}, Specific I/E for disease condition: ${values.study_overview?.disease_indication || 'Not specified'}, Overall primary disease condition: ${values.study_overview?.therapeutic_area || 'Not specified'} to design a strategy to summarize benefits of the investigational benefits for the target patient population.`,
  
  risks_investigational: (values) => `Reference Primary Investigational Product (IP) Sources: ${values.study_treatments?.regimen_arm_1 || 'Not specified'}, Supporting Product Documents: ${values.study_overview?.therapeutic_area || 'Not specified'}, Specific I/E for disease condition: ${values.study_overview?.disease_indication || 'Not specified'}, Overall primary disease condition: ${values.study_overview?.therapeutic_area || 'Not specified'} to design a strategy to summarize benefits of the investigational benefits for the target patient population.`,

  // // New rational fields
   purpose_of_trial: (values) => `You are an expert medical writer. Your task is to analyze the provided context of a clinical trial and create a structured, bullet-point outline for the "Purpose of the Trial" section. This outline must present the core logical arguments for why the trial is necessary.

Trial Context: 
- Disease/Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Disease Indication: ${values.study_overview?.disease_indication || 'Not specified'}
- Study Type: ${values.study_overview?.study_type_dropdown || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Investigational Product: ${values.study_treatments?.regimen_arms?.regimen1 || 'Not specified'}
- Control Regimen: ${values.study_treatments?.control_regimen || 'Not specified'}
- Target Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}

Background Information Generated from Documents:
- Disease Epidemiology: ${values.background_information?.disease_epidemiology || 'Not available'}
- Current Standard of Care: ${values.background_information?.current_standard_of_care || 'Not available'}
- Outcomes with Current SOC: ${values.background_information?.outcomes_current_soc || 'Not available'}
- Benefits of Investigational Product: ${values.background_information?.benefits_investigational || 'Not available'}
- Risks of Investigational Product: ${values.background_information?.risks_investigational || 'Not available'}

Based on the Trial Context and Background Information provided above, generate a concise, bullet-point outline that follows this exact structure. You must synthesize the information from both the trial context and the background information to create each point. You must only use information present in the provided Trial Context and Background Information. Do not add, assume, or fabricate any facts.

Structure your response as follows:

**Unmet Medical Need:**
• Synthesize the information on disease impact and limitations of the current standard of care into few statements defining the problem. Use the Disease Epidemiology, Current Standard of Care, and Outcomes with Current SOC sections to support your statements.

**Proposed Solution & Rationale:**
• Introduce the investigational product as a potential solution and state that its scientific rationale is detailed in the Investigator's Brochure (e.g., "See IB Section X.X for preclinical data"). Reference the Benefits of Investigational Product section for potential advantages.

**Central Clinical Question:**
• Articulate the core uncertainty about the product's benefit-risk profile in humans that the trial must resolve. Consider both the Benefits and Risks of the Investigational Product sections.

**Justification for This Trial:**
• Explain why this specific study, with its stated design, is the necessary and appropriate method to answer the central clinical question and inform future medical practice.`,
  
  rationale_for_estimands: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.1 "Rationale for Estimand(s)."

Trial Context: 
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Secondary Endpoints: ${JSON.stringify(values.study_endpoints?.secondary_endpoints || [])}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Treatment Arms: ${JSON.stringify(values.study_treatments?.regimen_arms || {})}
- Control Regimen: ${values.study_treatments?.control_regimen || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for the choice of estimands. Include:
1. Clinical relevance of the primary endpoint for measuring treatment effect
2. Strategy for handling intercurrent events (e.g., treatment discontinuation, rescue medication)
3. Statistical considerations for the estimand choice
4. Regulatory alignment and guidance compliance

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_interventional_type: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.2 "Rationale for Intervention Model."

Trial Context: 
- Study Type: ${values.study_overview?.study_type_dropdown || 'Not specified'}
- Investigational Product: ${values.study_treatments?.regimen_arms?.regimen1 || 'Not specified'}
- Control Regimen: ${values.study_treatments?.control_regimen || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for the intervention model choice. Include:
1. Justification for the chosen intervention design (e.g., parallel-group, crossover, factorial)
2. Scientific rationale for the intervention approach
3. Regulatory considerations and guidance compliance
4. Clinical benefits and practical considerations

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_control_method: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.3 "Rationale for Control Type."

Trial Context: 
- Control Regimen: ${values.study_treatments?.control_regimen || 'Not specified'}
- Control Method: ${values.study_overview?.control_method || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for the control type choice. Include:
1. Justification for the chosen control type (e.g., placebo, active control, historical control)
2. Ethical considerations and patient safety
3. Scientific validity and ability to demonstrate treatment effect
4. Regulatory acceptability and guidance compliance

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_trial_duration: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.4 "Rationale for Trial Duration."

Trial Context: 
- Study Duration: ${values.study_overview?.study_duration || 'Not specified'}
- Follow-up Period: ${values.study_overview?.follow_up_period || 'Not specified'}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}

Based on the Trial Context provided above, create a comprehensive rationale for the trial duration. Include:
1. Clinical considerations for the chosen duration
2. Endpoint assessment timing and measurement requirements
3. Disease pathophysiology and expected treatment response timeline
4. Regulatory requirements and guidance compliance

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_adaptive_design: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.5 "Rationale for Adaptive or Novel Trial Design."

Trial Context: 
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Study Type: ${values.study_overview?.study_type_dropdown || 'Not specified'}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for any adaptive or novel trial design elements. Include:
1. Description of adaptive or novel design elements (if any)
2. Statistical considerations and methodology
3. Operational benefits and feasibility
4. Regulatory guidance compliance and approval pathway

Note: If the study uses a traditional fixed design without adaptive elements, state this clearly and explain why a traditional design is appropriate.

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_interim_analysis: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.6 "Rationale for Interim Analysis."

Trial Context: 
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Study Duration: ${values.study_overview?.study_duration || 'Not specified'}
- Safety Considerations: ${values.study_overview?.safety_considerations || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for interim analysis plans. Include:
1. Description of planned interim analyses (timing, endpoints, sample size)
2. Statistical stopping rules (efficacy, futility, safety)
3. Ethical considerations and patient safety
4. Regulatory requirements and guidance compliance

Note: If no interim analyses are planned, state this clearly and explain why none are necessary.

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_other_design_aspects: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.7 "Rationale for Other Trial Design Aspects."

Trial Context: 
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Study Type: ${values.study_overview?.study_type_dropdown || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for other trial design aspects not covered in previous subsections. Include:
1. Randomization methods and procedures
2. Blinding procedures (if applicable)
3. Special considerations unique to this trial
4. Any other design elements that require justification

Note: Only include aspects that are not already covered in other rationale subsections.

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,
  
  rationale_for_trial_population: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and create a detailed rationale for Section 4.2.8 "Rationale for Trial Population."

Trial Context: 
- Target Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}
- Inclusion Criteria: ${values.target_population?.inclusion_criteria || 'Not specified'}
- Exclusion Criteria: ${values.target_population?.exclusion_criteria || 'Not specified'}
- Disease Indication: ${values.study_overview?.disease_indication || 'Not specified'}
- Therapeutic Area: ${values.study_overview?.therapeutic_area || 'Not specified'}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}

Based on the Trial Context provided above, create a comprehensive rationale for the chosen trial population. Include:
1. Clinical relevance of the target population
2. Justification for inclusion and exclusion criteria
3. Safety considerations for the selected population
4. Regulatory guidance compliance and patient selection rationale

You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`,

  // Function to determine relevant rationale subsections
  determine_rationale_relevance: (values) => `You are an expert medical writer and clinical trial methodologist. Analyze the provided trial context and determine which rationale subsections are relevant for Section 4.2 "Rationale for Trial Design."

Trial Context: 
- Study Type: ${values.study_overview?.study_type_dropdown || 'Not specified'}
- Study Design: ${JSON.stringify(values.study_overview?.study_design || {})}
- Primary Endpoint: ${values.study_endpoints?.primary_endpoint || 'Not specified'}
- Treatment Arms: ${JSON.stringify(values.study_treatments?.regimen_arms || {})}
- Control Regimen: ${values.study_treatments?.control_regimen || 'Not specified'}
- Study Duration: ${values.study_overview?.study_duration || 'Not specified'}
- Follow-up Period: ${values.study_overview?.follow_up_period || 'Not specified'}
- Study Population: ${values.target_population?.conditions_related_to_primary_disease || 'Not specified'}

Your Task: Based on the Trial Context provided above, determine which of the following subsections are applicable. For each subsection, state Yes/No and provide a brief justification.

**Part A: Relevant Subsections**
* **4.2.1 Rationale for Estimand(s):** [Yes/No] [Brief justification based on trial context]
* **4.2.2 Rationale for Intervention Model:** [Yes/No] [Brief justification based on trial context]
* **4.2.3 Rationale for Control Type:** [Yes/No] [Brief justification based on trial context]
* **4.2.4 Rationale for Trial Duration:** [Yes/No] [Brief justification based on trial context]
* **4.2.5 Rationale for Adaptive or Novel Trial Design:** [Yes/No] [Brief justification based on trial context]
* **4.2.6 Rationale for Interim Analysis:** [Yes/No] [Brief justification based on trial context]
* **4.2.7 Rationale for Other Trial Design Aspects:** [Yes/No] [Brief justification based on trial context]
* **4.2.8 Rationale for Trial Population:** [Yes/No] [Brief justification based on trial context]

Return your response in the exact format above. You must only use information present in the provided Trial Context. Do not add, assume, or fabricate any facts.`
};

const buildBackgroundSectionsForGeneration = (values, currentRelevance = {}) => {
  const basicBackgroundSections = [
    {
      key: 'disease_epidemiology',
      prompt: BACKGROUND_PROMPTS.disease_epidemiology(values),
      fieldName: 'background_information.disease_epidemiology',
      displayName: 'Disease Epidemiology',
    },
    {
      key: 'current_standard_of_care',
      prompt: BACKGROUND_PROMPTS.current_standard_of_care(values),
      fieldName: 'background_information.current_standard_of_care',
      displayName: 'Current Standard of Care',
    },
    {
      key: 'benefits_investigational',
      prompt: BACKGROUND_PROMPTS.benefits_investigational(values),
      fieldName: 'background_information.benefits_investigational',
      displayName: 'Benefits with Investigational Agent',
    },
    {
      key: 'risks_investigational',
      prompt: BACKGROUND_PROMPTS.risks_investigational(values),
      fieldName: 'background_information.risks_investigational',
      displayName: 'Risks with Investigational Agent',
    },
    {
      key: 'purpose_of_trial',
      prompt: BACKGROUND_PROMPTS.purpose_of_trial(values),
      fieldName: 'background_information.purpose_of_trial',
      displayName: 'Purpose of the Trial',
    },
  ];

  const rationaleFields = [
    {
      key: 'rationale_for_estimands',
      prompt: BACKGROUND_PROMPTS.rationale_for_estimands(values),
      fieldName: 'background_information.rationale_for_estimands',
      displayName: 'Rationale for Estimands',
    },
    {
      key: 'rationale_for_interventional_type',
      prompt: BACKGROUND_PROMPTS.rationale_for_interventional_type(values),
      fieldName: 'background_information.rationale_for_interventional_type',
      displayName: 'Rationale for Interventional Type',
    },
    {
      key: 'rationale_for_control_method',
      prompt: BACKGROUND_PROMPTS.rationale_for_control_method(values),
      fieldName: 'background_information.rationale_for_control_method',
      displayName: 'Rationale for Control Method',
    },
    {
      key: 'rationale_for_trial_duration',
      prompt: BACKGROUND_PROMPTS.rationale_for_trial_duration(values),
      fieldName: 'background_information.rationale_for_trial_duration',
      displayName: 'Rationale for Trial Duration',
    },
    {
      key: 'rationale_for_adaptive_design',
      prompt: BACKGROUND_PROMPTS.rationale_for_adaptive_design(values),
      fieldName: 'background_information.rationale_for_adaptive_design',
      displayName: 'Rationale for Adaptive or Novel Trial Design',
    },
    {
      key: 'rationale_for_interim_analysis',
      prompt: BACKGROUND_PROMPTS.rationale_for_interim_analysis(values),
      fieldName: 'background_information.rationale_for_interim_analysis',
      displayName: 'Rationale for Interim Analysis',
    },
    {
      key: 'rationale_for_other_design_aspects',
      prompt: BACKGROUND_PROMPTS.rationale_for_other_design_aspects(values),
      fieldName: 'background_information.rationale_for_other_design_aspects',
      displayName: 'Rationale for Other Trial Design Aspects',
    },
    {
      key: 'rationale_for_trial_population',
      prompt: BACKGROUND_PROMPTS.rationale_for_trial_population(values),
      fieldName: 'background_information.rationale_for_trial_population',
      displayName: 'Rationale for Trial Population',
    },
  ].filter((field) => currentRelevance[field.key]);

  return [...basicBackgroundSections, ...rationaleFields];
};

const applyBackgroundInformationToForm = (setFieldValue, backgroundInformation = {}) => {
  Object.entries(backgroundInformation).forEach(([key, text]) => {
    setFieldValue(`background_information.${key}`, text);
  });
};

/** Fingerprint trial schema fields used for rationale / background relevance (skip when empty). */
const buildTrialSchemaContextFingerprint = (values) => {
  const so = values?.study_overview;
  const parts = [
    so?.study_type_dropdown,
    so?.number_of_study_parts_new,
    so?.study_phase,
    JSON.stringify(so?.study_design || {}),
    JSON.stringify(so?.study_parts_cohorts || {}),
    values?.study_treatments?.control_regimen,
    values?.study_endpoints?.primary_endpoint,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts.join('|') : '';
};

// Validation Schema
const validationSchema = Yup.object({
  study_identification: Yup.object({
    protocol_number: Yup.string().required('Required'),
    preferred_short_title: Yup.string(),
    official_title: Yup.string(),
    document_short_title: Yup.string(),
    document_official_title: Yup.string(),
    preferred_study_identifier: Yup.string(),
    date_of_project_creation: Yup.string(),
  }),
  study_overview: Yup.object({
    study_type_dropdown: Yup.string(),
    study_phase: Yup.string(),
    study_design: Yup.object({
      single_arm: Yup.boolean(),
      multi_arm_randomized: Yup.boolean(),
      multi_arm_non_randomized: Yup.boolean(),
      crossover: Yup.boolean(),
      multi_part_study: Yup.boolean(),
      single_part_multi_cohort: Yup.boolean(),
      multi_part_multi_cohort: Yup.boolean(),
      platform_umbrella_basket: Yup.boolean(),
      adaptive: Yup.boolean(),
    }).test(
      'exclusive-study-design',
      'Invalid Study Design selection',
      function (value) {
        if (!value || typeof value !== 'object') return true;
        const v = value || {};

        // Arm structure: at most one of these 3
        const armSelected = ['single_arm', 'multi_arm_randomized', 'multi_arm_non_randomized'].filter((k) => v[k] === true);
        if (armSelected.length > 1) {
          return this.createError({
            message: 'Select only one of: Single Arm, Multi Arm Randomized, Multi Arm non-randomized.',
          });
        }

        // Part/cohort structure: single_part_multi_cohort cannot coexist with multi-part selections
        if (v.single_part_multi_cohort && (v.multi_part_study || v.multi_part_multi_cohort)) {
          return this.createError({
            message: 'Single Part Multi cohort cannot be selected with Multi-Part options.',
          });
        }

        return true;
      }
    ),
    number_of_study_parts_new: Yup.number().min(0, 'Must be 0 or greater'),
    study_parts_cohorts: Yup.object().test('cohorts-validation', 'Cohort values must be non-negative', function(value) {
      if (!value) return true; // Allow empty object
      
      const errors = [];
      Object.keys(value).forEach(key => {
        const cohortValue = parseInt(value[key]);
        if (isNaN(cohortValue) || cohortValue < 0) {
          errors.push(`${key} must be a non-negative number`);
        }
      });
      
      if (errors.length > 0) {
        return this.createError({ message: errors.join(', ') });
      }
      return true;
    }),
    study_parts: Yup.object(),
    number_of_study_arms: Yup.mixed()
      .transform((value) => {
        if (value === '') return undefined;
        if (typeof value === 'string') {
          // Convert word numbers to digits
          const wordToNumber = {
            'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
            'One': 1, 'Two': 2, 'Three': 3, 'Four': 4, 'Five': 5
          };
          return wordToNumber[value] || parseInt(value, 10);
        }
        return value;
      })
      .test('is-valid-arm-count', 'Invalid number of arms', function(value) {
        if (value === undefined || value === '') return true;
        const num = Number(value);
        return !isNaN(num) && num >= 0;
      }),
    number_of_study_parts: Yup.string(),
    stratification_factors: Yup.string(),
    study_periods: Yup.mixed().transform((value) => {
      if (typeof value === 'string') {
        // If it's a string, split by newlines and filter out empty items
        return value.split('\n').filter(item => item.trim() !== '');
      }
      if (Array.isArray(value)) {
        // If it's already an array, return as is
        return value;
      }
      // If it's undefined/null, return empty array
      return [];
    }).test('is-array', 'Study periods must be an array', (value) => {
      return Array.isArray(value);
    }),
    participant_input_into_design: Yup.string(),
  }),
  endpoints_objectives: Yup.object({
    primary_objective_endpoints: Yup.string().required('Required'),
    key_secondary_objectives_endpoints: Yup.string()
      .transform((value) => {
        if (Array.isArray(value)) {
          return value.join('\n');
        }
        return value;
      }),
    secondary_objectives_endpoints: Yup.string()
      .transform((value) => {
        if (Array.isArray(value)) {
          return value.join('\n');
        }
        return value;
      }),
    exploratory_objectives_endpoints: Yup.string()
      .transform((value) => {
        if (Array.isArray(value)) {
          return value.join('\n');
        }
        return value;
      }),
  }),
  target_population: Yup.object({
    conditions_related_to_primary_disease: Yup.array().of(Yup.string()).required('Required'),
    tissue_sample_procedure_compliance: Yup.string(),
    patient_performance_status: Yup.string(),
    life_expectancy: Yup.string(),
    organ_function_lab_parameters: Yup.string(),
    concomitant_meds_washout: Yup.string(),
    comorbidities_infections: Yup.string(),
    reproductive_status_contraception: Yup.string(),
    eligibility_criteria: Yup.string(),
  }),
  study_treatments: Yup.object({
    num_regimens: Yup.number().min(0, 'Must be ≥ 0').required('Number of regimens is required'),
    regimens_per_part: Yup.object().test(
      'regimens-per-part-sum',
      'Sum of regimens per part must equal total regimens',
      function (value) {
        const numRegimens = parseInt(this.parent?.num_regimens) || 0;
        if (!value || numRegimens <= 0) return true;
        const sum = Object.values(value).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
        if (sum === 0) return true;
        return sum === numRegimens;
      }
    ),
    num_agents: Yup.number().min(0, 'Must be ≥ 0').nullable(true),
    additional_information: Yup.array().of(Yup.string()),
    treatment_table: Yup.object(), // Dynamic treatment table data
    regimen_arms: Yup.object(),
    control_regimen: Yup.string(),
    concomitant_medications_allowed: Yup.string(),
    concomitant_medications_prohibited: Yup.string(),
    fda_pregnancy_risk_categories: Yup.string(),
  }),
  discontinuation_rules: Yup.object({
    discontinuation_trial_intervention_individual: Yup.string()
      .transform((value) => {
        if (Array.isArray(value)) {
          return value.join('\n');
        }
        return value;
      }),
    permanent_discontinuation_trial_intervention: Yup.string(),
    temporary_discontinuation_trial_intervention: Yup.string(),
    temporary_discontinuation_decisions: Yup.object({
      temporary_discontinuation_allowed: Yup.string(),
      not_described_output_mode: Yup.string(),
      protocol_source_excerpts: Yup.string(),
    }),
    rechallenge: Yup.string(),
    rechallenge_decisions: Yup.object({
      rechallenge_after_temporary_discontinuation: Yup.string(),
      liver_restart_after_stopping: Yup.string(),
      protocol_requirements: Yup.array().of(Yup.string()),
      other_requirement_text: Yup.string(),
      protocol_source_excerpts: Yup.string(),
    }),
    participant_discontinuation_withdrawal: Yup.string(),
    management_loss_to_followup: Yup.string(),
    screen_failure_rescreening_rules: Yup.string(),
    additional_information: Yup.array().of(Yup.string())
  }),
  study_assessments: Yup.object({
    screening_baseline_assessments: Yup.string(),
    efficacy_assessments: Yup.string(),
    safety_assessments_general: Yup.string(),
    physical_examination: Yup.string(),
    vital_signs: Yup.string(),
    electrocardiograms: Yup.string(),
    clinical_laboratory_assessment: Yup.string(),
    pregnancy_testing: Yup.string(),
    suicidal_ideation_risk_monitoring: Yup.string(),
    pharmacokinetics: Yup.string(),
    biomarkers: Yup.string(),
    genetics_pharmacogenomics: Yup.string(),
    pharmacodynamic_biomarkers: Yup.string(),
    other_biomarkers: Yup.string(),
  }),
  statistical_considerations: Yup.object({
    analysis_sets: Yup.mixed()
      .transform((value) => {
        if (Array.isArray(value)) {
          return value.join('\n');
        }
        return value;
      }),
    primary_objective_analysis: Yup.string(),
    secondary_objective_analysis: Yup.string(),
    exploratory_analysis: Yup.string(),
    safety_analysis: Yup.string(),
    other_analyses: Yup.string(),
    interim_analyses: Yup.string(),
    sample_size_determination: Yup.string(),
  }),
  regulatory_requirements: Yup.object({
    countries_for_submission: Yup.string(),
    committees: Yup.array().of(Yup.string())
      .transform((value) => {
        if (typeof value === 'string') {
          return value.split('\n').filter(item => item.trim() !== '');
        }
        return value;
      }),
    informed_consent_process: Yup.string(),
    quality_tolerance_limits: Yup.string(),
    data_quality_assurance: Yup.string(),
    source_data: Yup.string(),
  }),
  template_language_fields: Yup.object({
    patient_library_decisions: Yup.object({
      childbearing_population_pathway: Yup.string(),
      genotoxicity_risk: Yup.string(),
      female_reproductive_risk: Yup.string(),
      male_ejaculate_risk: Yup.string(),
      has_study_specific_reproductive_requirements: Yup.boolean(),
      study_specific_reproductive_requirements: Yup.string(),
    }),
    liver_patient_library_decisions: Yup.object({
      study_phase_bucket: Yup.string(),
      liver_algorithm: Yup.string(),
      use_standard_algorithm: Yup.string(),
      protocol_modifications: Yup.string(),
      restart_rechallenge: Yup.string(),
      restart_requirements: Yup.string(),
      monitoring_pathway: Yup.string(),
      monitoring_description: Yup.string(),
      modified_narrative_override: Yup.string(),
    }),
    definitions_childbearing_potential: Yup.string(),
    contraception_requirements: Yup.string(),
    liver_event_stopping_criteria: Yup.string(),
    liver_event_stopping_flowchart_mermaid: Yup.string(),
    liver_restart_rechallenge: Yup.string(),
    definition_adverse_event: Yup.string(),
    definition_serious_adverse_event: Yup.string(),
    definitions_product_complaints: Yup.string(),
    ae_reporting_timing_procedures: Yup.string(),
    ae_reporting_timing: Yup.string(),
    ae_reporting_collection_procedure: Yup.string(),
    ae_reporting_reporting: Yup.string(),
    ae_reporting_special_interest: Yup.string(),
    ae_reporting_disease_related_events: Yup.string(),
    pregnancy_postpartum_information: Yup.string(),
    participants_pregnant_during_trial: Yup.string(),
    special_safety_situations: Yup.string(),
    comments: Yup.string(),
  }),
  appendices: Yup.array().of(
    Yup.object({
      title: Yup.string(),
      description: Yup.string(),
      contentHtml: Yup.string(),
      uploads: Yup.array().of(
        Yup.object({
          name: Yup.string(),
          filename: Yup.string(),
          size: Yup.number(),
          contentType: Yup.string(),
          azureBlobName: Yup.string(),
          azureBlobUrl: Yup.string(),
          uploadedAt: Yup.mixed(),
        })
      ),
    })
  ),
  additional_comments: Yup.string(),
  document_uploads: Yup.object({
    primary_documents: Yup.object({
      investigator_brochure: Yup.boolean(),
      label: Yup.boolean(),
      additional_reports: Yup.boolean(),
    }),
    supporting_documents: Yup.object({
      pharmacy_manual: Yup.boolean(),
      risk_management_guidelines: Yup.boolean(),
      user_defined: Yup.boolean(),
    }),
    study_design_outline: Yup.boolean(),
    control_arm_documents: Yup.boolean(),
    disease_background_documents: Yup.boolean(),
    uploaded_files: Yup.object({
      investigator_brochure: Yup.array(),
      label: Yup.array(),
      additional_reports: Yup.array(),
      pharmacy_manual: Yup.array(),
      risk_management_guidelines: Yup.array(),
      user_defined: Yup.array(),
      study_design_outline: Yup.array(),
      control_arm_documents: Yup.array(),
      disease_background_documents: Yup.array(),
      trial_schema: Yup.array(),
    })
  }),
  study_monitoring_logistics: Yup.object({
    data_collection_method: Yup.string().required('Required'),
    monitoring_frequency: Yup.string(),
    on_site_or_remote: Yup.string().required('Required'),
    key_contacts: Yup.object({
      sponsor_contact: Yup.string(),
      cro_contact: Yup.string()
    })
  }),
  background_information: Yup.object({
    disease_epidemiology: Yup.string(),
    current_standard_of_care: Yup.string(),
    outcomes_current_soc: Yup.string(),
    benefits_investigational: Yup.string(),
    risks_investigational: Yup.string(),
    // New rational fields
    purpose_of_trial: Yup.string(),
    rationale_for_estimands: Yup.string(),
    rationale_for_interventional_type: Yup.string(),
    rationale_for_control_method: Yup.string(),
    rationale_for_trial_duration: Yup.string(),
    rationale_for_adaptive_design: Yup.string(),
    rationale_for_interim_analysis: Yup.string(),
    rationale_for_other_design_aspects: Yup.string(),
    rationale_for_trial_population: Yup.string(),
    // Custom prompts for individual regeneration
    custom_prompts: Yup.object({
      disease_epidemiology: Yup.string(),
      current_standard_of_care: Yup.string(),
      outcomes_current_soc: Yup.string(),
      benefits_investigational: Yup.string(),
      risks_investigational: Yup.string(),
      purpose_of_trial: Yup.string(),
      rationale_for_estimands: Yup.string(),
      rationale_for_interventional_type: Yup.string(),
      rationale_for_control_method: Yup.string(),
      rationale_for_trial_duration: Yup.string(),
      rationale_for_adaptive_design: Yup.string(),
      rationale_for_interim_analysis: Yup.string(),
      rationale_for_other_design_aspects: Yup.string(),
      rationale_for_trial_population: Yup.string()
    })
  }),
});

// Helper function to sanitize values and prevent null values
const sanitizeValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map(item => sanitizeValue(item));
  }
  if (typeof value === 'object' && value !== null) {
    const sanitized = {};
    Object.keys(value).forEach(key => {
      sanitized[key] = sanitizeValue(value[key]);
    });
    return sanitized;
  }
  return value;
};

const deepMerge = (target, source) => {
  const output = { ...target };
  if (source) {
    Object.keys(source).forEach(key => {
      if (source[key] instanceof Object && !Array.isArray(source[key])) {
        output[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = sanitizeValue(source[key]);
      }
    });
  }
  return output;
};

const DEFAULT_DATE_OF_PROJECT_CREATION_HOVER =
  'Displays the date this study record was created. This field is system generated and cannot be edited.';

const getNonEmptyTextOrFallback = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const hasTrialSchemaData = (studyOverview = {}) =>
  Boolean(
    hasConfiguredTrialPartCount(studyOverview) ||
      Object.keys(studyOverview?.study_parts_cohorts || {}).length > 0 ||
      Object.keys(studyOverview?.trial_schema_configuration?.parts || {}).length > 0
  );

const hasNonemptyStudyParts = (studyParts) =>
  studyParts &&
  typeof studyParts === 'object' &&
  !Array.isArray(studyParts) &&
  Object.keys(studyParts).length > 0;

const hasNonemptyTreatmentTable = (table) =>
  table &&
  typeof table === 'object' &&
  !Array.isArray(table) &&
  Object.keys(table).length > 0;

const hasNonEmptyRegimenArms = (arms) =>
  arms &&
  typeof arms === 'object' &&
  !Array.isArray(arms) &&
  Object.values(arms).some((v) => typeof v === 'string' && v.trim().length > 0);

const resolveRegimenArms = (preferredArms, fallbackArms) => {
  if (hasNonEmptyRegimenArms(preferredArms)) return preferredArms;
  if (hasNonEmptyRegimenArms(fallbackArms)) return fallbackArms;
  if (preferredArms && typeof preferredArms === 'object' && !Array.isArray(preferredArms)) {
    return preferredArms;
  }
  return fallbackArms && typeof fallbackArms === 'object' ? fallbackArms : {};
};

/** Prefer parent prop trial schema when server draft is stale (e.g. auto-populate quiet save still in flight). */
const mergeServerDraftWithInitialDataProp = (serverData, propData) => {
  if (!serverData) return propData ? sanitizeValue(propData) : null;
  const sanitizedServer = sanitizeValue(serverData);
  if (!propData || typeof propData !== 'object') return sanitizedServer;

  const propHasSchema = hasTrialSchemaData(propData.study_overview);
  if (!propHasSchema) return sanitizedServer;

  const serverHasSchema = hasTrialSchemaData(sanitizedServer.study_overview);
  const propParts = propData.study_overview?.study_parts;
  const serverParts = sanitizedServer.study_overview?.study_parts;
  const propHasParts = hasNonemptyStudyParts(propParts);
  const serverHasParts = hasNonemptyStudyParts(serverParts);
  const propTable = propData.study_treatments?.treatment_table;
  const serverTable = sanitizedServer.study_treatments?.treatment_table;

  const preferPropTrialSchema = !serverHasSchema || (propHasParts && !serverHasParts);
  if (!preferPropTrialSchema) return sanitizedServer;

  return sanitizeValue({
    ...sanitizedServer,
    study_overview: {
      ...sanitizedServer.study_overview,
      ...propData.study_overview,
      study_parts: propHasParts ? propParts : serverParts || propParts,
    },
    study_treatments: {
      ...sanitizedServer.study_treatments,
      ...propData.study_treatments,
      treatment_table: hasNonemptyTreatmentTable(propTable)
        ? propTable
        : hasNonemptyTreatmentTable(serverTable)
          ? serverTable
          : propTable || serverTable,
      regimen_arms: resolveRegimenArms(
        propData.study_treatments?.regimen_arms,
        sanitizedServer.study_treatments?.regimen_arms
      ),
    },
    document_uploads: {
      ...sanitizedServer.document_uploads,
      ...propData.document_uploads,
    },
  });
};

const REGULATORY_TEMPLATE_FIELD_KEYS = Object.keys(TEMPLATE_INTAKE_FIELD_META).filter(
  (key) =>
    !ALL_TEMPLATE_LANGUAGE_FIELD_KEYS.includes(key) &&
    !DISCONTINUATION_TEMPLATE_FIELD_KEYS.includes(key)
);

const TEMPLATE_PROTECTED_PARSE_FIELDS = {
  discontinuation_rules: DISCONTINUATION_TEMPLATE_FIELD_KEYS.filter(
    (key) =>
      key !== 'temporary_discontinuation_trial_intervention' && key !== 'rechallenge'
  ),
  regulatory_requirements: REGULATORY_TEMPLATE_FIELD_KEYS,
  template_language_fields: ALL_TEMPLATE_LANGUAGE_FIELD_KEYS,
};

/** Decision-tree nests that must survive shallow section merges on save/lock. */
const DISCONTINUATION_DECISION_NEST_KEYS = [
  'temporary_discontinuation_decisions',
  'rechallenge_decisions',
];

const setNestedFormField = (root, dottedPath, value) => {
  if (!root || typeof root !== 'object') return root;
  const parts = dottedPath.split('.');
  if (parts.length === 1) {
    return { ...root, [parts[0]]: value };
  }
  const [head, ...rest] = parts;
  return {
    ...root,
    [head]: setNestedFormField(root[head] || {}, rest.join('.')),
  };
};

const mergeParsedSectionRespectingTemplates = (
  sectionKey,
  currentSection,
  parsedSection,
  templateSelectionsMap
) => {
  const merged = { ...(currentSection || {}) };
  const protectedFields = TEMPLATE_PROTECTED_PARSE_FIELDS[sectionKey] || [];
  const selections = templateSelectionsMap || {};
  if (!parsedSection || typeof parsedSection !== 'object') {
    return merged;
  }
  Object.keys(parsedSection).forEach((fieldKey) => {
    const hasTemplateSelection =
      protectedFields.includes(fieldKey) && selections[fieldKey] === true;
    if (hasTemplateSelection) {
      return;
    }
    merged[fieldKey] = parsedSection[fieldKey];
  });
  return merged;
};

const resolveCommitteesUnlessTemplateSelected = (studyParts, committees, templateSelections = {}) =>
  templateSelections.committees
    ? committees ?? ''
    : resolveCommitteesForIntake(studyParts, committees);

const isTemplateGenerationPlaceholder = (value) =>
  typeof value === 'string' &&
  (/generating tailored template/i.test(value) ||
    /reviewing with google adk/i.test(value));

/**
 * Merge intake sources for save/lock. Template-checkbox fields prefer the snapshot from
 * persistTemplateFieldChoice when Formik still holds stale document-parse text.
 */
const consolidateIntakeValuesForSave = ({
  loaded,
  snapshot,
  form,
  templateSelections = {},
  fieldTemplateSelections = {},
}) => {
  const selections = { ...fieldTemplateSelections, ...templateSelections };
  const loadedSafe = loaded || {};
  const snap = snapshot || {};
  const formSafe = form || {};

  const mergeSection = (sectionKey) => ({
    ...(loadedSafe[sectionKey] || {}),
    ...(snap[sectionKey] || {}),
    ...(formSafe[sectionKey] || {}),
  });

  const merged = {
    ...loadedSafe,
    ...snap,
    ...formSafe,
    _id: formSafe._id || snap._id || loadedSafe._id,
    status: formSafe.status || snap.status || loadedSafe.status,
    template_language_fields: mergeSection('template_language_fields'),
    regulatory_requirements: mergeSection('regulatory_requirements'),
    discontinuation_rules: mergeSection('discontinuation_rules'),
  };

  // Deep-merge nested decision objects (shallow section merge can drop them)
  DISCONTINUATION_DECISION_NEST_KEYS.forEach((nestKey) => {
    merged.discontinuation_rules = {
      ...(merged.discontinuation_rules || {}),
      [nestKey]: {
        ...(loadedSafe.discontinuation_rules?.[nestKey] || {}),
        ...(snap.discontinuation_rules?.[nestKey] || {}),
        ...(formSafe.discontinuation_rules?.[nestKey] || {}),
      },
    };
  });

  // Prefer non-empty Formik values for panel-assembled Section 7.1.2 / 7.1.3 fields
  ['temporary_discontinuation_trial_intervention', 'rechallenge'].forEach((fieldKey) => {
    const formVal = formSafe.discontinuation_rules?.[fieldKey];
    if (formVal !== undefined && formVal !== null && String(formVal).trim() !== '') {
      merged.discontinuation_rules = {
        ...(merged.discontinuation_rules || {}),
        [fieldKey]: formVal,
      };
    }
  });

  // Prefer non-empty Formik liver Patient Library assembled outputs
  [
    'liver_patient_library_decisions',
    'liver_event_stopping_criteria',
    'liver_event_stopping_flowchart_mermaid',
    'liver_restart_rechallenge',
  ].forEach((fieldKey) => {
    const formVal = formSafe.template_language_fields?.[fieldKey];
    if (formVal === undefined || formVal === null) return;
    if (typeof formVal === 'object') {
      merged.template_language_fields = {
        ...(merged.template_language_fields || {}),
        [fieldKey]: {
          ...(loadedSafe.template_language_fields?.[fieldKey] || {}),
          ...(snap.template_language_fields?.[fieldKey] || {}),
          ...formVal,
        },
      };
      return;
    }
    if (String(formVal).trim() !== '') {
      merged.template_language_fields = {
        ...(merged.template_language_fields || {}),
        [fieldKey]: formVal,
      };
    }
  });

  Object.entries(TEMPLATE_PROTECTED_PARSE_FIELDS).forEach(([sectionKey, fieldKeys]) => {
    fieldKeys.forEach((fieldKey) => {
      if (!selections[fieldKey]) return;
      const snapVal = snap[sectionKey]?.[fieldKey];
      const formVal = formSafe[sectionKey]?.[fieldKey];
      const loadedVal = loadedSafe[sectionKey]?.[fieldKey];
      if (snapVal === undefined || snapVal === '') return;
      const userClearedTemplate =
        formVal === '' && Boolean(snapVal && String(snapVal).trim());
      const userEditedTemplate =
        formVal !== loadedVal &&
        formVal !== snapVal &&
        formVal !== '' &&
        !isTemplateGenerationPlaceholder(formVal);
      if (userClearedTemplate || userEditedTemplate) {
        merged[sectionKey] = { ...(merged[sectionKey] || {}), [fieldKey]: formVal };
        return;
      }
      if (!userEditedTemplate) {
        merged[sectionKey] = { ...(merged[sectionKey] || {}), [fieldKey]: snapVal };
      }
    });
  });

  return merged;
};

const intakeValuesContainTemplatePlaceholder = (values) => {
  if (!values || typeof values !== 'object') return false;
  for (const sectionKey of Object.keys(TEMPLATE_PROTECTED_PARSE_FIELDS)) {
    const section = values[sectionKey];
    if (!section || typeof section !== 'object') continue;
    for (const fieldKey of TEMPLATE_PROTECTED_PARSE_FIELDS[sectionKey]) {
      if (isTemplateGenerationPlaceholder(section[fieldKey])) return true;
    }
  }
  return false;
};

const INTAKE_CELL_PLACEHOLDER = 'Not Provided';

const isIntakeCellEmpty = (v) =>
  v == null ||
  (typeof v === 'string' && (v.trim() === '' || v.trim() === INTAKE_CELL_PLACEHOLDER));

const normalizeIntakeCellValue = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' && v.trim() === INTAKE_CELL_PLACEHOLDER) return '';
  return v;
};

const scrubIntakePlaceholdersFromStudyParts = (studyParts) => {
  if (!studyParts || typeof studyParts !== 'object') return studyParts;
  const out = {};
  Object.keys(studyParts).forEach((partKey) => {
    const part = studyParts[partKey];
    if (!part || typeof part !== 'object') {
      out[partKey] = part;
      return;
    }
    out[partKey] = {};
    Object.keys(part).forEach((slotKey) => {
      const slot = part[slotKey];
      if (!slot || typeof slot !== 'object') {
        out[partKey][slotKey] = slot;
        return;
      }
      out[partKey][slotKey] = {};
      Object.keys(slot).forEach((fieldKey) => {
        out[partKey][slotKey][fieldKey] = normalizeIntakeCellValue(slot[fieldKey]);
      });
    });
  });
  return out;
};

const scrubIntakePlaceholdersFromTreatmentTable = (table) => {
  if (!table || typeof table !== 'object') return table;
  const out = {};
  Object.keys(table).forEach((regimenKey) => {
    const regimen = table[regimenKey];
    if (!regimen || typeof regimen !== 'object') {
      out[regimenKey] = regimen;
      return;
    }
    out[regimenKey] = {};
    Object.keys(regimen).forEach((fieldKey) => {
      out[regimenKey][fieldKey] = normalizeIntakeCellValue(regimen[fieldKey]);
    });
  });
  return out;
};

const scrubIntakePlaceholdersFromDoc = (doc) => {
  if (!doc || typeof doc !== 'object') return doc;
  const next = { ...doc };
  if (next.study_overview?.study_parts) {
    next.study_overview = {
      ...next.study_overview,
      study_parts: scrubIntakePlaceholdersFromStudyParts(next.study_overview.study_parts),
    };
  }
  if (next.study_treatments?.treatment_table) {
    next.study_treatments = {
      ...next.study_treatments,
      treatment_table: scrubIntakePlaceholdersFromTreatmentTable(next.study_treatments.treatment_table),
    };
  }
  return next;
};

// Initial values setup
const getInitialValues = (initialData) => {
  const defaultValues = {
  study_identification: {
    protocol_number: '',
    preferred_short_title: '',
    official_title: '',
    document_short_title: '',
    document_official_title: '',
    preferred_study_identifier: '',
      date_of_project_creation: new Date().toISOString().split('T')[0], // System generated date
      comments: '',
    },
    ui_hover_texts: {
      study_identification: {
        date_of_project_creation:
          initialData?.ui_hover_texts?.study_identification?.date_of_project_creation ||
          DEFAULT_DATE_OF_PROJECT_CREATION_HOVER,
      },
    },
    study_overview: {
      //template_selection: 'default', // Default template selection
      study_type_dropdown: '',
      study_phase: initialData?.study_overview?.study_phase ?? '',
      study_design: {
        single_arm: false,
        multi_arm_randomized: false,
        multi_arm_non_randomized: false,
        crossover: false,
        multi_part_study: false,
        single_part_multi_cohort: false,
        multi_part_multi_cohort: false,
        platform_umbrella_basket: false,
        adaptive: false,
      },
      number_of_study_parts_new: '',
      study_parts_cohorts: {},
      study_parts_part_names: {}, // { part1: 'Custom Part Name', part2: '...' }
      study_parts_cohort_names: {}, // { part1: { cohort1: 'Custom Name', cohort2: 'Another Name' }, part2: {...} }
      study_parts: {},
      study_periods: (() => {
        // Handle study_periods from backend - could be string or array
        if (initialData?.study_overview?.study_periods) {
          if (typeof initialData.study_overview.study_periods === 'string') {
            // If it's a string, split by newlines and filter out empty items
            const periods = initialData.study_overview.study_periods.split('\n').filter(item => item.trim() !== '');
            return periods.length > 0 ? periods : [''];
          } else if (Array.isArray(initialData.study_overview.study_periods)) {
            // If it's already an array, return as is
            return initialData.study_overview.study_periods;
          }
        }
        // Default to empty array with one empty string
        return [''];
      })(),
      participant_input_into_design: '',
      comments: '',
      trial_schema_configuration: buildTrialSchemaFromLegacy(
        initialData?.study_overview,
        initialData?.study_treatments
      ),
    },
    endpoints_objectives: {
      primary_objective_endpoints: '',
      key_secondary_objectives_endpoints: '',
      secondary_objectives_endpoints: '',
      exploratory_objectives_endpoints: '',
    },
    target_population: {
      conditions_related_to_primary_disease: [],
      tissue_sample_procedure_compliance: '',
      patient_performance_status: '',
      life_expectancy: '',
      organ_function_lab_parameters: '',
      concomitant_meds_washout: '',
      comorbidities_infections: '',
      reproductive_status_contraception: '',
      eligibility_criteria: '',
    },
    study_treatments: {
      num_regimens: parseInt(initialData?.study_treatments?.num_regimens) || 0,
      num_agents: parseInt(initialData?.study_treatments?.num_agents) || 0,
      additional_information: initialData?.study_treatments?.additional_information || [],
      treatment_table: initialData?.study_treatments?.treatment_table || {},
      regimens_per_part: initialData?.study_treatments?.regimens_per_part || {},
      regimen_arms: initialData?.study_treatments?.regimen_arms || {},
      control_regimen: initialData?.study_treatments?.control_regimen || '',
      concomitant_medications_allowed: initialData?.study_treatments?.concomitant_medications_allowed || '',
      concomitant_medications_prohibited: initialData?.study_treatments?.concomitant_medications_prohibited || '',
      fda_pregnancy_risk_categories: initialData?.study_treatments?.fda_pregnancy_risk_categories || '',
    },
    discontinuation_rules: {
      discontinuation_trial_intervention_individual:
        initialData?.discontinuation_rules?.discontinuation_trial_intervention_individual || '',
      permanent_discontinuation_trial_intervention:
        initialData?.discontinuation_rules?.permanent_discontinuation_trial_intervention || '',
      temporary_discontinuation_trial_intervention:
        initialData?.discontinuation_rules?.temporary_discontinuation_trial_intervention || '',
      temporary_discontinuation_decisions: {
        ...DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
        ...(initialData?.discontinuation_rules?.temporary_discontinuation_decisions || {}),
      },
      rechallenge: initialData?.discontinuation_rules?.rechallenge || '',
      rechallenge_decisions: {
        ...DEFAULT_RECHALLENGE_DECISIONS,
        ...(initialData?.discontinuation_rules?.rechallenge_decisions || {}),
        protocol_requirements:
          initialData?.discontinuation_rules?.rechallenge_decisions?.protocol_requirements || [],
      },
      participant_discontinuation_withdrawal:
        initialData?.discontinuation_rules?.participant_discontinuation_withdrawal || '',
      management_loss_to_followup:
        initialData?.discontinuation_rules?.management_loss_to_followup || '',
      screen_failure_rescreening_rules:
        initialData?.discontinuation_rules?.screen_failure_rescreening_rules || '',
      additional_information: Array.isArray(initialData?.discontinuation_rules?.additional_information)
        ? initialData.discontinuation_rules.additional_information
        : [],
    },
    study_assessments: {
      screening_baseline_assessments:
        initialData?.study_assessments?.screening_baseline_assessments || '',
      efficacy_assessments: initialData?.study_assessments?.efficacy_assessments || '',
      safety_assessments_general: initialData?.study_assessments?.safety_assessments_general || '',
      physical_examination: initialData?.study_assessments?.physical_examination || '',
      vital_signs: initialData?.study_assessments?.vital_signs || '',
      electrocardiograms: initialData?.study_assessments?.electrocardiograms || '',
      clinical_laboratory_assessment:
        initialData?.study_assessments?.clinical_laboratory_assessment || '',
      pregnancy_testing: initialData?.study_assessments?.pregnancy_testing || '',
      suicidal_ideation_risk_monitoring:
        initialData?.study_assessments?.suicidal_ideation_risk_monitoring || '',
      pharmacokinetics: initialData?.study_assessments?.pharmacokinetics || '',
      biomarkers: initialData?.study_assessments?.biomarkers || '',
      genetics_pharmacogenomics: initialData?.study_assessments?.genetics_pharmacogenomics || '',
      pharmacodynamic_biomarkers: initialData?.study_assessments?.pharmacodynamic_biomarkers || '',
      other_biomarkers: initialData?.study_assessments?.other_biomarkers || '',
      comments: initialData?.study_assessments?.comments || '',
    },
    statistical_considerations: {
      analysis_sets: initialData?.statistical_considerations?.analysis_sets || '',
      primary_objective_analysis:
        initialData?.statistical_considerations?.primary_objective_analysis || '',
      secondary_objective_analysis:
        initialData?.statistical_considerations?.secondary_objective_analysis || '',
      exploratory_analysis: initialData?.statistical_considerations?.exploratory_analysis || '',
      safety_analysis: initialData?.statistical_considerations?.safety_analysis || '',
      other_analyses: initialData?.statistical_considerations?.other_analyses || '',
      interim_analyses: initialData?.statistical_considerations?.interim_analyses || '',
      sample_size_determination:
        initialData?.statistical_considerations?.sample_size_determination || '',
    },
    regulatory_requirements: {
      regulatory_ethical_considerations:
        initialData?.regulatory_requirements?.regulatory_ethical_considerations || '',
      investigator_responsibilities:
        initialData?.regulatory_requirements?.investigator_responsibilities || '',
      sponsor_responsibilities:
        initialData?.regulatory_requirements?.sponsor_responsibilities || '',
      informed_consent_rescreening:
        initialData?.regulatory_requirements?.informed_consent_rescreening || '',
      informed_consent_remaining_samples:
        initialData?.regulatory_requirements?.informed_consent_remaining_samples || '',
      committees: initialData?.regulatory_requirements?.committees || '',
      insurance_indemnity: initialData?.regulatory_requirements?.insurance_indemnity || '',
      risk_based_quality_management:
        initialData?.regulatory_requirements?.risk_based_quality_management || '',
      data_governance: initialData?.regulatory_requirements?.data_governance || '',
      data_protection: initialData?.regulatory_requirements?.data_protection || '',
      source_data: initialData?.regulatory_requirements?.source_data || '',
      protocol_deviations: initialData?.regulatory_requirements?.protocol_deviations || '',
      early_site_closure: initialData?.regulatory_requirements?.early_site_closure || '',
      data_dissemination: initialData?.regulatory_requirements?.data_dissemination || '',
      comments: initialData?.regulatory_requirements?.comments || '',
      ...(initialData?.regulatory_requirements || {}),
    },
    template_language_fields: {
      ...(initialData?.template_language_fields || {}),
      patient_library_decisions: {
        ...DEFAULT_PATIENT_LIBRARY_DECISIONS,
        ...(initialData?.template_language_fields?.patient_library_decisions || {}),
      },
      liver_patient_library_decisions: {
        ...DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
        ...(initialData?.template_language_fields?.liver_patient_library_decisions || {}),
      },
      definitions_childbearing_potential:
        initialData?.template_language_fields?.definitions_childbearing_potential || '',
      contraception_requirements:
        initialData?.template_language_fields?.contraception_requirements || '',
      liver_event_stopping_criteria:
        initialData?.template_language_fields?.liver_event_stopping_criteria || '',
      liver_event_stopping_flowchart_mermaid:
        initialData?.template_language_fields?.liver_event_stopping_flowchart_mermaid || '',
      liver_restart_rechallenge:
        initialData?.template_language_fields?.liver_restart_rechallenge || '',
      definition_adverse_event: initialData?.template_language_fields?.definition_adverse_event || '',
      definition_serious_adverse_event:
        initialData?.template_language_fields?.definition_serious_adverse_event || '',
      definitions_product_complaints:
        initialData?.template_language_fields?.definitions_product_complaints || '',
      ae_reporting_timing_procedures:
        initialData?.template_language_fields?.ae_reporting_timing_procedures || '',
      ae_reporting_timing: initialData?.template_language_fields?.ae_reporting_timing || '',
      ae_reporting_collection_procedure:
        initialData?.template_language_fields?.ae_reporting_collection_procedure || '',
      ae_reporting_reporting: initialData?.template_language_fields?.ae_reporting_reporting || '',
      ae_reporting_special_interest:
        initialData?.template_language_fields?.ae_reporting_special_interest || '',
      ae_reporting_disease_related_events:
        initialData?.template_language_fields?.ae_reporting_disease_related_events || '',
      pregnancy_postpartum_information:
        initialData?.template_language_fields?.pregnancy_postpartum_information || '',
      participants_pregnant_during_trial:
        initialData?.template_language_fields?.participants_pregnant_during_trial || '',
      special_safety_situations:
        initialData?.template_language_fields?.special_safety_situations || '',
      comments: initialData?.template_language_fields?.comments || '',
    },
    appendices: (() => {
      const emptyRow = () => ({ title: '', description: '', contentHtml: '', uploads: [] });
      const defaults = [
        { title: '12.1 Clinical Laboratory Tests', description: '', contentHtml: '', uploads: [] },
        { title: '12.2 Country/Region-Specific Differences', description: '', contentHtml: '', uploads: [] },
        { title: '12.3 Prior Protocol Amendment(s)', description: '', contentHtml: '', uploads: [] },
        { title: '12.4 Additional Supporting Materials', description: '', contentHtml: '', uploads: [] },
      ];
      const normalize = (list) =>
        (Array.isArray(list) ? list : []).map((a) => ({
          title: a?.title || '',
          description: a?.description || '',
          contentHtml: a?.contentHtml || '',
          uploads: Array.isArray(a?.uploads) ? a.uploads : [],
        }));
      // Only seed the 12.1-12.4 defaults for a brand-new intake. Once saved data exists, trust it
      // as-is — auto re-adding a missing 12.x row here used to silently undo the user deliberately
      // removing that appendix (e.g. deleting 12.4) every time the form reinitialized.
      if (initialData?.appendices && initialData.appendices.length > 0) {
        return normalize(initialData.appendices);
      }
      return defaults.map((d) => ({ ...d, ...emptyRow(), title: d.title }));
    })(),
    additional_comments: '',
    document_uploads: {
      primary_documents: {
        investigator_brochure: false,
        label: false,
        additional_reports: false,
      },
      supporting_documents: {
        pharmacy_manual: false,
        risk_management_guidelines: false,
        user_defined: false,
      },
      study_design_outline: false,
      control_arm_documents: false,
      disease_background_documents: false,
      uploaded_files: {
        investigator_brochure: [],
        label: [],
        additional_reports: [],
        pharmacy_manual: [],
        risk_management_guidelines: [],
        user_defined: [],
        study_design_outline: [],
        control_arm_documents: [],
        disease_background_documents: [],
        trial_schema: [],
      },
      document_table: initialData?.document_uploads?.document_table || [],
      comments: initialData?.document_uploads?.comments || {},
      validation_state: initialData?.document_uploads?.validation_state || {
        fingerprint: '',
        allValid: false,
        validationCompleted: false,
        fileStatus: {},
      },
      source_documents_workflow: initialData?.document_uploads?.source_documents_workflow || {
        auto_populate_succeeded: false,
        intake_parse_succeeded: false,
      },
    },
    study_monitoring_logistics: {
      data_collection_method: 'Electronic Data Capture (EDC)',
      monitoring_frequency: '',
      on_site_or_remote: 'On-Site',
      key_contacts: {
        sponsor_contact: '',
        cro_contact: ''
      }
    },
    background_information: {
      disease_epidemiology: '',
      current_standard_of_care: '',
      outcomes_current_soc: '',
      benefits_investigational: '',
      risks_investigational: '',
      // New rational fields
      purpose_of_trial: '',
      rationale_for_estimands: '',
      rationale_for_interventional_type: '',
      rationale_for_control_method: '',
      rationale_for_trial_duration: '',
      rationale_for_adaptive_design: '',
      rationale_for_interim_analysis: '',
      rationale_for_other_design_aspects: '',
      rationale_for_trial_population: ''
    }
  };

  const mergedValues = deepMerge(defaultValues, initialData || {});

  // Explicitly restore Section 7 decision trees + liver library (deepMerge defaults can mask them)
  if (initialData?.discontinuation_rules) {
    mergedValues.discontinuation_rules = {
      ...(mergedValues.discontinuation_rules || {}),
      ...initialData.discontinuation_rules,
      temporary_discontinuation_decisions: {
        ...DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
        ...(initialData.discontinuation_rules.temporary_discontinuation_decisions || {}),
      },
      rechallenge_decisions: {
        ...DEFAULT_RECHALLENGE_DECISIONS,
        ...(initialData.discontinuation_rules.rechallenge_decisions || {}),
        protocol_requirements:
          initialData.discontinuation_rules.rechallenge_decisions?.protocol_requirements || [],
      },
    };
  }
  if (initialData?.template_language_fields) {
    mergedValues.template_language_fields = {
      ...(mergedValues.template_language_fields || {}),
      ...initialData.template_language_fields,
      patient_library_decisions: {
        ...DEFAULT_PATIENT_LIBRARY_DECISIONS,
        ...(initialData.template_language_fields.patient_library_decisions || {}),
      },
      liver_patient_library_decisions: {
        ...DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
        ...(initialData.template_language_fields.liver_patient_library_decisions || {}),
      },
    };
  }

  if (initialData?.official_title && !mergedValues.study_identification?.official_title) {
    mergedValues.study_identification.official_title = initialData.official_title;
  }
  if (initialData?.short_title && !mergedValues.study_identification?.preferred_short_title) {
    mergedValues.study_identification.preferred_short_title = initialData.short_title;
  }

  if (mergedValues?.study_overview?.study_parts) {
    migrateStudyPartsPopulationKeys(mergedValues.study_overview.study_parts);
  }
  
  console.log('getInitialValues - initialData received:', initialData);
  console.log('getInitialValues - initialData study_treatments:', initialData?.study_treatments);
  console.log('getInitialValues - initialData treatment_table:', initialData?.study_treatments?.treatment_table);
  console.log('getInitialValues - mergedValues study_treatments:', mergedValues.study_treatments);
  
  // Function to force generate treatment table for AI auto-populated data
  const forceGenerateTreatmentTable = (values, existingTreatmentTable = null) => {
    console.log('forceGenerateTreatmentTable called with:', { values, existingTreatmentTable });
    
    // If we have existing treatment table data and it matches the expected structure, return it
    if (existingTreatmentTable && Object.keys(existingTreatmentTable).length > 0) {
      const { num_regimens } = values?.study_treatments || {};
      const expectedRegimenCount = num_regimens || 0;
      const existingRegimenCount = Object.keys(existingTreatmentTable).length;
      
      console.log('forceGenerateTreatmentTable - checking existing data:', {
        expectedRegimenCount,
        existingRegimenCount,
        existingTreatmentTable
      });
      
      // If the existing data has the right number of regimens, return it as-is
      if (expectedRegimenCount > 0 && existingRegimenCount === expectedRegimenCount) {
        console.log('forceGenerateTreatmentTable - returning existing treatment table data as-is');
        return existingTreatmentTable;
      }
    }
    
    // Otherwise, generate new treatment table structure
    if (values?.study_treatments?.num_regimens > 0) {
      const { num_regimens, regimen_arms } = values.study_treatments;
      console.log('forceGenerateTreatmentTable - generating new table for num_regimens:', num_regimens, 'regimen_arms:', regimen_arms);
      const treatmentTable = {};
      const treatmentFields = [
        'dose_frequency', 'duration', 'fda_pregnancy_risk_category', 'dose_modifications', 'overdose_management',
        'preparation_storage_handling_accountability',
        'preparation_of_investigational_trial_intervention',
        'storage_handling_of_investigational_trial_intervention',
        'accountability_of_investigational_trial_intervention',
        'experimental_or_control', 'con_meds_allowed', 'con_meds_prohibited', 'investigational_trial_intervention_adherence',
        'rescue_medication',
        'adverse_events_special_interest',
        'disease_related_events_outcomes',
      ];
      
      for (let regimenNum = 1; regimenNum <= num_regimens; regimenNum++) {
        const regimenKey = `regimen${regimenNum}`;
        treatmentTable[regimenKey] = {};
        
        // Get agent name from regimen_arms for this regimen
        const agentName = regimen_arms && regimen_arms[regimenNum] ? regimen_arms[regimenNum] : '';
        const regimenDisplayName = formatRegimenColumnHeader(regimenNum, agentName);
        
        // Add regimen name field with agent name in brackets
        treatmentTable[regimenKey]['regimen_name'] = regimenDisplayName;
        
        // Add all treatment condition fields - preserve existing AI data if available
        treatmentFields.forEach(field => {
          // Check if we have existing AI data for this field
          const existingValue =
            existingTreatmentTable?.[regimenKey]?.[field];
          treatmentTable[regimenKey][field] = normalizeIntakeCellValue(existingValue) || '';
        });
      }
      
      console.log('forceGenerateTreatmentTable - returning newly generated treatmentTable:', treatmentTable);
      return treatmentTable;
    }
    console.log('forceGenerateTreatmentTable - returning empty object (num_regimens <= 0)');
    return {};
  };
  
  // Generate treatment table if we have regimens (regimen_arms is optional for AI auto-population)
  console.log('getInitialValues - checking treatment table generation:', {
    num_regimens: mergedValues.study_treatments?.num_regimens,
    regimen_arms: mergedValues.study_treatments?.regimen_arms,
    has_regimen_arms: !!mergedValues.study_treatments?.regimen_arms,
    regimen_arms_keys: Object.keys(mergedValues.study_treatments?.regimen_arms || {}),
    condition_met: mergedValues.study_treatments?.num_regimens > 0
  });
  
  // FIXED: Generate treatment table if we have regimens (regimen_arms can be empty for AI auto-population)
  if (mergedValues.study_treatments?.num_regimens > 0) {
    const { num_regimens, regimen_arms } = mergedValues.study_treatments;
    
    // Check if treatment table needs to be generated
    const currentRegimenCount = Object.keys(mergedValues.study_treatments.treatment_table || {}).length;
    const expectedRegimenCount = num_regimens;
    
    console.log('getInitialValues - checking if treatment table needs generation:', { 
      num_regimens, 
      currentRegimenCount, 
      expectedRegimenCount,
      regimen_arms,
      hasExistingTable: !!mergedValues.study_treatments.treatment_table,
      existingTableKeys: Object.keys(mergedValues.study_treatments.treatment_table || {})
    });
    
    // Only generate treatment table if we don't have one or if it's incomplete
    if (!mergedValues.study_treatments.treatment_table || 
        Object.keys(mergedValues.study_treatments.treatment_table).length === 0 ||
        currentRegimenCount !== expectedRegimenCount) {
      
      console.log('Generating treatment table in getInitialValues:', { 
        reason: !mergedValues.study_treatments.treatment_table ? 'no existing table' : 
                Object.keys(mergedValues.study_treatments.treatment_table).length === 0 ? 'empty table' :
                'regimen count mismatch'
      });
      
      // Force generate treatment table (handles AI auto-population) - preserve existing AI data
      const existingTable = mergedValues.study_treatments.treatment_table || {};
      const treatmentTable = forceGenerateTreatmentTable(mergedValues, existingTable);
      mergedValues.study_treatments.treatment_table = treatmentTable;
      console.log('getInitialValues - Generated treatment table with AI data:', treatmentTable);
      console.log('getInitialValues - existingTable was:', existingTable);
    } else {
      console.log('getInitialValues - Preserving existing treatment table data:', mergedValues.study_treatments.treatment_table);
    }
  } else {
    console.log('getInitialValues - Condition not met for treatment table generation');
  }

  const numPartsInit = parseInt(mergedValues.study_overview?.number_of_study_parts_new) || 0;
  const numRegInit = parseInt(mergedValues.study_treatments?.num_regimens) || 0;
  mergedValues.study_overview.trial_schema_configuration = normalizeTrialSchemaConfiguration(
    buildTrialSchemaFromLegacy(mergedValues.study_overview, mergedValues.study_treatments)
  );
  if (numPartsInit > 0 && numRegInit > 0) {
    mergedValues.study_treatments.regimens_per_part = distributeRegimensAcrossParts(
      numPartsInit,
      numRegInit,
      mergedValues.study_treatments?.regimens_per_part || {}
    );
  } else if (numPartsInit === 0 && numRegInit > 0) {
    mergedValues.study_treatments.regimens_per_part = { part1: numRegInit };
  }

  mergedValues.study_overview.study_parts = backfillStudyPartsCommitteesFromRegulatory(
    mergedValues.study_overview?.study_parts,
    mergedValues.regulatory_requirements?.committees
  );
  
  // Migrate old document_uploads format to new table format if needed
  if (mergedValues.document_uploads) {
    const uploadedFiles = mergedValues.document_uploads.uploaded_files || {};
    const comments = mergedValues.document_uploads.comments || {};
    
    // If document_table doesn't exist or is empty, but we have uploaded files, migrate
    if ((!mergedValues.document_uploads.document_table || mergedValues.document_uploads.document_table.length === 0) && 
        Object.keys(uploadedFiles).some(key => uploadedFiles[key] && uploadedFiles[key].length > 0)) {
      const documentTable = [];
      const documentTypes = [
        'study_design_outline',
        'investigator_brochure',
        'label',
        'additional_reports',
        'pharmacy_manual',
        'risk_management_guidelines',
        'user_defined',
        'control_arm_documents',
        'disease_background_documents'
      ];
      
      documentTypes.forEach(docType => {
        if (uploadedFiles[docType] && uploadedFiles[docType].length > 0) {
          documentTable.push({
            documentType: docType,
            additionalDetails: comments[docType] || ''
          });
        }
      });
      
      if (documentTable.length > 0) {
        mergedValues.document_uploads.document_table = documentTable;
        console.log('Migrated old document format to table format:', documentTable);
      }
    }
  }
  
  return scrubIntakePlaceholdersFromDoc(mergedValues);
};

// Update the API endpoint
const SUBMIT_FORM_ENDPOINT = API_ENDPOINTS.clinicalIntake.submit;
// Component function
const ClinicalIntakeForm = ({ onSubmit, initialData, isViewMode, documentsOnly = false, hideDocuments = false, onAdminDataPopulated, onIntakeParsed }) => {
  const { id: formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('project');

  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin';

  const { currentProject, loading: projectLoading, error: projectError, loadProject } = useProject();

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      console.log('ClinicalIntakeForm: Loading project from URL:', projectIdFromUrl);
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [parsingWarningsState, setParsingWarningsState] = useState([]);
  const [showParsingWarnings, setShowParsingWarnings] = useState(false);
  const [pipelineSteps, setPipelineSteps] = useState([]);
  const [pipelineStepLabel, setPipelineStepLabel] = useState('');
  const [pipelineJobProgress, setPipelineJobProgress] = useState(0);
  const [autoPopulatingAdminTrialSchema, setAutoPopulatingAdminTrialSchema] = useState(false);
  const [autoPopulateSucceeded, setAutoPopulateSucceeded] = useState(false);
  const [intakeParseSucceeded, setIntakeParseSucceeded] = useState(false);
  const lastRationaleContextFingerprintRef = React.useRef('');

  const tryParseJsonObject = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return null;
    try {
      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const parseAnalysisSupport = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return {};
    try {
      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      // Backward compatibility: older values were free text
      return { summary: rawValue };
    }
  };

  const serializeAnalysisSupport = (obj) => {
    try {
      const cleaned = Object.fromEntries(
        Object.entries(obj || {}).filter(([, v]) => (typeof v === 'string' ? v.trim() !== '' : v != null))
      );
      return JSON.stringify(cleaned);
    } catch {
      return '';
    }
  };

   const ANALYSIS_SUBFIELD_ORDER = [
    'handling_of_data',
    'handling_of_missing_data',
    'sensitivity_analysis',
    'supplementary_analysis',
  ];

  const isAnalysisJsonBlob = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    return (
      ANALYSIS_SUBFIELD_ORDER.some((k) => Object.prototype.hasOwnProperty.call(obj, k)) ||
      Object.prototype.hasOwnProperty.call(obj, 'summary')
    );
  };

  /** Collapse AI/client fan-out where every enabled sub-key repeats the same text. */
  const ANALYSIS_NOT_PROVIDED = '';
  /** @deprecated Kept for HMR compatibility; sub-rows use `value`, not this placeholder. */
  const ANALYSIS_SUBFIELD_PLACEHOLDER = ANALYSIS_NOT_PROVIDED;

    const hasRealAnalysisText = (v) => {
    const t = v == null ? '' : String(v).trim();
    return t.length > 0 && t !== ANALYSIS_NOT_PROVIDED;
  };

    const ensureAnalysisFieldStructure = (obj, enabledMap) => {
    const enabledKeys = ANALYSIS_SUBFIELD_ORDER.filter((k) => enabledMap?.[k]);
    const base = obj && typeof obj === 'object' && !Array.isArray(obj) ? { ...obj } : {};

    base.summary = hasRealAnalysisText(base.summary)
      ? String(base.summary).trim()
      : ANALYSIS_NOT_PROVIDED;

    enabledKeys.forEach((k) => {
      base[k] = hasRealAnalysisText(base[k])
        ? String(base[k]).trim()
        : ANALYSIS_NOT_PROVIDED;
    });

    return base;
  };

  /**
   * Get the display value for the *parent* analysis field row (not a sub-field row).
   * Priority:
   *   1. `summary` key if present and non-empty
   *   2. Empty string when sub-keys have distinct content (sub-rows carry the detail)
   *   3. The shared text when all non-empty sub-keys contain the same value (collapsed state)
   */
  // NOTE: these return the field's own live-typed value untouched (no .trim()) once it has
  // real content — trimming here would strip a just-typed trailing space back off on every
  // re-render, since the return value becomes the TextField's controlled `value`.
  // hasRealAnalysisText() still trims internally for its own emptiness check only.
  const getParentAnalysisDisplayValue = (obj, enabledMap) => {
    if (!obj || typeof obj !== 'object') return ANALYSIS_NOT_PROVIDED;
    const enabledKeys = ANALYSIS_SUBFIELD_ORDER.filter((k) => enabledMap?.[k]);
    const subsAnyFilled = enabledKeys.some((k) => hasRealAnalysisText(obj[k]));
    if (subsAnyFilled) return ANALYSIS_NOT_PROVIDED;
    return hasRealAnalysisText(obj.summary) ? String(obj.summary) : ANALYSIS_NOT_PROVIDED;
  };

  const buildAnalysisJsonFromPlainText = (raw, enabledMap) => {
    const enabledKeys = ANALYSIS_SUBFIELD_ORDER.filter((k) => enabledMap?.[k]);
    if (enabledKeys.length === 0) return raw;
    const trimmed = String(raw).trim();
    const out = {};
    if (hasRealAnalysisText(trimmed)) {
      out.summary = ANALYSIS_NOT_PROVIDED;
      enabledKeys.forEach((k, index) => {
        out[k] = index === 0 ? trimmed : ANALYSIS_NOT_PROVIDED;
      });
    }
    return serializeAnalysisSupport(ensureAnalysisFieldStructure(out, enabledMap));
  };

  const normalizeStoredAnalysisField = (obj, enabledMap) =>
    ensureAnalysisFieldStructure(obj, enabledMap);

  const getAnalysisSubFieldDisplayValue = (analysisObj, subKey) => {
    const direct = analysisObj?.[subKey];
    return hasRealAnalysisText(direct) ? String(direct) : ANALYSIS_NOT_PROVIDED;
  };

  const formatPlainAnalysisParentDisplay = (rawValue) => {
    const t = rawValue == null ? '' : String(rawValue);
    return t.trim().length > 0 ? t : ANALYSIS_NOT_PROVIDED;
  };

  const parseRescueMedication = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return {};
    try {
      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return { rescue_medication: rawValue };
    }
  };

  const serializeRescueMedication = (obj) => {
    try {
      const cleaned = Object.fromEntries(
        Object.entries(obj || {}).filter(([, v]) =>
          typeof v === 'string' ? v.trim() !== '' : v != null
        )
      );
      return JSON.stringify(cleaned);
    } catch {
      return '';
    }
  };

  const rescueEnabledSnakeKeys = (enabledMap) => {
    const keys = [];
    if (enabledMap?.backgroundTrialIntervention) keys.push('background_trial_intervention');
    if (enabledMap?.otherNonInvestigationalIntervention) {
      keys.push('other_noninvestigational_trial_intervention');
    }
    return keys;
  };

  const ensureRescueMedicationStructure = (obj, enabledMap) => {
    const enabledKeys = rescueEnabledSnakeKeys(enabledMap);
    const base = obj && typeof obj === 'object' && !Array.isArray(obj) ? { ...obj } : {};

    base.rescue_medication = hasRealAnalysisText(base.rescue_medication)
      ? String(base.rescue_medication).trim()
      : ANALYSIS_NOT_PROVIDED;

    enabledKeys.forEach((k) => {
      base[k] = hasRealAnalysisText(base[k])
        ? String(base[k]).trim()
        : ANALYSIS_NOT_PROVIDED;
    });

    return base;
  };

  const normalizeStoredRescueField = (obj, enabledMap) =>
    ensureRescueMedicationStructure(obj, enabledMap);

  const buildRescueJsonFromPlainText = (raw, enabledMap) => {
    const enabledKeys = rescueEnabledSnakeKeys(enabledMap);
    if (enabledKeys.length === 0) return raw;
    const trimmed = String(raw).trim();
    const out = {};
    if (hasRealAnalysisText(trimmed)) {
      out.rescue_medication = ANALYSIS_NOT_PROVIDED;
      enabledKeys.forEach((k, index) => {
        out[k] = index === 0 ? trimmed : ANALYSIS_NOT_PROVIDED;
      });
    }
    return serializeRescueMedication(ensureRescueMedicationStructure(out, enabledMap));
  };

  // NOTE: these three return the field's own live-typed value untouched (no .trim()) once
  // it has real content — trimming here would strip a just-typed trailing space back off on
  // every re-render, since this return value becomes the TextField's controlled `value`.
  // hasRealAnalysisText() still trims internally for its own emptiness check, which is fine
  // since that copy is never what's rendered back.
  const getParentRescueDisplayValue = (obj, enabledMap) => {
    if (!obj || typeof obj !== 'object') return ANALYSIS_NOT_PROVIDED;
    const enabledKeys = rescueEnabledSnakeKeys(enabledMap);
    const subsAnyFilled = enabledKeys.some((k) => hasRealAnalysisText(obj[k]));
    if (subsAnyFilled) return ANALYSIS_NOT_PROVIDED;
    return hasRealAnalysisText(obj.rescue_medication)
      ? String(obj.rescue_medication)
      : ANALYSIS_NOT_PROVIDED;
  };

  const getRescueSubFieldDisplayValue = (rescueObj, subKey) => {
    const direct = rescueObj?.[subKey];
    return hasRealAnalysisText(direct) ? String(direct) : ANALYSIS_NOT_PROVIDED;
  };

  const formatPlainRescueParentDisplay = (rawValue) => {
    const t = rawValue == null ? '' : String(rawValue);
    return t.trim().length > 0 ? t : ANALYSIS_NOT_PROVIDED;
  };

  // Inline editing for hover/help text (persisted with the intake record)
  const [hoverHelpDialogOpen, setHoverHelpDialogOpen] = useState(false);
  const [hoverHelpDraft, setHoverHelpDraft] = useState('');
  
  // File validation state - tracks readability status of each uploaded file
  const [fileValidationStatus, setFileValidationStatus] = useState({}); // { filename: { isValid: true/false, reason: '' } }
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);
  const [allFilesValid, setAllFilesValid] = useState(false);
  const [validationCompleted, setValidationCompleted] = useState(false);
  const validationInFlightRef = React.useRef(false);
  const lastValidatedFingerprintRef = React.useRef('');
  const allFilesValidRef = React.useRef(false);

  // Document summaries generated immediately on upload (keyed by `${azureBlobName}_${documentType}`)
  const [documentSummaries, setDocumentSummaries] = useState({});
  const [summarizingDocs, setSummarizingDocs] = useState({}); // tracks which docs are being summarized
  const [appendixUploadingIndex, setAppendixUploadingIndex] = useState(null);
  
  // PDF Viewer Dialog state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfTabs, setPdfTabs] = useState([]); // [{ url, name }]
  const [activePdfIndex, setActivePdfIndex] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [renderedPages, setRenderedPages] = useState([]); // Array of canvas refs for all pages
  const canvasRefs = React.useRef([]); // Array to hold refs for all page canvases
  
  // Load PDF when URL or active tab changes
  useEffect(() => {
    const loadPdf = async () => {
      if (!pdfUrl) return;
      
      setPdfLoading(true);
      setPdfError(null);
      setRenderedPages([]);
      canvasRefs.current = [];
      
      try {
        // pdfUrl here is always our own /api/documents/download (auth-gated —
        // SEC-001); pdf.js's fetch needs the Authorization header passed explicitly.
        const token = getToken();
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          isEvalSupported: false,
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const loadedPdf = await loadingTask.promise;
        setPdfDoc(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setPageNumber(1);
        
        // Initialize canvas refs array - use objects to store canvas elements
        canvasRefs.current = new Array(loadedPdf.numPages).fill(null);
        setRenderedPages(new Array(loadedPdf.numPages).fill(false));
        
        setPdfLoading(false);
      } catch (error) {
        console.error('PDF load error:', error);
        setPdfError('Failed to load PDF. Please try downloading the file instead.');
        setPdfLoading(false);
      }
    };
    
    loadPdf();
  }, [pdfUrl]);
  
  // Render all PDF pages when document loads and dialog is open
  useEffect(() => {
    if (!pdfDoc || numPages === 0 || !pdfViewerOpen) return;
    
    let renderTasks = [];
    let cancelled = false;
    
    const renderAllPages = async () => {
      // Wait for all canvases to be mounted
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds
      while (attempts < maxAttempts) {
        const allCanvasesReady = Array.from({ length: numPages }, (_, i) => canvasRefs.current[i]).every(canvas => canvas !== null);
        if (allCanvasesReady) break;
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
        if (cancelled) return;
      }
      
      const container = document.querySelector('[class*="MuiDialogContent"]');
      const containerWidth = container ? container.clientWidth - 80 : 1200;
      
      renderTasks = [];
      
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        if (cancelled) break;
        
        const pageIndex = pageNum - 1;
        const canvas = canvasRefs.current[pageIndex];
        
        if (!canvas) continue;
        
        try {
          const page = await pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.0 });
          const scale = Math.min(containerWidth / viewport.width, 4.0);
          const scaledViewport = page.getViewport({ scale });
          
          const context = canvas.getContext('2d');
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          
          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport
          };
          
          const renderTask = page.render(renderContext);
          renderTasks.push(renderTask);
          
          renderTask.promise.then(() => {
            if (!cancelled) {
              setRenderedPages(prev => {
                const newRendered = [...prev];
                newRendered[pageIndex] = true;
                return newRendered;
              });
            }
          }).catch(error => {
            if (error.name !== 'RenderingCancelledException' && !cancelled) {
              console.error(`Error rendering PDF page ${pageNum}:`, error);
            }
          });
        } catch (error) {
          if (error.name !== 'RenderingCancelledException' && !cancelled) {
            console.error(`Error rendering PDF page ${pageNum}:`, error);
          }
        }
      }
      
      await Promise.all(renderTasks.map(t => t.promise));
    };
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      renderAllPages();
    }, 100);
    
    // Add resize listener to re-render when dialog is resized
    const handleResize = () => {
      if (pdfDoc && numPages > 0 && !cancelled) {
        renderAllPages();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      renderTasks.forEach(task => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
    };
  }, [pdfDoc, numPages, pdfViewerOpen]);
  
  // Template-related state
  const [templateData, setTemplateData] = useState(null);
  const [templateSelections, setTemplateSelections] = useState({});
  const templateSelectionsRef = React.useRef({});
  const [fieldTemplateSelections, setFieldTemplateSelections] = useState({});
  const fieldTemplateSelectionsRef = React.useRef({});

  React.useEffect(() => {
    templateSelectionsRef.current = templateSelections;
  }, [templateSelections]);

  React.useEffect(() => {
    fieldTemplateSelectionsRef.current = fieldTemplateSelections;
  }, [fieldTemplateSelections]);

  const getMergedTemplateSelections = () => ({
    ...fieldTemplateSelectionsRef.current,
    ...templateSelectionsRef.current,
  });

  const syncFieldTemplateSelections = (fieldName, isSelected) => {
    const next = { ...fieldTemplateSelectionsRef.current };
    if (isSelected) {
      next[fieldName] = true;
    } else {
      delete next[fieldName];
    }
    fieldTemplateSelectionsRef.current = next;
    setFieldTemplateSelections(next);
  };

  const buildPersistableIntakeValues = (fieldPath, text) => {
    const merged = consolidateIntakeValuesForSave({
      loaded: loadedInitialData || formData,
      snapshot: lastPopulatedIntakeRef.current,
      form: formikValuesRef.current,
      templateSelections: templateSelectionsRef.current,
      fieldTemplateSelections: fieldTemplateSelectionsRef.current,
    });
    return setNestedFormField(merged, fieldPath, text);
  };

  const persistTemplateFieldChoice = async (fieldPath, text, fieldName, isSelected, setFieldValue) => {
    setFieldValue(fieldPath, text);
    // Keep ref in sync immediately — Formik setState is async and quiet saves read this ref.
    if (formikValuesRef.current) {
      formikValuesRef.current = setNestedFormField(formikValuesRef.current, fieldPath, text);
    }

    const nextSelections = { ...templateSelectionsRef.current };
    if (isSelected) {
      nextSelections[fieldName] = true;
    } else {
      delete nextSelections[fieldName];
    }
    templateSelectionsRef.current = nextSelections;
    setTemplateSelections(nextSelections);
    syncFieldTemplateSelections(fieldName, isSelected);

    const valuesWithTemplate = buildPersistableIntakeValues(fieldPath, text);
    rememberPopulatedIntakeSnapshot(valuesWithTemplate);

    // Do NOT call setLoadedInitialData/setFormData here. enableReinitialize would remount
    // Formik initialValues and wipe the just-generated Section 9/11 template text.

    if (currentProject?._id && handleSaveAsDraftRef.current) {
      try {
        await handleSaveAsDraftRef.current(valuesWithTemplate, {
          manageLoading: false,
          quiet: true,
          preferClientTemplateFields: true,
          skipFormReinitialize: true,
          section9TemplateSelections: nextSelections,
          fieldTemplateSelections: fieldTemplateSelectionsRef.current,
        });
      } catch (err) {
        const errMsg =
          (typeof err === 'object' && err?.message) ||
          (typeof err === 'string' ? err : 'Save failed');
        console.warn('Failed to persist template field choice:', fieldName, err);
        setSnackbarMessage(
          `Template text was generated but could not be saved: ${errMsg}. Use Save draft / Submit to retry.`
        );
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    }
  };

  const applyTemplateBulk = (fieldKeys, mode, currentValues, setValuesFn, section = 'template_language_fields') => {
    if (mode === 'load' && !templateData) return;
    const newValues = { ...currentValues };
    if (!newValues[section]) {
      newValues[section] = {};
    }
    const newSelections = { ...templateSelections };
    const resolveTemplateText = (fieldName) => {
      const fromCache =
        templateData?.[fieldName]?.content || templateData?.[fieldName]?.userSuggestion || '';
      if (fromCache) return stripHtmlToPlainText(fromCache);
      const baseTitle = TEMPLATE_INTAKE_FIELD_META[fieldName]?.baseTitle;
      if (baseTitle && templateLanguageUpdatedBase[baseTitle]) {
        return stripHtmlToPlainText(templateLanguageUpdatedBase[baseTitle]);
      }
      return '';
    };
    fieldKeys.forEach((fieldName) => {
      if (mode === 'load') {
        const templateContent = resolveTemplateText(fieldName);
        if (templateContent) {
          newValues[section][fieldName] = templateContent;
          newSelections[fieldName] = true;
        }
      } else {
        newValues[section][fieldName] = '';
        delete newSelections[fieldName];
        syncFieldTemplateSelections(fieldName, false);
      }
    });
    if (mode === 'load') {
      Object.keys(newSelections).forEach((fieldName) => {
        if (newSelections[fieldName]) {
          syncFieldTemplateSelections(fieldName, true);
        }
      });
    }
    setTemplateSelections(newSelections);
    templateSelectionsRef.current = newSelections;
    setValuesFn(newValues);
    setLoadedInitialData(sanitizeValue(newValues));
    setFormData(sanitizeValue(newValues));
    rememberPopulatedIntakeSnapshot(newValues);
    if (currentProject?._id && handleSaveAsDraftRef.current) {
      handleSaveAsDraftRef.current(newValues, {
        manageLoading: false,
        quiet: true,
        section9TemplateSelections: newSelections,
        fieldTemplateSelections: fieldTemplateSelectionsRef.current,
      }).catch((err) => console.warn('Failed to persist bulk template change:', err));
    }
  };
  /** When set, template "Use template" checkboxes are disabled to avoid overlapping LLM calls. */
  const [templateLlmLoadingField, setTemplateLlmLoadingField] = useState(null);
  /** Monotonic id so stale/overlapping regenerate completions cannot wipe newer text. */
  const templateLlmGenerationIdRef = React.useRef(0);
  const templateLlmInFlightFieldRef = React.useRef(null);
  //const [templateLoading, setTemplateLoading] = useState(false);

  // SOA State Variables
  const [generatedSoAData, setGeneratedSoAData] = useState(null);
  const [generatingSoA, setGeneratingSoA] = useState(false);
  const [generatingOfficialTitle, setGeneratingOfficialTitle] = useState(false);
  const [generateSoAError, setGenerateSoAError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLocked, setFormLocked] = useState(false);
  const [activeProtocolSection, setActiveProtocolSection] = useState(PROTOCOL_INTAKE_NAV[0].id);
  const [protocolSectionExpanded, setProtocolSectionExpanded] = useState({
    trialIdentifier: true,
    trialSchema: true,
    trialDesign: true,
    arguments: true,
  });
  const protocolSectionRefs = React.useRef({});

  const scrollToProtocolSection = React.useCallback((sectionId) => {
    const navItem = PROTOCOL_INTAKE_NAV.find((s) => s.id === sectionId);
    if (!navItem) return;
    setActiveProtocolSection(sectionId);
    if (navItem.accordionKey) {
      setProtocolSectionExpanded((prev) => ({ ...prev, [navItem.accordionKey]: true }));
    }
    window.setTimeout(() => {
      protocolSectionRefs.current[sectionId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, navItem.accordionKey ? 150 : 0);
  }, []);

  // Field-level Template Selection State Variables
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const templateCheckboxDisabled = (isViewMode || formLocked) || templateLoading;
  const isTemplateCheckboxDisabled = (fieldName) =>
    templateCheckboxDisabled || Boolean(templateLlmLoadingField);
  const [isCheckingLock, setIsCheckingLock] = useState(true);
  // Add this state with the other useState hooks
  const [formData, setFormData] = useState(null);
  const [loadedInitialData, setLoadedInitialData] = useState(null);
  /** Prevent draft autosave from writing empty defaults before /latest finishes hydrating. */
  const intakeHydratedRef = React.useRef(false);
  // Ref to hold current Formik values so Lock sends unsaved edits (e.g. phase) instead of stale server data
  const formikValuesRef = React.useRef(null);
  const setFieldValueRef = React.useRef(null);
  const handleSaveAsDraftRef = React.useRef(null);
  const lastPopulatedTrialSchemaRef = React.useRef(null);
  const lastPopulatedIntakeRef = React.useRef(null);
  const initialDataRef = React.useRef(initialData);
  /**
   * Memoized so Formik's `enableReinitialize` reset effect only re-evaluates when
   * loadedInitialData/initialData actually change. Previously this was rebuilt via an
   * inline IIFE inline in JSX on every render, creating a fresh object identity each time.
   * Formik deep-compares before resetting, but recomputing `getInitialValues` from
   * scratch on every re-render (e.g. sibling state changes while a user is mid-typing in
   * a field) still runs the full transform every render and is fragile — the same
   * "enableReinitialize wipes in-progress edits" issue already documented for autosave
   * (search "wiping Section 9 CPT text" in this file) can show up as edits (including
   * whitespace, which is easy to lose unnoticed) getting reverted while typing.
   */
  const formikInitialValues = React.useMemo(
    () => getInitialValues(loadedInitialData || initialData),
    [loadedInitialData, initialData]
  );

  React.useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);
  
  // Background Information Generation State Variables
  const [generatingBackgroundInfo, setGeneratingBackgroundInfo] = useState(false);
  const [backgroundInfoProgress, setBackgroundInfoProgress] = useState('');
  const [backgroundInfoError, setBackgroundInfoError] = useState(null);
  const [customRegenerationLoading, setCustomRegenerationLoading] = useState({});


  // Rationale Field Relevance State Variables
  const [rationaleFieldRelevance, setRationaleFieldRelevance] = useState({
    rationale_for_estimands: false,
    rationale_for_interventional_type: false,
    rationale_for_control_method: false,
    rationale_for_trial_duration: false,
    rationale_for_adaptive_design: false,
    rationale_for_interim_analysis: false,
    rationale_for_other_design_aspects: false,
    rationale_for_trial_population: false
  });
  const [determiningRationaleRelevance, setDeterminingRationaleRelevance] = useState(false);
  const [assessmentHintDialog, setAssessmentHintDialog] = useState({ open: false, title: '', content: '', fieldKey: '' });
  const [lifestyleRestrictionEnabledSubfields, setLifestyleRestrictionEnabledSubfields] = useState({
    meals: false,
    caffeine: false,
    physical: false,
    other: false
  });
  const [investigationalInterventionHandlingEnabledSubfields, setInvestigationalInterventionHandlingEnabledSubfields] = useState({
    preparation: false,
    storageHandling: false,
    accountability: false
  });
  const [rescueMedicationEnabledSubfields, setRescueMedicationEnabledSubfields] = useState({
    backgroundTrialIntervention: false,
    otherNonInvestigationalIntervention: false
  });
  const [primaryObjectiveAnalysisEnabledSubfields, setPrimaryObjectiveAnalysisEnabledSubfields] = useState({
    handling_of_data: false,
    handling_of_missing_data: false,
    sensitivity_analysis: false,
    supplementary_analysis: false
  });
  const [secondaryObjectiveAnalysisEnabledSubfields, setSecondaryObjectiveAnalysisEnabledSubfields] = useState({
    handling_of_data: false,
    handling_of_missing_data: false,
    sensitivity_analysis: false,
    supplementary_analysis: false
  });

  // Pre-parse chooser: lets user enable optional combined-field sub-rows BEFORE parsing starts
  const [preParseOptionsOpen, setPreParseOptionsOpen] = useState(false);
  const preParseArgsRef = React.useRef(null);
  const preParseBypassOnceRef = React.useRef(false);

  const FIELD_HOVER_INFO = CLINICAL_INTAKE_FIELD_HOVER_INFO;

  const getHoverInfo = React.useCallback((key) => FIELD_HOVER_INFO[key], [FIELD_HOVER_INFO]);

  const DOCUMENT_TYPE_HOVER_KEY = React.useMemo(
    () => ({
      study_design_outline: 'doc_upload_study_design_outline',
      investigator_brochure: 'doc_upload_investigator_brochure',
      label: 'doc_upload_label',
      additional_reports: 'doc_upload_additional_reports',
      pharmacy_manual: 'doc_upload_pharmacy_manual',
      risk_management_guidelines: 'doc_upload_risk_management_guidelines',
      user_defined: 'doc_upload_user_defined',
      control_arm_documents: 'doc_upload_control_arm_documents',
      disease_background_documents: 'doc_upload_disease_background_documents',
    }),
    []
  );

  const getStudyTableSubfieldSelectionsPayload = React.useCallback(
    () => ({
      lifestyle_restrictions: lifestyleRestrictionEnabledSubfields,
      investigational_intervention_handling: investigationalInterventionHandlingEnabledSubfields,
      rescue_medication: rescueMedicationEnabledSubfields,
      analyses_primary_objective: primaryObjectiveAnalysisEnabledSubfields,
      analyses_secondary_objective: secondaryObjectiveAnalysisEnabledSubfields
    }),
    [
      lifestyleRestrictionEnabledSubfields,
      investigationalInterventionHandlingEnabledSubfields,
      rescueMedicationEnabledSubfields,
      primaryObjectiveAnalysisEnabledSubfields,
      secondaryObjectiveAnalysisEnabledSubfields
    ]
  );

   const repairClubbedAnalysisFieldsInDoc = (doc) => {
    if (!doc || typeof doc !== 'object') return doc;
    if (doc?.study_overview?.study_parts) {
      migrateStudyPartsPopulationKeys(doc.study_overview.study_parts);
    }
    const sp = doc?.study_overview?.study_parts;
    if (!sp || typeof sp !== 'object') return doc;

    const selections = doc.studyTableSubfieldSelections || {};
    const primEnabled = selections.analyses_primary_objective || primaryObjectiveAnalysisEnabledSubfields;
    const secEnabled = selections.analyses_secondary_objective || secondaryObjectiveAnalysisEnabledSubfields;

    const repairAnalysisField = (cohort, fieldName, enabledMap) => {
      const anyEnabled = Object.values(enabledMap || {}).some(Boolean);
      if (!anyEnabled) return;

      const raw = cohort[fieldName];
      if (!raw || typeof raw !== 'string') {
        cohort[fieldName] = buildAnalysisJsonFromPlainText('', enabledMap);
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          cohort[fieldName] = buildAnalysisJsonFromPlainText(raw, enabledMap);
          return;
        }
        cohort[fieldName] = serializeAnalysisSupport(
          normalizeStoredAnalysisField(parsed, enabledMap)
        );
      } catch {
        cohort[fieldName] = buildAnalysisJsonFromPlainText(raw, enabledMap);
      }
    };

    Object.values(sp).forEach((part) => {
      if (!part || typeof part !== 'object') return;
      Object.values(part).forEach((cohort) => {
        if (!cohort || typeof cohort !== 'object') return;
        repairAnalysisField(cohort, 'analyses_primary_objective', primEnabled);
        repairAnalysisField(cohort, 'analyses_secondary_objective', secEnabled);
      });
    });

    normalizeStudyPartsParentSubFields(sp);

    const rescueEnabled =
      selections.rescue_medication || rescueMedicationEnabledSubfields;
    const anyRescueEnabled = Object.values(rescueEnabled || {}).some(Boolean);
    const tt = doc?.study_treatments?.treatment_table;
    if (anyRescueEnabled && tt && typeof tt === 'object') {
      const repairRescueOnRegimen = (regimen) => {
        if (!regimen || typeof regimen !== 'object') return;
        const raw = regimen.rescue_medication;
        if (!raw || typeof raw !== 'string') {
          regimen.rescue_medication = buildRescueJsonFromPlainText('', rescueEnabled);
          return;
        }
        try {
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            regimen.rescue_medication = buildRescueJsonFromPlainText(raw, rescueEnabled);
            return;
          }
          const legacyMap = {
            backgroundTrialIntervention: 'background_trial_intervention',
            otherNonInvestigationalIntervention: 'other_noninvestigational_trial_intervention',
          };
          const migrated = { ...parsed };
          Object.entries(legacyMap).forEach(([camel, snake]) => {
            if (
              Object.prototype.hasOwnProperty.call(migrated, camel) &&
              !Object.prototype.hasOwnProperty.call(migrated, snake)
            ) {
              migrated[snake] = migrated[camel];
              delete migrated[camel];
            }
          });
          regimen.rescue_medication = serializeRescueMedication(
            normalizeStoredRescueField(migrated, rescueEnabled)
          );
        } catch {
          regimen.rescue_medication = buildRescueJsonFromPlainText(raw, rescueEnabled);
        }
      };
      Object.values(tt).forEach(repairRescueOnRegimen);
    }

    const prepHandlingEnabled =
      selections.investigational_intervention_handling || investigationalInterventionHandlingEnabledSubfields;
    normalizeTreatmentTableParentSubFields(tt, prepHandlingEnabled);
    return doc;
  };

  const applyStudyTableSubfieldSelectionsFromServer = React.useCallback((doc) => {
    const raw = doc?.studyTableSubfieldSelections;
    if (!raw || typeof raw !== 'object') return;
    if (raw.lifestyle_restrictions && typeof raw.lifestyle_restrictions === 'object') {
      setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, ...raw.lifestyle_restrictions }));
    }
    if (raw.investigational_intervention_handling && typeof raw.investigational_intervention_handling === 'object') {
      setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({ ...prev, ...raw.investigational_intervention_handling }));
    }
    if (raw.rescue_medication && typeof raw.rescue_medication === 'object') {
      setRescueMedicationEnabledSubfields((prev) => ({ ...prev, ...raw.rescue_medication }));
    }
    if (raw.analyses_primary_objective && typeof raw.analyses_primary_objective === 'object') {
      setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, ...raw.analyses_primary_objective }));
    }
    if (raw.analyses_secondary_objective && typeof raw.analyses_secondary_objective === 'object') {
      setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, ...raw.analyses_secondary_objective }));
    }
  }, []);

  // Function to determine which rationale fields are relevant based on trial design and return the result
  const determineRationaleFieldRelevanceAndReturn = async (values) => {
    setDeterminingRationaleRelevance(true);
    try {
      const prompt = BACKGROUND_PROMPTS.determine_rationale_relevance(values);
      console.log('Sending rationale relevance determination prompt:', prompt);
      
      const response = await axiosInstance.post(
        API_ENDPOINTS.ai.generateBackgroundInfo,
        { prompt },
        { timeout: AI_LONG_REQUEST_TIMEOUT_MS }
      );
      console.log('Raw AI response for rationale relevance:', response);
      console.log('Response data structure:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'no data',
        dataValue: response.data
      });
      
      if (response && response.data) {
        // Handle different response structures
        let relevanceText;
        if (response.data.success && response.data.result) {
          relevanceText = response.data.result; // Backend returns text in 'result' field
        } else if (response.data.result) {
          relevanceText = response.data.result;
        } else if (response.data.success && response.data.data) {
          relevanceText = response.data.data;
        } else if (response.data.data) {
          relevanceText = response.data.data;
        } else if (typeof response.data === 'string') {
          relevanceText = response.data;
        } else {
          relevanceText = JSON.stringify(response.data);
        }
        console.log('Rationale relevance determination response text:', relevanceText);
        
        // More robust parsing with multiple patterns
        const parseRelevance = (text, sectionNumber) => {
          // Try multiple patterns to catch different response formats
          const patterns = [
            new RegExp(`${sectionNumber}.*?Yes`, 'i'),
            new RegExp(`${sectionNumber}.*?applicable.*?Yes`, 'i'),
            new RegExp(`\\*.*?${sectionNumber}.*?Yes`, 'i'),
            new RegExp(`${sectionNumber}.*?\\[.*?Yes.*?\\]`, 'i')
          ];
          
          return patterns.some(pattern => pattern.test(text));
        };
        
        // Parse the response to extract Yes/No values for each subsection
        const newRelevance = {
          rationale_for_estimands: parseRelevance(relevanceText, '4.2.1'),
          rationale_for_interventional_type: parseRelevance(relevanceText, '4.2.2'),
          rationale_for_control_method: parseRelevance(relevanceText, '4.2.3'),
          rationale_for_trial_duration: parseRelevance(relevanceText, '4.2.4'),
          rationale_for_adaptive_design: parseRelevance(relevanceText, '4.2.5'),
          rationale_for_interim_analysis: parseRelevance(relevanceText, '4.2.6'),
          rationale_for_other_design_aspects: parseRelevance(relevanceText, '4.2.7'),
          rationale_for_trial_population: parseRelevance(relevanceText, '4.2.8')
        };
        
        setRationaleFieldRelevance(newRelevance);
        console.log('Updated rationale field relevance:', newRelevance);
        console.log('Relevant fields count:', Object.values(newRelevance).filter(Boolean).length);
        console.log('Relevant fields:', Object.keys(newRelevance).filter(key => newRelevance[key]));
        
        // If no fields were marked as relevant, use intelligent defaults
        if (Object.values(newRelevance).every(value => !value)) {
          console.warn('No rationale fields were marked as relevant. Using intelligent defaults based on trial context.');
          console.warn('Full AI response:', relevanceText);
          
          // Intelligent defaults based on common trial patterns
          const intelligentDefaults = {
            rationale_for_estimands: true, // Almost always relevant for primary endpoints
            rationale_for_interventional_type: false, // Usually standard
            rationale_for_control_method: values.study_treatments?.control_regimen ? true : false, // Relevant if control is specified
            rationale_for_trial_duration: values.study_overview?.study_duration ? true : false, // Relevant if duration specified
            rationale_for_adaptive_design: /adaptive|novel|interim/i.test(JSON.stringify(values.study_overview?.study_design || {})), // Check for adaptive elements
            rationale_for_interim_analysis: /interim|stopping|futility/i.test(JSON.stringify(values.study_overview?.study_design || {})), // Check for interim analysis
            rationale_for_other_design_aspects: false, // Usually not needed
            rationale_for_trial_population: values.target_population?.conditions_related_to_primary_disease ? true : false // Relevant if population specified
          };
          
          setRationaleFieldRelevance(intelligentDefaults);
          console.log('Applied intelligent defaults:', intelligentDefaults);
          return intelligentDefaults;
        }
        
        return newRelevance;
        
      } else {
        console.error('Invalid response from AI service:', response);
        throw new Error('Invalid response from AI service');
      }
    } catch (error) {
      console.error('Error determining rationale field relevance:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Default to showing all fields if determination fails
      const fallbackRelevance = {
        rationale_for_estimands: true,
        rationale_for_interventional_type: true,
        rationale_for_control_method: true,
        rationale_for_trial_duration: true,
        rationale_for_adaptive_design: true,
        rationale_for_interim_analysis: true,
        rationale_for_other_design_aspects: true,
        rationale_for_trial_population: true
      };
      
      setRationaleFieldRelevance(fallbackRelevance);
      return fallbackRelevance;
    } finally {
      setDeterminingRationaleRelevance(false);
    }
  };

  useEffect(() => {
    if (!currentProject?._id) return;
    const values = loadedInitialData || initialData;
    if (!values?.document_uploads) return;

    const fingerprint = buildValidationFingerprint(values);
    const persisted = loadDocumentValidationState(
      currentProject._id,
      values.document_uploads,
      fingerprint
    );

    const summaries = {};
    const uploaded = values.document_uploads.uploaded_files || {};
    Object.entries(uploaded).forEach(([docType, arr]) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((f) => {
        if (!f?.contentSummary?.trim()) return;
        [f.azureBlobName, f.filename, f.name].filter(Boolean).forEach((base) => {
          summaries[`${base}_${docType}`] = f.contentSummary;
        });
      });
    });
    if (Object.keys(summaries).length > 0) {
      setDocumentSummaries((prev) => ({ ...prev, ...summaries }));
    }

    if (persisted) {
      setFileValidationStatus(flattenFileStatusMap(persisted.validationMap || {}));
      setAllFilesValid(Boolean(persisted.allValid));
      allFilesValidRef.current = Boolean(persisted.allValid);
      setValidationCompleted(true);
      lastValidatedFingerprintRef.current = fingerprint;
      if (persisted.source === 'server') {
        saveDocumentValidationCache(currentProject._id, {
          fingerprint,
          allValid: persisted.allValid,
          validationMap: flattenFileStatusMap(persisted.validationMap || {}),
        });
      }
    }

    if (values.document_uploads.source_documents_workflow?.auto_populate_succeeded) {
      setAutoPopulateSucceeded(true);
    }

    if (values.document_uploads.source_documents_workflow?.intake_parse_succeeded) {
      setIntakeParseSucceeded(true);
    }
  }, [currentProject?._id, loadedInitialData, initialData]);

  // Function to determine which rationale fields are relevant based on trial design (original function for useEffect)
  const determineRationaleFieldRelevance = async (values) => {
    await determineRationaleFieldRelevanceAndReturn(values);
  };

  // Function to check if form is locked and load submitted data
  const syncLockFromIntakeDoc = (doc) => {
    if (!doc || typeof doc !== 'object') return;
    if (typeof doc.isLocked === 'boolean') {
      setFormLocked(doc.isLocked);
    }
    if (doc.status) {
      setFormSubmitted(doc.status === 'submitted');
    }
  };

  const checkFormLockStatus = async () => {
    if (!currentProject?._id) return;
    
    try {
      setIsCheckingLock(true);
      intakeHydratedRef.current = false;
      const submittedData = await clinicalIntakeService.fetchLatestClinicalIntakeData(currentProject._id);
      
      if (submittedData) {
        // PRIMARY source of truth: backend isLocked flag and status
        const hasIsLockedFlag = typeof submittedData.isLocked === 'boolean';
        const backendLocked = hasIsLockedFlag ? submittedData.isLocked : false;
        const backendStatus = submittedData.status || 'draft';

        // For legacy projects without isLocked, fall back to old heuristic once
        let effectiveLocked = backendLocked;
        if (!hasIsLockedFlag) {
          const legacyShouldLock = await clinicalIntakeService.isClinicalIntakeSubmitted(currentProject._id);
          effectiveLocked = legacyShouldLock;
        }

        setFormLocked(effectiveLocked);
        setFormSubmitted(backendStatus === 'submitted');
        
        console.log('Loaded submitted data in checkFormLockStatus:', submittedData); // Debug log
        console.log('Form lock status - backendLocked:', backendLocked, 'effectiveLocked:', effectiveLocked, 'status:', backendStatus);
        console.log(
          'Section 7 nests on load:',
          {
            tempDecisions: submittedData.discontinuation_rules?.temporary_discontinuation_decisions,
            rechallengeDecisions: submittedData.discontinuation_rules?.rechallenge_decisions,
            liverDecisions: submittedData.template_language_fields?.liver_patient_library_decisions,
            liverCriteria: Boolean(
              submittedData.template_language_fields?.liver_event_stopping_criteria
            ),
            liverMermaid: Boolean(
              submittedData.template_language_fields?.liver_event_stopping_flowchart_mermaid
            ),
          }
        );
        
        setFormData(submittedData);
        // Also set loadedInitialData to ensure consistency
        setLoadedInitialData(sanitizeValue(submittedData));
        rememberPopulatedIntakeSnapshot(submittedData);
        intakeHydratedRef.current = true;
        if (hasTrialSchemaData(submittedData.study_overview)) {
          lastPopulatedTrialSchemaRef.current = {
            study_overview: submittedData.study_overview,
            study_treatments: submittedData.study_treatments,
          };
        }
        
        // Load field template selections from the submitted data
        const mergedTemplateSelections = {
          ...(submittedData.fieldTemplateSelections || {}),
          ...(submittedData.section9TemplateSelections || {}),
        };
        if (Object.keys(mergedTemplateSelections).length > 0) {
          fieldTemplateSelectionsRef.current = mergedTemplateSelections;
          setFieldTemplateSelections(mergedTemplateSelections);
        } else if (submittedData.fieldTemplateSelections) {
          fieldTemplateSelectionsRef.current = submittedData.fieldTemplateSelections;
          setFieldTemplateSelections(submittedData.fieldTemplateSelections);
        }

        // Load Section 9 template selections from the submitted data
        if (submittedData.section9TemplateSelections) {
          setTemplateSelections(submittedData.section9TemplateSelections);
          templateSelectionsRef.current = submittedData.section9TemplateSelections;
          const withSection9 = {
            ...fieldTemplateSelectionsRef.current,
            ...submittedData.section9TemplateSelections,
          };
          fieldTemplateSelectionsRef.current = withSection9;
          setFieldTemplateSelections(withSection9);
          console.log('Loaded Section 9 template selections from submitted data:', submittedData.section9TemplateSelections);
        }
        applyStudyTableSubfieldSelectionsFromServer(submittedData);
      } else {
        setFormLocked(false);
        intakeHydratedRef.current = true;
      }
    } catch (error) {
      console.error('Error checking form lock status:', error);
      setFormLocked(false);
      intakeHydratedRef.current = true;
    } finally {
      setIsCheckingLock(false);
    }
  };

  // Function to load saved SOA data
  const loadSavedSoA = async () => {
    if (!currentProject?._id || !formData) return;
    
    try {
      const protocolId = formData.study_identification?.protocol_number || currentProject._id;
      const response = await scheduleOfActivitiesService.getScheduleOfActivities(protocolId);
      
      if (response && response.success && response.data) {
        setGeneratedSoAData(response.data);
        setFormSubmitted(true); // Mark as submitted so SOA section shows
        console.log('SOA data loaded successfully:', response.data);
      } else {
        console.log('No existing SOA found for this project');
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error('Error loading SOA:', error);
      } else {
        console.log('No existing SOA found for this project');
      }
    }
  };

  // Function to unlock clinical intake form
  const handleUnlockForm = async () => {
    if (!currentProject?._id) {
      setError('No project selected');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to unlock this form? You will be able to make changes, but you may need to regenerate protocol sections if significant changes are made.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await clinicalIntakeService.unlockClinicalIntake(currentProject._id, 'User requested unlock');
      setFormLocked(false);
      setSnackbarMessage('Form unlocked successfully. You can now make changes.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reload form data to get latest state
      await checkFormLockStatus();
      
      // Load saved SOA data if form was previously submitted
      await loadSavedSoA();
    } catch (error) {
      console.error('Error unlocking form:', error);
      setSnackbarMessage(`Failed to unlock form: ${error.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Function to lock clinical intake form (persists consolidated values, then locks)
  const handleLockForm = async () => {
    if (!currentProject?._id) {
      setError('No project selected');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to lock this form? This will save your current changes and prevent further edits.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const consolidated = consolidateIntakeValuesForSave({
        loaded: loadedInitialData,
        snapshot: lastPopulatedIntakeRef.current,
        form: formikValuesRef.current,
        templateSelections: templateSelectionsRef.current,
        fieldTemplateSelections: fieldTemplateSelectionsRef.current,
      });

      if (!consolidated?._id && !resolveIntakeMongoId(consolidated)) {
        setSnackbarMessage('No existing form found to lock. Submit the form first.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      if (intakeValuesContainTemplatePlaceholder(consolidated)) {
        setSnackbarMessage(
          'Cannot lock while a template is still generating. Wait for generation to finish, then try again.'
        );
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      // Persist current edits first (with lockForm hint), then force lock via dedicated API
      await handleSaveAsDraft(consolidated, {
        manageLoading: false,
        quiet: true,
        lockForm: true,
        section9TemplateSelections: templateSelectionsRef.current,
        fieldTemplateSelections: fieldTemplateSelectionsRef.current,
      });

      await clinicalIntakeService.lockClinicalIntake(currentProject._id);
      const latest = await clinicalIntakeService.fetchLatestClinicalIntakeData(currentProject._id);
      if (!latest?.isLocked) {
        throw new Error('Server did not confirm the form is locked. Please try again.');
      }
      syncLockFromIntakeDoc(latest);
      setFormLocked(true);
      setFormSubmitted(latest.status === 'submitted' || formSubmitted);

      setSnackbarMessage('Form locked successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error locking form:', error);
      setSnackbarMessage(
        `Failed to lock form: ${error?.message || error?.error || 'Unknown error'}`
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Function to load draft data when component mounts or formId/projectId changes
  const loadDraftData = async () => {
    try {
      setLoading(true);
      
      if (formId) {
        // Load specific form by ID
        const response = await clinicalIntakeService.getForm(formId, currentProject._id);
        if (response.data) {
          console.log('Full response data:', response.data); // Debug log
          console.log('Field template selections in response:', response.data.fieldTemplateSelections); // Debug log
          
          // Sanitize the data to prevent null values
          const sanitizedData = sanitizeValue(response.data);
          applyStudyTableSubfieldSelectionsFromServer(response.data);
          repairClubbedAnalysisFieldsInDoc(sanitizedData);
          console.log('Loaded draft data - num_regimens:', sanitizedData?.study_treatments?.num_regimens);
          console.log('Loaded draft data - num_regimens type:', typeof sanitizedData?.study_treatments?.num_regimens);
          
          // Load field template selections from the database
          const mergedTemplateSelections = {
            ...(response.data.fieldTemplateSelections || {}),
            ...(response.data.section9TemplateSelections || {}),
          };
          if (Object.keys(mergedTemplateSelections).length > 0) {
            fieldTemplateSelectionsRef.current = mergedTemplateSelections;
            setFieldTemplateSelections(mergedTemplateSelections);
          } else if (response.data.fieldTemplateSelections) {
            fieldTemplateSelectionsRef.current = response.data.fieldTemplateSelections;
            setFieldTemplateSelections(response.data.fieldTemplateSelections);
          }

          // Load Section 9 template selections from the database
          if (response.data.section9TemplateSelections) {
            setTemplateSelections(response.data.section9TemplateSelections);
            templateSelectionsRef.current = response.data.section9TemplateSelections;
            const withSection9 = {
              ...fieldTemplateSelectionsRef.current,
              ...response.data.section9TemplateSelections,
            };
            fieldTemplateSelectionsRef.current = withSection9;
            setFieldTemplateSelections(withSection9);
            console.log('Loaded Section 9 template selections:', response.data.section9TemplateSelections);
          }
          //applyStudyTableSubfieldSelectionsFromServer(response.data);
          const mergedData = scrubIntakePlaceholdersFromDoc(
            mergeServerDraftWithInitialDataProp(sanitizedData, initialDataRef.current)
          );
          setFormData(mergedData);
          setLoadedInitialData(mergedData);
          lastPopulatedIntakeRef.current = mergedData;
          syncLockFromIntakeDoc(mergedData);
          if (hasTrialSchemaData(mergedData?.study_overview)) {
            lastPopulatedTrialSchemaRef.current = {
              study_overview: mergedData.study_overview,
              study_treatments: mergedData.study_treatments,
            };
          }
        }
      } else {
        // Load latest form data for the project
        console.log('No formId provided, loading latest data for project:', currentProject._id);
        const latestData = await clinicalIntakeService.fetchLatestClinicalIntakeData(currentProject._id);
        
        if (latestData) {
          console.log('Loaded latest data:', latestData);
          console.log('Latest data - study_treatments:', latestData.study_treatments);
          console.log('Latest data - treatment_table:', latestData.study_treatments?.treatment_table);
          
          // Sanitize the data to prevent null values
          const sanitizedData = sanitizeValue(latestData);
          applyStudyTableSubfieldSelectionsFromServer(latestData);
          repairClubbedAnalysisFieldsInDoc(sanitizedData);
          console.log('Merged latest data - study_treatments:', sanitizedData.study_treatments);
          
          // Load field template selections from the database
          const mergedTemplateSelections = {
            ...(latestData.fieldTemplateSelections || {}),
            ...(latestData.section9TemplateSelections || {}),
          };
          if (Object.keys(mergedTemplateSelections).length > 0) {
            fieldTemplateSelectionsRef.current = mergedTemplateSelections;
            setFieldTemplateSelections(mergedTemplateSelections);
          } else if (latestData.fieldTemplateSelections) {
            fieldTemplateSelectionsRef.current = latestData.fieldTemplateSelections;
            setFieldTemplateSelections(latestData.fieldTemplateSelections);
          }

          // Load Section 9 template selections from the database
          if (latestData.section9TemplateSelections) {
            setTemplateSelections(latestData.section9TemplateSelections);
            templateSelectionsRef.current = latestData.section9TemplateSelections;
            const withSection9 = {
              ...fieldTemplateSelectionsRef.current,
              ...latestData.section9TemplateSelections,
            };
            fieldTemplateSelectionsRef.current = withSection9;
            setFieldTemplateSelections(withSection9);
            console.log('Loaded Section 9 template selections:', latestData.section9TemplateSelections);
          }
          //applyStudyTableSubfieldSelectionsFromServer(latestData);
          const mergedData = scrubIntakePlaceholdersFromDoc(
            mergeServerDraftWithInitialDataProp(sanitizedData, initialDataRef.current)
          );
          setFormData(mergedData);
          setLoadedInitialData(mergedData);
          lastPopulatedIntakeRef.current = mergedData;
          syncLockFromIntakeDoc(mergedData);
          if (hasTrialSchemaData(mergedData?.study_overview)) {
            lastPopulatedTrialSchemaRef.current = {
              study_overview: mergedData.study_overview,
              study_treatments: mergedData.study_treatments,
            };
          }
        } else {
          console.log('No latest data found, using initial values');
          const propOnly = mergeServerDraftWithInitialDataProp(null, initialDataRef.current);
          if (propOnly) {
            setFormData(propOnly);
            setLoadedInitialData(propOnly);
            lastPopulatedIntakeRef.current = propOnly;
            if (hasTrialSchemaData(propOnly?.study_overview)) {
              lastPopulatedTrialSchemaRef.current = {
                study_overview: propOnly.study_overview,
                study_treatments: propOnly.study_treatments,
              };
            }
          } else {
            setLoadedInitialData(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load draft:', err);
      setError('Failed to load draft data.');
      setLoadedInitialData(null); // Fallback to default values
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentProject) {
      loadDraftData();
    }
  }, [formId, currentProject]); // Depend on formId and currentProject

  // Load available templates based on selected template
  const loadTemplateDataForSelection = async (selectedTemplate) => {
    try {
      setTemplateLoading(true);

      const { templateLanguageUpdatedBase } = await import('../../data/templateLanguageUpdatedBase');

      console.log(`Loading template data for: ${selectedTemplate} (from Template Language Current vs Updates base + DB)`);

      const regulatoryFieldToTitle = {
        investigator_responsibilities: '11.2.1 Investigators Responsibilities',
        sponsor_responsibilities: '11.2.2 Sponsor Responsibilities',
        committees: '11.4 Committees',
        insurance_indemnity: '11.5 Insurance and Indemnity',
        risk_based_quality_management: '11.6 Risk-Based Quality Management',
        data_governance: '11.7 Data Governance',
        data_protection: '11.8 Data Protection',
        source_data: '11.9 Source Reports',
        protocol_deviations: '11.10 Protocol Deviations',
        early_site_closure: '11.11 Early Site Closure',
        data_dissemination: '11.12 Data Dissemination',
      };

      const templateLanguageFieldToTitle = {
        definitions_childbearing_potential: '5.4.1 Definitions Related to Childbearing Potential',
        contraception_requirements: '5.4.2: Contraception Requirements',
        permanent_discontinuation_trial_intervention: '7.1 Permanent discontinuation of Trial Intervention',
        temporary_discontinuation_trial_intervention: '7.1.2 Temporary Discontinuation of Trial Intervention',
        rechallenge: '7.1.3 Rechallenge',
        participant_discontinuation_withdrawal: '7.2 Participant Discontinuation or Withdrawal from Trial',
        management_loss_to_followup: '7.3 : Management of Loss to Follow-Up',
        definition_adverse_event: '9.1.1 Definition of Adverse event',
        definition_serious_adverse_event: '9.1.2 Definition of Serious Adverse event',
        definitions_product_complaints: '9.1.3 Definitions of Product Complaints',
        ae_reporting_timing_procedures: '9.2 Timing and Procedures for Collection and Reporting',
        ae_reporting_timing: '9.2.1 Timing',
        ae_reporting_collection_procedure: '9.2.2 Collection procedure',
        ae_reporting_reporting: '9.2.3 Reporting',
        ae_reporting_special_interest: '9.2.4 Adverse events of special interest',
        ae_reporting_disease_related_events: '9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs',
        pregnancy_postpartum_information: '9.3 Pregnancy and Postpartum Information',
        participants_pregnant_during_trial: '9.3.1 Participants Who Become Pregnant During the Trial',
        special_safety_situations: '9.4 Special Safety situations',
      };

      const section9TemplateData = {};

      const fillFromBase = (fieldKey, title) => {
        const content = templateLanguageUpdatedBase[title] || '';
        if (content) {
          section9TemplateData[fieldKey] = { content, userSuggestion: '' };
        }
      };

      Object.entries(regulatoryFieldToTitle).forEach(([fieldKey, title]) => fillFromBase(fieldKey, title));
      Object.entries(templateLanguageFieldToTitle).forEach(([fieldKey, title]) => fillFromBase(fieldKey, title));

      const titleToFieldMap = {
        '5.4.1 Definitions Related to Childbearing Potential': 'definitions_childbearing_potential',
        '5.4.2: Contraception Requirements': 'contraception_requirements',
        '7.1 Permanent discontinuation of Trial Intervention': 'permanent_discontinuation_trial_intervention',
        '7.1.2 Temporary Discontinuation of Trial Intervention': 'temporary_discontinuation_trial_intervention',
        '7.1.3 Rechallenge': 'rechallenge',
        '7.2 Participant Discontinuation or Withdrawal from Trial': 'participant_discontinuation_withdrawal',
        '7.3 : Management of Loss to Follow-Up': 'management_loss_to_followup',
        '9.1.1 Definition of Adverse event': 'definition_adverse_event',
        '9.1.2 Definition of Serious Adverse event': 'definition_serious_adverse_event',
        '9.1.3 Definitions of Product Complaints': 'definitions_product_complaints',
        '9.2 Timing and Procedures for Collection and Reporting': 'ae_reporting_timing_procedures',
        '9.2.1 Timing': 'ae_reporting_timing',
        '9.2.2 Collection procedure': 'ae_reporting_collection_procedure',
        '9.2.3 Reporting': 'ae_reporting_reporting',
        '9.2.4 Adverse events of special interest': 'ae_reporting_special_interest',
        '9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs':
          'ae_reporting_disease_related_events',
        '9.3 Pregnancy and Postpartum Information': 'pregnancy_postpartum_information',
        '9.3.1 Participants Who Become Pregnant During the Trial': 'participants_pregnant_during_trial',
        '9.4 Special Safety situations': 'special_safety_situations',
        '11.2.1 Investigators Responsibilities': 'investigator_responsibilities',
        '11.2.2 Sponsor Responsibilities': 'sponsor_responsibilities',
        '11.4 Committees': 'committees',
        '11.5 Insurance and Indemnity': 'insurance_indemnity',
        '11.6 Risk-Based Quality Management': 'risk_based_quality_management',
        '11.7 Data Governance': 'data_governance',
        '11.8 Data Protection': 'data_protection',
        '11.9 Source Reports': 'source_data',
        '11.10 Protocol Deviations': 'protocol_deviations',
        '11.11 Early Site Closure': 'early_site_closure',
        '11.12 Data Dissemination': 'data_dissemination',
      };

      Object.entries(titleToFieldMap).forEach(([title, fieldKey]) => {
        if (!section9TemplateData[fieldKey]) {
          fillFromBase(fieldKey, title);
        }
      });

      try {
        console.log('Fetching template language data from service...');
        const templateLanguageData = await templateLanguageService.getLatestTemplateLanguage();
        console.log('Template language data received:', templateLanguageData);

        if (templateLanguageData && templateLanguageData.tableData) {
          console.log(`Processing ${templateLanguageData.tableData.length} rows from template language form`);
          let matchedCount = 0;

          templateLanguageData.tableData.forEach((row) => {
            if (row.title && titleToFieldMap[row.title]) {
              const fieldKey = titleToFieldMap[row.title];
              const baseContent =
                (templateLanguageUpdatedBase[row.title] || section9TemplateData[fieldKey]?.content || '').trim();
              const dbContent = (row.content || '').trim();
              section9TemplateData[fieldKey] = {
                content: dbContent || baseContent,
                userSuggestion: row.userSuggestion || '',
              };
              matchedCount++;
              console.log(
                `✓ Merged template language field "${fieldKey}" (${row.title}) — DB content when present, else updated base text`
              );
            } else if (row.title) {
              const isSectionDField =
                row.title.includes('7.1') || row.title.includes('7.2') || row.title.includes('7.3');
              if (isSectionDField) {
                console.log(`⚠ Section D field found but not mapped: "${row.title}"`);
              }
            }
          });

          console.log(`Successfully merged ${matchedCount} template language fields from database`);
          console.log('Section D fields loaded:', {
            permanent_discontinuation: !!section9TemplateData.permanent_discontinuation_trial_intervention,
            temporary_discontinuation: !!section9TemplateData.temporary_discontinuation_trial_intervention,
            rechallenge: !!section9TemplateData.rechallenge,
            participant_withdrawal: !!section9TemplateData.participant_discontinuation_withdrawal,
            loss_to_followup: !!section9TemplateData.management_loss_to_followup,
          });
        } else {
          console.warn('Template language data is empty or missing tableData');
        }
      } catch (error) {
        console.warn('Error fetching from template language service, using updated base text only:', error);
      }

      setTemplateData(section9TemplateData);
      console.log(`Template data loaded for ${selectedTemplate}:`, Object.keys(section9TemplateData));
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Load templates on mount
  useEffect(() => {
    loadTemplateDataForSelection('default');
  }, []);

  // // State to track template selection changes
  // const [currentTemplateSelection, setCurrentTemplateSelection] = useState('default');
  
  // // Function to handle template selection change
  // const handleTemplateSelectionChange = async (newTemplate) => {
  //   if (newTemplate && newTemplate !== currentTemplateSelection) {
  //     console.log(`Template selection changed to: ${newTemplate}`);
  //     await loadTemplateDataForSelection(newTemplate);
  //     setCurrentTemplateSelection(newTemplate);
  //   }
  // };

  // Check form lock status when project changes
  useEffect(() => {
    if (currentProject?._id) {
      checkFormLockStatus();
    }
  }, [currentProject?._id]);

  // Load submitted data when form is locked
  useEffect(() => {
    if (formLocked && formData) {
      // Set the form values to the submitted data
      setFormSubmitted(true);
      // Load saved SOA when form is locked
      loadSavedSoA();
    }
  }, [formLocked, formData]);

  // Load SOA when form is unlocked and was previously submitted
  useEffect(() => {
    if (!formLocked && formSubmitted && currentProject?._id) {
      loadSavedSoA();
    }
  }, [formLocked, formSubmitted, currentProject?._id]);

  // Auto-save functionality when form is unlocked
  const autoSaveTimeoutRef = React.useRef(null);
  
  const handleAutoSave = React.useCallback(async (values) => {
    if (!currentProject?._id || formLocked || !formSubmitted) {
      return;
    }
    if (!intakeHydratedRef.current) {
      return;
    }
    if (templateLlmLoadingField) {
      return;
    }
    if (intakeValuesContainTemplatePlaceholder(values)) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce auto-save (save 2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        if (!handleSaveAsDraftRef.current) return;
        await handleSaveAsDraftRef.current(values, { manageLoading: false, quiet: true });
        console.log('Form auto-saved successfully');
      } catch (error) {
        console.error('Error auto-saving form:', error);
        // Don't show error to user for auto-save failures
      }
    }, 2000);
  }, [
    currentProject?._id,
    formLocked,
    formSubmitted,
    templateLlmLoadingField,
    loadedInitialData,
    fieldTemplateSelections,
    templateSelections,
    getStudyTableSubfieldSelectionsPayload
  ]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

    // Persist optional study/treatment sub-row visibility when user toggles guidance checkboxes (submitted, unlocked)
  React.useEffect(() => {
    if (!currentProject?._id || formLocked || !formSubmitted) return;
    const values = formikValuesRef.current;
    if (!values) return;
    handleAutoSave(values);
  }, [
    lifestyleRestrictionEnabledSubfields,
    investigationalInterventionHandlingEnabledSubfields,
    rescueMedicationEnabledSubfields,
    primaryObjectiveAnalysisEnabledSubfields,
    secondaryObjectiveAnalysisEnabledSubfields,
    currentProject?._id,
    formLocked,
    formSubmitted,
    handleAutoSave
  ]);

  // Debug log for fieldTemplateSelections state
  useEffect(() => {
    console.log('fieldTemplateSelections state updated:', fieldTemplateSelections);
  }, [fieldTemplateSelections]);

  // Determine rationale field relevance once trial schema context exists (after auto-populate / parse)
  useEffect(() => {
    if (documentsOnly) return;

    const values = loadedInitialData || initialData;
    if (!values || determiningRationaleRelevance) return;

    const fingerprint = buildTrialSchemaContextFingerprint(values);
    if (!fingerprint) {
      return;
    }
    if (fingerprint === lastRationaleContextFingerprintRef.current) {
      return;
    }

    lastRationaleContextFingerprintRef.current = fingerprint;
    console.log('Determining rationale field relevance — trial schema context is ready');
    determineRationaleFieldRelevance(values);
  }, [loadedInitialData, initialData, documentsOnly]);

  // Regenerate treatment table when regimen arms change
  useEffect(() => {
    if (formData && formData.study_treatments) {
      const { num_regimens, regimen_arms } = formData.study_treatments;
      
      if (num_regimens > 0 && regimen_arms) {
        // Check if any regimen arm has a value
        const hasRegimenArms = Object.values(regimen_arms).some(value => value && value.trim() !== '');
        
        if (hasRegimenArms) {
          console.log('Regenerating treatment table due to regimen arms change:', { num_regimens, regimen_arms });
          
          const updatedTreatmentTable = generateDynamicTreatmentTable(
            num_regimens,
            regimen_arms,
            null,
            formData.study_treatments?.treatment_table || {},
            formData.study_overview?.study_parts || null
          );
          
          // Update the form data
          setFormData(prev => ({
            ...prev,
            study_treatments: {
              ...prev.study_treatments,
              treatment_table: updatedTreatmentTable
            }
          }));
        }
      }
    }
  }, [formData?.study_treatments?.regimen_arms]);

  /**
   * Some base template blocks (e.g. "9.2 Timing and Procedures") carry a NOTE
   * telling the model to "maintain" a range of Level-3 sub-headings — correct
   * for the assembled protocol document, where those sub-headings really are
   * part of the same continuous section, but wrong here: each sub-heading is
   * a *separate* intake-form field generated independently. The prompt tells
   * the model to ignore that NOTE, but as a backstop, strip any stray
   * standalone "<parent>.<n> Title" lines it echoes anyway.
   */
  const SIBLING_HEADING_STRIP_RULES = {
    ae_reporting_timing_procedures: /^[ \t]*9\.2\.\d+\b[^:\n]*$\n?/gm,
  };

  const stripSiblingHeadingLines = (text, fieldName) => {
    const re = SIBLING_HEADING_STRIP_RULES[fieldName];
    if (!re || !text) return text || '';
    return String(text)
      .replace(re, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  /**
   * When user checks a "template" checkbox on Clinical Intake: pull standard text
   * (Template Language Current vs Updates base + any merged DB copy), tailor with LLM
   * using latest clinical intake, write result into that field only.
   */
  const runIntakeTemplateCheckboxLlm = async (fieldName, fieldPath, setFieldValue, generationId) => {
    const meta = TEMPLATE_INTAKE_FIELD_META[fieldName];
    if (!meta) {
      return { ok: false, useFallback: true };
    }

    const isStale = () => generationId !== templateLlmGenerationIdRef.current;

    if (isRechallengeDeterministicField(fieldName)) {
      const intakeSource =
        formikValuesRef.current || lastPopulatedIntakeRef.current || loadedInitialData || {};
      const assembled = assembleRechallengeSection713(intakeSource);
      if (assembled.rechallenge) {
        if (!isStale()) setFieldValue(fieldPath, assembled.rechallenge);
        return { ok: true, finalText: assembled.rechallenge };
      }
      setSnackbarMessage(
        assembled.warnings.join(' ') ||
          'Complete the Section 7.1.3 decision tree and generate from protocol source when Yes.'
      );
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return { ok: false, useFallback: true };
    }

    if (isTemporaryDiscontinuationDeterministicField(fieldName)) {
      const intakeSource =
        formikValuesRef.current || lastPopulatedIntakeRef.current || loadedInitialData || {};
      const assembled = assembleTemporaryDiscontinuationSection712(intakeSource);
      if (assembled.omitSection) {
        if (!isStale()) setFieldValue(fieldPath, '');
        setSnackbarMessage('Section 7.1.2 omitted per intake decision (Option B).');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return { ok: true, finalText: '', allowEmpty: true };
      }
      if (assembled.temporary_discontinuation_trial_intervention) {
        if (!isStale()) {
          setFieldValue(fieldPath, assembled.temporary_discontinuation_trial_intervention);
        }
        return { ok: true, finalText: assembled.temporary_discontinuation_trial_intervention };
      }
      setSnackbarMessage(
        assembled.warnings.join(' ') ||
          'Complete the Section 7.1.2 decision tree and generate from protocol source when Yes.'
      );
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return { ok: false, useFallback: true };
    }

    if (meta.deterministicOnly || isPatientLibraryDeterministicField(fieldName)) {
      const intakeSource =
        formikValuesRef.current || lastPopulatedIntakeRef.current || loadedInitialData || {};
      const assembled = assemblePatientLibrarySection54(intakeSource);
      const finalText =
        fieldName === 'definitions_childbearing_potential'
          ? assembled.definitions_childbearing_potential
          : assembled.contraception_requirements;
      if (finalText) {
        if (!isStale()) setFieldValue(fieldPath, finalText);
        return { ok: true, finalText };
      }
      setSnackbarMessage(
        assembled.warnings.join(' ') ||
          'Complete the Patient Library decision tree for Sections 5.4.1 and 5.4.2 above.'
      );
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return { ok: false, useFallback: true };
    }
    // Scope intake to THIS study: prefer URL ?project= (authoritative for the open page), then context.
    const urlPid = projectIdFromUrl || null;
    const ctxPid = currentProject?._id || null;
    if (urlPid && ctxPid && String(urlPid) !== String(ctxPid)) {
      console.warn(
        '[Template LLM] URL projectId and ProjectContext differ; using URL for intake fetch:',
        urlPid,
        'vs',
        ctxPid
      );
    }
    const pid = urlPid || ctxPid || null;
    if (!pid) {
      setSnackbarMessage('Add ?project=... to the URL (or open a project) to generate template text.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return { ok: false };
    }

    setTemplateLlmLoadingField(fieldName);
    templateLlmInFlightFieldRef.current = fieldName;
    setFieldValue(fieldPath, 'Generating tailored template language…');
    try {
      const fromForm = formikValuesRef.current;
      const fromSnapshot = lastPopulatedIntakeRef.current;
      let intakeSource = fromForm
        ? { ...(fromSnapshot || loadedInitialData || {}), ...fromForm }
        : fromSnapshot || loadedInitialData || null;
      if (!intakeSource) {
        intakeSource = await clinicalIntakeService.fetchLatestClinicalIntakeData(String(pid));
      }
      if (isStale()) return { ok: false, aborted: true };
      const intakeBody = formatClinicalIntakeForLLM(intakeSource, { forTemplateGeneration: true });
      const intakeContext = `Project ID (clinical intake scope — API query): ${pid}\n\n${
        intakeBody || '(No intake document returned for this project ID.)'
      }`;
      const baseFromLibrary = meta.baseTitle ? templateLanguageUpdatedBase[meta.baseTitle] || '' : '';
      const fallbackStatic =
        templateData?.[fieldName]?.content || templateData?.[fieldName]?.userSuggestion || '';
      const standardBlock = (baseFromLibrary || fallbackStatic || '').trim();
      if (!standardBlock && !meta.allowEmptyBase) {
        throw new Error('No standard template text available for this field.');
      }
      const useCptColor = shouldUseCptColoredOutput(fieldName);
      const focus = meta.focusNote || '';
      const cptSections = buildCptTemplateLlmPromptSections({
        sectionLabel: meta.sectionLabel,
        fieldName,
        standardBlock,
        focusNote: focus,
        colorCodedHtml: useCptColor,
      });
      const prompt = `ROLE: You are an expert medical writer drafting a single Clinical Trial Protocol subsection using the CPT Core Basic Word Edition (BWE) template rules.

Produce FINAL protocol text for this section ONLY: "${meta.sectionLabel}"

CRITICAL: Do NOT copy, reuse, or paraphrase language from uploaded protocol documents or any previously saved template-language fields. Generate from the CPT standard template block below and tailor using factual study data only (design, population, treatments, endpoints, regulatory region, etc.).

${cptSections}

=== Full standard template block (reference — apply colour-coding rules above) ===
${
standardBlock ||
'(No base block was supplied. Write concise, regulator-aligned protocol language appropriate for this section title, consistent with ICH GCP and common practice.)'
}

=== End reference block ===
`;
      const text = await generateContent(
        prompt,
        intakeContext || '',
        '',
        'protocol',
        meta.sectionLabel,
        null
      );
      if (isStale()) return { ok: false, aborted: true };
      const textNoSiblingHeadings = stripSiblingHeadingLines(text, fieldName);
      const cleanedRaw = useCptColor
        ? coerceCptTemplateHtml((textNoSiblingHeadings || '').trim())
        : normalizeTemplateLanguagePlainText((textNoSiblingHeadings || '').trim());
      const cleaned = stripLeadingDuplicateHeading(cleanedRaw, meta.sectionLabel);
      if (!cleaned) {
        throw new Error('Empty response from model.');
      }

      setFieldValue(fieldPath, 'Reviewing with Google ADK (template language QC)…');
      const reviewTitle = (meta.baseTitle || meta.sectionLabel || '').trim();
      const baselineForReview = (baseFromLibrary || fallbackStatic || '').trim();
      let finalText = cleaned;
      try {
        const revRes = await axiosInstance.post(
          API_ENDPOINTS.adk.templateLanguageReview,
          {
            items: [
              {
                title: reviewTitle,
                baselineContent: baselineForReview,
                generatedContent: useCptColor ? cptHtmlToPlainText(cleaned) : cleaned,
              },
            ],
            clinicalIntakeContext: intakeContext,
            templateId: 'default',
          },
          { timeout: 600000 }
        );
        if (isStale()) return { ok: false, aborted: true };
        const reviews = Array.isArray(revRes.data?.reviews) ? revRes.data.reviews : [];
        const r = reviews.find((x) => String(x?.title || '').trim() === reviewTitle) || reviews[0];
        const decision = String(r?.decision || '')
          .toUpperCase()
          .trim();
        const revised = String(r?.revised_plain_text || r?.revisedPlainText || '').trim();
        // Only replace LLM output when ADK returns non-empty revised text.
        // Never wipe reviewed/generated content with an empty ADK revision.
        if (decision === 'REVISION_REQUIRED' && revised) {
          const revisedNoSiblingHeadings = stripSiblingHeadingLines(revised, fieldName);
          const revisedCleaned = useCptColor
            ? coerceCptTemplateHtml(revisedNoSiblingHeadings) || cleaned
            : normalizeTemplateLanguagePlainText(revisedNoSiblingHeadings);
          finalText = stripLeadingDuplicateHeading(revisedCleaned, meta.sectionLabel);
        }
      } catch (adkErr) {
        console.warn('ADK template-language review failed (intake checkbox flow); keeping LLM text:', adkErr);
        setSnackbarMessage(
          `Template text was generated, but Google ADK review failed: ${
            adkErr?.response?.data?.error || adkErr.message || 'Unknown error'
          }. LLM output was kept.`
        );
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }

      if (isStale()) return { ok: false, aborted: true };
      if (!String(finalText || '').trim()) {
        throw new Error('Empty template text after generation/review.');
      }
      // Grammar guard: capitalize sentence-starting words (and the "I"
      // pronoun) that came back lowercase from the model — see
      // grammarGuards.js. Only applied to this LLM-generated path, never to
      // the deterministic/verbatim template assemblies above.
      finalText = applyGrammarGuardsToHtml(finalText);
      setFieldValue(fieldPath, finalText);
      if (formikValuesRef.current) {
        formikValuesRef.current = setNestedFormField(formikValuesRef.current, fieldPath, finalText);
      }
      return { ok: true, finalText };
    } catch (e) {
      console.error('Template checkbox LLM error:', fieldName, e);
      return { ok: false, error: e };
    } finally {
      if (!isStale()) {
        setTemplateLlmLoadingField(null);
        templateLlmInFlightFieldRef.current = null;
      }
    }
  };

  // Template population for Section 9 / discontinuation / template-language fields on Clinical Intake.
  // First check generates tailored template language (LLM + ADK). Click again to regenerate.
  // Shift+click a checked box (or uncheck) clears the field.
  const handleTemplateSelection = async (
    fieldName,
    isSelected,
    setValues,
    setFieldValue,
    values,
    section = 'regulatory_requirements',
    { regenerate = false } = {}
  ) => {
    let fieldPath;
    if (section === 'template_language_fields') {
      fieldPath = `template_language_fields.${fieldName}`;
    } else if (section === 'discontinuation_rules') {
      fieldPath = `discontinuation_rules.${fieldName}`;
    } else {
      fieldPath = `regulatory_requirements.${fieldName}`;
    }

    if (!isSelected && !regenerate) {
      void persistTemplateFieldChoice(fieldPath, '', fieldName, false, setFieldValue);
      return;
    }

    // Ignore duplicate clicks while this (or another) template is generating.
    if (templateLlmInFlightFieldRef.current) {
      return;
    }

    const resolveTemplateText = (name) => {
      const fromCache =
        templateData?.[name]?.content || templateData?.[name]?.userSuggestion || '';
      if (fromCache) return stripHtmlToPlainText(fromCache);
      const meta = TEMPLATE_INTAKE_FIELD_META[name];
      const baseTitle = meta?.baseTitle;
      if (baseTitle && templateLanguageUpdatedBase[baseTitle]) {
        return stripHtmlToPlainText(templateLanguageUpdatedBase[baseTitle]);
      }
      return '';
    };

    const readCurrentFieldText = () => {
      const fromForm = fieldPath.split('.').reduce((obj, key) => obj?.[key], formikValuesRef.current);
      const fromSnap = fieldPath
        .split('.')
        .reduce((obj, key) => obj?.[key], lastPopulatedIntakeRef.current);
      const fromLoaded = fieldPath.split('.').reduce((obj, key) => obj?.[key], loadedInitialData);
      for (const candidate of [fromForm, fromSnap, fromLoaded]) {
        if (
          typeof candidate === 'string' &&
          candidate.trim() &&
          !isTemplateGenerationPlaceholder(candidate)
        ) {
          return candidate;
        }
      }
      return '';
    };

    const previousText = readCurrentFieldText();

    const revertTemplateSelection = () => {
      const nextSelections = { ...templateSelectionsRef.current };
      delete nextSelections[fieldName];
      templateSelectionsRef.current = nextSelections;
      setTemplateSelections(nextSelections);
      syncFieldTemplateSelections(fieldName, false);
    };

    // Keep checkbox checked while generation runs (controlled by templateSelections).
    const nextSelections = { ...templateSelectionsRef.current, [fieldName]: true };
    templateSelectionsRef.current = nextSelections;
    setTemplateSelections(nextSelections);
    syncFieldTemplateSelections(fieldName, true);

    const restorePreviousOrClear = () => {
      if (previousText) {
        void persistTemplateFieldChoice(fieldPath, previousText, fieldName, true, setFieldValue);
        return;
      }
      // First-time generate with nothing to restore — leave empty and uncheck.
      revertTemplateSelection();
      setFieldValue(fieldPath, '');
    };

    const applyFallbackTemplate = async () => {
      const fallback = resolveTemplateText(fieldName);
      if (!fallback) {
        return false;
      }
      const normalized = normalizeTemplateLanguagePlainText(fallback);
      void persistTemplateFieldChoice(fieldPath, normalized, fieldName, true, setFieldValue);
      return true;
    };

    const generationId = ++templateLlmGenerationIdRef.current;
    const llmResult = await runIntakeTemplateCheckboxLlm(
      fieldName,
      fieldPath,
      setFieldValue,
      generationId
    );
    if (llmResult?.aborted) {
      return;
    }

    const hasUsableFinalText =
      llmResult?.ok &&
      llmResult.finalText != null &&
      (String(llmResult.finalText).trim() !== '' || llmResult.allowEmpty === true);

    if (hasUsableFinalText) {
      void persistTemplateFieldChoice(
        fieldPath,
        llmResult.finalText,
        fieldName,
        true,
        setFieldValue
      );
      if (regenerate) {
        setSnackbarMessage(`Regenerated template for ${fieldName.replace(/_/g, ' ')}.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
      return;
    }
    if (llmResult?.useFallback) {
      const applied = await applyFallbackTemplate();
      if (!applied) {
        restorePreviousOrClear();
        setSnackbarMessage(
          previousText
            ? 'Could not regenerate template language. Previous text was kept.'
            : 'No template text is available for this field yet. Wait for templates to finish loading, then try again.'
        );
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
      return;
    }

    const usedFallback = await applyFallbackTemplate();
    if (!usedFallback) {
      restorePreviousOrClear();
    }
    setSnackbarMessage(
      previousText && !usedFallback
        ? llmResult?.error?.message
          ? `${llmResult.error.message} Previous text was kept.`
          : 'Could not regenerate template language. Previous text was kept.'
        : llmResult?.error?.message ||
            (regenerate
              ? 'Could not regenerate with AI; standard template text was applied instead.'
              : 'AI generation failed; standard template text was applied instead.')
    );
    setSnackbarSeverity('warning');
    setSnackbarOpen(true);
  };

  const handleTemplateCheckboxChange = (
    fieldName,
    nextChecked,
    setValues,
    setFieldValue,
    values,
    section = 'regulatory_requirements',
    options = {}
  ) => {
    void handleTemplateSelection(
      fieldName,
      nextChecked,
      setValues,
      setFieldValue,
      values,
      section,
      options
    );
  };

  /** LLM template generation lives on Protocol → Template Language, not on this Clinical Intake screen. */
  const goToProtocolTemplateLanguageTab = () => {
    const pid = currentProject?._id || projectIdFromUrl;
    if (!pid) {
      setSnackbarMessage('Open this study with a project in the URL (?project=...) first.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    const basePath = location.pathname || '/clinical-intake';
    const params = new URLSearchParams(location.search);
    params.set('project', pid);
    params.set('tab', 'protocol');
    params.set('protocolSubTab', 'template-language');
    navigate(`${basePath}?${params.toString()}`);
  };

  const handleClearAllTemplates = async (setValues, values) => {
    const confirmed = window.confirm(
      'This will clear ALL template content from Section 11 fields. Are you sure you want to continue?'
    );

    if (!confirmed) return;

    const newValues = { ...values };
    if (!newValues.regulatory_requirements) {
      newValues.regulatory_requirements = {};
    }

    const newSelections = { ...templateSelectionsRef.current };
    Object.keys(templateData || {}).forEach((fieldName) => {
      newValues.regulatory_requirements[fieldName] = '';
      delete newSelections[fieldName];
      syncFieldTemplateSelections(fieldName, false);
    });

    setTemplateSelections(newSelections);
    templateSelectionsRef.current = newSelections;
    setValues(newValues);
    setLoadedInitialData(sanitizeValue(newValues));
    setFormData(sanitizeValue(newValues));
    rememberPopulatedIntakeSnapshot(newValues);

    if (currentProject?._id && handleSaveAsDraftRef.current) {
      try {
        await handleSaveAsDraftRef.current(newValues, {
          manageLoading: false,
          quiet: true,
          section9TemplateSelections: newSelections,
          fieldTemplateSelections: fieldTemplateSelectionsRef.current,
        });
      } catch (error) {
        console.error('Error saving after clearing templates:', error);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    console.log('Form submission started'); // Debug log
    
    if (!currentProject) {
      setError('No project selected. Please select a project before submitting.');
      setSubmitting(false);
      return;
    }

    // Transform arrays to strings in endpoints_objectives and ensure regimen_arms is properly formatted
    const consolidatedForSubmit = consolidateIntakeValuesForSave({
      loaded: loadedInitialData || formData,
      snapshot: lastPopulatedIntakeRef.current,
      form: values,
      templateSelections: templateSelectionsRef.current,
      fieldTemplateSelections: fieldTemplateSelectionsRef.current,
    });
    const transformedValuesWithoutId = transformFormValues(sanitizeValue(consolidatedForSubmit));
    
    // Remove _id from the data if it exists (to prevent duplicate key error)
    const { _id, ...dataWithoutMongoId } = transformedValuesWithoutId;
    
    const transformedValues = {
      ...dataWithoutMongoId,
      projectId: currentProject._id,
      id: formId || _id,
      status: 'submitted',
      lockForm: true,
      fieldTemplateSelections: fieldTemplateSelections,
      // Include Section 9 template selections
      section9TemplateSelections: templateSelections,
      studyTableSubfieldSelections: getStudyTableSubfieldSelectionsPayload()
    };

    try {
      setSubmitting(true);
      console.log('Preparing form data for submission...'); // Debug log
      
      
      console.log('Submitting transformed form data:', transformedValues); // Debug log
      console.log('Regimen arms before submission:', transformedValues.study_treatments.regimen_arms); // Debug log
      
      // First save to MongoDB
      const response = await axiosInstance.post(API_ENDPOINTS.clinicalIntake.submit, transformedValues, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.data) {
        console.log('Form submitted successfully'); // Debug log
        const savedDoc = response.data.data || response.data;
        setSuccess(true);
        setFormSubmitted(true); // Enable SOA section
        setFormData(consolidatedForSubmit);
        syncLockFromIntakeDoc(savedDoc);
        if (savedDoc?._id) {
          setLoadedInitialData((prev) =>
            sanitizeValue({ ...(prev || {}), ...consolidatedForSubmit, _id: savedDoc._id, isLocked: savedDoc.isLocked })
          );
        }
      } else {
        setError(response.data.error || 'Failed to submit form');
        setSnackbarMessage(`Submission failed: ${response.data.error || 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to submit form');
      setSnackbarMessage(`Submission failed: ${error.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle saving as draft
  const mergeTrialSchemaForDraftSave = (values) => {
    if (!values || hasTrialSchemaData(values.study_overview)) {
      return values;
    }
    const persisted =
      lastPopulatedTrialSchemaRef.current ||
      (hasTrialSchemaData(loadedInitialData?.study_overview)
        ? {
            study_overview: loadedInitialData.study_overview,
            study_treatments: loadedInitialData.study_treatments,
          }
        : hasTrialSchemaData(initialData?.study_overview)
          ? {
              study_overview: initialData.study_overview,
              study_treatments: initialData.study_treatments,
            }
          : null);
    if (!persisted) {
      return values;
    }
    return {
      ...values,
      study_overview: { ...values.study_overview, ...persisted.study_overview },
      study_treatments: { ...values.study_treatments, ...persisted.study_treatments },
    };
  };

  const prepareDraftSaveValues = (values) => {
    const baseline = lastPopulatedIntakeRef.current || loadedInitialData || initialData || null;
    if (documentsOnly && baseline) {
      return mergeTrialSchemaForDraftSave({
        ...baseline,
        ...values,
        document_uploads: values.document_uploads,
        _id: values._id || baseline._id,
      });
    }
    return mergeTrialSchemaForDraftSave(values);
  };

  const rememberPopulatedIntakeSnapshot = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return;
    lastPopulatedIntakeRef.current = snapshot;
    if (hasTrialSchemaData(snapshot.study_overview)) {
      lastPopulatedTrialSchemaRef.current = {
        study_overview: snapshot.study_overview,
        study_treatments: snapshot.study_treatments,
      };
    }
  };

  /** Keep submitted intakes submitted when saving (backend rejects draft status on submitted records). */
  const resolveIntakePersistStatus = (values) => {
    const status =
      values?.status ||
      loadedInitialData?.status ||
      formData?.status ||
      lastPopulatedIntakeRef.current?.status ||
      'draft';
    return status === 'submitted' ? 'submitted' : 'draft';
  };

  const resolveIntakeMongoId = (values) =>
    formId ||
    values?._id ||
    loadedInitialData?._id ||
    formData?._id ||
    lastPopulatedIntakeRef.current?._id ||
    null;

  const handleSaveAsDraft = async (
    values,
    {
      manageLoading = true,
      quiet = false,
      section9TemplateSelections: section9SelectionsOverride,
      fieldTemplateSelections: fieldSelectionsOverride,
      studyTableSubfieldSelections: studyTableSubfieldSelectionsOverride,
      lockForm = false,
      skipConsolidate = false,
      preferClientTemplateFields = false,
      skipFormReinitialize = false,
    } = {}
  ) => {
    if (!currentProject) {
      setError('No project selected. Please select a project before saving as draft.');
      return;
    }
    try {
      if (manageLoading) {
        setLoading(true);
      }

      const templateSelectionsForSave =
        section9SelectionsOverride ?? templateSelectionsRef.current;
      const fieldSelectionsForSave =
        fieldSelectionsOverride ?? fieldTemplateSelectionsRef.current;
      const studyTableSubfieldSelectionsForSave =
        studyTableSubfieldSelectionsOverride ?? getStudyTableSubfieldSelectionsPayload();

      const consolidated = skipConsolidate
        ? values
        : consolidateIntakeValuesForSave({
            loaded: loadedInitialData || formData,
            snapshot: lastPopulatedIntakeRef.current,
            form: values,
            templateSelections: templateSelectionsForSave,
            fieldTemplateSelections: fieldSelectionsForSave,
          });

      if (intakeValuesContainTemplatePlaceholder(consolidated)) {
        if (lockForm) {
          throw new Error(
            'Cannot lock while a template is still generating. Wait for generation to finish, then try again.'
          );
        }
        return;
      }

      const valuesToSave = prepareDraftSaveValues(consolidated);
      
      // Transform arrays to strings in endpoints_objectives and ensure regimen_arms is properly formatted
      const mergedSelectionsForTransform = {
        ...fieldSelectionsForSave,
        ...templateSelectionsForSave,
      };
      const transformedValuesWithoutId = transformFormValues(
        sanitizeValue(valuesToSave),
        mergedSelectionsForTransform
      );
      
      // Remove _id from the data if it exists (to prevent duplicate key error)
      const { _id, ...dataWithoutMongoId } = transformedValuesWithoutId;
      let mongoId = resolveIntakeMongoId(values);
      if (!mongoId && currentProject._id) {
        try {
          const latest = await clinicalIntakeService.fetchLatestClinicalIntakeData(currentProject._id);
          mongoId = latest?._id || null;
        } catch {
          /* use POST create if no existing intake */
        }
      }
      const persistStatus = resolveIntakePersistStatus(consolidated);
      const selectionsForSave = templateSelectionsForSave;

      const transformedValues = {
        ...dataWithoutMongoId,
        projectId: currentProject._id,
        id: mongoId,
        status: persistStatus,
        lockForm,
        fieldTemplateSelections: fieldSelectionsForSave,
        section9TemplateSelections: selectionsForSave,
        studyTableSubfieldSelections: studyTableSubfieldSelectionsForSave,
      };
      
      console.log('handleSaveAsDraft - saving treatment_table:', transformedValues.study_treatments?.treatment_table);
      console.log('handleSaveAsDraft - saving num_regimens:', transformedValues.study_treatments?.num_regimens);
      console.log('handleSaveAsDraft - saving regimen_arms:', transformedValues.study_treatments?.regimen_arms);
      
      const response = await clinicalIntakeService.saveDraft(transformedValues, {
        status: persistStatus,
        lockForm,
      });
      if (response) {
        const savedDoc = response.data || response;
        // Prefer server document for reload fidelity (decisions / mermaid nests)
        let authoritative = savedDoc;
        try {
          const latest = await clinicalIntakeService.fetchLatestClinicalIntakeData(currentProject._id);
          if (latest) authoritative = latest;
        } catch {
          /* fall back to save response */
        }
        rememberPopulatedIntakeSnapshot(
          preferClientTemplateFields || skipFormReinitialize || quiet
            ? valuesToSave
            : authoritative || valuesToSave
        );
        if (authoritative?._id || savedDoc?._id) {
          const savedId = authoritative?._id || savedDoc?._id;
          const savedStatus = authoritative?.status || savedDoc?.status || persistStatus;
          const savedLocked =
            typeof authoritative?.isLocked === 'boolean'
              ? authoritative.isLocked
              : savedDoc?.isLocked;

          // Quiet / template saves must not touch loadedInitialData/formData at all.
          // Any setLoadedInitialData triggers Formik enableReinitialize and remounts
          // initialValues — wiping Section 9 CPT text that only lives in Formik yet.
          if (quiet || skipFormReinitialize || preferClientTemplateFields) {
            intakeHydratedRef.current = true;
            if (lockForm) {
              syncLockFromIntakeDoc(authoritative || savedDoc);
            }
          } else {
            const serverDoc = authoritative || savedDoc || {};
            const preferTemplateSections = (sectionKey) => {
              const serverSection = serverDoc[sectionKey] || {};
              const clientSection = valuesToSave[sectionKey] || {};
              const mergedSection = { ...serverSection };
              Object.keys(clientSection).forEach((fieldKey) => {
                const clientVal = clientSection[fieldKey];
                if (
                  typeof clientVal === 'string' &&
                  clientVal.trim() &&
                  !isTemplateGenerationPlaceholder(clientVal)
                ) {
                  mergedSection[fieldKey] = clientVal;
                }
              });
              return mergedSection;
            };
            const withId = sanitizeValue({
              ...(serverDoc || valuesToSave),
              template_language_fields: preferTemplateSections('template_language_fields'),
              regulatory_requirements: preferTemplateSections('regulatory_requirements'),
              discontinuation_rules: preferTemplateSections('discontinuation_rules'),
              _id: savedId,
              status: savedStatus,
              isLocked: savedLocked,
            });
            setLoadedInitialData(withId);
            setFormData(withId);
            intakeHydratedRef.current = true;
            if (lockForm) {
              syncLockFromIntakeDoc(authoritative || savedDoc);
            }
          }
        }
        if (!quiet) {
          setSnackbarMessage('Draft saved successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }
      }
    } catch (error) {
      const errMsg =
        (typeof error === 'object' && error?.message) ||
        (typeof error === 'string' ? error : null) ||
        'Failed to save draft';
      console.error('Error saving draft:', error);
      if (!quiet) {
        setError(errMsg);
        setSnackbarMessage(`Failed to save draft: ${errMsg}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      if (manageLoading) {
        setLoading(false);
      }
    }
  };
  handleSaveAsDraftRef.current = handleSaveAsDraft;

  const persistUploadedFilesQuietly = () => {
    const latest = formikValuesRef.current;
    if (!latest || !handleSaveAsDraftRef.current) return;
    handleSaveAsDraftRef.current(latest, { manageLoading: false, quiet: true }).catch((err) => {
      console.warn('Failed to persist uploaded file metadata:', err);
    });
  };


  const STUDY_ASSESSMENT_FIELD_KEYS = [
    'screening_baseline_assessments',
    'efficacy_assessments',
    'safety_assessments_general',
    'physical_examination',
    'vital_signs',
    'electrocardiograms',
    'clinical_laboratory_assessment',
    'pregnancy_testing',
    'suicidal_ideation_risk_monitoring',
    'pharmacokinetics',
    'biomarkers',
    'genetics_pharmacogenomics',
    'pharmacodynamic_biomarkers',
    'other_biomarkers',
    'comments',
  ];

  const syncStudyAssessmentsFromStudyParts = (studyParts, existingAssessments = {}) => {
    const isEmptyVal = (v) =>
      v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));
    const merged = { ...(existingAssessments || {}) };
    STUDY_ASSESSMENT_FIELD_KEYS.forEach((key) => {
      if (!isEmptyVal(merged[key])) return;
      for (const partKey of Object.keys(studyParts || {})) {
        const part = studyParts[partKey];
        if (!part || typeof part !== 'object') continue;
        if (key === 'comments') {
          const folded = getMergedNotesForPartDirect(
            studyParts,
            partKey,
            existingAssessments?.comments
          );
          if (!isEmptyVal(folded)) {
            merged[key] = folded;
            return;
          }
        }
        const directVal = part.direct?.[key];
        if (!isEmptyVal(directVal)) {
          merged[key] = directVal;
          return;
        }
        for (const cohortKey of Object.keys(part).filter(isStudyPartCohortKey)) {
          if (cohortKey === 'direct') continue;
          const val = part[cohortKey]?.[key];
          if (!isEmptyVal(val)) {
            merged[key] = val;
            return;
          }
        }
      }
    });
    return merged;
  };


  // Helper function to transform form values for backend submission
  const transformFormValues = (values, templateSelectionsForTransform = null) => {
    const mergedSel =
      templateSelectionsForTransform ||
      getMergedTemplateSelections();
    console.log('transformFormValues input - num_regimens:', values?.study_treatments?.num_regimens);
    console.log('transformFormValues input - num_regimens type:', typeof values?.study_treatments?.num_regimens);

    if (values?.study_overview?.study_parts) {
      migrateStudyPartsPopulationKeys(values.study_overview.study_parts);
    }
    
    const transformed = {
      ...values,
      endpoints_objectives: {
        ...values.endpoints_objectives,
        key_secondary_objectives_endpoints: Array.isArray(values.endpoints_objectives?.key_secondary_objectives_endpoints) 
          ? values.endpoints_objectives.key_secondary_objectives_endpoints.join('\n')
          : values.endpoints_objectives?.key_secondary_objectives_endpoints || '',
        secondary_objectives_endpoints: Array.isArray(values.endpoints_objectives?.secondary_objectives_endpoints)
          ? values.endpoints_objectives.secondary_objectives_endpoints.join('\n')
          : values.endpoints_objectives?.secondary_objectives_endpoints || '',
        exploratory_objectives_endpoints: Array.isArray(values.endpoints_objectives?.exploratory_objectives_endpoints)
          ? values.endpoints_objectives.exploratory_objectives_endpoints.join('\n')
          : values.endpoints_objectives?.exploratory_objectives_endpoints || '',
      },
      discontinuation_rules: {
        ...values.discontinuation_rules,
        discontinuation_trial_intervention_individual: Array.isArray(values.discontinuation_rules?.discontinuation_trial_intervention_individual)
          ? values.discontinuation_rules.discontinuation_trial_intervention_individual.join('\n')
          : values.discontinuation_rules?.discontinuation_trial_intervention_individual || '',
        additional_information: Array.isArray(values.discontinuation_rules?.additional_information)
          ? values.discontinuation_rules.additional_information.join('\n')
          : values.discontinuation_rules?.additional_information || '',
      },
      study_assessments: syncStudyAssessmentsFromStudyParts(
        values.study_overview?.study_parts,
        values.study_assessments
      ),
      statistical_considerations: {
        ...values.statistical_considerations,
        analysis_sets: Array.isArray(values.statistical_considerations?.analysis_sets)
          ? values.statistical_considerations.analysis_sets.join('\n')
          : values.statistical_considerations?.analysis_sets || '',
        primary_objective_analysis: Array.isArray(values.statistical_considerations?.primary_objective_analysis)
          ? values.statistical_considerations.primary_objective_analysis.join('\n')
          : values.statistical_considerations?.primary_objective_analysis || '',
        secondary_objective_analysis: Array.isArray(values.statistical_considerations?.secondary_objective_analysis)
          ? values.statistical_considerations.secondary_objective_analysis.join('\n')
          : values.statistical_considerations?.secondary_objective_analysis || '',
        exploratory_analysis: Array.isArray(values.statistical_considerations?.exploratory_analysis)
          ? values.statistical_considerations.exploratory_analysis.join('\n')
          : values.statistical_considerations?.exploratory_analysis || '',
        safety_analysis: Array.isArray(values.statistical_considerations?.safety_analysis)
          ? values.statistical_considerations.safety_analysis.join('\n')
          : values.statistical_considerations?.safety_analysis || '',
        other_analyses: Array.isArray(values.statistical_considerations?.other_analyses)
          ? values.statistical_considerations.other_analyses.join('\n')
          : values.statistical_considerations?.other_analyses || '',
        interim_analyses: Array.isArray(values.statistical_considerations?.interim_analyses)
          ? values.statistical_considerations.interim_analyses.join('\n')
          : values.statistical_considerations?.interim_analyses || '',
        sample_size_determination: Array.isArray(values.statistical_considerations?.sample_size_determination)
          ? values.statistical_considerations.sample_size_determination.join('\n')
          : values.statistical_considerations?.sample_size_determination || ''
      },
      target_population: {
        ...values.target_population,
        conditions_related_to_primary_disease: Array.isArray(values.target_population?.conditions_related_to_primary_disease)
          ? values.target_population.conditions_related_to_primary_disease
          : [values.target_population?.conditions_related_to_primary_disease || '']
      },
      regulatory_requirements: {
        ...values.regulatory_requirements,
        countries_for_submission: Array.isArray(values.regulatory_requirements?.countries_for_submission)
          ? values.regulatory_requirements.countries_for_submission.join('\n')
          : values.regulatory_requirements?.countries_for_submission || '',
        committees: resolveCommitteesUnlessTemplateSelected(
          values.study_overview?.study_parts,
          values.regulatory_requirements?.committees,
          mergedSel
        ),
      },
      study_assessments: {
        ...values.study_assessments
      },
      appendices: Array.isArray(values.appendices)
        ? values.appendices.length > 0 &&
          typeof values.appendices[0] === 'object' &&
          values.appendices[0] !== null
          ? values.appendices
          : values.appendices
        : values.appendices || [],
      document_uploads: values.document_uploads
        ? {
            ...values.document_uploads,
            uploaded_files: Object.fromEntries(
              Object.entries(values.document_uploads.uploaded_files || {}).map(([key, arr]) => [
                key,
                Array.isArray(arr)
                  ? arr.map((f) => {
                      if (!f || typeof f !== 'object') return f;
                      const { summaryPending, intakeSectionsPending, ...rest } = f;
                      return rest;
                    })
                  : arr,
              ])
            ),
            ...(validationCompleted &&
            lastValidatedFingerprintRef.current &&
            Object.keys(fileValidationStatus).length > 0
              ? {
                  validation_state: buildPersistedValidationState(
                    lastValidatedFingerprintRef.current,
                    allFilesValid,
                    fileValidationStatus,
                    true
                  ),
                }
              : {}),
            ...(autoPopulateSucceeded
              ? {
                  source_documents_workflow: {
                    auto_populate_succeeded: true,
                    auto_populate_completed_at:
                      values.document_uploads?.source_documents_workflow
                        ?.auto_populate_completed_at || new Date().toISOString(),
                  },
                }
              : values.document_uploads.source_documents_workflow
                ? { source_documents_workflow: values.document_uploads.source_documents_workflow }
                : {}),
          }
        : values.document_uploads,
    };

    // Remove trial-schema template selection from the payload.
    if (
      transformed?.study_overview &&
      Object.prototype.hasOwnProperty.call(transformed.study_overview, 'template_selection')
    ) {
      delete transformed.study_overview.template_selection;
    }

    if (
      transformed?.study_treatments &&
      Object.prototype.hasOwnProperty.call(transformed.study_treatments, '_debug_regimen_arms')
    ) {
      delete transformed.study_treatments._debug_regimen_arms;
    }

    repairClubbedAnalysisFieldsInDoc({ study_overview: transformed.study_overview });

    const plAssembly = assemblePatientLibrarySection54(transformed);
    const liverAssembly = assemblePatientLibrarySection7111(transformed);
    const tempDiscAssembly = assembleTemporaryDiscontinuationSection712(transformed);
    const rechallengeAssembly = assembleRechallengeSection713(transformed);
    if (transformed.discontinuation_rules) {
      transformed.discontinuation_rules = {
        ...transformed.discontinuation_rules,
        temporary_discontinuation_decisions: {
          ...DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
          ...(transformed.discontinuation_rules.temporary_discontinuation_decisions || {}),
        },
        temporary_discontinuation_trial_intervention:
          tempDiscAssembly.omitSection
            ? ''
            : tempDiscAssembly.temporary_discontinuation_trial_intervention ||
              transformed.discontinuation_rules.temporary_discontinuation_trial_intervention ||
              '',
        rechallenge_decisions: {
          ...DEFAULT_RECHALLENGE_DECISIONS,
          ...(transformed.discontinuation_rules.rechallenge_decisions || {}),
          protocol_requirements:
            transformed.discontinuation_rules.rechallenge_decisions?.protocol_requirements || [],
        },
        rechallenge:
          rechallengeAssembly.rechallenge ||
          transformed.discontinuation_rules.rechallenge ||
          '',
      };
    }
    if (transformed.template_language_fields) {
      transformed.template_language_fields = {
        ...transformed.template_language_fields,
        patient_library_decisions: {
          ...DEFAULT_PATIENT_LIBRARY_DECISIONS,
          ...(transformed.template_language_fields.patient_library_decisions || {}),
        },
        liver_patient_library_decisions: {
          ...DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
          ...(transformed.template_language_fields.liver_patient_library_decisions || {}),
        },
        definitions_childbearing_potential:
          plAssembly.definitions_childbearing_potential ||
          transformed.template_language_fields.definitions_childbearing_potential ||
          '',
        contraception_requirements:
          plAssembly.contraception_requirements ||
          transformed.template_language_fields.contraception_requirements ||
          '',
        liver_event_stopping_criteria:
          liverAssembly.liver_event_stopping_criteria ||
          transformed.template_language_fields.liver_event_stopping_criteria ||
          '',
        liver_event_stopping_flowchart_mermaid:
          liverAssembly.liver_event_stopping_flowchart_mermaid ||
          transformed.template_language_fields.liver_event_stopping_flowchart_mermaid ||
          '',
        liver_restart_rechallenge:
          liverAssembly.liver_restart_rechallenge ||
          transformed.template_language_fields.liver_restart_rechallenge ||
          '',
      };
    }

    return scrubIntakePlaceholdersFromDoc(transformed);
  };

  const handleNumberOfArmsChange = (value, setFieldValue, values) => {
    const numArms = parseInt(value) || 0;
    const currentArms = values.study_treatments?.regimen_arms || {};
    const newArms = {};

    // Create new arms up to the specified number
    for (let i = 1; i <= numArms; i++) {
      newArms[i] = currentArms[i] || '';
    }

    setFieldValue('study_treatments.regimen_arms', newArms);
    
    // Regenerate treatment table with updated regimen arms
    if (values.study_treatments?.num_regimens > 0) {
      const updatedTreatmentTable = generateDynamicTreatmentTable(
        values.study_treatments?.num_regimens,
        newArms,
        null,
        values.study_treatments?.treatment_table || {},
        values.study_overview?.study_parts || null
      );
      setFieldValue('study_treatments.treatment_table', updatedTreatmentTable);
    }
  };
  const handleRegimenArmChange = (armNumber, value, setFieldValue, values) => {
    const currentArms = values.study_treatments?.regimen_arms || {};
    const newArms = { ...currentArms, [armNumber]: value };
    
    // Update the individual field
    setFieldValue(`study_treatments.regimen_arms.${armNumber}`, value);
    
    // ALSO update the entire regimen_arms object to ensure it's properly saved
    setFieldValue('study_treatments.regimen_arms', newArms);
    
    console.log('Updated regimen arms:', newArms);
    
    // Regenerate treatment table with updated regimen arms
    if (values.study_treatments?.num_regimens > 0) {
      console.log('handleRegimenArmChange regenerating treatment table with num_regimens:', values.study_treatments?.num_regimens);
      const updatedTreatmentTable = generateDynamicTreatmentTable(
        values.study_treatments?.num_regimens,
        newArms,
        null,
        values.study_treatments?.treatment_table || {},
        values.study_overview?.study_parts || null
      );
      setFieldValue('study_treatments.treatment_table', updatedTreatmentTable);
      console.log('handleRegimenArmChange set treatment table:', updatedTreatmentTable);
    } else {
      console.log('handleRegimenArmChange num_regimens is 0, not regenerating treatment table');
    }
  };

  const handleNumberOfRegimensChange = (value, setFieldValue, values) => {
    const numRegimens = parseInt(value) || 0;
    console.log('handleNumberOfRegimensChange called with:', { value, numRegimens, type: typeof numRegimens });
    console.log('handleNumberOfRegimensChange current regimen_arms:', values.study_treatments?.regimen_arms);
    setFieldValue('study_treatments.num_regimens', numRegimens);

    const numParts = parseInt(values.study_overview?.number_of_study_parts_new) || 0;
    if (isMultiPartTrial(numParts) && numRegimens > 0) {
      const synced = distributeRegimensAcrossParts(
        numParts,
        numRegimens,
        values.study_treatments?.regimens_per_part || {}
      );
      setFieldValue('study_treatments.regimens_per_part', synced);
    } else if (!isMultiPartTrial(numParts) && numRegimens > 0) {
      setFieldValue('study_treatments.regimens_per_part', { part1: numRegimens });
    } else if (numRegimens === 0) {
      setFieldValue('study_treatments.regimens_per_part', {});
    }
    
    // Regenerate treatment table with current regimen arms
    if (numRegimens > 0) {
      console.log('handleNumberOfRegimensChange generating treatment table for', numRegimens, 'regimens');
      const updatedTreatmentTable = generateDynamicTreatmentTable(
        numRegimens,
        values.study_treatments?.regimen_arms || {},
        null,
        values.study_treatments?.treatment_table || {},
        values.study_overview?.study_parts || null
      );
      setFieldValue('study_treatments.treatment_table', updatedTreatmentTable);
      console.log('handleNumberOfRegimensChange set treatment table:', updatedTreatmentTable);
    } else {
      // Clear treatment table if no regimens
      console.log('handleNumberOfRegimensChange numRegimens is 0, clearing treatment table');
      setFieldValue('study_treatments.treatment_table', {});
    }
  };

  const handleNumberOfStudyPartsChange = (value, setFieldValue, values) => {
    const numParts = parseInt(value) || 0;
    setFieldValue('study_overview.number_of_study_parts_new', numParts);

    const numRegimens = parseInt(values.study_treatments?.num_regimens) || 0;
    if (numParts > 0 && numRegimens > 0) {
      const synced = distributeRegimensAcrossParts(
        numParts,
        numRegimens,
        values.study_treatments?.regimens_per_part || {}
      );
      setFieldValue('study_treatments.regimens_per_part', synced);
    } else if (!isMultiPartTrial(numParts)) {
      setFieldValue(
        'study_treatments.regimens_per_part',
        numRegimens > 0 ? { part1: numRegimens } : {}
      );
    }
  };

  // Document type configurations for the dynamic table
  const getDocumentTypeConfig = (documentType) => {
    const configs = {
      study_design_outline: {
        label: 'Study Design Outline Document',
        accept: '.pdf,.ppt,.pptx,.doc,.docx,.txt,.mp3,.wav,.m4a,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: true,
      },
      investigator_brochure: {
        label: 'Investigator Brochure (IB)',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      label: {
        label: 'Label',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      additional_reports: {
        label: 'Additional Safety/Efficacy Reports',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      pharmacy_manual: {
        label: 'Pharmacy Manual',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      risk_management_guidelines: {
        label: 'Risk Management Guidelines',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      user_defined: {
        label: 'Additional (User Defined)',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      control_arm_documents: {
        label: 'Control Arm related documents',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: false,
      },
      disease_background_documents: {
        label: 'Disease Background',
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv',
        multiple: true,
        hasDescription: true,
      },
      trial_schema: {
        label: 'Trial Schema Image',
        accept: 'image/*',
        multiple: false,
        hasDescription: false,
      },
    };
    return configs[documentType] || { label: documentType, accept: '.pdf,.doc,.docx', multiple: false, hasDescription: false };
  };

  const getAllDocumentTypes = () => [
    { value: 'study_design_outline', label: 'Study Design Outline Document' },
    { value: 'investigator_brochure', label: 'Investigator Brochure (IB)' },
    { value: 'label', label: 'Label' },
    { value: 'additional_reports', label: 'Additional Safety/Efficacy Reports' },
    { value: 'pharmacy_manual', label: 'Pharmacy Manual' },
    { value: 'risk_management_guidelines', label: 'Risk Management Guidelines' },
    { value: 'user_defined', label: 'Additional (User Defined)' },
    { value: 'control_arm_documents', label: 'Control Arm related documents' },
    { value: 'disease_background_documents', label: 'Disease Background' },
  ];

  const UPLOAD_DOC_TYPES = [
    'study_design_outline',
    'investigator_brochure',
    'label',
    'additional_reports',
    'pharmacy_manual',
    'risk_management_guidelines',
    'user_defined',
    'control_arm_documents',
    'disease_background_documents',
  ];

  const getActiveDocumentTypes = (currentValues) => {
    const rows = currentValues?.document_uploads?.document_table || [];
    const fromTable = [...new Set(rows.map((r) => r.documentType).filter(Boolean))];
    if (fromTable.length > 0) {
      return fromTable;
    }
    const uploaded = currentValues?.document_uploads?.uploaded_files || {};
    return UPLOAD_DOC_TYPES.filter((docType) => uploaded[docType]?.length > 0);
  };

  const collectActiveUploadedFiles = (currentValues, { includeIntakeSections = false } = {}) => {
    const activeTypes = getActiveDocumentTypes(currentValues);
    const uploaded = currentValues?.document_uploads?.uploaded_files || {};
    const allFiles = [];
    const seen = new Set();

    activeTypes.forEach((docType) => {
      (uploaded[docType] || []).forEach((file) => {
        const azureBlobName = file.azureBlobName || file.filename;
        if (!azureBlobName) return;

        const dedupeKey = `${docType}:${azureBlobName}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        const entry = {
          filename: file.filename,
          originalname: file.originalname || file.name,
          documentType: docType,
          azureBlobName,
          mimeType: file.mimeType || file.type,
        };
        if (includeIntakeSections) {
          entry.intakeSectionsForAutoPopulate = Array.isArray(file.intakeSectionsForAutoPopulate)
            ? file.intakeSectionsForAutoPopulate
            : [];
        }
        allFiles.push(entry);
      });
    });

    return allFiles;
  };

  const hasAnyUploadedFiles = (currentValues) => {
    const uploaded = currentValues?.document_uploads?.uploaded_files || {};
    return Object.values(uploaded).some((arr) => Array.isArray(arr) && arr.length > 0);
  };

  const buildValidationFingerprint = (currentValues) =>
    collectActiveUploadedFiles(currentValues)
      .map((f) => `${f.documentType}:${f.azureBlobName}`)
      .sort()
      .join('|');

  const clearValidationStatusForDocumentType = (documentType, fileObj = null) => {
    const fileBases = fileObj
      ? [fileObj.azureBlobName, fileObj.filename, fileObj.originalname, fileObj.name].filter(Boolean)
      : [];

    const shouldRemoveKey = (key) => {
      if (!key.endsWith(`_${documentType}`)) return false;
      if (fileBases.length === 0) return true;
      return fileBases.some((base) => key === `${base}_${documentType}`);
    };

    setFileValidationStatus((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (shouldRemoveKey(key)) delete next[key];
      });
      return next;
    });

    setSummarizingDocs((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (shouldRemoveKey(key)) delete next[key];
      });
      return next;
    });

    setAllFilesValid(false);
    allFilesValidRef.current = false;
    setValidationCompleted(false);
    lastValidatedFingerprintRef.current = '';
    if (currentProject?._id) {
      clearDocumentValidationCache(currentProject._id);
    }
    if (setFieldValueRef.current) {
      setFieldValueRef.current('document_uploads.validation_state', {
        fingerprint: '',
        allValid: false,
        validationCompleted: false,
        fileStatus: {},
      });
    }
    const latest = formikValuesRef.current;
    if (latest && handleSaveAsDraftRef.current) {
      handleSaveAsDraftRef.current(
        {
          ...latest,
          document_uploads: {
            ...latest.document_uploads,
            validation_state: {
              fingerprint: '',
              allValid: false,
              validationCompleted: false,
              fileStatus: {},
            },
          },
        },
        { manageLoading: false, quiet: true }
      ).catch((err) => console.warn('Failed to clear persisted validation state:', err));
    }
  };

  const handleFileUpload = async (file, documentType, setFieldValue, values) => {
    if (!file) return;

    if (documentType === 'trial_schema' && file.type?.startsWith('image/')) {
      try {
        const rejectionReason = await getTrialSchemaImageRejectionReason(file);
        if (rejectionReason) {
          setSnackbarMessage(rejectionReason);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
      } catch (analysisError) {
        console.warn('Trial schema image pre-check failed, deferring to server validation:', analysisError);
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    formData.append('studyId', values.study_identification?.protocol_number || '');
    formData.append('projectId', currentProject._id); // Add projectId to formData
    formData.append('title', file.name); // Add title for backend requirement

    try {
      const response = await axiosInstance.post(API_ENDPOINTS.documents.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const newDocument = response.data.file;
        const blobName = newDocument.azureBlobName || newDocument.filename || newDocument.name || '';
        const docEntry = {
          ...newDocument,
          contentSummary: '',
          summaryPending: true,
          summaryError: '',
          intakeSectionsForAutoPopulate: [],
          intakeSectionsPending: true,
          intakeSectionsError: '',
        };
        const uploadConfig = getDocumentTypeConfig(documentType);
        const existingFiles = values.document_uploads.uploaded_files[documentType] || [];
        if (!uploadConfig.multiple && existingFiles.length > 0) {
          clearValidationStatusForDocumentType(documentType);
        }
        setFieldValue(
          `document_uploads.uploaded_files.${documentType}`,
          uploadConfig.multiple ? [...existingFiles, docEntry] : [docEntry]
        );
        setSnackbarMessage('File uploaded successfully! Generating summary…');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Fire-and-forget: generate a comprehensive summary independent of validation.
        // Persist summary on the file object in Formik so the table always has a stable source of truth.
        const aliasBases = [
          ...new Set(
            [blobName, newDocument.filename, newDocument.azureBlobName, newDocument.originalname, newDocument.name].filter(
              Boolean
            )
          ),
        ];
        if (blobName && aliasBases.length) {
          setSummarizingDocs((prev) => {
            const next = { ...prev };
            aliasBases.forEach((b) => {
              next[`${b}_${documentType}`] = true;
            });
            return next;
          });
          axiosInstance
            .post(API_ENDPOINTS.ai.summarizeDocument, {
              azureBlobName: blobName,
              filename: newDocument.originalname || newDocument.name || blobName,
            })
            .then((sumRes) => {
              const summary = String(sumRes.data?.summary ?? '').trim();
              const reason = String(sumRes.data?.reason ?? '').trim();
              const apply = () => {
                const latest = formikValuesRef.current;
                const list = latest?.document_uploads?.uploaded_files?.[documentType];
                if (!Array.isArray(list)) return;

                const updated = list.map((f) => {
                  if (f.azureBlobName === blobName || f.filename === blobName) {
                    return {
                      ...f,
                      contentSummary: sumRes.data?.success && summary ? summary : f.contentSummary || '',
                      summaryPending: false,
                      summaryError: sumRes.data?.success
                        ? (summary ? '' : (reason || f.summaryError || 'No summary could be generated (no readable text)'))
                        : (reason || f.summaryError || 'Summary generation failed'),
                    };
                  }
                  return f;
                });
                setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated);

                if (sumRes.data?.success && summary) {
                  setDocumentSummaries((prev) => {
                    const next = { ...prev };
                    aliasBases.forEach((b) => {
                      next[`${b}_${documentType}`] = summary;
                    });
                    return next;
                  });

                  // Map ALL uploaded documents with summaries to intake sections (batch),
                  // so the model can see enough context across multiple uploads.
                  // IMPORTANT: `setFieldValue` above is async; `formikValuesRef.current` may still have stale
                  // summaries. Build the batch from the `updated` list we just computed, merged into the
                  // latest Formik snapshot.
                  const latestAll = formikValuesRef.current;
                  const uploadedLatest = latestAll?.document_uploads?.uploaded_files || {};
                  const uploadedVirtual = { ...uploadedLatest, [documentType]: updated };

                  const docsForBatch = Object.entries(uploadedVirtual)
                    .flatMap(([docTypeKey, arr]) => {
                      if (!Array.isArray(arr)) return [];
                      return arr
                        .filter((x) => typeof x?.contentSummary === 'string' && x.contentSummary.trim())
                        .map((x) => ({
                          // Prefer azureBlobName for stable identity across UI/backend.
                          id: x.azureBlobName || x.filename || x.originalname || x.name,
                          filename: x.originalname || x.name || x.filename || x.azureBlobName || '',
                          azureBlobName: x.azureBlobName || '',
                          documentType: docTypeKey,
                          summary: String(x.contentSummary || '').trim(),
                        }));
                    })
                    .slice(0, 20);

                  // If something went wrong and we can't build batch docs, fallback to single-document mapping.
                  const doSingleMapFallback = () =>
                    axiosInstance.post(API_ENDPOINTS.ai.mapDocumentToIntakeSections, { summary, documentType });

                  (docsForBatch.length > 1
                    ? axiosInstance.post(API_ENDPOINTS.ai.mapDocumentsToIntakeSections, { documents: docsForBatch })
                    : doSingleMapFallback()
                  )
                    .then((mapRes) => {
                      // batch response shape: { documents:[{id,sections}], overallSections:[...] }
                      const docs = Array.isArray(mapRes.data?.documents) ? mapRes.data.documents : null;
                      const isBatch = Array.isArray(docs);

                      queueMicrotask(() => {
                        const latest2 = formikValuesRef.current;
                        const uploaded2 = latest2?.document_uploads?.uploaded_files || {};

                        if (isBatch) {
                          const byId = new Map(
                            docs
                              .filter((d) => d && (d.id || d.filename))
                              .map((d) => [String(d.id || d.filename), Array.isArray(d.sections) ? d.sections : []])
                          );

                          Object.entries(uploaded2).forEach(([docTypeKey, arr]) => {
                            if (!Array.isArray(arr)) return;
                            const updatedArr = arr.map((ff) => {
                              const key = String(
                                ff.azureBlobName || ff.filename || ff.originalname || ff.name || ''
                              );
                              const sections = byId.get(key) || [];
                              // Mark complete for docs with summaries; leave others as pending/empty.
                              if (typeof ff?.contentSummary === 'string' && ff.contentSummary.trim()) {
                                return {
                                  ...ff,
                                  intakeSectionsForAutoPopulate: sections,
                                  intakeSectionsPending: false,
                                  intakeSectionsError: '',
                                };
                              }
                              return ff;
                            });
                            setFieldValue(`document_uploads.uploaded_files.${docTypeKey}`, updatedArr);
                          });
                          setTimeout(() => persistUploadedFilesQuietly(), 200);
                          return;
                        }

                        // single-document response shape: { sections: [...] }
                        const sections = Array.isArray(mapRes.data?.sections) ? mapRes.data.sections : [];
                        const list2 = uploaded2?.[documentType];
                        if (!Array.isArray(list2)) return;
                        const updated2 = list2.map((ff) => {
                          if (ff.azureBlobName === blobName || ff.filename === blobName) {
                            return {
                              ...ff,
                              intakeSectionsForAutoPopulate: sections,
                              intakeSectionsPending: false,
                              intakeSectionsError: '',
                            };
                          }
                          return ff;
                        });
                        setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated2);
                        setTimeout(() => persistUploadedFilesQuietly(), 200);
                      });
                    })
                    .catch((err) => {
                      console.warn('Mapping document(s) to intake sections failed:', err.message);
                      queueMicrotask(() => {
                        const latest2 = formikValuesRef.current;
                        const list2 = latest2?.document_uploads?.uploaded_files?.[documentType];
                        if (!Array.isArray(list2)) return;
                        const updated2 = list2.map((ff) => {
                          if (ff.azureBlobName === blobName || ff.filename === blobName) {
                            return {
                              ...ff,
                              intakeSectionsForAutoPopulate: [],
                              intakeSectionsPending: false,
                              intakeSectionsError: err?.message || 'Failed to map intake sections',
                            };
                          }
                          return ff;
                        });
                        setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated2);
                        setTimeout(() => persistUploadedFilesQuietly(), 200);
                      });
                    });
                } else {
                  // No summary => we cannot map sections; mark mapping as finished with reason
                  const latest2 = formikValuesRef.current;
                  const list2 = latest2?.document_uploads?.uploaded_files?.[documentType];
                  if (Array.isArray(list2)) {
                    const updated2 = list2.map((ff) =>
                      ff.azureBlobName === blobName || ff.filename === blobName
                        ? {
                            ...ff,
                            intakeSectionsForAutoPopulate: [],
                            intakeSectionsPending: false,
                            intakeSectionsError: 'No summary available to map sections',
                          }
                        : ff
                    );
                    setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated2);
                  }
                  setTimeout(() => persistUploadedFilesQuietly(), 200);
                }
              };
              queueMicrotask(apply);
            })
            .catch((err) => {
              console.warn('Document summarization failed:', err.message);
              queueMicrotask(() => {
                const latest = formikValuesRef.current;
                const list = latest?.document_uploads?.uploaded_files?.[documentType];
                if (Array.isArray(list)) {
                  const updated = list.map((f) =>
                  f.azureBlobName === blobName || f.filename === blobName
                    ? { ...f, summaryPending: false, summaryError: err?.message || 'Summary generation failed' }
                    : f
                  );
                  setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated);
                }
                setTimeout(() => persistUploadedFilesQuietly(), 200);
              });
            })
            .finally(() =>
              setSummarizingDocs((prev) => {
                const next = { ...prev };
                aliasBases.forEach((b) => {
                  delete next[`${b}_${documentType}`];
                });
                return next;
              })
            );
        }
      } else {
        throw new Error(response.data.error || 'File upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const serverMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      setSnackbarMessage(`File upload failed: ${serverMessage}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  /** Find protocol section 12 (List of Appendices) and overwrite its output from the given
   * appendices array — keeps the generated document in sync after any appendix upload/removal.
   * Returns whether a section 12 was found and updated. */
  const resyncProtocolSection12 = async (appendices) => {
    if (!currentProject?._id) return false;
    const allSections = await documentEditorService.getAllSections(currentProject._id);
    const list = Array.isArray(allSections)
      ? allSections
      : Array.isArray(allSections?.data)
        ? allSections.data
        : Array.isArray(allSections?.documents)
          ? allSections.documents
          : [];
    const section12 = list.find(
      (s) =>
        /^12\b/.test(String(s?.title || '').trim()) ||
        /APPENDIX: SUPPORTING DETAILS/i.test(String(s?.title || '')),
    );
    if (!section12?._id) return false;
    const nextOutput = mergeAppendicesIntoSection12Output(section12.output || '', appendices);
    await documentEditorService.updateSection(
      section12._id,
      {
        title: section12.title,
        context: section12.context,
        prompt: section12.prompt,
        output: nextOutput,
        documentType: 'PROTOCOL',
      },
      currentProject._id,
    );
    return true;
  };

  /** Upload PDF/Word/image for an appendix row; append extracted HTML (text + images). */
  const handleAppendixFileUpload = async (file, appendixIndex, setFieldValue, values) => {
    if (!file || appendixIndex == null || appendixIndex < 0) return;
    setAppendixUploadingIndex(appendixIndex);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'studyId',
        values?.study_identification?.protocol_number || currentProject?._id || '',
      );
      formData.append('projectId', currentProject?._id || '');

      const response = await axiosInstance.post(
        API_ENDPOINTS.clinicalIntake.appendixExtractUpload,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Appendix extract failed');
      }

      const extractedHtml = String(response.data.contentHtml || '').trim();
      // Client-generated, independent of whatever the backend's fileMeta shape includes — used to
      // tag this file's slice of contentHtml so a later per-file removal can cleanly excise just
      // that content instead of clearing the whole appendix (contentHtml is otherwise an opaque
      // concatenation of every uploaded file's extracted HTML with no other boundary markers).
      const uploadId = `upl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const fileMeta = {
        ...(response.data.fileMeta || {
          name: file.name,
          size: file.size,
          contentType: file.type,
          azureBlobUrl: response.data.azureBlobUrl || '',
          uploadedAt: new Date().toISOString(),
        }),
        uploadId,
      };

      const appendices = Array.isArray(values.appendices) ? [...values.appendices] : [];
      const current = appendices[appendixIndex] || {
        title: '',
        description: '',
        contentHtml: '',
        uploads: [],
      };
      const priorHtml = String(current.contentHtml || '').trim();
      const taggedHtml = extractedHtml
        ? `<!--APPENDIX_UPLOAD:${uploadId}-->\n${extractedHtml}\n<!--/APPENDIX_UPLOAD:${uploadId}-->`
        : '';
      const nextHtml = priorHtml
        ? `${priorHtml}\n${taggedHtml}`
        : taggedHtml;

      // If description is empty, seed a short plain-text summary from the extract
      let nextDescription = current.description || '';
      if (!String(nextDescription).trim() && extractedHtml) {
        const plain = extractedHtml
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (plain) {
          nextDescription = plain.length > 280 ? `${plain.slice(0, 277)}…` : plain;
        }
      }

      appendices[appendixIndex] = {
        ...current,
        title: current.title || '',
        description: nextDescription,
        contentHtml: nextHtml,
        uploads: [...(Array.isArray(current.uploads) ? current.uploads : []), fileMeta],
      };
      setFieldValue('appendices', appendices);

      // Persist intake so contentHtml survives refresh
      const valuesWithAppendices = { ...values, appendices };
      if (handleSaveAsDraftRef.current) {
        handleSaveAsDraftRef.current(valuesWithAppendices, {
          manageLoading: false,
          quiet: true,
        }).catch((err) => console.warn('Failed to save appendix upload to intake:', err));
      }

      // Auto-populate protocol section 12 (12.1 / 12.2 / …) when it already exists
      let section12Synced = false;
      try {
        section12Synced = await resyncProtocolSection12(appendices);
      } catch (syncErr) {
        console.warn('Could not auto-populate protocol section 12 from appendix upload:', syncErr);
      }

      setSnackbarMessage(
        section12Synced
          ? `Appendix uploaded and filled into protocol section 12 (${current.title || 'appendix'}).`
          : `Appendix content saved on this row. Open Document Authoring and generate section 12 to see it in the protocol (section 12 not created yet).`,
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Appendix upload failed:', error);
      setSnackbarMessage(
        `Appendix upload failed: ${error?.response?.data?.message || error.message}`,
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setAppendixUploadingIndex(null);
    }
  };

  /** Remove one uploaded file from an appendix row — strips its tagged content slice from
   * contentHtml when available (uploads made before this feature have no marker, so their
   * content can't be cleanly separated from the rest; the file is still removed from the list). */
  const handleRemoveAppendixUpload = async (appendixIndex, uploadIndex, setFieldValue, values) => {
    if (appendixIndex == null || appendixIndex < 0 || uploadIndex == null || uploadIndex < 0) return;

    const appendices = Array.isArray(values.appendices) ? [...values.appendices] : [];
    const current = appendices[appendixIndex];
    if (!current || !Array.isArray(current.uploads)) return;

    const removedUpload = current.uploads[uploadIndex];
    if (!removedUpload) return;

    const nextUploads = current.uploads.filter((_, i) => i !== uploadIndex);

    let nextHtml = String(current.contentHtml || '');
    if (removedUpload.uploadId) {
      const escapedId = removedUpload.uploadId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const blockRe = new RegExp(
        `<!--APPENDIX_UPLOAD:${escapedId}-->[\\s\\S]*?<!--/APPENDIX_UPLOAD:${escapedId}-->`,
      );
      nextHtml = nextHtml.replace(blockRe, '').replace(/\n{3,}/g, '\n\n').trim();
    }

    appendices[appendixIndex] = {
      ...current,
      uploads: nextUploads,
      contentHtml: nextHtml,
    };
    setFieldValue('appendices', appendices);

    const valuesWithAppendices = { ...values, appendices };
    if (handleSaveAsDraftRef.current) {
      handleSaveAsDraftRef.current(valuesWithAppendices, {
        manageLoading: false,
        quiet: true,
      }).catch((err) => console.warn('Failed to save appendix removal to intake:', err));
    }

    // Re-sync protocol section 12 so the removed file's content also disappears there.
    try {
      await resyncProtocolSection12(appendices);
    } catch (syncErr) {
      console.warn('Could not re-sync protocol section 12 after appendix file removal:', syncErr);
    }

    setSnackbarMessage(`Removed "${removedUpload.name || 'file'}" from ${current.title || 'appendix'}.`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  /** Remove one entire appendix row — title, description, uploaded files, and extracted content
   * all go together — and re-sync protocol section 12 so that appendix's content fully
   * disappears from the generated document too (not just the intake form row). */
  const handleRemoveAppendix = async (appendixIndex, setFieldValue, values) => {
    if (appendixIndex == null || appendixIndex < 0) return;
    const appendices = Array.isArray(values.appendices) ? [...values.appendices] : [];
    if (appendixIndex >= appendices.length) return;

    const removed = appendices[appendixIndex];
    const nextAppendices = appendices.filter((_, i) => i !== appendixIndex);
    setFieldValue('appendices', nextAppendices);

    const valuesWithAppendices = { ...values, appendices: nextAppendices };
    if (handleSaveAsDraftRef.current) {
      handleSaveAsDraftRef.current(valuesWithAppendices, {
        manageLoading: false,
        quiet: true,
      }).catch((err) => console.warn('Failed to save appendix removal to intake:', err));
    }

    try {
      await resyncProtocolSection12(nextAppendices);
    } catch (syncErr) {
      console.warn('Could not re-sync protocol section 12 after appendix removal:', syncErr);
    }

    setSnackbarMessage(`Removed appendix "${removed?.title || 'Untitled appendix'}" and its uploaded content.`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Helper: resolve validation status for a file in a document section
  // Flattens Mongo-nested keys (caused by `.` in filenames like `.pdf`) and fuzzy-matches re-uploads.
  const resolveFileValidationStatus = (file, documentType) =>
    resolveValidationStatusFromMap(fileValidationStatus, file, documentType);

  // Helper function to render file list with validation status
  const renderFileListWithValidation = (files, documentType, setFieldValue, isFormDisabled) => {
    if (!files || files.length === 0) return null;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {files.map((file, idx) => {
          const validationStatus = resolveFileValidationStatus(file, documentType);
          const isValid = validationStatus?.isValid;
          const positionValid = validationStatus?.positionValid;
          const consistencyValid = validationStatus?.consistencyValid; // may be true/false/null
          const contentValid = validationStatus?.contentValid; // may be true/false/null
          const hasStatus = validationStatus !== undefined;

          const contentFailed = contentValid === false;
          // Determine UI state: only green (all good) or red (any validation failure)
          const state = !hasStatus
            ? (validationCompleted ? 'pending' : 'none')
            : (isValid && positionValid !== false && consistencyValid !== false && !contentFailed) ? 'success' : 'error';

          const muted = Boolean(isFormDisabled);
          const successBorder = muted ? '#9e9e9e' : '#4caf50';
          const errorBorder = muted ? '#9e9e9e' : '#f44336';
          const successBg = muted ? '#f5f5f5' : '#f1f8f4';
          const errorBg = muted ? '#f5f5f5' : '#ffebee';
          const successIcon = muted ? 'action.disabled' : '#4caf50';
          const errorIcon = muted ? 'action.disabled' : '#f44336';
          const warnIcon = muted ? 'action.disabled' : '#ed6c02';

          const displayName = file.originalname || file.name || file.filename || file.azureBlobName || `File ${idx + 1}`;

          // Plain block layout (not a flex row) for the filename, mirroring the
          // Extracted Summary box below (width:'100%' + boxSizing:'border-box' on
          // every ancestor) — that pattern wraps correctly in this table. Putting
          // the filename in a flex row next to the icon/delete button, competing
          // for space via flex-grow, collapsed to ~1 character of width in this
          // column no matter what min-width/flex-basis was set, so the icon and
          // delete button now live in their own small row above the filename.
          return (
            <Box
              key={idx}
              sx={{
                border: state === 'none' || state === 'pending'
                  ? '1px solid #e0e0e0'
                  : `2px solid ${state === 'success' ? successBorder : errorBorder}`,
                borderRadius: 1,
                backgroundColor:
                  state === 'none' || state === 'pending'
                    ? 'transparent'
                    : (state === 'success' ? successBg : errorBg),
                p: 1,
                width: '100%',
                boxSizing: 'border-box',
                ...(muted ? { opacity: 0.85 } : {}),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasStatus && state === 'success' && <CheckCircleIcon sx={{ color: successIcon, fontSize: 20 }} />}
                  {hasStatus && state === 'error' && <CancelIcon sx={{ color: errorIcon, fontSize: 20 }} />}
                  {!hasStatus && validationCompleted && <CancelIcon sx={{ color: warnIcon, fontSize: 20 }} />}
                </Box>
                <IconButton
                  aria-label="delete"
                  disabled={isFormDisabled}
                  onClick={() => {
                    const updated = files.filter((_, i) => i !== idx);
                    setFieldValue(`document_uploads.uploaded_files.${documentType}`, updated);
                    clearValidationStatusForDocumentType(documentType, file);
                  }}
                  color={muted ? 'default' : 'error'}
                  size="small"
                  title="Remove file"
                  sx={{ p: 0.5 }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: hasStatus && state === 'error' ? 'bold' : 'normal',
                  width: '100%',
                  wordBreak: 'break-word',
                  mt: 0.5,
                }}
              >
                {displayName}
              </Typography>
              {!hasStatus && validationCompleted && (
                <Typography variant="caption" color={muted ? 'text.disabled' : 'warning.main'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                  Validation result not linked to this file — re-validate or re-upload if needed
                </Typography>
              )}
              {hasStatus && state === 'error' && (
                <>
                  {!isValid && (
                    <Typography variant="caption" color={muted ? 'text.disabled' : 'error'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                      ✗ {validationStatus.reason}
                    </Typography>
                  )}
                  {isValid && positionValid === false && (
                    <Typography variant="caption" color={muted ? 'text.disabled' : 'error'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                      ✗ Wrong position: {validationStatus.positionReason}
                    </Typography>
                  )}
                  {isValid && (positionValid !== false) && consistencyValid === false && (
                    <Typography variant="caption" color={muted ? 'text.disabled' : 'error'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                      ✗ Study inconsistency: {validationStatus.consistencyReason}
                    </Typography>
                  )}
                  {isValid && positionValid !== false && consistencyValid !== false && contentFailed && (
                    <Typography variant="caption" color={muted ? 'text.disabled' : 'error'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                      ✗ Content validation: {validationStatus.contentReason || 'Document content does not match expected type'}
                    </Typography>
                  )}
                </>
              )}
              {hasStatus && state !== 'error' && (
                <Typography variant="caption" color={muted ? 'text.disabled' : 'success.main'} sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                  ✓ Readable, correctly positioned, content-validated, and consistent
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const isValidationResultPassing = (result) =>
    result.isValid &&
    result.positionValid !== false &&
    result.consistencyValid !== false &&
    result.contentValid !== false;

  const mergeValidationResultsIntoMap = (validationMap, validationResults, allFiles, failedFiles) => {
    validationResults.forEach((result) => {
      const statusObj = {
        isValid: result.isValid,
        reason: result.reason || (result.isValid ? 'File is readable' : 'Unknown error'),
        positionValid: result.positionValid,
        positionReason: result.positionReason,
        detectedType: result.detectedType,
        expectedType: result.expectedType,
        consistencyValid: result.consistencyValid,
        consistencyReason: result.consistencyReason,
        contentValid: result.contentValid,
        contentReason: result.contentReason,
        matchedKeywords: result.matchedKeywords,
        missingKeywords: result.missingKeywords,
        extractedSummary:
          result?.extractedMetadata?.contentSummary ||
          result?.extractedMetadata?.summary ||
          '',
      };

      const originalFile = allFiles.find(
        (f) =>
          f.azureBlobName === result.azureBlobName ||
          f.originalname === result.originalname ||
          f.filename === result.filename ||
          f.azureBlobName === result.filename
      );

      if (originalFile) {
        const fileKey =
          originalFile.azureBlobName ||
          result.azureBlobName ||
          originalFile.filename ||
          result.filename;
        const sectionKey = `${fileKey}_${result.expectedType}`;
        validationMap[sectionKey] = statusObj;

        const aliasBases = [
          originalFile.azureBlobName,
          result.azureBlobName,
          originalFile.filename,
          result.filename,
          originalFile.originalname,
          result.originalname,
          originalFile.name,
        ].filter(Boolean);
        aliasBases.forEach((base) => {
          validationMap[`${base}_${result.expectedType}`] = statusObj;
        });
      } else {
        const fallbackFileKey = result.azureBlobName || result.filename;
        if (fallbackFileKey) {
          validationMap[`${fallbackFileKey}_${result.expectedType}`] = statusObj;
        }
      }

      if (!isValidationResultPassing(result)) {
        failedFiles.push({
          filename: result.filename,
          reason: result.isValid
            ? result.contentValid === false
              ? result.contentReason || 'Content validation failed'
              : result.consistencyValid === false
                ? result.consistencyReason || 'Study inconsistency'
                : result.positionReason || 'Wrong position'
            : result.reason,
        });
      }
    });
  };

  // Function to validate all uploaded files for readability
 const validateUploadedFiles = async (currentValues, { skipIfFresh = false } = {}) => {
    if (validationInFlightRef.current) {
      console.log('⏭️ Validation already in progress — skipping duplicate request');
      return allFilesValidRef.current;
    }
    const fingerprint = buildValidationFingerprint(currentValues);
    if (skipIfFresh && fingerprint) {
      const latestValues = formikValuesRef.current || currentValues;
      const persisted = loadDocumentValidationState(
        currentProject?._id,
        latestValues?.document_uploads,
        fingerprint
      );
      if (persisted?.validationCompleted) {
        console.log(`⏭️ Reusing persisted document validation (${persisted.source || 'cache'})`);
        setFileValidationStatus(flattenFileStatusMap(persisted.validationMap || {}));
        setAllFilesValid(Boolean(persisted.allValid));
        allFilesValidRef.current = Boolean(persisted.allValid);
        setValidationCompleted(true);
        lastValidatedFingerprintRef.current = fingerprint;
        return Boolean(persisted.allValid);
      }
      if (
        allFilesValidRef.current &&
        fingerprint === lastValidatedFingerprintRef.current
      ) {
        console.log('⏭️ Files unchanged since last successful validation — skipping re-validation');
        return true;
      }
    }
    console.log('🔍 Starting file validation...');
    validationInFlightRef.current = true;
    setIsValidatingFiles(true);
    setFileValidationStatus({});
    setValidationCompleted(false);
    
    let hadPartialValidationResults = false;
    try {
      const allFiles = collectActiveUploadedFiles(currentValues);
      allFiles.forEach((f) => {
        console.log(`📂 ${f.documentType}: ${f.originalname || f.filename}`);
      });
      console.log(`📊 Total files collected: ${allFiles.length}`);

      if (allFiles.length === 0) {
        setSnackbarMessage('⚠️ No files uploaded. Please upload at least one document.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        //setIsValidatingFiles(false);
        return false;
      }

      console.log(`📄 Validating ${allFiles.length} files (one request per file to avoid gateway timeouts)...`);

      const validationMap = {};
      const aggregatedResults = [];
      const failedFiles = [];
      const PER_FILE_TIMEOUT_MS = 240000; // 4 minutes per file (Azure ~230s gateway limit)

      for (let i = 0; i < allFiles.length; i++) {
        const file = allFiles[i];
        const label = file.originalname || file.filename || `File ${i + 1}`;
        setPipelineStepLabel(`Validating file ${i + 1} of ${allFiles.length}: ${label}…`);

        if (file.documentType === 'user_defined') {
          // User-defined documents are accepted as-is; no validation is run.
          const batch = [{
            isValid: true,
            reason: 'User-defined document — validation skipped',
            positionValid: true,
            positionReason: 'User-defined document — validation skipped',
            detectedType: file.documentType,
            expectedType: file.documentType,
            consistencyValid: null,
            contentValid: null,
            azureBlobName: file.azureBlobName,
            filename: file.filename,
            originalname: file.originalname,
          }];
          aggregatedResults.push(...batch);
          mergeValidationResultsIntoMap(validationMap, batch, allFiles, failedFiles);
          setFileValidationStatus({ ...validationMap });
          hadPartialValidationResults = true;
          continue;
        }

        const response = await axiosInstance.post(
          API_ENDPOINTS.ai.validateDocuments,
          { files: [file], skipConsistency: allFiles.length > 1 },
          { timeout: PER_FILE_TIMEOUT_MS }
        );

        if (!response.data.success) {
          throw new Error(response.data.error || `Validation failed for ${label}`);
        }

        const batch = response.data.validationResults || [];
        aggregatedResults.push(...batch);
        mergeValidationResultsIntoMap(validationMap, batch, allFiles, failedFiles);
        setFileValidationStatus({ ...validationMap });
        hadPartialValidationResults = true;
      }

      if (allFiles.length > 1 && aggregatedResults.length > 1) {
        setPipelineStepLabel('Checking study consistency across documents…');
        const consistencyRes = await axiosInstance.post(
          API_ENDPOINTS.ai.validateDocumentsConsistency,
          { validationResults: aggregatedResults },
          { timeout: AI_LONG_REQUEST_TIMEOUT_MS }
        );
        if (!consistencyRes.data.success) {
          throw new Error(consistencyRes.data.error || 'Consistency check failed');
        }
        const withConsistency = consistencyRes.data.validationResults || [];
        aggregatedResults.length = 0;
        aggregatedResults.push(...withConsistency);
        failedFiles.length = 0;
        Object.keys(validationMap).forEach((k) => delete validationMap[k]);
        mergeValidationResultsIntoMap(validationMap, withConsistency, allFiles, failedFiles);
        setFileValidationStatus({ ...validationMap });
      }

      const validationResults = aggregatedResults;
      const allValid =
        validationResults.length > 0 && validationResults.every(isValidationResultPassing);

      console.log('🗺️ Final validation map:', validationMap);
      const flatValidationMap = flattenFileStatusMap(validationMap);
      setFileValidationStatus(flatValidationMap);
      setAllFilesValid(allValid);
      allFilesValidRef.current = allValid;
      lastValidatedFingerprintRef.current = fingerprint;
      setValidationCompleted(true);
      setPipelineStepLabel('');
      if (fingerprint && currentProject?._id) {
        saveDocumentValidationCache(currentProject._id, {
          fingerprint,
          allValid,
          validationMap: flatValidationMap,
        });
      }
      const validationState = buildPersistedValidationState(
        fingerprint,
        allValid,
        flatValidationMap,
        true
      );
      if (setFieldValueRef.current) {
        setFieldValueRef.current('document_uploads.validation_state', validationState);
      }
      const latestForSave = formikValuesRef.current || currentValues;
      if (latestForSave && handleSaveAsDraftRef.current) {
        handleSaveAsDraftRef.current(
          {
            ...latestForSave,
            document_uploads: {
              ...latestForSave.document_uploads,
              validation_state: validationState,
            },
          },
          { manageLoading: false, quiet: true }
        ).catch((err) => console.warn('Failed to persist validation state:', err));
      }

      if (allValid) {
        setSnackbarMessage(
          `✓ All ${validationResults.length} files passed readability, placement, and content validation!`
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const unreadableCount = validationResults.filter((r) => !r.isValid).length;
        const wrongPositionCount = validationResults.filter(
          (r) => r.isValid && r.positionValid === false
        ).length;
        const contentFailCount = validationResults.filter(
          (r) => r.isValid && r.positionValid !== false && r.contentValid === false
        ).length;
        const inconsistentCount = validationResults.filter(
          (r) =>
            r.isValid &&
            r.positionValid !== false &&
            r.contentValid !== false &&
            r.consistencyValid === false
        ).length;
        const okCount = validationResults.filter(isValidationResultPassing).length;

        setParsingWarningsState(failedFiles);
        setSnackbarMessage(
          `⚠️ ${unreadableCount} unreadable, ${wrongPositionCount} wrong section, ${contentFailCount} content mismatch, ${inconsistentCount} inconsistent (${okCount} OK). Fix before processing.`
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }

      return allValid;
    } catch (error) {
      console.error('Error validating files:', error);
      const isTimeout =
        error.code === 'ECONNABORTED' ||
        /timeout/i.test(String(error.message || ''));
      const isNetwork = !error.response && !isTimeout;
      const message = isTimeout
        ? 'Validation timed out on a large file. Wait and click Validate once (do not double-click).'
        : isNetwork
          ? 'Connection lost during validation (often Azure gateway timeout). Deploy the latest server/client build, then validate again once.'
          : error.response?.data?.error || error.message || 'File validation failed';
      setSnackbarMessage(`File validation error: ${message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setAllFilesValid(false);
      allFilesValidRef.current = false;
      setValidationCompleted(hadPartialValidationResults);
      setPipelineStepLabel('');
      lastValidatedFingerprintRef.current = '';
      if (currentProject?._id && !hadPartialValidationResults) {
        clearDocumentValidationCache(currentProject._id);
      }
      return false;
    } finally {
      validationInFlightRef.current = false;
      setIsValidatingFiles(false);
      setPipelineStepLabel('');
    }
  };

  // Function to save trial schema fields to database
  const saveTrialSchemaFieldsToDB = async (formValues) => {
    if (!currentProject) {
      console.error('No project selected. Cannot save trial schema fields.');
      return;
    }

    try {
      console.log('Saving trial schema fields to database...');
      
      // Extract only the trial schema related fields
      const rebuiltStudyParts = generateDynamicStudyPartsTable(
        formValues.study_overview?.number_of_study_parts_new,
        formValues.study_overview?.study_parts_cohorts,
        {},
        formValues.study_overview?.study_parts || {}
      );
      const rebuiltTreatmentTable = generateDynamicTreatmentTable(
        formValues.study_treatments?.num_regimens,
        formValues.study_treatments?.regimen_arms,
        {},
        formValues.study_treatments?.treatment_table || {},
        rebuiltStudyParts
      );

      const intakeId =
        formId ||
        formValues._id ||
        loadedInitialData?._id ||
        lastPopulatedIntakeRef.current?._id;

      const trialSchemaData = scrubIntakePlaceholdersFromDoc({
        projectId: currentProject._id,
        id: intakeId,
        study_overview: {
          study_type_dropdown: formValues.study_overview?.study_type_dropdown || '',
          study_phase: formValues.study_overview?.study_phase || '',
          study_design: formValues.study_overview?.study_design || {},
          number_of_study_parts_new: formValues.study_overview?.number_of_study_parts_new || '',
          study_parts_cohorts: formValues.study_overview?.study_parts_cohorts || {},
          study_parts_part_names: formValues.study_overview?.study_parts_part_names || {},
          study_parts_cohort_names: formValues.study_overview?.study_parts_cohort_names || {},
          study_parts: rebuiltStudyParts,
          trial_schema_configuration: formValues.study_overview?.trial_schema_configuration || {},
        },
        study_treatments: {
          num_regimens: formValues.study_treatments?.num_regimens || 0,
          regimens_per_part: formValues.study_treatments?.regimens_per_part || {},
          regimen_arms: formValues.study_treatments?.regimen_arms || {},
          treatment_table: rebuiltTreatmentTable,
        },
      });

      console.log('Trial schema data to save:', trialSchemaData);

      await clinicalIntakeService.saveDraft(trialSchemaData);

      console.log('Trial schema fields saved successfully to database');
      const snapshot = {
        ...(lastPopulatedIntakeRef.current || loadedInitialData || formValues),
        study_overview: trialSchemaData.study_overview,
        study_treatments: trialSchemaData.study_treatments,
        _id: intakeId || lastPopulatedIntakeRef.current?._id,
      };
      rememberPopulatedIntakeSnapshot(snapshot);
      setSnackbarMessage('Trial schema information saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving trial schema fields:', error);
      setSnackbarMessage(`Failed to save trial schema fields: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to render dynamic study parts table
   const renderDynamicStudyPartsTable = (
    studyParts,
    isFormDisabled = false,
    cohortNames = {},
    partNames = {},
    declaredPartCount = 0
  ) => {
    if (!studyParts || Object.keys(studyParts).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          Please fill out the trial schema in section <strong>B</strong> on this tab and use <strong>Parse Protocol Intake from Documents</strong> to generate the study parts table.
        </Typography>
      );
    }

    const { allCohorts, partGroups } = buildStudyPartCohortColumns(
      studyParts,
      partNames,
      cohortNames,
      declaredPartCount
    );

    // Define field configurations (ordered to match protocol section flow)
    const fieldConfigs = [
      { key: 'objectives_estimands_title', label: 'M11 Section 3 — Trial Objectives And Associated Estimands', bgColor: '#f5f5f5', isTitleOnly: true },
      { key: 'primary_objective', label: 'Primary objective', bgColor: '#fff8e1' },
      { key: 'key_secondary_objectives', label: 'Key Secondary objectives', bgColor: '#fff8e1' },
      { key: 'secondary_objective', label: 'Secondary Objective', bgColor: '#fff8e1' },
      { key: 'additional_info_endpoints', label: 'Additional Info about endpoints', bgColor: '#fff8e1' },
      { key: 'section3_additional_notes', label: 'Additional Notes', bgColor: '#fff8e1', studyOverviewComments: true },

      { key: 'study_design_overview_title', label: 'M11 Section 4 — TRIAL DESIGN', bgColor: '#f5f5f5', isTitleOnly: true },
      { key: 'method_treatment_allocation', label: 'Method of treatment allocation/Randomization', bgColor: '#e3f2fd' },
      { key: 'blinding_masking', label: 'Blinding/Masking', bgColor: '#e3f2fd' },
      { key: 'stratification_factors', label: 'Stratification factors', bgColor: '#e3f2fd' },
      { key: 'conditions_primary_disease', label: 'Conditions related to primary disease (Including disease stage, biomarker status, prior therapies, CNS involvement)', bgColor: '#e3f2fd' },
      { key: 'study_periods', label: 'Study Periods (Study Periods are distinct time blocks in the trial that group activities, assessments, and treatments together according to their purpose ex:Screening, Treatment, Safety follow-up, Long term survival follow-up)', bgColor: '#f9f9f9' },
      { key: 'study_design', label: 'Study Design', bgColor: '#e3f2fd' },
      { key: 'stakeholder_input_into_design', label: 'Stakeholder input into design', bgColor: '#e3f2fd' },
      { key: 'trial_stopping_rules', label: 'Trial Stopping Rules', bgColor: '#e3f2fd' },
      { key: 'committees', label: 'Committees', bgColor: '#e3f2fd' },
      { key: 'start_end_trial', label: 'Start and End of Trial', bgColor: '#e3f2fd' },
      { key: 'access_to_intervention_after_trial', label: 'Access to Intervention after trial', bgColor: '#e3f2fd' },
      { key: 'section4_additional_notes', label: 'Additional Notes', bgColor: '#e3f2fd', mergeComments: true, storageKey: 'additional_notes' },

      { key: 'study_population_title', label: 'M11 Section 5 — Trial Population', bgColor: '#f5f5f5', isTitleOnly: true },
      {
        key: BRIEF_DESCRIPTION_OF_TRIAL_POPULATION,
        label: STUDY_PARTS_POPULATION_FIELD_LABELS[BRIEF_DESCRIPTION_OF_TRIAL_POPULATION],
        bgColor: '#f9f9f9',
      },
      {
        key: GENERAL_DESCRIPTION_OF_TRIAL_POPULATION,
        label: STUDY_PARTS_POPULATION_FIELD_LABELS[GENERAL_DESCRIPTION_OF_TRIAL_POPULATION],
        bgColor: '#fff3cd',
      },
      { key: 'tissue_sample_compliance', label: 'Tissue sample or Procedure compliance requirements', bgColor: '#f9f9f9' },
      { key: 'performance_status', label: 'Patient performance status, Life expectancy, organ function and/or Lab parameter status', bgColor: '#f9f9f9' },
      { key: 'concomitant_meds', label: 'Concomitant meds / wash-out for existing therapies', bgColor: '#f9f9f9' },
      { key: 'comorbidities', label: 'Comorbidities & infections', bgColor: '#f9f9f9' },
      { key: 'reproductive_status', label: 'Reproductive status & contraception', bgColor: '#f9f9f9' },
      { key: 'eligibility_criteria', label: 'Eligibility criteria which make patient eligible for any of the treatments', bgColor: '#f9f9f9' },
      {
        key: 'lifestyle_restrictions',
        label: 'Lifestyle Restrictions (Use “View guidance” to optionally enable sub-fields)',
        bgColor: '#f9f9f9'
      },
      ...(lifestyleRestrictionEnabledSubfields.meals
        ? [
            {
              key: 'meals_dietary_restrictions',
              label: 'Meals and Dietary Restrictions',
              bgColor: '#f9f9f9'
            }
          ]
        : []),
      ...(lifestyleRestrictionEnabledSubfields.caffeine
        ? [
            {
              key: 'caffeine_alcohol_tobacco_other_restrictions',
              label: 'Caffeine, Alcohol, Tobacco, and Other Restrictions',
              bgColor: '#f9f9f9'
            }
          ]
        : []),
      ...(lifestyleRestrictionEnabledSubfields.physical
        ? [
            {
              key: 'physical_activity_restrictions',
              label: 'Physical Activity Restrictions',
              bgColor: '#f9f9f9'
            }
          ]
        : []),
      ...(lifestyleRestrictionEnabledSubfields.other
        ? [
            {
              key: 'other_activity_restrictions',
              label: 'Other Activity Restrictions',
              bgColor: '#f9f9f9'
            }
          ]
        : []),

      { key: 'section5_additional_notes', label: 'Additional Notes', bgColor: '#f9f9f9', fullMergeComments: true },
    ];

    // Calculate dynamic column widths based on number of parts/cohorts
    const fieldNamesWidth = '15%'; // Fixed width for field names
    const inputColumnsWidth = `${85 / allCohorts.length}%`; // Dynamic width for each input column
    
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
        <colgroup>
          <col style={{ width: fieldNamesWidth }} /> {/* Column 1: Field names - fixed */}
          {allCohorts.map((cohort, index) => (
            <col key={`input-col-${cohort.partKey}-${cohort.cohortKey}`} style={{ width: inputColumnsWidth }} />
          ))}
        </colgroup>
        {/* Table Header */}
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}></th>
            {Object.keys(partGroups).map((partKey, partIndex) => (
              <th 
                key={partKey}
                colSpan={partGroups[partKey].length || 1}
                style={{
                  border: '1px solid #ddd', 
                  padding: '12px', 
                  backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8', 
                  fontWeight: 'bold', 
                  textAlign: 'left' 
                }}
              >
                {getStudyPartDisplayName(partKey, partNames, declaredPartCount)}
              </th>
            ))}
          </tr>
          {/* Only show cohort sub-header row when there are actual cohorts (not direct structure) */}
          {allCohorts.some(cohort => cohort.cohortKey !== 'direct') && (
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}></th>
              {Object.keys(partGroups).map((partKey, partIndex) => {
                const cohorts = partGroups[partKey];
                console.log(`Frontend: Processing part ${partKey}, cohorts:`, cohorts);
                
                // Check if this part has a "direct" cohort (0 cohorts scenario)
                const hasDirectCohort = cohorts.some(cohort => cohort.cohortKey === 'direct');
                
                if (hasDirectCohort) {
                  // Create empty sub-header for parts with direct structure (0 cohorts)
                  console.log(`Frontend: Creating empty sub-header for ${partKey} (direct structure)`);
                  return (
                    <th 
                      key={`${partKey}-empty`}
                      style={{
                        border: '1px solid #ddd', 
                        padding: '12px', 
                        backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8', 
                        fontWeight: 'bold', 
                        textAlign: 'left' 
                      }}
                    >
                      {/* Empty content for parts with 0 cohorts */}
                    </th>
                  );
                } else {
                  // Show cohort names for parts with actual cohorts
                  console.log(`Frontend: Creating cohort sub-headers for ${partKey}`);
                  return cohorts.map((cohort) => (
                    <th 
                      key={`${cohort.partKey}-${cohort.cohortKey}`}
                      style={{
                        border: '1px solid #ddd', 
                        padding: '12px', 
                        backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8', 
                        fontWeight: 'bold', 
                        textAlign: 'left' 
                      }}
                    >
                      {cohort.cohortName}
                    </th>
                  ));
                }
              })}
            </tr>
          )}
        </thead>
        
        {/* Table Body */}
        <tbody>
          {fieldConfigs.map(fieldConfig => (
            <tr key={fieldConfig.key}>
              {fieldConfig.isTitleOnly ? (
                <td colSpan={allCohorts.length + 1} style={M11_SECTION_TITLE_ROW_STYLE}>
                  {(() => {
                    const hoverInfo = getHoverInfo(fieldConfig.key);
                    return hoverInfo ? (
                      <Tooltip title={hoverInfo} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                        <span>{fieldConfig.label}</span>
                      </Tooltip>
                    ) : (
                      fieldConfig.label
                    );
                  })()}
                </td>
              ) : (
                <>
                  {(() => {
                    const { shortLabel, hintText, hasHint } = splitFieldLabelGuidance(fieldConfig.label);
                    // For sub-fields stored under a combined JSON/string field (e.g. rescue_medication),
                    // allow a field-specific hover tooltip keyed by `fieldConfig.key`.
                    const hoverKey =
                      (fieldConfig.rescueSubKey || fieldConfig.analysisSubKey ? fieldConfig.key : null) ||
                      fieldConfig.storageKey ||
                      fieldConfig.key;
                    const hoverInfo = getHoverInfo(hoverKey);
                    return (
                  <td style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: fieldConfig.bgColor, fontWeight: 'bold', verticalAlign: 'top' }}>
                     {hoverInfo ? (
                      <Tooltip title={hoverInfo} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                        <span>{shortLabel}</span>
                      </Tooltip>
                    ) : (
                      <div>{shortLabel}</div>
                    )}
                    {hasHint && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', fontSize: '11px', textTransform: 'none', mt: 0.5 }}
                        onClick={() =>
                          setAssessmentHintDialog({
                            open: true,
                            title: shortLabel,
                            content: hintText,
                            fieldKey: fieldConfig.key,
                          })
                        }
                      >
                        View guidance
                      </Button>
                    )}
                  </td>
                    );
                  })()}
                  {allCohorts.map(cohort => (
                    <td key={`${cohort.partKey}-${cohort.cohortKey}-${fieldConfig.key}`} style={{ 
                      border: '1px solid #ddd', 
                      padding: '0px',
                      verticalAlign: 'top',
                      height: 'auto',
                      minHeight: '60px',
                      position: 'relative'
                    }}>
                    {fieldConfig.studyOverviewComments ? (
                      <Field name="study_overview.comments">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            fullWidth
                            multiline
                            variant="outlined"
                            size="small"
                            disabled={isFormDisabled}
                            placeholder="Enter additional notes for Section 3"
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            minRows={3}
                            sx={{
                              width: '100%',
                              '& .MuiOutlinedInput-root': { alignItems: 'flex-start', width: '100%' },
                              '& .MuiInputBase-inputMultiline': {
                                padding: '12px 14px',
                                resize: 'none',
                                overflow: 'hidden',
                                lineHeight: '1.4',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                boxSizing: 'border-box',
                                width: '100%',
                                fontSize: '14px',
                              },
                            }}
                          />
                        )}
                      </Field>
                    ) : fieldConfig.fullMergeComments ? (
                      <Field
                        name={`study_overview.study_parts.${cohort.partKey}.${cohort.cohortKey}.additional_notes`}
                      >
                        {({ field, meta, form }) => {
                          // Once the other two note sources have been consolidated into this
                          // field (their first edit here blanks them — see
                          // applyFullMergedNotesToCohort), recomputing the merge every render
                          // means the shown value is `.trim()`-ed on every keystroke, which
                          // silently strips a just-typed trailing space right back off. Only
                          // show the merged/deduped preview while there's still something
                          // else to merge; once this field is the sole source, show its own
                          // value untouched.
                          const partDirectComments =
                            form.values?.study_overview?.study_parts?.[cohort.partKey]?.direct
                              ?.comments;
                          const studyOverviewComments = form.values?.study_overview?.comments;
                          const hasOtherSources =
                            !isEmptyStudyPartNote(studyOverviewComments) ||
                            !isEmptyStudyPartNote(partDirectComments);
                          const mergedValue = hasOtherSources
                            ? getFullMergedNotesForCohort(
                                form.values?.study_overview?.study_parts,
                                cohort.partKey,
                                cohort.cohortKey,
                                studyOverviewComments
                              )
                            : field.value ?? '';
                          return (
                            <TextField
                              {...field}
                              value={mergedValue}
                              fullWidth
                              multiline
                              variant="outlined"
                              size="small"
                              disabled={isFormDisabled}
                              placeholder="Enter additional notes for Section 5"
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              minRows={3}
                              onChange={(e) => {
                                applyFullMergedNotesToCohort(
                                  form,
                                  cohort.partKey,
                                  cohort.cohortKey,
                                  e.target.value
                                );
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': { alignItems: 'flex-start', width: '100%' },
                                '& .MuiInputBase-inputMultiline': {
                                  padding: '12px 14px',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  lineHeight: '1.4',
                                  whiteSpace: 'pre-wrap',
                                  wordWrap: 'break-word',
                                  boxSizing: 'border-box',
                                  width: '100%',
                                  fontSize: '14px',
                                },
                              }}
                            />
                          );
                        }}
                      </Field>
                    ) : (
                    <Field
                      name={`study_overview.study_parts.${cohort.partKey}.${cohort.cohortKey}.${fieldConfig.storageKey || fieldConfig.key}`}
                    >
                      {({ field, meta, form }) => {
                        const isMergedNotesField = Boolean(fieldConfig.mergeComments);
                        const isAnalysisSubField = Boolean(fieldConfig.storageKey && fieldConfig.analysisSubKey);
                        const analysisObj = isAnalysisSubField ? parseAnalysisSupport(field.value) : null;
                        // const analysisEnabledMap =
                        //   fieldConfig.storageKey === 'analyses_secondary_objective'
                        //     ? secondaryObjectiveAnalysisEnabledSubfields
                        //     : primaryObjectiveAnalysisEnabledSubfields;
                        const analysisValue = isAnalysisSubField
                          // ? getAnalysisSubFieldDisplayValue(
                          //     analysisObj,
                          //     fieldConfig.analysisSubKey,
                          //     analysisEnabledMap
                          //   )
                          ? getAnalysisSubFieldDisplayValue(analysisObj, fieldConfig.analysisSubKey)
                          : null;

                        const isAnalysisParentField =
                          !isAnalysisSubField &&
                          (fieldConfig.key === 'analyses_primary_objective' ||
                            fieldConfig.key === 'analyses_secondary_objective');
                        const analysisEnabledMap =
                          fieldConfig.key === 'analyses_secondary_objective'
                            ? secondaryObjectiveAnalysisEnabledSubfields
                            : primaryObjectiveAnalysisEnabledSubfields;
                        const parentJsonObj = isAnalysisParentField ? tryParseJsonObject(field.value) : null;
                        const isJsonBlob = isAnalysisParentField && isAnalysisJsonBlob(parentJsonObj);
                        const isLifestyleParentField = fieldConfig.key === 'lifestyle_restrictions';
                        const cohortData =
                          form.values?.study_overview?.study_parts?.[cohort.partKey]?.[cohort.cohortKey];
                        const lifestyleParentDisplay = isLifestyleParentField
                          ? getLifestyleParentDisplayValueLive(cohortData)
                          : null;
                        const mergedNotesValue = isMergedNotesField
                          ? (() => {
                              // Once other sources are blanked out (see applyMergedNotesToCohort),
                              // this field is the sole contributor and the merge is a no-op
                              // except for its internal .trim() — which would strip a
                              // just-typed trailing space back off on every render. Fall back
                              // to the field's own untouched value in that case.
                              const merged = getMergedNotesForCohort(
                                form.values?.study_overview?.study_parts,
                                cohort.partKey,
                                cohort.cohortKey
                              );
                              const ownValue = String(field.value ?? '');
                              return merged.trim() === ownValue.trim() ? ownValue : merged;
                            })()
                          : '';

                        return (
                        <TextField
                          {...field}
                          value={
                            isMergedNotesField
                              ? mergedNotesValue
                              : isLifestyleParentField
                              ? lifestyleParentDisplay
                              : isAnalysisSubField
                              ? analysisValue
                              : isJsonBlob
                                 ? getParentAnalysisDisplayValue(parentJsonObj, analysisEnabledMap)
                                : formatPlainAnalysisParentDisplay(field.value)
                          }
                          fullWidth
                          multiline
                          variant="outlined"
                          size="small"
                          disabled={isFormDisabled}
                          placeholder={`Enter ${fieldConfig.label.toLowerCase()} for ${cohort.partName}${cohort.cohortKey === 'direct' ? '' : ', ' + cohort.cohortName}`}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        minRows={3}
                        onChange={(e) => {
                            if (isMergedNotesField) {
                              applyMergedNotesToCohort(
                                form,
                                cohort.partKey,
                                cohort.cohortKey,
                                e.target.value
                              );
                              return;
                            }
                            if (!isAnalysisSubField) {
                              if (isLifestyleParentField) {
                                field.onChange(e);
                                return;
                              }
                              if (isJsonBlob) {
                                const nextObj = { ...(parentJsonObj || {}) };
                                nextObj.summary = e.target.value;
                                form.setFieldValue(field.name, serializeAnalysisSupport(nextObj));
                                return;
                              }
                              field.onChange(e);
                              return;
                            }
                            const nextObj = { ...(analysisObj || {}) };
                            nextObj[fieldConfig.analysisSubKey] = e.target.value;
                            form.setFieldValue(field.name, serializeAnalysisSupport(nextObj));
                          }}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              alignItems: 'flex-start',
                              width: '100%',
                            },
                            '& .MuiInputBase-inputMultiline': {
                              padding: '12px 14px',
                              resize: 'none',
                              overflow: 'hidden',
                              lineHeight: '1.4',
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word',
                              boxSizing: 'border-box',
                              width: '100%',
                              fontSize: '14px',
                            },
                          }}
                        />
                        );
                      }}
                  </Field>
                    )}
                    </td>
                  ))}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Visual-only: render Statistical Considerations rows from study_parts (same storage as the Study Parts table)
   const renderStatisticalConsiderationsFromStudyParts = (
    studyParts,
    isFormDisabled = false,
    cohortNames = {},
    partNames = {},
    declaredPartCount = 0
  ) => {
    if (!studyParts || Object.keys(studyParts).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          Please generate the study parts table first to view Statistical Considerations.
        </Typography>
      );
    }

    // const parts = Object.keys(studyParts).sort();
    // const allCohorts = [];

    // parts.forEach((partKey) => {
    //   const cohorts = Object.keys(studyParts[partKey] || {}).sort();
    //   cohorts.forEach((cohortKey) => {
    //     if (cohortKey === 'direct') {
    //       allCohorts.push({ partKey, cohortKey, partName: partKey.replace('part', 'Part '), cohortName: 'Direct' });
    //     } else {
    //       const customName = cohortNames?.[partKey]?.[cohortKey];
    //       const defaultName = cohortKey.replace('cohort', 'Cohort ');
    //       allCohorts.push({
    //         partKey,
    //         cohortKey,
    //         partName: partKey.replace('part', 'Part '),
    //         cohortName: customName && customName.trim() ? customName : defaultName,
    //       });
    //     }
    //   });
    // });

    const partColumns = buildStudyPartOnlyColumns(studyParts, partNames, declaredPartCount);

    if (partColumns.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          No study parts available yet. Please generate the trial schema first.
        </Typography>
      );
    }

    const fieldConfigs = [
      { key: 'general_considerations', label: 'General Considerations', bgColor: '#f5f5f5' },
      { key: 'analysis_sets', label: 'Analysis sets', bgColor: '#f5f5f5' },
      { key: 'analyses_demographics_baseline', label: 'Analyses of Demographics and Other Baseline Variables', bgColor: '#f5f5f5' },
      {
        key: 'analyses_primary_objective',
        label: 'Analyses Supporting Primary Objective (View guidance to enable sub-fields)',
        bgColor: '#f5f5f5',
      },
      ...(primaryObjectiveAnalysisEnabledSubfields.handling_of_data
        ? [
            {
              key: 'analyses_primary_objective_handling_of_data',
              label: 'Handling of Data in Relation to Primary Estimands',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_primary_objective',
              analysisSubKey: 'handling_of_data',
            },
          ]
        : []),
      ...(primaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data
        ? [
            {
              key: 'analyses_primary_objective_handling_of_missing_data',
              label: 'Handling of Missing Data in Relation to Primary Estimands',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_primary_objective',
              analysisSubKey: 'handling_of_missing_data',
            },
          ]
        : []),
      ...(primaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis
        ? [
            {
              key: 'analyses_primary_objective_sensitivity',
              label: 'Sensitivity Analysis',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_primary_objective',
              analysisSubKey: 'sensitivity_analysis',
            },
          ]
        : []),
      ...(primaryObjectiveAnalysisEnabledSubfields.supplementary_analysis
        ? [
            {
              key: 'analyses_primary_objective_supplementary',
              label: 'Supplementary Analysis',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_primary_objective',
              analysisSubKey: 'supplementary_analysis',
            },
          ]
        : []),
      {
        key: 'analyses_secondary_objective',
        label: 'Analyses Supporting Secondary Objective (View guidance to enable sub-fields)',
        bgColor: '#f5f5f5',
      },
      ...(secondaryObjectiveAnalysisEnabledSubfields.handling_of_data
        ? [
            {
              key: 'analyses_secondary_objective_handling_of_data',
              label: 'Handling of Data in Relation to Secondary Estimands',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_secondary_objective',
              analysisSubKey: 'handling_of_data',
            },
          ]
        : []),
      ...(secondaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data
        ? [
            {
              key: 'analyses_secondary_objective_handling_of_missing_data',
              label: 'Handling of Missing Data in Relation to Secondary Estimands',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_secondary_objective',
              analysisSubKey: 'handling_of_missing_data',
            },
          ]
        : []),
      ...(secondaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis
        ? [
            {
              key: 'analyses_secondary_objective_sensitivity',
              label: 'Sensitivity Analysis',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_secondary_objective',
              analysisSubKey: 'sensitivity_analysis',
            },
          ]
        : []),
      ...(secondaryObjectiveAnalysisEnabledSubfields.supplementary_analysis
        ? [
            {
              key: 'analyses_secondary_objective_supplementary',
              label: 'Supplementary Analysis',
              bgColor: '#f5f5f5',
              storageKey: 'analyses_secondary_objective',
              analysisSubKey: 'supplementary_analysis',
            },
          ]
        : []),
      { key: 'analyses_exploratory_objective', label: 'Analyses Supporting Exploratory Objective', bgColor: '#f5f5f5' },
      { key: 'safety_analysis', label: 'Safety Analysis', bgColor: '#f5f5f5' },
      { key: 'other_analysis', label: 'Other Analysis', bgColor: '#f5f5f5' },
      { key: 'interim_analysis', label: 'Interim Analysis', bgColor: '#f5f5f5' },
      { key: 'multiplicity_adjustments', label: 'Multiplicity Adjustments', bgColor: '#f5f5f5' },
      {
        key: 'sample_size_determination',
        label: 'Sample size, Sample size determination methodology and underlying assumptions',
        bgColor: '#f5f5f5',
      },
      { key: 'comments', label: 'Additional Notes', bgColor: '#f5f5f5', mergeComments: true },
    ];

    // const partGroups = {};
    // parts.forEach((partKey) => {
    //   partGroups[partKey] = [];
    // });
    // allCohorts.forEach((cohort) => {
    //   partGroups[cohort.partKey].push(cohort);
    // });

    const fieldNamesWidth = '20%';
    const inputColumnsWidth = `${80 / partColumns.length}%`;

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
        <colgroup>
          <col style={{ width: fieldNamesWidth }} />
          {partColumns.map((part) => (
            <col key={`stat-input-col-${part.partKey}`} style={{ width: inputColumnsWidth }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }} />
            {partColumns.map((part, partIndex) => (
              <th
                key={`stat-${part.partKey}`}
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                {part.partName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fieldConfigs.map((fieldConfig) => (
            <tr key={`stat-row-${fieldConfig.key}`}>
              {fieldConfig.isTitleOnly ? (
                <td
                  colSpan={partColumns.length + 1}
                  style={M11_SECTION_TITLE_ROW_STYLE}
                >
                  {fieldConfig.label}
                </td>
              ) : (
                <>
                  {(() => {
                    const { shortLabel, hintText, hasHint } = splitFieldLabelGuidance(fieldConfig.label);
                    return (
                      <td
                        style={{
                          border: '1px solid #ddd',
                          padding: '12px',
                          backgroundColor: fieldConfig.bgColor,
                          fontWeight: 'bold',
                          verticalAlign: 'top',
                        }}
                      >
                        <div>{shortLabel}</div>
                        {hasHint && (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 'auto', fontSize: '11px', textTransform: 'none', mt: 0.5 }}
                            onClick={() =>
                              setAssessmentHintDialog({
                                open: true,
                                title: shortLabel,
                                content: hintText,
                                fieldKey: fieldConfig.key,
                              })
                            }
                          >
                            View guidance
                          </Button>
                        )}
                      </td>
                    );
                  })()}
                  {partColumns.map((part) => (
                    <td
                      key={`stat-cell-${part.partKey}-${fieldConfig.key}`}
                      style={{
                        border: '1px solid #ddd',
                        padding: '0px',
                        verticalAlign: 'top',
                        height: 'auto',
                        minHeight: '60px',
                        position: 'relative',
                      }}
                    >
                      <Field
                        name={`study_overview.study_parts.${part.partKey}.${part.storageKey}.${fieldConfig.storageKey || fieldConfig.key}`}
                      >
                        {({ field, form }) => {
                          const isMergedNotesField = Boolean(fieldConfig.mergeComments);
                          const isAnalysisSubField = Boolean(fieldConfig.storageKey && fieldConfig.analysisSubKey);
                          const analysisObj = isAnalysisSubField ? parseAnalysisSupport(field.value) : null;
                          // const analysisEnabledMap =
                          //   fieldConfig.storageKey === 'analyses_secondary_objective'
                          //     ? secondaryObjectiveAnalysisEnabledSubfields
                          //     : primaryObjectiveAnalysisEnabledSubfields;

                          // Scope JSON-blob detection to known analysis parent fields only.
                          const isAnalysisParentField =
                            !isAnalysisSubField &&
                            (fieldConfig.key === 'analyses_primary_objective' ||
                              fieldConfig.key === 'analyses_secondary_objective');
                          const analysisEnabledMap =
                            fieldConfig.key === 'analyses_secondary_objective'
                              ? secondaryObjectiveAnalysisEnabledSubfields
                              : primaryObjectiveAnalysisEnabledSubfields;
                          const parentJsonObj = isAnalysisParentField ? tryParseJsonObject(field.value) : null;
                          const isJsonBlob = isAnalysisParentField && isAnalysisJsonBlob(parentJsonObj);
                          const mergedNotesValue = isMergedNotesField
                            ? (() => {
                                // Same reasoning as the cohort-level merge box: once other
                                // sources are blanked (see applyMergedNotesToPartDirect), don't
                                // re-trim this field's own value on every render.
                                const merged = getMergedNotesForPartDirect(
                                  form.values?.study_overview?.study_parts,
                                  part.partKey,
                                  form.values?.study_assessments?.comments
                                );
                                const ownValue = String(field.value ?? '');
                                return merged.trim() === ownValue.trim() ? ownValue : merged;
                              })()
                            : '';

                           const displayValue = isMergedNotesField
                            ? mergedNotesValue
                            : isAnalysisSubField
                            ? getAnalysisSubFieldDisplayValue(analysisObj, fieldConfig.analysisSubKey)
                            : isJsonBlob
                              ? getParentAnalysisDisplayValue(parentJsonObj, analysisEnabledMap)
                              : formatPlainAnalysisParentDisplay(field.value);

                          return (
                            <TextField
                              {...field}
                              value={displayValue}
                              fullWidth
                              multiline
                              variant="outlined"
                              size="small"
                              disabled={isFormDisabled}
                              placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
                              minRows={3}
                              onChange={(e) => {
                                if (isMergedNotesField) {
                                  applyMergedNotesToPartDirect(
                                    form,
                                    part.partKey,
                                    e.target.value,
                                    'study_assessments.comments'
                                  );
                                  return;
                                }
                                if (!isAnalysisSubField) {
                                   if (isJsonBlob) {
                                    const nextObj = { ...(parentJsonObj || {}) };
                                    nextObj.summary = e.target.value;
                                    form.setFieldValue(field.name, serializeAnalysisSupport(nextObj));
                                    return;
                                  }
                                  field.onChange(e);
                                  return;
                                }
                                const nextObj = { ...(analysisObj || {}) };
                                nextObj[fieldConfig.analysisSubKey] = e.target.value;
                                form.setFieldValue(field.name, serializeAnalysisSupport(nextObj));
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                  alignItems: 'flex-start',
                                  width: '100%',
                                },
                                '& .MuiInputBase-inputMultiline': {
                                  padding: '12px 14px',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  lineHeight: '1.4',
                                  whiteSpace: 'pre-wrap',
                                  wordWrap: 'break-word',
                                  boxSizing: 'border-box',
                                  width: '100%',
                                  fontSize: '14px',
                                },
                              }}
                            />
                          );
                        }}
                      </Field>
                    </td>
                  ))}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Section E: assessments per study part/cohort (stored on study_overview.study_parts)
  const STUDY_ASSESSMENT_FIELD_CONFIGS = [
    {
      key: 'screening_baseline_assessments',
      label: 'Screening and Baseline Assessments',
      guidanceTitle: 'Screening and Baseline Assessments Guidance',
      guidanceContent:
        'List any assessments and procedures that are unique to screening/baseline; for example, collection of participant characteristics and assessments/procedures used to determine eligibility or stratification.',
    },
    {
      key: 'efficacy_assessments',
      label: 'Efficacy assessments and procedures',
      guidanceTitle: 'Efficacy Assessments Guidance',
      guidanceContent:
        'List efficacy assessments based on endpoints and standard expectations for this section.',
    },
    {
      key: 'safety_assessments_general',
      label: 'Safety Assessments & Procedures',
      guidanceTitle: 'Safety Assessments Guidance',
      guidanceContent:
        'List standard procedures and modify if there are specific instructions in supporting documents (e.g., physical examination, vital signs, ECG, clinical laboratory assessments, suicidal ideation and behavior risk monitoring).',
    },
    { key: 'physical_examination', label: 'Physical Examination' },
    { key: 'vital_signs', label: 'Vital Signs' },
    { key: 'electrocardiograms', label: 'Electrocardiograms (ECGs)' },
    { key: 'clinical_laboratory_assessment', label: 'Clinical Laboratory Assessments' },
    { key: 'pregnancy_testing', label: 'Pregnancy Testing' },
    {
      key: 'suicidal_ideation_risk_monitoring',
      label: 'Suicidal Ideation and Behavior Risk Monitoring',
    },
    { key: 'pharmacokinetics', label: 'Pharmacokinetics (PK)' },
    { key: 'biomarkers', label: 'Biomarkers' },
    { key: 'genetics_pharmacogenomics', label: 'Genetics and Pharmacogenomics' },
    { key: 'pharmacodynamic_biomarkers', label: 'Pharmacodynamic Biomarkers' },
    { key: 'other_biomarkers', label: 'Other Biomarkers' },
    { key: 'comments', label: 'Additional Notes', mergeComments: true },
  ];

  const renderStudyAssessmentsTable = (
    studyParts,
    isFormDisabled = false,
    cohortNames = {},
    partNames = {},
    declaredPartCount = 0
  ) => {
    if (!studyParts || Object.keys(studyParts).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          Please generate the study parts table first to enter assessments per study part.
        </Typography>
      );
    }

    const partColumns = buildStudyPartOnlyColumns(studyParts, partNames, declaredPartCount);

    if (partColumns.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          No study parts available yet. Please generate the trial schema first.
        </Typography>
      );
    }

    const fieldNamesWidth = '22%';
    const inputColumnsWidth = `${78 / partColumns.length}%`;
    const assessmentCellSx = {
      width: '100%',
      '& .MuiOutlinedInput-root': { width: '100%', alignItems: 'flex-start' },
      '& .MuiInputBase-inputMultiline': {
        padding: '12px 14px',
        overflow: 'hidden',
        resize: 'none',
        fontSize: '14px',
        lineHeight: 1.4,
      },
    };

    return (
      <table
        className="assessments-table"
        style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}
      >
        <colgroup>
          <col style={{ width: fieldNamesWidth }} />
          {partColumns.map((part) => (
            <col
              key={`assess-col-${part.partKey}`}
              style={{ width: inputColumnsWidth }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
                textAlign: 'left',
                verticalAlign: 'middle',
              }}
            >
              Name of the Assessment
            </th>
            {partColumns.map((part, partIndex) => (
              <th
                key={`assess-part-${part.partKey}`}
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                {part.partName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {STUDY_ASSESSMENT_FIELD_CONFIGS.map((fieldConfig) => (
            <tr key={`assess-row-${fieldConfig.key}`}>
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  verticalAlign: 'top',
                }}
              >
                {getHoverInfo(fieldConfig.key) ? (
                  <Tooltip
                    title={getHoverInfo(fieldConfig.key)}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                  >
                    <div style={{ marginBottom: fieldConfig.guidanceContent ? '8px' : 0 }}>
                      {fieldConfig.label}
                    </div>
                  </Tooltip>
                ) : (
                  <div style={{ marginBottom: fieldConfig.guidanceContent ? '8px' : 0 }}>
                    {fieldConfig.label}
                  </div>
                )}
                {fieldConfig.guidanceContent && (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', fontSize: '11px', textTransform: 'none' }}
                    onClick={() =>
                      setAssessmentHintDialog({
                        open: true,
                        title: fieldConfig.guidanceTitle || fieldConfig.label,
                        content: fieldConfig.guidanceContent,
                        fieldKey: fieldConfig.key,
                      })
                    }
                  >
                    View guidance
                  </Button>
                )}
              </td>
              {partColumns.map((part) => (
                <td
                  key={`assess-cell-${part.partKey}-${fieldConfig.key}`}
                  style={{
                    border: '1px solid #ddd',
                    padding: 0,
                    verticalAlign: 'top',
                    minHeight: '80px',
                  }}
                >
                  <Field
                    name={`study_overview.study_parts.${part.partKey}.${part.storageKey}.${fieldConfig.key}`}
                  >
                    {({ field, form }) => {
                      const mergedNotesValue = fieldConfig.mergeComments
                        ? getMergedNotesForPartDirect(
                            form.values?.study_overview?.study_parts,
                            part.partKey,
                            form.values?.study_assessments?.comments
                          )
                        : '';
                      return (
                      <TextField
                        {...field}
                        value={fieldConfig.mergeComments ? mergedNotesValue : (field.value ?? '')}
                        multiline
                        minRows={3}
                        variant="outlined"
                        fullWidth
                        disabled={isFormDisabled}
                        placeholder={`Enter ${fieldConfig.label.toLowerCase()} for ${part.partName}`}
                        sx={assessmentCellSx}
                        onChange={(e) => {
                          if (fieldConfig.mergeComments) {
                            applyMergedNotesToPartDirect(
                              form,
                              part.partKey,
                              e.target.value,
                              'study_assessments.comments'
                            );
                            return;
                          }
                          field.onChange(e);
                        }}
                      />
                      );
                    }}
                  </Field>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };
  // Function to render dynamic treatment table
  const renderDynamicTreatmentTable = (treatmentTable, isFormDisabled = false, values = {}, options = {}) => {
    const { includeOnlyKeys, excludeKeys, hideColumnHeaders = false } = options;
    if (!treatmentTable || Object.keys(treatmentTable).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
          Please enter the number of regimens above to generate the treatment table.
        </Typography>
      );
    }

    const regimenArms = values.study_treatments?.regimen_arms || {};
    const numRegimens = parseInt(values.study_treatments?.num_regimens) || 0;
    const regimensPerPart = values.study_treatments?.regimens_per_part || {};
    const partNames = values.study_overview?.study_parts_part_names || {};
    const cohortNames = values.study_overview?.study_parts_cohort_names || {};
    const trialSchemaConfig = values.study_overview?.trial_schema_configuration || {};
    const declaredPartCount = values.study_overview?.number_of_study_parts_new;
    const { allRegimens, partGroups } = buildTreatmentTableColumns({
      trialSchemaConfig,
      regimensPerPart,
      numRegimens,
      partNames,
      cohortNames,
      regimenArms,
      declaredPartCount,
    });
    const regimens = allRegimens.length > 0
      ? allRegimens.map((r) => r.regimenKey)
      : Object.keys(treatmentTable);
    const showPartHeaders =
      isMultiPartTrial(declaredPartCount) &&
      numRegimens > 0 &&
      Object.keys(partGroups).some((k) => partGroups[k]?.length > 0);
    const showCohortHeaders = allRegimens.some((entry) => entry.cohortKey && entry.cohortKey !== 'direct');
    console.log('renderDynamicTreatmentTable - treatmentTable:', treatmentTable);
    console.log('renderDynamicTreatmentTable - regimens keys:', regimens);
    console.log('renderDynamicTreatmentTable - number of columns:', regimens.length);
    console.log('renderDynamicTreatmentTable - regimenArms:', regimenArms);
    console.log('renderDynamicTreatmentTable - regimenArms type:', typeof regimenArms);
    console.log('renderDynamicTreatmentTable - regimenArms keys:', Object.keys(regimenArms));
    console.log('renderDynamicTreatmentTable - regimenArms values:', Object.values(regimenArms));

    //  const parseRescueMedication = (rawValue) => {
    //   if (!rawValue || typeof rawValue !== 'string') return {};
    //   try {
    //     const parsed = JSON.parse(rawValue);
    //     return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    //   } catch {
    //     // Backward compatibility: older values were free text
    //     return { rescue_medication: rawValue };
    //   }
    // };

    // const serializeRescueMedication = (obj) => {
    //   try {
    //     const cleaned = Object.fromEntries(
    //       Object.entries(obj || {}).filter(([, v]) => (typeof v === 'string' ? v.trim() !== '' : v != null))
    //     );
    //     return JSON.stringify(cleaned);
    //   } catch {
    //     return '';
    //   }
    // };

    // const RESCUE_JSON_KEYS = [
    //   'background_trial_intervention',
    //   'other_noninvestigational_trial_intervention',
    //   'rescue_medication',
    // ];

    // /**
    //  * Display value for the parent Rescue Medication row (not a sub-field row).
    //  * If the JSON has distinct content per sub-key, show general rescue_medication text if present,
    //  * otherwise show empty (sub-rows carry the detail).
    //  * If all sub-keys share the same text, show that text once.
    //  */
    // const getParentRescueDisplayValue = (obj) => {
    //   if (!obj || typeof obj !== 'object') return '';
    //   const general = obj.rescue_medication;
    //   if (general && String(general).trim()) return String(general).trim();
    //   const filled = RESCUE_JSON_KEYS
    //     .map((k) => obj[k])
    //     .filter((v) => v != null && String(v).trim() !== '' && String(v).trim() !== 'Not Provided')
    //     .map((v) => String(v).trim());
    //   if (filled.length === 0) return '';
    //   if (filled.every((t) => t === filled[0])) return filled[0];
    //   return '';
    // };

    // /**
    //  * Display value for a rescue sub-field row.
    //  * Returns the sub-key's own value if present;
    //  * falls back to general rescue text on first sub-field only when sub-keys are absent.
    //  */
    // const getRescueSubFieldDisplayValue = (rescueObj, subKey) => {
    //   const direct = rescueObj?.[subKey];
    //   const directTrimmed = direct != null ? String(direct).trim() : '';
    //   if (directTrimmed && directTrimmed !== 'Not Provided') return directTrimmed;
    //   const general = rescueObj?.rescue_medication;
    //   const generalTrimmed = general != null ? String(general).trim() : '';
    //   if (generalTrimmed && generalTrimmed !== 'Not Provided') {
    //     return subKey === RESCUE_JSON_KEYS[0] ? generalTrimmed : '';
    //   }
    //   return '';
    // };

    const fieldConfigs = [
      { key: 'trial_intervention_title', label: 'TRIAL INTERVENTION', bgColor: '#f5f5f5', isTitleOnly: true },
      { key: 'dose_frequency', label: 'Dose/ Frequency', bgColor: '#f5f5f5' },
      { key: 'duration', label: 'Duration', bgColor: '#f5f5f5' },
      { key: 'fda_pregnancy_risk_category', label: 'FDA Pregnancy Risk Category', bgColor: '#f5f5f5' },
      { key: 'dose_modifications_overdose', label: 'Dose Modifications and Overdose Management', bgColor: '#f5f5f5', isTitleOnly: true },
      { key: 'dose_modifications', label: 'Dose Modifications', bgColor: '#f5f5f5' },
      { key: 'overdose_management', label: 'Overdose Management', bgColor: '#f5f5f5' },
      { key: 'experimental_or_control', label: 'Experimental Or Control', bgColor: '#f5f5f5' },
      { key: 'con_meds_allowed', label: 'Con-meds Allowed', bgColor: '#f5f5f5' },
      { key: 'con_meds_prohibited', label: 'Con-Meds Prohibited', bgColor: '#f5f5f5' },
      ...(investigationalInterventionHandlingEnabledSubfields.preparation
        ? [{ key: 'preparation_of_investigational_trial_intervention', label: 'Preparation of Investigational Trial Intervention', bgColor: '#f5f5f5' }]
        : []),
      ...(investigationalInterventionHandlingEnabledSubfields.storageHandling
        ? [{ key: 'storage_handling_of_investigational_trial_intervention', label: 'Storage and Handling of Investigational Trial Intervention', bgColor: '#f5f5f5' }]
        : []),
      ...(investigationalInterventionHandlingEnabledSubfields.accountability
        ? [{ key: 'accountability_of_investigational_trial_intervention', label: 'Accountability of Investigational Trial Intervention', bgColor: '#f5f5f5' }]
        : []),
      {
        key: 'preparation_storage_handling_accountability',
        label: 'Preparation, Storage, Handling and accountability of Trial Intervention',
        bgColor: '#f5f5f5',
        hintContent:
          'Use the checkboxes in View guidance to optionally enable the sub-fields: Preparation; Storage and Handling; Accountability.'
      },
      { key: 'investigational_trial_intervention_adherence', label: 'Investigation Trial Intervention adherence', bgColor: '#f5f5f5' },
      {
        key: 'rescue_medication',
        label: 'Rescue Medication',
        bgColor: '#f5f5f5',
        hintContent:
          'Per Section 6.9, capture Rescue Therapy/Rescue Medication. Use View guidance to optionally enable sub-fields for Background Trial Intervention and Other Noninvestigational Trial Intervention (their data will be stored inside Rescue Medication).'
      },
      ...(rescueMedicationEnabledSubfields.backgroundTrialIntervention
        ? [
            {
              key: 'rescue_medication_691',
              label: 'Background trial Intervention',
              bgColor: '#f5f5f5',
              storageKey: 'rescue_medication',
              rescueSubKey: 'background_trial_intervention'
            }
          ]
        : []),
      ...(rescueMedicationEnabledSubfields.otherNonInvestigationalIntervention
        ? [
            {
              key: 'rescue_medication_693',
              label: 'Other Non-investigational Trial Intervention',
              bgColor: '#f5f5f5',
              storageKey: 'rescue_medication',
              rescueSubKey: 'other_noninvestigational_trial_intervention'
            }
          ]
        : []),
      { key: 'intervention_additional_notes', label: 'Additional Notes', bgColor: '#f5f5f5', mergeTreatmentAdditionalInfo: true },
      { key: 'adverse_events_title', label: 'ADVERSE EVENTS', bgColor: '#f5f5f5', isTitleOnly: true },
      { key: 'adverse_events_special_interest', label: 'Adverse events of special interest related to any Agent', bgColor: '#f5f5f5' },
      {
        key: 'disease_related_events_outcomes',
        label: 'Disease-Related Events or Outcomes Not Qualifying as AEs or SAEs',
        bgColor: '#f5f5f5'
      }
    ];

    let activeFieldConfigs = fieldConfigs;
    if (includeOnlyKeys?.length) {
      const allowed = new Set(includeOnlyKeys);
      activeFieldConfigs = fieldConfigs.filter(
        (f) => allowed.has(f.key) || (!hideColumnHeaders && f.key === 'adverse_events_title')
      );
    } else if (excludeKeys?.length) {
      const blocked = new Set(excludeKeys);
      activeFieldConfigs = fieldConfigs.filter((f) => !blocked.has(f.key));
    }

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
        <colgroup>
          <col style={{ minWidth: '220px' }} />
          {regimens.map((regimenKey) => (
            <col key={`input-col-${regimenKey}`} style={{ minWidth: '280px' }} />
          ))}
        </colgroup>
        
        {!hideColumnHeaders && (
        <thead>
          {showPartHeaders && (
            <tr>
              <th
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                }}
              />
              {Object.keys(partGroups).map((partKey, partIndex) => {
                const cols = partGroups[partKey];
                if (!cols?.length) return null;
                return (
                  <th
                    key={`part-header-${partKey}`}
                    colSpan={cols.length}
                    style={{
                      border: '1px solid #ddd',
                      padding: '12px',
                      backgroundColor: partIndex % 2 === 0 ? '#e3f2fd' : '#e8f5e8',
                      fontWeight: 'bold',
                      textAlign: 'left',
                    }}
                  >
                    {getStudyPartDisplayName(partKey, partNames, declaredPartCount)}
                  </th>
                );
              })}
            </tr>
          )}
          {showCohortHeaders && (
            <tr>
              <th
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                }}
              />
              {allRegimens.map((entry) => (
                <th
                  key={`cohort-header-${entry.regimenKey}`}
                  style={{
                    border: '1px solid #ddd',
                    padding: '10px',
                    backgroundColor: '#fff3e0',
                    fontWeight: 600,
                    textAlign: 'left',
                    fontSize: '0.85rem',
                  }}
                >
                  {entry.cohortKey && entry.cohortKey !== 'direct' ? entry.cohortName : ''}
                </th>
              ))}
            </tr>
          )}
          <tr>
            <th style={{ 
              border: '1px solid #ddd', 
              padding: '12px', 
              backgroundColor: '#f8f9fa', 
              fontWeight: 'bold',
              textAlign: 'left'
            }}>
              Regimen / Treatment Conditions
            </th>
            {(allRegimens.length > 0 ? allRegimens : regimens.map((k, i) => ({ regimenKey: k, globalIndex: i + 1 }))).map((entry) => {
              const regimenKey = entry.regimenKey;
              const regimenNumber = entry.globalIndex || 1;
              const regimenData = treatmentTable[regimenKey];

              const descriptor =
                entry.descriptor ||
                regimenArms[regimenNumber] ||
                regimenArms[regimenNumber.toString()] ||
                regimenArms[`regimen${regimenNumber}`] ||
                regimenArms[`regimen${regimenNumber}_agent_name`] ||
                '';

              let regimenName;
              if (
                regimenData?.regimen_name &&
                regimenData.regimen_name !== `Regimen ${regimenNumber} (Enter Name)` &&
                !regimenData.regimen_name.includes('(Enter Name)')
              ) {
                regimenName = regimenData.regimen_name;
              } else if (entry.displayLabel) {
                regimenName = entry.displayLabel;
              } else {
                regimenName = formatRegimenColumnHeader(regimenNumber, descriptor);
              }
              
              return (
                <th key={`header-${regimenKey}`} style={{ 
                  border: '1px solid #ddd', 
                  padding: '12px', 
                  backgroundColor: '#e9ecef', 
                  fontWeight: 'bold',
                  textAlign: 'left',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  lineHeight: 1.3
                }} title={regimenName}>
                  {regimenName}
                </th>
              );
            })}
          </tr>
        </thead>
        )}
        
        <tbody>
          {activeFieldConfigs.map((fieldConfig) => (
            <tr key={fieldConfig.key}>
              {fieldConfig.isTitleOnly ? (
                <td
                  colSpan={regimens.length + 1}
                  style={M11_SECTION_TITLE_ROW_STYLE}
                >
                  {(() => {
                    const hoverInfo = getHoverInfo(fieldConfig.key);
                    return hoverInfo ? (
                      <Tooltip title={hoverInfo} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                        <span>{fieldConfig.label}</span>
                      </Tooltip>
                    ) : (
                      fieldConfig.label
                    );
                  })()}
                </td>
              ) : (
                <>
                  <td
                    style={{ 
                border: '1px solid #ddd', 
                padding: '12px', 
                backgroundColor: fieldConfig.bgColor,
                fontWeight: 'bold',
                verticalAlign: 'top'
              }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      {(() => {
                        // For rescue sub-fields, use the field-specific key (e.g. rescue_medication_691)
                        // so it can have its own tooltip even though the value is stored in rescue_medication.
                        const hoverInfo = getHoverInfo(fieldConfig.key);
                        return hoverInfo ? (
                          <Tooltip title={hoverInfo} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                            <Box component="span">{fieldConfig.label}</Box>
                          </Tooltip>
                        ) : (
                          <Box component="span">{fieldConfig.label}</Box>
                        );
                      })()}
                      {fieldConfig.hintContent && (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ p: 0, minWidth: 'auto', fontSize: '11px', textTransform: 'none' }}
                          onClick={() =>
                            setAssessmentHintDialog({
                              open: true,
                              title: fieldConfig.label,
                              content: fieldConfig.hintContent,
                              fieldKey: fieldConfig.key,
                            })
                          }
                        >
                          View guidance
                        </Button>
                      )}
                    </Box>
              </td>
              {regimens.map((regimenKey) => (
                <td
                      key={`${regimenKey}-${fieldConfig.key}`}
                      style={{ 
                  border: '1px solid #ddd',
                  padding: '6px',
                  verticalAlign: 'top'
                }}>
                   <Field name={`study_treatments.treatment_table.${regimenKey}.${fieldConfig.storageKey || fieldConfig.key}`}>
                        {({ field, form }) => {
                          if (fieldConfig.mergeTreatmentAdditionalInfo) {
                            // Live (non-trimming) variants — see their definitions in
                            // studyPartNotesMerge.js for why the trimming versions can't be
                            // used for the controlled value/onChange of an actively-edited box.
                            const mergedValue = joinTreatmentAdditionalInformationLive(
                              form.values?.study_treatments?.additional_information
                            );
                            return (
                              <TextField
                                value={mergedValue}
                                multiline
                                minRows={3}
                                variant="outlined"
                                fullWidth
                                disabled={isFormDisabled}
                                placeholder="Enter additional notes for Section 6"
                                onChange={(e) => {
                                  form.setFieldValue(
                                    'study_treatments.additional_information',
                                    splitTreatmentAdditionalInformationLive(e.target.value)
                                  );
                                }}
                              />
                            );
                          }

                          const isRescueSubField = Boolean(
                            fieldConfig.storageKey === 'rescue_medication' && fieldConfig.rescueSubKey
                          );
                          const isRescueParent =
                            !isRescueSubField && fieldConfig.key === 'rescue_medication';
                          const isPrepParentField =
                            fieldConfig.key === 'preparation_storage_handling_accountability';
                          const regimenData =
                            form.values?.study_treatments?.treatment_table?.[regimenKey];

                          // For sub-field rows use parseRescueMedication (graceful fallback).
                          // For the parent row, only treat the value as a blob when it is
                          // actual stored JSON (tryParseJsonObject succeeds on the raw string).
                          // parseRescueMedication always produces a rescue_medication key even
                          // for plain text, so it must NOT be used for blob detection.
                          const rescueObj = isRescueSubField ? parseRescueMedication(field.value) : null;
                          const parentRawJson = isRescueParent ? tryParseJsonObject(field.value) : null;
                          const parentRescueJsonBlob = isRescueParent && parentRawJson !== null;

                          const displayValue = isRescueSubField
                            ? getRescueSubFieldDisplayValue(rescueObj, fieldConfig.rescueSubKey)
                            : isPrepParentField
                              ? getPrepHandlingParentDisplayValueLive(regimenData)
                            : parentRescueJsonBlob
                              ? getParentRescueDisplayValue(parentRawJson, rescueMedicationEnabledSubfields)
                             : formatPlainRescueParentDisplay(field.value);

                          return (
                            <TextField
                              {...field}
                              value={displayValue}
                              multiline
                              minRows={3}
                              variant="outlined"
                              fullWidth
                              disabled={isFormDisabled}
                              placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
                              onChange={(e) => {
                                if (isRescueSubField) {
                                  const nextObj = { ...(rescueObj || {}) };
                                  nextObj[fieldConfig.rescueSubKey] = e.target.value;
                                  form.setFieldValue(field.name, serializeRescueMedication(nextObj));
                                  return;
                                }
                                if (isPrepParentField) {
                                  field.onChange(e);
                                  return;
                                }
                                if (parentRescueJsonBlob) {
                                  const nextObj = { ...(parentRawJson || {}) };
                                  nextObj.rescue_medication = e.target.value;
                                  form.setFieldValue(field.name, serializeRescueMedication(nextObj));
                                  return;
                                }
                                field.onChange(e);
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                  alignItems: 'flex-start',
                                },
                                '& .MuiInputBase-inputMultiline': {
                                  overflow: 'hidden',
                                  resize: 'none',
                                  lineHeight: 1.4,
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'anywhere',
                                },
                              }}
                            />
                          );
                        }}
                      </Field>
                </td>
              ))}
              </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Function to generate dynamic study parts table based on user input
  // IMPORTANT: preserve any existing user-entered values in existingStudyParts; only fill from parsedData when empty.
  const generateDynamicStudyPartsTable = (numberOfParts, studyPartsCohorts, parsedData, existingStudyParts = {}) => {
    console.log('Generating dynamic table with:', { numberOfParts, studyPartsCohorts, parsedData, existingStudyParts });
    
    // Parse number of study parts — 0 means single Main Study (one internal part1 slot)
    const declaredParts = parseDeclaredStudyPartCount(numberOfParts);
    const numParts = effectiveStudyPartSlotCount(declaredParts);
    
    // Get cohorts per part from individual inputs
    const cohortsPerPart = [];
    for (let i = 1; i <= numParts; i++) {
      const cohortCount = parseInt(studyPartsCohorts?.[`part${i}`]) || 0;
      cohortsPerPart.push(cohortCount);
      console.log(`Study Part ${i}: ${cohortCount} cohorts`);
    }
    
    console.log('Cohorts per part from individual inputs:', cohortsPerPart);
    
    const studyParts = {};

    const pickFirstNonEmpty = (...candidates) => {
      for (const c of candidates) {
        if (typeof c === 'string') {
          const trimmed = c.trim();
          if (trimmed.length > 0 && trimmed !== 'Not Provided') return c;
          continue;
        }
        if (c !== null && c !== undefined) return c;
      }
      return undefined;
    };

    const legacyPopulationField = (field) => {
      if (field === BRIEF_DESCRIPTION_OF_TRIAL_POPULATION) return LEGACY_PATIENT_POPULATION;
      if (field === GENERAL_DESCRIPTION_OF_TRIAL_POPULATION) return LEGACY_SHORT_DESCRIPTION;
      return null;
    };

    const isEmptyVal = (v) =>
      v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));

    const getPopulationValue = (partKey, cohortKey, field) => {
      const existing = existingStudyParts?.[partKey]?.[cohortKey];
      const ai = parsedData?.study_overview?.study_parts?.[partKey]?.[cohortKey];
      const legacy = legacyPopulationField(field);
      return pickFirstNonEmpty(
        existing?.[field],
        legacy ? existing?.[legacy] : undefined,
        ai?.[field],
        legacy ? ai?.[legacy] : undefined
      );
    };

    const OBJECTIVE_ENDPOINT_FIELDS = new Set([
      'primary_objective',
      'key_secondary_objectives',
      'secondary_objective',
      'additional_info_endpoints',
    ]);

    const getCellValue = (partKey, cohortKey, field) => {
      const existing = existingStudyParts?.[partKey]?.[cohortKey]?.[field];
      const ai = parsedData?.study_overview?.study_parts?.[partKey]?.[cohortKey]?.[field];
      if (OBJECTIVE_ENDPOINT_FIELDS.has(field)) {
        return pickFirstNonEmpty(ai, existing, getPopulationValue(partKey, cohortKey, field)) ?? '';
      }
      return pickFirstNonEmpty(existing, getPopulationValue(partKey, cohortKey, field)) ?? '';
    };

    const getPartLevelValue = (partKey, field) => {
      const existing = existingStudyParts?.[partKey]?.direct;
      const ai = parsedData?.study_overview?.study_parts?.[partKey]?.direct;
      const legacy = legacyPopulationField(field);
      const fromDirect = pickFirstNonEmpty(
        existing?.[field],
        legacy ? existing?.[legacy] : undefined,
        ai?.[field],
        legacy ? ai?.[legacy] : undefined
      );
      if (!isEmptyVal(fromDirect)) return fromDirect;
      // Migrate legacy per-cohort values into part-level direct
      const part = existingStudyParts?.[partKey] || parsedData?.study_overview?.study_parts?.[partKey];
      if (part && typeof part === 'object') {
        for (const cohortKey of Object.keys(part).filter(isStudyPartCohortKey)) {
          if (cohortKey === 'direct') continue;
          const val = part[cohortKey]?.[field];
          if (!isEmptyVal(val)) return val;
        }
      }
      return '';
    };

    const COHORT_LEVEL_FIELDS = [
      'study_periods',
      ...STUDY_PARTS_POPULATION_FIELD_KEYS,
      'tissue_sample_compliance',
      'performance_status',
      'concomitant_meds',
      'comorbidities',
      'reproductive_status',
      'eligibility_criteria',
      'lifestyle_restrictions',
      'meals_dietary_restrictions',
      'caffeine_alcohol_tobacco_other_restrictions',
      'physical_activity_restrictions',
      'other_activity_restrictions',
      'study_design',
      'method_treatment_allocation',
      'blinding_masking',
      'stratification_factors',
      'stakeholder_input_into_design',
      'trial_stopping_rules',
      'committees',
      'start_end_trial',
      'access_to_intervention_after_trial',
      'additional_notes',
      'primary_objective',
      'key_secondary_objectives',
      'secondary_objective',
      'additional_info_endpoints',
    ];

    const PART_LEVEL_FIELDS = [
      'statistical_considerations',
      'general_considerations',
      'analysis_sets',
      'analyses_demographics_baseline',
      'analyses_primary_objective',
      'analyses_secondary_objective',
      'analyses_exploratory_objective',
      'safety_analysis',
      'other_analysis',
      'interim_analysis',
      'multiplicity_adjustments',
      'sample_size_determination',
      'adverse_events_special_interest',
      'disease_related_events_outcomes',
      'screening_baseline_assessments',
      'efficacy_assessments',
      'safety_assessments_general',
      'physical_examination',
      'vital_signs',
      'electrocardiograms',
      'clinical_laboratory_assessment',
      'pregnancy_testing',
      'suicidal_ideation_risk_monitoring',
      'pharmacokinetics',
      'biomarkers',
      'genetics_pharmacogenomics',
      'pharmacodynamic_biomarkers',
      'other_biomarkers',
      'comments',
    ];
    
    // Generate dynamic table structure
    for (let partNum = 1; partNum <= numParts; partNum++) {
      const partKey = `part${partNum}`;
      const numCohorts = cohortsPerPart[partNum - 1] || 0;
      
      studyParts[partKey] = {};

      studyParts[partKey].direct = {};
      PART_LEVEL_FIELDS.forEach((field) => {
        studyParts[partKey].direct[field] = getPartLevelValue(partKey, field);
      });
      
      if (numCohorts === 0) {
        COHORT_LEVEL_FIELDS.forEach((field) => {
          studyParts[partKey].direct[field] = getCellValue(partKey, 'direct', field);
        });
      } else {
        for (let cohortNum = 1; cohortNum <= numCohorts; cohortNum++) {
          const cohortKey = `cohort${cohortNum}`;
          studyParts[partKey][cohortKey] = {};
          COHORT_LEVEL_FIELDS.forEach((field) => {
            studyParts[partKey][cohortKey][field] = getCellValue(partKey, cohortKey, field);
          });
        }
      }
    }

    Object.keys(studyParts).forEach((partKey) => {
      const part = studyParts[partKey];
      if (!part || typeof part !== 'object') return;
      Object.keys(part).forEach((slotKey) => {
        if (/^regimen\d+$/i.test(slotKey)) {
          delete part[slotKey];
        }
      });
    });
    
    console.log('Generated study parts structure:', studyParts);
    return studyParts;
  };

  const TREATMENT_TABLE_DATA_FIELDS = [
    'dose_frequency',
    'duration',
    'fda_pregnancy_risk_category',
    'dose_modifications',
    'overdose_management',
    'preparation_storage_handling_accountability',
    'preparation_of_investigational_trial_intervention',
    'storage_handling_of_investigational_trial_intervention',
    'accountability_of_investigational_trial_intervention',
    'experimental_or_control',
    'con_meds_allowed',
    'con_meds_prohibited',
    'investigational_trial_intervention_adherence',
    'rescue_medication',
    'adverse_events_special_interest',
    'disease_related_events_outcomes',
  ];

  const isEmptyTreatmentCell = (v) =>
    v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));

  const firstNonEmptyFromStudyParts = (studyParts, field) => {
    if (!studyParts || typeof studyParts !== 'object') return undefined;
    for (const partKey of Object.keys(studyParts)) {
      const part = studyParts[partKey];
      if (!part || typeof part !== 'object') continue;
      for (const cohortKey of Object.keys(part).filter(isStudyPartCohortKey)) {
        const v = part[cohortKey]?.[field];
        if (!isEmptyTreatmentCell(v)) return v;
      }
    }
    return undefined;
  };

  // Preserve existing + parsed AI values; fall back to study_parts for AE rows (UI no longer shows them on Study Parts table).
  const generateDynamicTreatmentTable = (
    numberOfRegimens,
    regimenArms,
    parsedData,
    existingTreatmentTable = {},
    studyPartsForFallback = null
  ) => {
    console.log('Generating dynamic treatment table with:', {
      numberOfRegimens,
      regimenArms,
      parsedData,
      existingTreatmentTable,
      studyPartsForFallback,
    });

    const numRegimens = parseInt(numberOfRegimens) || 0;
    const treatmentTable = {};
    const studyParts =
      studyPartsForFallback ||
      parsedData?.study_overview?.study_parts ||
      null;

    const pickFirstNonEmpty = (...candidates) => {
      for (const c of candidates) {
        if (typeof c === 'string') {
          if (c.trim().length > 0 && c.trim() !== 'Not Provided') return c;
          continue;
        }
        if (c !== null && c !== undefined) return c;
      }
      return undefined;
    };

    const aeFieldsFromStudyParts = new Set([
      'adverse_events_special_interest',
      'disease_related_events_outcomes',
    ]);

    for (let regimenNum = 1; regimenNum <= numRegimens; regimenNum++) {
      const regimenKey = `regimen${regimenNum}`;
      treatmentTable[regimenKey] = {};

      const agentName =
        regimenArms?.[regimenNum] ||
        regimenArms?.[regimenNum.toString()] ||
        regimenArms?.[`regimen${regimenNum}`] ||
        regimenArms?.[`regimen${regimenNum}_agent_name`] ||
        '';
      const regimenDisplayName = formatRegimenColumnHeader(regimenNum, agentName);

      const getExisting = (field) => existingTreatmentTable?.[regimenKey]?.[field];
      const getAI = (field) => parsedData?.study_treatments?.treatment_table?.[regimenKey]?.[field];

      treatmentTable[regimenKey].regimen_name = pickFirstNonEmpty(
        agentName ? regimenDisplayName : undefined,
        getExisting('regimen_name'),
        parsedData?.study_treatments?.treatment_table?.[regimenKey]?.regimen_name,
        regimenDisplayName
      );

      TREATMENT_TABLE_DATA_FIELDS.forEach((field) => {
        let value = pickFirstNonEmpty(getExisting(field), getAI(field));
        if (value === undefined && aeFieldsFromStudyParts.has(field)) {
          value = firstNonEmptyFromStudyParts(studyParts, field);
        }
        treatmentTable[regimenKey][field] = value === undefined ? '' : normalizeIntakeCellValue(value);
      });
    }
    
    console.log('Generated treatment table structure:', treatmentTable);
    return treatmentTable;
  };


  // Function to parse cohorts description text
  const parseCohortsDescription = (description, numParts) => {
    if (!description || description.trim() === '') {
      // Default: 0 cohorts per part when no input provided - user must specify
      return Array(numParts).fill(0);
    }
    
    const cohortsPerPart = [];
    
    try {
      // Parse patterns like "Part 1: 2 cohorts, Study part 2: 3 cohorts"
      console.log('Frontend: Parsing cohorts description:', description, 'for', numParts, 'parts');
      console.log('Frontend: Description length:', description.length);
      console.log('Frontend: Description characters:', description.split('').map((c, i) => `${i}:${c}`).join(' '));
      
      // Try multiple regex patterns
      let partMatches = description.match(/(?:Part|Study part)\s+(\d+):\s*(\d+)\s*cohorts?/gi);
      console.log('Frontend: Regex matches (pattern 1):', partMatches);
      
      if (!partMatches || partMatches.length === 0) {
        // Try simpler pattern
        partMatches = description.match(/Part\s+(\d+):\s*(\d+)\s*cohorts?/gi);
        console.log('Frontend: Regex matches (pattern 2):', partMatches);
      }
      
      if (!partMatches || partMatches.length === 0) {
        // Try even simpler pattern
        partMatches = description.match(/(\d+):\s*(\d+)\s*cohorts?/gi);
        console.log('Frontend: Regex matches (pattern 3):', partMatches);
      }
      
      if (partMatches && partMatches.length > 0) {
        console.log('Frontend: Found part matches:', partMatches);
        for (let i = 0; i < numParts; i++) {
          const match = partMatches.find(m => m.toLowerCase().includes(`part ${i + 1}`));
          if (match) {
            // Extract the cohort count (second number in the pattern)
            const numbers = match.match(/\d+/g);
            const cohortCount = numbers && numbers.length >= 2 ? parseInt(numbers[1]) : 0;
            cohortsPerPart.push(cohortCount);
            console.log(`Frontend: Part ${i + 1}: ${cohortCount} cohorts`);
          } else {
            cohortsPerPart.push(0); // Default to 0 cohorts if not specified
            console.log(`Frontend: Part ${i + 1}: default 0 cohorts (no match found)`);
          }
        }
      } else {
        console.log('Frontend: No part matches found, trying fallback');
        // Fallback: try to extract numbers from the description
        const numbers = description.match(/\d+/g);
        console.log('Frontend: Fallback - extracted numbers:', numbers);
        if (numbers && numbers.length >= numParts) {
          // For "Part 1: 2 cohorts, Part 2: 3 cohorts", numbers will be [1, 2, 2, 3]
          // We want the cohort counts: [2, 3]
          // So we take every second number starting from index 1
          for (let i = 0; i < numParts; i++) {
            const cohortIndex = (i * 2) + 1; // Get the cohort number (second number of each pair)
            const cohortCount = numbers[cohortIndex] ? parseInt(numbers[cohortIndex]) : 0;
            cohortsPerPart.push(cohortCount);
          }
          console.log('Frontend: Used fallback numbers:', cohortsPerPart);
        } else {
          // Default: 0 cohorts per part when no parsing is possible
          cohortsPerPart.push(...Array(numParts).fill(0));
          console.log('Frontend: Used default:', cohortsPerPart);
        }
      }
    } catch (error) {
      console.warn('Error parsing cohorts description:', error);
      // Default: 0 cohorts per part when parsing fails
      return Array(numParts).fill(0);
    }
    
    console.log('Frontend: Final cohorts per part:', cohortsPerPart);
    return cohortsPerPart;
  };
  const mergeTrialSchemaFromPipeline = (currentValues, trialSchemaFromPipeline = {}) => {
    const numParts =
      trialSchemaFromPipeline.number_of_study_parts_new ||
      currentValues.study_overview?.number_of_study_parts_new ||
      Object.keys(trialSchemaFromPipeline.trial_schema_configuration?.parts || {}).length ||
      0;
    const normalizedConfig = normalizeTrialSchemaConfiguration(
      trialSchemaFromPipeline.trial_schema_configuration ||
        currentValues.study_overview?.trial_schema_configuration ||
        {}
    );
    const synced = syncLegacyFieldsFromTrialSchema(normalizedConfig, numParts);
  // Cohort counts come from trial schema config (num_cohorts), not regimens — prefer synced values.
    return {
      ...currentValues,
      study_overview: {
        ...currentValues.study_overview,
        ...synced.study_overview,
        study_type_dropdown:
          trialSchemaFromPipeline.study_type_dropdown ||
          synced.study_overview?.study_type_dropdown ||
          currentValues.study_overview?.study_type_dropdown,
        study_phase:
          trialSchemaFromPipeline.study_phase ||
          synced.study_overview?.study_phase ||
          currentValues.study_overview?.study_phase,
        study_design:
          trialSchemaFromPipeline.study_design ||
          synced.study_overview?.study_design ||
          currentValues.study_overview?.study_design,
        number_of_study_parts_new:
          synced.study_overview?.number_of_study_parts_new ||
          trialSchemaFromPipeline.number_of_study_parts_new ||
          currentValues.study_overview?.number_of_study_parts_new,
        study_parts_cohorts:
          synced.study_overview?.study_parts_cohorts ||
          trialSchemaFromPipeline.study_parts_cohorts ||
          currentValues.study_overview?.study_parts_cohorts,
        study_parts_part_names:
          synced.study_overview?.study_parts_part_names ||
          trialSchemaFromPipeline.study_parts_part_names ||
          currentValues.study_overview?.study_parts_part_names,
        study_parts_cohort_names:
          synced.study_overview?.study_parts_cohort_names ||
          trialSchemaFromPipeline.study_parts_cohort_names ||
          currentValues.study_overview?.study_parts_cohort_names,
        trial_schema_configuration: normalizedConfig,
      },
      study_treatments: {
        ...currentValues.study_treatments,
        ...synced.study_treatments,
        num_regimens:
          synced.study_treatments?.num_regimens ||
          trialSchemaFromPipeline.num_regimens ||
          currentValues.study_treatments?.num_regimens,
        regimens_per_part:
          synced.study_treatments?.regimens_per_part ||
          trialSchemaFromPipeline.regimens_per_part ||
          currentValues.study_treatments?.regimens_per_part ||
          {},
        regimen_arms: resolveRegimenArms(
          trialSchemaFromPipeline.regimen_arms,
          synced.study_treatments?.regimen_arms || currentValues.study_treatments?.regimen_arms
        ),
      },
    };
  };

  const buildTrialSchemaRequestPayload = (formValues) => ({
    study_type_dropdown: formValues.study_overview?.study_type_dropdown || '',
    study_phase: formValues.study_overview?.study_phase || '',
    study_design: formValues.study_overview?.study_design || {},
    number_of_study_parts_new: formValues.study_overview?.number_of_study_parts_new || '',
    study_parts_cohorts: formValues.study_overview?.study_parts_cohorts || {},
    study_parts_part_names: formValues.study_overview?.study_parts_part_names || {},
    study_parts_cohort_names: formValues.study_overview?.study_parts_cohort_names || {},
    trial_schema_configuration: formValues.study_overview?.trial_schema_configuration || {},
    num_regimens: formValues.study_treatments?.num_regimens || '0',
    regimens_per_part: formValues.study_treatments?.regimens_per_part || {},
    regimen_arms: formValues.study_treatments?.regimen_arms || {},
  });

  const buildFormFieldsForParse = (currentValues) => ({
    study_identification: Object.keys(currentValues.study_identification),
    study_overview: Object.keys(currentValues.study_overview),
    endpoints_objectives: Object.keys(currentValues.endpoints_objectives),
    target_population: Object.keys(currentValues.target_population),
    study_treatments: {
      ...Object.fromEntries(
        Object.entries(currentValues.study_treatments).map(([key, val]) => {
          if (key === 'regimen_arms') {
            return [key, {}];
          }
          return [key, val];
        })
      ),
    },
    discontinuation_rules: Object.keys(currentValues.discontinuation_rules),
    study_assessments: {
      ...Object.fromEntries(
        Object.entries(currentValues.study_assessments).map(([key, val]) => {
          if (typeof val === 'object' && !Array.isArray(val)) {
            return [key, Object.keys(val)];
          }
          return [key, val];
        })
      ),
    },
    statistical_considerations: Object.keys(currentValues.statistical_considerations),
    regulatory_requirements: {
      ...Object.fromEntries(
        Object.entries(currentValues.regulatory_requirements).map(([key, val]) => {
          if (key === 'countries_for_submission' || key === 'committees') {
            return [key, []];
          }
          return [key, val];
        })
      ),
    },
    appendices: [],
    additional_comments: '',
  });

  const resolveCurrentValuesForProcessing = (values, getCurrentValues) => {
    let currentValues = getCurrentValues ? getCurrentValues() : values;
    if (currentValues?.study_treatments?.num_regimens === 0 && values?.study_treatments?.num_regimens > 0) {
      currentValues = values;
    }
    if (getCurrentValues) {
      const latestValues = getCurrentValues();
      if (
        latestValues?.study_treatments?.num_regimens > 0 &&
        currentValues?.study_treatments?.num_regimens === 0
      ) {
        currentValues = latestValues;
      }
    }
    return currentValues;
  };

  const ensureValidatedBeforeProcessing = async (currentValues) => {
    const validationFingerprint = buildValidationFingerprint(currentValues);
    const persisted = loadDocumentValidationState(
      currentProject?._id,
      currentValues?.document_uploads,
      validationFingerprint
    );
    if (persisted?.validationCompleted) {
      setFileValidationStatus(flattenFileStatusMap(persisted.validationMap || {}));
      setAllFilesValid(Boolean(persisted.allValid));
      allFilesValidRef.current = Boolean(persisted.allValid);
      setValidationCompleted(true);
      lastValidatedFingerprintRef.current = validationFingerprint;
      return Boolean(persisted.allValid);
    }
    return validateUploadedFiles(currentValues, { skipIfFresh: true });
  };

  const applySourceDocumentJobProgress = (job) => {
    if (typeof job?.progress === 'number') {
      setPipelineJobProgress(job.progress);
    }
    if (Array.isArray(job?.steps) && job.steps.length > 0) {
      setPipelineSteps(job.steps);
    }
    if (job?.pipelineStepLabel) {
      setPipelineStepLabel(job.pipelineStepLabel);
    }
  };

  const handleAutoPopulateAdminAndTrialSchema = async (setFieldValue, values, setValues, getCurrentValues) => {
    const currentValues = resolveCurrentValuesForProcessing(values, getCurrentValues);
    const studyOutlineFiles = currentValues.document_uploads?.uploaded_files.study_design_outline || [];
    if (studyOutlineFiles.length === 0) {
      alert('Please upload a Study Design Outline document before auto-populating.');
      return;
    }

    const filesAreValid = await ensureValidatedBeforeProcessing(currentValues);
    if (!filesAreValid) {
      setSnackbarMessage(
        'Validate files on the Source Documents tab first, then retry auto-population.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShowParsingWarnings(true);
      return;
    }

    const allFiles = collectActiveUploadedFiles(currentValues, { includeIntakeSections: true });
    if (allFiles.length === 0) {
      alert('No files available for auto-population.');
      return;
    }
    if (!currentProject?._id) {
      alert('No project selected. Please select a project before processing documents.');
      return;
    }
    if (!/^[0-9a-fA-F]{24}$/.test(String(currentProject._id))) {
      // currentProject is populated asynchronously (ProjectContext.loadProject) and
      // can end up stale/wrong if a slower, competing load resolves after the
      // correct one — catch that here with a clear message instead of sending a
      // malformed id to the server and surfacing its generic "Invalid projectId".
      alert('The current project failed to load correctly. Please reload the page and try again.');
      return;
    }

    setAutoPopulatingAdminTrialSchema(true);
    setAutoPopulateSucceeded(false);
    setPipelineSteps([]);
    setPipelineJobProgress(0);
    setPipelineStepLabel('Parsing documents and auto-populating Administrative Info & Trial Schema…');
    try {
      const projectIdentity = getProjectIdentityFields(currentProject);
      const { _id: intakeMongoId } = currentValues;
      const jobResult = await runSourceDocumentJob(
        SOURCE_DOCUMENT_JOB_TYPES.ADMIN_POPULATE,
        {
          files: allFiles,
          projectId: currentProject._id,
          projectIdentity,
          clinicalIntakeId: formId || intakeMongoId || undefined,
          fieldGuidance: buildAdminTrialSchemaFieldGuidance(currentValues.ui_hover_texts),
        },
        { onProgress: applySourceDocumentJobProgress }
      );

      if (!jobResult.success) {
        throw new Error(jobResult.error || 'Auto-population failed');
      }

      if (Array.isArray(jobResult.steps)) {
        setPipelineSteps(jobResult.steps);
      }
      setPipelineJobProgress(100);

      const mergedValues = mergeTrialSchemaFromPipeline(
        currentValues,
        jobResult.trialSchemaData || {}
      );
      const rebuiltStudyParts = generateDynamicStudyPartsTable(
        mergedValues.study_overview?.number_of_study_parts_new,
        mergedValues.study_overview?.study_parts_cohorts,
        {},
        currentValues.study_overview?.study_parts || {}
      );
      const rebuiltTreatmentTable = generateDynamicTreatmentTable(
        mergedValues.study_treatments?.num_regimens,
        mergedValues.study_treatments?.regimen_arms,
        {},
        currentValues.study_treatments?.treatment_table || {},
        rebuiltStudyParts
      );
      const valuesWithTables = scrubIntakePlaceholdersFromDoc({
        ...mergedValues,
        study_overview: {
          ...mergedValues.study_overview,
          study_parts: rebuiltStudyParts,
        },
        study_treatments: {
          ...mergedValues.study_treatments,
          treatment_table: rebuiltTreatmentTable,
        },
      });
      setValues(valuesWithTables);

      lastPopulatedTrialSchemaRef.current = {
        study_overview: valuesWithTables.study_overview,
        study_treatments: valuesWithTables.study_treatments,
      };

      if (onAdminDataPopulated) {
        onAdminDataPopulated({
          adminData: jobResult.adminData,
          intakeTrialSchemaUpdate: {
            study_overview: valuesWithTables.study_overview,
            study_treatments: valuesWithTables.study_treatments,
          },
        });
      }

      // Protocol Intake tab will pick up trial schema via parent formData; reset so rationale runs with new context
      lastRationaleContextFingerprintRef.current = '';

      setSnackbarMessage(
        '✓ Administrative Info and Trial Schema auto-populated. Review those tabs, then save before parsing. Study Registration is filled manually on its tab.'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setAutoPopulateSucceeded(true);
      const workflowState = {
        auto_populate_succeeded: true,
        auto_populate_completed_at: new Date().toISOString(),
      };
      if (setFieldValueRef.current) {
        setFieldValueRef.current('document_uploads.source_documents_workflow', workflowState);
      }
      const valuesToPersist = {
        ...valuesWithTables,
        document_uploads: {
          ...valuesWithTables.document_uploads,
          source_documents_workflow: workflowState,
        },
      };
      setLoadedInitialData((prev) =>
        sanitizeValue({
          ...(prev || loadedInitialData || initialData || {}),
          ...valuesToPersist,
        })
      );
      rememberPopulatedIntakeSnapshot(valuesToPersist);
      if (handleSaveAsDraftRef.current) {
        handleSaveAsDraftRef.current(valuesToPersist, { manageLoading: false, quiet: true }).catch((err) =>
          console.warn('Failed to persist auto-populated trial schema:', err)
        );
      }
    } catch (error) {
      console.error('Error auto-populating admin and trial schema:', error);
      setAutoPopulateSucceeded(false);
      setError(error.response?.data?.error || error.message || 'Auto-population failed');
      if (error.response?.data?.steps) {
        setPipelineSteps(error.response.data.steps);
      }
    } finally {
      setAutoPopulatingAdminTrialSchema(false);
      setPipelineStepLabel('');
    }
  };

  const handleParseClinicalIntakeFromDocuments = async (setFieldValue, values, setValues, getCurrentValues) => {
    const currentValues = resolveCurrentValuesForProcessing(values, getCurrentValues);
    const studyOutlineFiles = currentValues.document_uploads?.uploaded_files.study_design_outline || [];
    if (studyOutlineFiles.length === 0) {
      alert('Please upload a Study Design Outline document before proceeding.');
      return;
    }

    if (!hasConfiguredTrialPartCount(currentValues.study_overview)) {
      alert(
        'Trial schema is required before parsing. Auto-populate from Source Documents, then review and save trial schema in section B on this tab.'
      );
      return;
    }

    const filesAreValid = await ensureValidatedBeforeProcessing(currentValues);
    if (!filesAreValid) {
      setSnackbarMessage(
        'Processing blocked. Validate files on the Source Documents tab first, then retry parsing.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShowParsingWarnings(true);
      return;
    }

    if (!preParseBypassOnceRef.current) {
      preParseArgsRef.current = { setFieldValue, values, setValues, getCurrentValues };
      setPreParseOptionsOpen(true);
      return;
    }
    preParseBypassOnceRef.current = false;

    const allFiles = collectActiveUploadedFiles(currentValues, { includeIntakeSections: true });
    if (allFiles.length === 0) {
      alert('No files available for parsing.');
      return;
    }
    if (!currentProject?._id) {
      alert('No project selected. Please select a project before processing documents.');
      return;
    }
    if (templateLlmLoadingField) {
      setSnackbarMessage(
        'Template language is still generating. Please wait for it to finish before parsing documents.'
      );
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setPipelineSteps([]);
    setPipelineJobProgress(0);
    setPipelineStepLabel('Parsing documents and auto-populating protocol intake…');
    try {
      const formFields = buildFormFieldsForParse(currentValues);
      const projectIdentity = getProjectIdentityFields(currentProject);
      const { _id: intakeMongoId } = currentValues;
      const trialSchemaData = buildTrialSchemaRequestPayload(currentValues);

      const jobResult = await runSourceDocumentJob(
        SOURCE_DOCUMENT_JOB_TYPES.INTAKE_PARSE,
        {
          files: allFiles,
          formFields,
          projectId: currentProject._id,
          projectIdentity,
          clinicalIntakeId: formId || intakeMongoId || undefined,
          trialSchemaData,
          fieldGuidance: buildClinicalIntakeFieldGuidance(currentValues.ui_hover_texts),
        },
        { onProgress: applySourceDocumentJobProgress }
      );
  
      if (jobResult.success) {
        if (Array.isArray(jobResult.steps)) {
          setPipelineSteps(jobResult.steps);
        }
        setPipelineJobProgress(100);

        // Check for parsing warnings
        const parsingWarnings = jobResult.parsingWarnings || [];
        const parsingSummary = jobResult.parsingSummary || {};
        
        console.log('Document parsing summary:', parsingSummary);
        if (parsingWarnings.length > 0) {
          console.warn('Files with parsing warnings:', parsingWarnings);
        }
        
        // Update form values with parsed data
        const parsedData = jobResult.parsedData;
        console.log('Frontend received parsedData:', parsedData);
        console.log('Frontend received regimen_arms:', parsedData.study_treatments?.regimen_arms);
        console.log('Frontend received regimen_arms type:', typeof parsedData.study_treatments?.regimen_arms);
        console.log('Frontend received regimen_arms keys:', Object.keys(parsedData.study_treatments?.regimen_arms || {}));
        console.log('Frontend received regimen_arms values:', Object.values(parsedData.study_treatments?.regimen_arms || {}));
        console.log('Frontend received treatment_table from AI:', parsedData.study_treatments?.treatment_table);
        console.log('Frontend received treatment_table keys:', Object.keys(parsedData.study_treatments?.treatment_table || {}));
        
        // Normalize parsed payload for optional sub-rows that are stored as JSON inside a parent field.
        // If the parser returns plain text, enablement would otherwise show blank sub-rows.
        const normalizeParsedForEnabledSubrows = (doc) => {
          if (doc?.study_overview?.study_parts) {
            migrateStudyPartsPopulationKeys(doc.study_overview.study_parts);
          }
          const isEmpty = (v) =>
            v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));

          const safeParseObj = (raw) => {
            if (!raw || typeof raw !== 'string') return null;
            try {
              const parsed = JSON.parse(raw);
              return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
            } catch {
              return null;
            }
          };

          const safeStringify = (obj) => {
            try {
              return JSON.stringify(obj);
            } catch {
              return '';
            }
          };

          // 1) Study parts: analyses_* stored as JSON when sub-rows enabled
          const sp = doc?.study_overview?.study_parts;
          if (sp && typeof sp === 'object') {
            Object.keys(sp).forEach((partKey) => {
              const part = sp[partKey];
              if (!part || typeof part !== 'object') return;
              Object.keys(part).forEach((cohortKey) => {
                const cohort = part[cohortKey];
                if (!cohort || typeof cohort !== 'object') return;

                const maybeNormalizeAnalysis = (fieldName, enabledMap) => {
                  const anyEnabled = Object.values(enabledMap || {}).some(Boolean);
                  if (!anyEnabled) return;
                  const raw = cohort[fieldName];
                  if (isEmpty(raw)) {
                    cohort[fieldName] = buildAnalysisJsonFromPlainText('', enabledMap);
                    return;
                  }
                  const parsedObj = safeParseObj(raw);
                  if (parsedObj) {
                    cohort[fieldName] = safeStringify(
                      normalizeStoredAnalysisField(parsedObj, enabledMap)
                    );
                    return;
                  }
                  cohort[fieldName] = buildAnalysisJsonFromPlainText(raw, enabledMap);
                };

                maybeNormalizeAnalysis('analyses_primary_objective', primaryObjectiveAnalysisEnabledSubfields);
                maybeNormalizeAnalysis('analyses_secondary_objective', secondaryObjectiveAnalysisEnabledSubfields);
              });
            });
            normalizeStudyPartsParentSubFields(sp);
          }

          // 3) Treatment table: rescue_medication — parent + enabled sub-keys → text or Not Provided
          const tt = doc?.study_treatments?.treatment_table;
          if (tt && typeof tt === 'object') {
            normalizeTreatmentTableParentSubFields(tt, investigationalInterventionHandlingEnabledSubfields);

            const anyRescueEnabled = Object.values(rescueMedicationEnabledSubfields || {}).some(Boolean);
            if (!anyRescueEnabled) return;

            Object.keys(tt).forEach((regimenKey) => {
              const regimen = tt[regimenKey];
              if (!regimen || typeof regimen !== 'object') return;
              const raw = regimen.rescue_medication;

              if (isEmpty(raw)) {
                regimen.rescue_medication = buildRescueJsonFromPlainText('', rescueMedicationEnabledSubfields);
                return;
              }

              const parsedObj = safeParseObj(raw);
              if (parsedObj) {
                const legacyMap = {
                  backgroundTrialIntervention: 'background_trial_intervention',
                  otherNonInvestigationalIntervention: 'other_noninvestigational_trial_intervention',
                };
                const migratedObj = { ...parsedObj };
                Object.entries(legacyMap).forEach(([camel, snake]) => {
                  if (
                    Object.prototype.hasOwnProperty.call(migratedObj, camel) &&
                    !Object.prototype.hasOwnProperty.call(migratedObj, snake)
                  ) {
                    migratedObj[snake] = migratedObj[camel];
                    delete migratedObj[camel];
                  }
                });
                regimen.rescue_medication = safeStringify(
                  normalizeStoredRescueField(migratedObj, rescueMedicationEnabledSubfields)
                );
                return;
              }

              regimen.rescue_medication = buildRescueJsonFromPlainText(
                raw,
                rescueMedicationEnabledSubfields
              );
            });
          }
        };

        normalizeParsedForEnabledSubrows(parsedData);
        scrubIntakePlaceholdersFromDoc(parsedData);

        const inferredLifestyle = inferLifestyleSubfieldEnables(parsedData?.study_overview?.study_parts);
        const inferredPrep = inferPrepHandlingSubfieldEnables(parsedData?.study_treatments?.treatment_table);
        const inferredPrimaryAnalysis = inferPrimaryAnalysisSubfieldEnables(
          parsedData?.study_overview?.study_parts
        );
        const inferredSecondaryAnalysis = inferSecondaryAnalysisSubfieldEnables(
          parsedData?.study_overview?.study_parts
        );
        const inferredRescue = inferRescueSubfieldEnables(parsedData?.study_treatments?.treatment_table);
        const parsedSubfieldSelections = {
          ...getStudyTableSubfieldSelectionsPayload(),
          lifestyle_restrictions: {
            ...lifestyleRestrictionEnabledSubfields,
            ...inferredLifestyle,
          },
          investigational_intervention_handling: {
            ...investigationalInterventionHandlingEnabledSubfields,
            ...inferredPrep,
          },
          rescue_medication: {
            ...rescueMedicationEnabledSubfields,
            ...inferredRescue,
          },
          analyses_primary_objective: {
            ...primaryObjectiveAnalysisEnabledSubfields,
            ...inferredPrimaryAnalysis,
          },
          analyses_secondary_objective: {
            ...secondaryObjectiveAnalysisEnabledSubfields,
            ...inferredSecondaryAnalysis,
          },
        };
        if (Object.values(inferredLifestyle).some(Boolean)) {
          setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, ...inferredLifestyle }));
        }
        if (Object.values(inferredPrep).some(Boolean)) {
          setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({ ...prev, ...inferredPrep }));
        }
        if (Object.values(inferredRescue).some(Boolean)) {
          setRescueMedicationEnabledSubfields((prev) => ({ ...prev, ...inferredRescue }));
        }
        if (Object.values(inferredPrimaryAnalysis).some(Boolean)) {
          setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, ...inferredPrimaryAnalysis }));
        }
        if (Object.values(inferredSecondaryAnalysis).some(Boolean)) {
          setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, ...inferredSecondaryAnalysis }));
        }

        const preParseTemplateSnapshot = lastPopulatedIntakeRef.current;
        const mergedTemplateSelections = getMergedTemplateSelections();

        // STEP 4: Generate dynamic study parts table based on user input
        console.log('Step 4: Generating dynamic study parts table...');
        console.log('Frontend: User input - number_of_study_parts_new:', currentValues.study_overview?.number_of_study_parts_new);
        console.log('Frontend: User input - study_parts_cohorts:', currentValues.study_overview?.study_parts_cohorts);
        console.log('Frontend: AI returned study_parts:', parsedData.study_overview?.study_parts);
        
        // CRITICAL: Use user's structure, not AI's structure
        const dynamicStudyParts = generateDynamicStudyPartsTable(
          currentValues.study_overview?.number_of_study_parts_new,
          currentValues.study_overview?.study_parts_cohorts,
          parsedData,
          currentValues.study_overview?.study_parts || {}
        );
        
        console.log('Frontend: Generated dynamic study parts (user-based):', dynamicStudyParts);

        // Generate dynamic treatment table based on number of regimens
        console.log('Frontend: About to generate treatment table with:', {
          num_regimens: currentValues.study_treatments?.num_regimens,
          num_regimens_type: typeof currentValues.study_treatments?.num_regimens,
          regimen_arms: currentValues.study_treatments?.regimen_arms,
          regimen_arms_type: typeof currentValues.study_treatments?.regimen_arms,
          regimen_arms_keys: Object.keys(currentValues.study_treatments?.regimen_arms || {}),
          regimen_arms_values: Object.values(currentValues.study_treatments?.regimen_arms || {})
        });
        
        const dynamicTreatmentTable = generateDynamicTreatmentTable(
          currentValues.study_treatments?.num_regimens,
          currentValues.study_treatments?.regimen_arms,
          parsedData,
          currentValues.study_treatments?.treatment_table || {},
          dynamicStudyParts
        );
        
        console.log('Frontend: Generated dynamic treatment table (user-based):', dynamicTreatmentTable);
        console.log('Frontend: AI returned treatment table data:', parsedData.study_treatments?.treatment_table);
        console.log('Frontend: Preserving user trial schema fields:', {
          study_type_dropdown: currentValues.study_overview?.study_type_dropdown,
          study_phase: currentValues.study_overview?.study_phase,
          study_design: currentValues.study_overview?.study_design,
          number_of_study_parts_new: currentValues.study_overview?.number_of_study_parts_new,
          study_parts_cohorts: currentValues.study_overview?.study_parts_cohorts
        });
        
        const updatedValues = {
          ...currentValues,
          // Trial schema fields populated from pipeline (step 3) — keep extracted values
          study_overview: {
            ...currentValues.study_overview,
            // Only update non-trial-schema fields from AI clinical intake parse
            ...Object.fromEntries(
              Object.entries(parsedData.study_overview || {}).filter(([key]) => 
                 ![
                  'study_type_dropdown',
                  'study_phase',
                  'study_design',
                  'number_of_study_parts_new',
                  'study_parts_cohorts',
                  'study_parts_part_names',
                  'study_parts_cohort_names',
                ].includes(key)
              )
            ),
            // Use our dynamically generated table structure
            study_parts: dynamicStudyParts
          },
          // Update other sections from AI but preserve user inputs
          study_identification: {
            ...currentValues.study_identification,
            ...parsedData.study_identification,
            // Remember the title as parsed from source documents so "Revert to Original
            // Title" can restore it later, even after AI generation or manual edits.
            document_official_title:
              parsedData.study_identification?.official_title?.trim() ||
              currentValues.study_identification?.document_official_title ||
              '',
            document_short_title:
              parsedData.study_identification?.preferred_short_title?.trim() ||
              currentValues.study_identification?.document_short_title ||
              '',
          },
          endpoints_objectives: {
            ...currentValues.endpoints_objectives,
            ...parsedData.endpoints_objectives
          },
          target_population: {
            ...currentValues.target_population,
            ...parsedData.target_population
          },
          study_treatments: (() => {
            const {
              regimen_arms: _parsedRegimenArms,
              treatment_table: _parsedTreatmentTable,
              _debug_regimen_arms: _parsedDebug,
              ...parsedTreatmentRest
            } = parsedData.study_treatments || {};
            return {
              ...currentValues.study_treatments,
              ...parsedTreatmentRest,
              treatment_table: dynamicTreatmentTable,
              regimen_arms: resolveRegimenArms(
                _parsedRegimenArms,
                currentValues.study_treatments?.regimen_arms
              ),
            };
          })(),
          discontinuation_rules: mergeParsedSectionRespectingTemplates(
            'discontinuation_rules',
            currentValues.discontinuation_rules,
            parsedData.discontinuation_rules,
            mergedTemplateSelections
          ),
          study_assessments: syncStudyAssessmentsFromStudyParts(dynamicStudyParts, {
            ...currentValues.study_assessments,
            ...parsedData.study_assessments,
          }),
          statistical_considerations: {
            ...currentValues.statistical_considerations,
            ...parsedData.statistical_considerations
          },
          regulatory_requirements: (() => {
            const mergedRegulatory = mergeParsedSectionRespectingTemplates(
              'regulatory_requirements',
              currentValues.regulatory_requirements,
              parsedData.regulatory_requirements,
              mergedTemplateSelections
            );
            return {
              ...mergedRegulatory,
              committees: resolveCommitteesUnlessTemplateSelected(
                dynamicStudyParts,
                parsedData.regulatory_requirements?.committees || mergedRegulatory.committees,
                mergedTemplateSelections
              ),
            };
          })(),
          template_language_fields: mergeParsedSectionRespectingTemplates(
            'template_language_fields',
            currentValues.template_language_fields,
            parsedData.template_language_fields,
            mergedTemplateSelections
          ),
          appendices: parsedData.appendices || currentValues.appendices,
          additional_comments: parsedData.additional_comments || currentValues.additional_comments
        };
        
        console.log('Updated values regimen_arms:', updatedValues.study_treatments?.regimen_arms);
        console.log('Generated dynamic study parts:', dynamicStudyParts);
        console.log('Frontend: Final trial schema fields preserved:', {
          study_type_dropdown: updatedValues.study_overview?.study_type_dropdown,
          study_phase: updatedValues.study_overview?.study_phase,
          study_design: updatedValues.study_overview?.study_design,
          number_of_study_parts_new: updatedValues.study_overview?.number_of_study_parts_new,
          study_parts_cohorts: updatedValues.study_overview?.study_parts_cohorts
        });
        
        // Log the final treatment table that will be used
        console.log('AI auto-population: Final treatment table:', updatedValues.study_treatments?.treatment_table);
        console.log('AI auto-population: Treatment table keys:', Object.keys(updatedValues.study_treatments?.treatment_table || {}));

        repairClubbedAnalysisFieldsInDoc({
          study_overview: updatedValues.study_overview,
          study_treatments: updatedValues.study_treatments,
          studyTableSubfieldSelections: parsedSubfieldSelections,
        });

        const cleanedValues = scrubIntakePlaceholdersFromDoc(updatedValues);

        const consolidatedValues = scrubIntakePlaceholdersFromDoc(
          consolidateIntakeValuesForSave({
            loaded: loadedInitialData || currentValues,
            snapshot: preParseTemplateSnapshot || loadedInitialData || currentValues,
            form: cleanedValues,
            templateSelections: mergedTemplateSelections,
            fieldTemplateSelections: fieldTemplateSelectionsRef.current,
          })
        );

        setValues(consolidatedValues);
        rememberPopulatedIntakeSnapshot(consolidatedValues);
        setLoadedInitialData(sanitizeValue(consolidatedValues));
        if (typeof onIntakeParsed === 'function') {
          onIntakeParsed(consolidatedValues);
        }
        
        const hasParsedContent = parsedData && typeof parsedData === 'object';
        const shouldBlockAutoGeneration =
          parsingWarnings.length > 0 &&
          (parsingSummary.successfulFiles == null || parsingSummary.successfulFiles === 0);

        // Display warnings if any files couldn't be parsed
        if (parsingWarnings.length > 0) {
          console.warn('Files with parsing warnings:', parsingWarnings);
          
          // Store warnings in state for detailed display
          setParsingWarningsState(parsingWarnings);
          setShowParsingWarnings(true);
          
          // Show brief snackbar notification
          setSnackbarMessage(
            `⚠️ Warning: ${parsingWarnings.length} file(s) had parsing issues. ` +
            `Successfully extracted from ${parsingSummary.successfulFiles}/${parsingSummary.totalFiles} files. ` +
            (shouldBlockAutoGeneration
              ? 'Processing stopped. Please review the warnings.'
              : 'Continuing with Arguments generation from successfully parsed content.')
          );
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          
          console.warn('Displaying parsing warnings to user:', parsingWarnings);
        }

        if (shouldBlockAutoGeneration) {
          console.log('Saving partial data extracted from readable files...');
          try {
            await handleSaveAsDraft(consolidatedValues, {
              manageLoading: false,
              quiet: true,
              studyTableSubfieldSelections: parsedSubfieldSelections,
            });
            console.log('Partial data saved successfully');
          } catch (saveError) {
            console.error('Error saving partial data:', saveError);
            setError('Failed to save partial data: ' + saveError.message);
          }
          console.log('⛔ Skipping automatic Arguments generation — no files were parsed successfully');
        } else if (hasParsedContent) {
          // ✅ Parsed content available — save intake then auto-generate Arguments (section D)
          console.log('✅ Proceeding with auto-save and Arguments generation');

          console.log('Auto-saving populated form to backend...');
          try {
            await handleSaveAsDraft(consolidatedValues, {
              manageLoading: false,
              quiet: true,
              studyTableSubfieldSelections: parsedSubfieldSelections,
            });
            console.log('Form auto-saved successfully');
          } catch (saveError) {
            console.error('Error auto-saving form:', saveError);
            setError('Form populated but failed to auto-save: ' + saveError.message);
          }

          console.log('Starting automatic background information (Arguments) generation...');
          setPipelineJobProgress(0);
          setPipelineStepLabel('Generating Arguments (section D)…');
          try {
            await handleAutoGenerateBackgroundInfo(
              consolidatedValues,
              setFieldValue,
              () => consolidatedValues
            );
            console.log('Background information generation completed');
            const hasOfficialTitle = Boolean(
              consolidatedValues.study_identification?.official_title?.trim()
            );
            setSnackbarMessage(
              hasOfficialTitle
                ? '✓ Protocol intake auto-populated and Arguments (section D) generated from documents.'
                : '✓ Protocol intake auto-populated and Arguments (section D) generated. Official title was not found in documents — use Generate from Intake in section A.'
            );
          } catch (bgError) {
            console.error('Error generating background information:', bgError);
            setError('Intake saved but Arguments generation failed: ' + bgError.message);
            setSnackbarMessage(
              'Intake auto-populated. Arguments generation failed — use section D to retry.'
            );
          }
          setSnackbarSeverity(parsingWarnings.length > 0 ? 'warning' : 'success');
          setSnackbarOpen(true);

          setIntakeParseSucceeded(true);
          const parseWorkflowState = {
            ...(consolidatedValues.document_uploads?.source_documents_workflow || {}),
            intake_parse_succeeded: true,
            intake_parse_completed_at: new Date().toISOString(),
          };
          if (setFieldValue) {
            setFieldValue('document_uploads.source_documents_workflow', parseWorkflowState);
          }
          const parseValuesToPersist = {
            ...consolidatedValues,
            document_uploads: {
              ...consolidatedValues.document_uploads,
              source_documents_workflow: parseWorkflowState,
            },
          };
          setLoadedInitialData((prev) =>
            sanitizeValue({
              ...(prev || loadedInitialData || initialData || {}),
              ...parseValuesToPersist,
            })
          );
          if (handleSaveAsDraftRef.current) {
            handleSaveAsDraftRef.current(parseValuesToPersist, { manageLoading: false, quiet: true }).catch((err) =>
              console.warn('Failed to persist intake-parse-succeeded flag:', err)
            );
          }
        }

      } else {
        throw new Error(jobResult.error || 'Failed to parse documents');
      }
    } catch (error) {
      console.error('Error parsing documents:', error);
      setError(error.response?.data?.error || error.message || 'Error parsing documents');
      if (error.response?.data?.steps) {
        setPipelineSteps(error.response.data.steps);
      }
    } finally {
      setLoading(false);
      setPipelineStepLabel('');
    }
  };

  const handleGenerateOfficialTitle = async (setFieldValue, values) => {
    if (!currentProject?._id) {
      setSnackbarMessage('Select a project before generating titles.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    setGeneratingOfficialTitle(true);
    setError(null);
    try {
      const titles = await generateOfficialTitlesFromIntake(currentProject._id);
      setFieldValue('study_identification.official_title', titles.official_title);
      if (titles.brief_title) {
        setFieldValue('study_identification.preferred_short_title', titles.brief_title);
      }
      setSnackbarMessage('Official and short titles generated and saved.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'Failed to generate titles. Complete intake auto-population first.';
      setError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setGeneratingOfficialTitle(false);
    }
  };

  const handleRevertOfficialTitle = (setFieldValue, values) => {
    const original = values.study_identification?.document_official_title || '';
    setFieldValue('study_identification.official_title', original);
    setSnackbarMessage('Official title reverted to the version populated from documents.');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleRevertShortTitle = (setFieldValue, values) => {
    const original = values.study_identification?.document_short_title || '';
    setFieldValue('study_identification.preferred_short_title', original);
    setSnackbarMessage('Short title reverted to the version populated from documents.');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // After useState hooks, add:
  const handleGenerateSoA = async (values) => {
    setGeneratingSoA(true);
    setGenerateSoAError(null);
    setGeneratedSoAData(null);

    try {
      const numberOfStudyParts = values.study_overview?.number_of_study_parts || "1";
      const studyPeriods = (() => {
        const periods = values.study_overview?.study_periods;
        if (typeof periods === 'string') {
          return periods.split('\n').filter(period => period.trim() !== '');
        } else if (Array.isArray(periods)) {
          return periods.filter(period => period.trim() !== '');
        }
        return ["Screening", "Baseline", "Treatment", "Follow-up"];
      })();
      // Use currentProject._id as fallback instead of "default" to match DocumentEditor behavior
      const protocolId = values.study_identification?.protocol_number || currentProject?._id || "default";

      const soaResponse = await scheduleOfActivitiesService.generateScheduleOfActivities({
        protocolInput: "Generate a standard Schedule of Activities table for a clinical trial",
        protocolId,
        numberOfStudyParts,
        studyPeriods
      });

      // generateScheduleOfActivities already saves to database, so we don't need to call saveScheduleOfActivities again
      // This prevents creating duplicate versions. The SOA is already saved with version 1.
      // Always use soaResponse.data (full MongoDB document) which includes visits, sections, footnotes, summary, versionNumber, etc.
      // soaResponse.soaData is just the generated table data without metadata, so we should not use it as fallback
      if (soaResponse.success && soaResponse.data) {
        setGeneratedSoAData(soaResponse.data);
      } else {
        // If data is missing, log error but don't set partial data
        console.error('SOA generation response missing data field:', soaResponse);
        setGenerateSoAError('SOA was generated but data structure is invalid. Please try again.');
      }
      setSuccessMessage('Schedule of Activities generated and saved successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (error) {
      setGenerateSoAError(error.message || 'Failed to generate Schedule of Activities');
    } finally {
      setGeneratingSoA(false);
    }
  };

  const handleRegenerateSoA = async (currentPrompt = '', formValues) => {
    setGeneratingSoA(true);
    setGenerateSoAError(null);
    setGeneratedSoAData(null);

    try {
      const numberOfStudyParts = formValues?.study_overview?.number_of_study_parts || "1";
      const studyPeriods = (() => {
        const periods = formValues?.study_overview?.study_periods;
        if (typeof periods === 'string') {
          return periods.split('\n').filter(period => period.trim() !== '');
        } else if (Array.isArray(periods)) {
          return periods.filter(period => period.trim() !== '');
        }
        return ["Screening", "Baseline", "Treatment", "Follow-up"];
      })();
      // Use currentProject._id as fallback instead of "default" to match DocumentEditor behavior
      const protocolId = formValues?.study_identification?.protocol_number || currentProject?._id || "default";

      const originalPrompt = "Generate a standard Schedule of Activities table for a clinical trial";
      const combinedPrompt = currentPrompt
        ? `${originalPrompt}\n\nAdditional requirements: ${currentPrompt}`
        : originalPrompt;

      const soaResponse = await scheduleOfActivitiesService.generateScheduleOfActivities({
        protocolInput: combinedPrompt,
        protocolId,
        numberOfStudyParts,
        studyPeriods
      });

      // generateScheduleOfActivities already saves to database, so we don't need to call saveScheduleOfActivities again
      // This prevents creating duplicate versions. The SOA is already saved with proper versioning.
      // Always use soaResponse.data (full MongoDB document) which includes visits, sections, footnotes, summary, versionNumber, etc.
      if (soaResponse.success && soaResponse.data) {
        setGeneratedSoAData(soaResponse.data);
      // Show version info if available
        if (soaResponse.data?.versionNumber) {
          setSuccessMessage(`Schedule of Activities regenerated successfully! Version: ${soaResponse.data.versionNumber}`);
      } else {
      setSuccessMessage('Schedule of Activities regenerated successfully!');
        }
      } else {
        // If data is missing, log error but don't set partial data
        console.error('SOA regeneration response missing data field:', soaResponse);
        setGenerateSoAError('SOA was regenerated but data structure is invalid. Please try again.');
      }
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (error) {
      setGenerateSoAError(error.message || 'Failed to regenerate Schedule of Activities');
    } finally {
      setGeneratingSoA(false);
    }
  };

  const handleAutoGenerateBackgroundInfo = async (values, setFieldValue, getCurrentValues) => {
    console.log('Starting automatic background information generation...');

    setGeneratingBackgroundInfo(true);
    setBackgroundInfoError(null);
    setBackgroundInfoProgress('Starting background information generation...');

    try {
      // First, determine rationale field relevance if not already done
      const hasRelevanceDetermined = Object.values(rationaleFieldRelevance).some(value => value === true);
      let currentRelevance = rationaleFieldRelevance;
      
      if (!hasRelevanceDetermined) {
        const schemaReady = Boolean(buildTrialSchemaContextFingerprint(values));
        if (!schemaReady) {
          console.log('Skipping rationale relevance — trial schema not populated yet');
        } else {
          console.log('Determining rationale field relevance before generating background info...');
          setBackgroundInfoProgress('Analyzing trial design to determine relevant rationale sections...');
          currentRelevance = await determineRationaleFieldRelevanceAndReturn(values);
        }
      }
      // Always include basic background fields
      const backgroundSections = buildBackgroundSectionsForGeneration(values, currentRelevance);
      
      console.log('Background sections to generate:', backgroundSections.map(s => s.key));
      console.log('Relevant rationale fields:', Object.keys(currentRelevance).filter(key => currentRelevance[key]));

      if (!currentProject?._id) {
        throw new Error('No project selected for Arguments generation');
      }

      const jobResult = await runSourceDocumentJob(
        SOURCE_DOCUMENT_JOB_TYPES.ARGUMENTS,
        {
          projectId: currentProject._id,
          clinicalIntakeId: formId || values._id || values.id,
          sections: backgroundSections.map(({ key, prompt, fieldName, displayName }) => ({
            key,
            prompt,
            fieldName,
            displayName,
          })),
          rationaleFieldRelevance: currentRelevance,
        },
        {
          onProgress: (job) => {
            applySourceDocumentJobProgress(job);
            if (job.pipelineStepLabel) {
              setBackgroundInfoProgress(job.pipelineStepLabel);
            }
          },
        }
      );

      applyBackgroundInformationToForm(setFieldValue, jobResult.background_information);
      if (Array.isArray(jobResult.steps)) {
        setPipelineSteps(jobResult.steps);
      }
      setPipelineJobProgress(100);
      setPipelineStepLabel('Arguments (section D) generation complete');

      setBackgroundInfoProgress('Background information generation completed successfully!');
      console.log('All background information sections generated successfully');
      
      // Auto-save the form again after background information is generated
      setTimeout(async () => {
        try {
          const currentFormValues = getCurrentValues ? getCurrentValues() : values;
          const mergedForSave = {
            ...currentFormValues,
            background_information: {
              ...(currentFormValues.background_information || {}),
              ...(jobResult.background_information || {}),
            },
          };
          await handleSaveAsDraft(mergedForSave, { manageLoading: false, quiet: true });
          console.log('Form auto-saved after background information generation');
        } catch (saveError) {
          console.error('Error auto-saving form after background generation:', saveError);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error in automatic background information generation:', error);
      setBackgroundInfoError(`Background information generation failed: ${error.message}`);
      throw new Error(`Background information generation failed: ${error.message}`);
    } finally {
      setGeneratingBackgroundInfo(false);
    }
  };

  // Add missing handleBlur function
  const handleBlur = (e) => {
    // This function handles blur events for form fields
    // It's required by Formik for proper form validation
    console.log('Field blurred:', e.target.name);
  };

  // Handle field-level template selection
  const handleFieldTemplateToggle = (fieldPath, useTemplate) => {
    setFieldTemplateSelections(prev => ({
      ...prev,
      [fieldPath]: useTemplate
    }));
  };

  const handleNextToDocumentEditor = async () => {
    console.log('handleNextToDocumentEditor called');
    console.log('currentProject:', currentProject);
    console.log('navigate function:', navigate);
    
    if (currentProject) {
      try {
        if (handleSaveAsDraftRef.current && formikValuesRef.current) {
          const consolidated = consolidateIntakeValuesForSave({
            loaded: loadedInitialData || formData,
            snapshot: lastPopulatedIntakeRef.current,
            form: formikValuesRef.current,
            templateSelections: templateSelectionsRef.current,
            fieldTemplateSelections: fieldTemplateSelectionsRef.current,
          });
          if (resolveIntakeMongoId(consolidated)) {
            await handleSaveAsDraftRef.current(consolidated, {
              lockForm: true,
              quiet: true,
              manageLoading: false,
            });
          }
        }

        // Mark the workflow as complete in the database
        await clinicalIntakeService.markClinicalIntakeWorkflowComplete(currentProject._id);
        console.log('Workflow marked as complete for project:', currentProject._id);

        await checkFormLockStatus();
        
        const url = `/clinical-intake?project=${currentProject._id}&tab=document-editor`;
        console.log('Navigating to:', url);
        navigate(url);
        console.log('Navigation called successfully');
      } catch (error) {
        console.error('Error marking workflow complete:', error);
        // Still navigate even if marking fails
        const url = `/clinical-intake?project=${currentProject._id}&tab=document-editor`;
        navigate(url);
      }
    } else {
      console.log('No currentProject, navigating to:', '/clinical-intake?tab=document-editor');
      try {
        navigate('/clinical-intake?tab=document-editor');
        console.log('Navigation called successfully');
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      {/* Loading state while checking lock status */}
      {isCheckingLock && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Checking form status...
          </Typography>
        </Box>
      )}

      {/* Lock indicator */}
      {!isCheckingLock && formLocked && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-icon': { fontSize: '1.5rem' }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleUnlockForm}
              disabled={loading}
              sx={{ ml: 2 }}
            >
              Unlock Form
            </Button>
          }
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            🔒 Form Locked - Read Only Mode
          </Typography>
          <Typography variant="body2">
            This clinical intake form has been submitted and is now locked. You can view the submitted content but cannot make changes. 
            Click "Unlock Form" to make changes. Note: You may need to regenerate protocol sections after making changes.
          </Typography>
        </Alert>
      )}

      {/* Unlocked indicator with lock button */}
      {!isCheckingLock && !formLocked && formSubmitted && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-icon': { fontSize: '1.5rem' }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleLockForm}
              disabled={loading}
              sx={{ ml: 2 }}
            >
              Lock Form
            </Button>
          }
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            🔓 Form Unlocked - Edit Mode
          </Typography>
          <Typography variant="body2">
            This form is unlocked and can be edited. Changes are automatically saved. Click "Lock Form" to prevent further changes.
          </Typography>
        </Alert>
      )}

      {!currentProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select a project first
        </Alert>
      )}

      {currentProject && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Sponsor Protocol Identifier: {resolveProjectSponsorProtocolId(currentProject) || projectListPrimaryLabel(currentProject)}
            {formLocked && (
              <Typography 
                component="span" 
                sx={{ 
                  ml: 2, 
                  fontSize: '0.8em', 
                  color: '#666',
                  fontStyle: 'italic'
                }}
              >
                (Form Locked)
              </Typography>
            )}
          </Typography>
        </Paper>
      )}

      {/* Determine if form should be disabled */}
      {(() => {
        const isFormDisabled = isViewMode || formLocked;

        // Debug: Log form state
        console.log('Form state debug:', {
          isViewMode,
          formLocked,
          isFormDisabled,
          currentProject: !!currentProject,
          formId,
          formSubmitted
        });
        
        return (
          <>
            <Formik
              initialValues={formikInitialValues}
              validationSchema={validationSchema}
              enableReinitialize={true}
              validateOnChange={false}
              validateOnBlur={true}
              onSubmit={async (values, formikHelpers) => {
          console.log('Formik onSubmit triggered with values:', values);
          console.log('Formik helpers:', formikHelpers);
          
          // Call the original handleSubmit
          await handleSubmit(values, formikHelpers);
        }}
      >
        {({ values, errors, touched, isSubmitting, dirty, setFieldValue, setValues, handleChange, handleBlur, getCurrentValues }) => {
          formikValuesRef.current = values;
          setFieldValueRef.current = setFieldValue;
          // Define handleBlur function if it doesn't exist
          const handleBlurWrapper = handleBlur || (() => {});
          
          const getFieldValue = (path, defaultValue = '') => {
            const value = path.split('.').reduce((obj, key) => obj?.[key], values);
            return value === null || value === undefined ? defaultValue : value;
          };

          const renderTemplateCheckbox = (fieldName, section = 'regulatory_requirements', { label = '' } = {}) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={templateSelections[fieldName] || false}
                  onClick={(e) => {
                    if (
                      !templateSelections[fieldName] ||
                      isTemplateCheckboxDisabled(fieldName) ||
                      e.shiftKey
                    ) {
                      return;
                    }
                    // Already checked: click again regenerates (do not uncheck)
                    e.preventDefault();
                    e.stopPropagation();
                    handleTemplateCheckboxChange(
                      fieldName,
                      true,
                      setValues,
                      setFieldValue,
                      values,
                      section,
                      { regenerate: true }
                    );
                  }}
                  onChange={(e) => {
                    // Already checked: regenerate is handled by onClick only.
                    // Ignore duplicate onChange (including checked=true after preventDefault).
                    if (templateSelections[fieldName]) {
                      if (!e.target.checked && e.nativeEvent?.shiftKey) {
                        handleTemplateCheckboxChange(
                          fieldName,
                          false,
                          setValues,
                          setFieldValue,
                          values,
                          section
                        );
                      }
                      return;
                    }
                    handleTemplateCheckboxChange(
                      fieldName,
                      e.target.checked,
                      setValues,
                      setFieldValue,
                      values,
                      section
                    );
                  }}
                  disabled={isTemplateCheckboxDisabled(fieldName)}
                  size="small"
                />
              }
              label={label}
              sx={{ margin: 0 }}
            />
          );

          // Helper function for custom regeneration
          const handleCustomRegeneration = async (fieldName, customPrompt, setFieldValue) => {
            if (!customPrompt?.trim()) {
              alert('Please enter a custom prompt first.');
              return;
            }

            // Set loading state for this specific field
            setCustomRegenerationLoading(prev => ({ ...prev, [fieldName]: true }));

            try {
              // Call individual regeneration endpoint
              const response = await axiosInstance.post(`${API_BASE_URL}/ai/regenerate-background-field`, {
                fieldName: fieldName,
                customPrompt: customPrompt,
                formData: values
              });
              if (response.data.success && response.data.result) {
                setFieldValue(`background_information.${fieldName}`, response.data.result);
              } else {
                throw new Error('Invalid response from server');
              }
            } catch (error) {
              console.error('Error regenerating field:', error);
              alert('Error regenerating content. Please try again.');
            } finally {
              // Clear loading state for this specific field
              setCustomRegenerationLoading(prev => ({ ...prev, [fieldName]: false }));
            }
          };
          
          // Debug: Log validation errors
          console.log('Form validation errors:', errors);
          console.log('Form touched fields:', touched);
          console.log('Form isSubmitting:', isSubmitting);
          
          return (
          <>
          <AutoSaveWatcher
            enabled={!formLocked && formSubmitted && !!currentProject?._id}
            values={values}
            onAutoSave={handleAutoSave}
          />
          <Form>
            <Paper
              sx={{
                p: documentsOnly ? { xs: 1, md: 1.5 } : { xs: 2, md: 3 },
                mb: 3,
                borderRadius: 3,
                border: '1px solid #dbe3eb',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                background: isFormDisabled
                  ? 'linear-gradient(180deg, #f7f8fa 0%, #f3f5f7 100%)'
                  : 'linear-gradient(180deg, #fcfdff 0%, #f8fafc 100%)',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'visible',
                // Locked/view mode: keep fields disabled, but do NOT use pointer-events:none
                // (that freezes page/field scrolling and feels overly aggressive).
                ...(isFormDisabled
                  ? {
                      opacity: 0.96,
                      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.35)',
                    }
                  : {}),
                '& .MuiFormControl-root': {
                  mt: 0.75,
                  mb: 0.75,
                },
                '& .MuiInputBase-root': {
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: 'rgba(0, 0, 0, 0.72)',
                  },
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.72)',
                  // Allow selecting/scrolling long disabled field content
                  pointerEvents: 'auto',
                  userSelect: 'text',
                  cursor: 'text',
                },
                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.18)',
                },
                '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled, & .MuiSwitch-root.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                '& .MuiButton-root.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
                '& .MuiChip-root': {
                  ...(isFormDisabled
                    ? {
                        opacity: 0.9,
                      }
                    : {}),
                },
                '& .MuiOutlinedInput-input': {
                  py: 1.1,
                },
                '& .MuiInputBase-inputMultiline': {
                  lineHeight: 1.45,
                },
                '& .MuiTypography-h6': {
                  fontWeight: 700,
                  color: '#0f172a',
                  letterSpacing: '0.01em',
                },
                '& .MuiAccordion-root': {
                  marginBottom: '4px !important',
                  '&:before': { display: 'none' },
                },
                '& .MuiAccordionSummary-root': {
                  minHeight: '40px !important',
                  '&.Mui-expanded': { minHeight: '40px !important' },
                },
                '& .MuiAccordionSummary-content': {
                  margin: '6px 0 !important',
                  '&.Mui-expanded': { margin: '6px 0 !important' },
                },
                '& .MuiAccordionDetails-root': {
                  paddingTop: '4px !important',
                  paddingBottom: '6px !important',
                },
                '& .MuiDivider-root': {
                  marginTop: '6px !important',
                  marginBottom: '6px !important',
                },
                '& table': {
                  width: '100%',
                  tableLayout: 'fixed !important',
                },
                '& table th, & table td': {
                  padding: '6px !important',
                  fontSize: '12px',
                  lineHeight: 1.35,
                  verticalAlign: 'top !important',
                  textAlign: 'left !important',
                  overflowWrap: 'anywhere',
                },
                '& table th': {
                  whiteSpace: 'normal',
                },
                '& table td': {
                  height: 'auto',
                },
                '& table .MuiFormControl-root, & table .MuiTextField-root, & table .MuiBox-root': {
                  width: '100%',
                  maxWidth: '100%',
                },
                /* Inset box styling for table inputs */
                '& table .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '10px',
                  alignItems: 'flex-start',
                  height: 'auto !important',
                  minHeight: 'unset !important',
                  width: '100%',
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    color: 'rgba(0, 0, 0, 0.55)',
                  },
                },
                '& table .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline': {
                  alignItems: 'flex-start',
                },
                '& table .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid #d6dee8 !important',
                },
                '& table .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.18) !important',
                },
                '& table .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.45)',
                },
                '& table .MuiInputBase-inputMultiline': {
                  padding: '8px 10px',
                  overflow: 'hidden',
                  resize: 'none',
                  boxSizing: 'border-box',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                },
                '& table .MuiInputBase-input': {
                  boxSizing: 'border-box',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                },
                '& table textarea.MuiInputBase-inputMultiline': {
                  overflow: 'hidden',
                },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {!documentsOnly && (
              <Box
                className="allow-when-locked"
                sx={{
                  mb: 3,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  backgroundColor: isFormDisabled ? '#f3f4f6' : '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.25 }}>
                    Protocol Intake Form
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Key Data Sheet for Oncology Clinical Study Protocol Design
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {PROTOCOL_INTAKE_NAV.map((section) => {
                    const isActive = activeProtocolSection === section.id;
                    return (
                      <Box
                        key={section.id}
                        component="button"
                        type="button"
                        onClick={() => scrollToProtocolSection(section.id)}
                        aria-current={isActive ? 'true' : undefined}
                        sx={{
                          px: 1.1,
                          py: 0.45,
                          borderRadius: 10,
                          border: isActive
                            ? (isFormDisabled ? '1px solid #9e9e9e' : '1px solid #1976d2')
                            : '1px solid #cbd5e1',
                          backgroundColor: isActive
                            ? (isFormDisabled ? '#9e9e9e' : '#1976d2')
                            : (isFormDisabled ? '#f5f5f5' : '#ffffff'),
                          color: isActive ? '#ffffff' : (isFormDisabled ? '#757575' : '#475569'),
                          fontSize: '0.73rem',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
                          '&:hover': {
                            backgroundColor: isActive
                              ? (isFormDisabled ? '#8e8e8e' : '#1565c0')
                              : '#f1f5f9',
                            borderColor: isActive
                              ? (isFormDisabled ? '#8e8e8e' : '#1565c0')
                              : '#94a3b8',
                          },
                          '&:focus-visible': {
                            outline: isFormDisabled ? '2px solid #9e9e9e' : '2px solid #1976d2',
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {section.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              )}
              {hideDocuments && !documentsOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Source documents are managed on the <strong>Source Documents</strong> tab (before Administrative Info).
                </Alert>
              )}
              {/* Source Documents — also shown on dedicated Source Documents tab */}
              {(!hideDocuments || documentsOnly) && (
              <Accordion defaultExpanded sx={{ mb: 1, border: '1px solid #dbe3eb', boxShadow: 'none', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Tooltip
                    title={getHoverInfo('upload_supporting_documents_title') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('upload_supporting_documents_title')}
                  >
                    <Typography component="div" variant="h6">
                      Source Documents
                    </Typography>
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    pt: 0,
                    px: documentsOnly ? 1 : 2,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'visible',
                  }}
                >
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Use the table below to manage your documents. Add rows as needed, select the document type from the dropdown, add comments, and upload files. Uploaded files will be listed with validation status.
                  </Typography>

              <FieldArray name="document_uploads.document_table">
                {({ push, remove, form }) => {
                  const documentTable = form.values.document_uploads?.document_table || [];
                  
                  return (
                    <>
                    <TableContainer
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        border: '1px solid #d6dee8',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        overflowY: 'visible',
                        backgroundColor: isFormDisabled ? '#f3f4f6' : '#ffffff',
                        boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.12)',
                      }}
                    >
                      <Table
                        size="small"
                        sx={{
                          width: '100%',
                          minWidth: 980,
                          tableLayout: 'fixed',
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                          '& thead th': {
                            borderBottom: '1px solid #d6dee8',
                            backgroundColor: '#f8fafc',
                          },
                          '& tbody td': {
                            borderTop: '1px solid #e7edf5',
                            verticalAlign: 'top',
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: '52px', whiteSpace: 'nowrap' }}>Sr No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Document Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Additional Details</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '24%' }}>Extracted Summary</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Used For (Auto-Populate Sections)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Upload</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '64px', textAlign: 'center', whiteSpace: 'nowrap' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documentTable.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="textSecondary">
                                  No documents added yet. Click "Add Row" to start.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {documentTable.map((row, index) => {
                            const documentType = row.documentType || '';
                            const config = getDocumentTypeConfig(documentType);
                            
                            return (
                              <TableRow
                                key={index}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: '#fafafa',
                                  },
                                }}
                              >
                                {/* Serial Number */}
                                <TableCell
                                  sx={{
                                    borderRight: '1px solid #cfd8dc',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    width: '52px',
                                  }}
                                >
                                  {index + 1}
                                </TableCell>
                                
                                {/* Document Name Dropdown */}
                                <TableCell sx={{ borderRight: '1px solid #cfd8dc', width: '16%' }}>
                                  {(() => {
                                    const selectedHoverKey = documentType
                                      ? DOCUMENT_TYPE_HOVER_KEY[documentType]
                                      : null;
                                    const selectedHoverText =
                                      selectedHoverKey && getHoverInfo(selectedHoverKey)
                                        ? getHoverInfo(selectedHoverKey)
                                        : '';
                                    return (
                                      <Tooltip
                                        title={selectedHoverText}
                                        arrow
                                        placement="top-start"
                                        enterDelay={450}
                                        enterNextDelay={450}
                                        disableHoverListener={!selectedHoverText}
                                      >
                                        <Box sx={{ width: '100%' }}>
                                          <Field
                                            as={TextField}
                                            name={`document_uploads.document_table.${index}.documentType`}
                                            select
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            disabled={isFormDisabled}
                                            onChange={(e) => {
                                              const newDocumentType = e.target.value;
                                              setFieldValue(
                                                `document_uploads.document_table.${index}.documentType`,
                                                newDocumentType
                                              );
                                              // Sync existing comments if any
                                              const currentComments =
                                                values.document_uploads?.comments?.[newDocumentType] || '';
                                              if (currentComments) {
                                                setFieldValue(
                                                  `document_uploads.document_table.${index}.additionalDetails`,
                                                  currentComments
                                                );
                                              }
                                            }}
                                          >
                                            {getAllDocumentTypes().map((option) => {
                                              const hoverKey = DOCUMENT_TYPE_HOVER_KEY[option.value];
                                              const hoverText = hoverKey ? getHoverInfo(hoverKey) : '';
                                              return (
                                                <MenuItem key={option.value} value={option.value}>
                                                  {hoverText ? (
                                                    <Tooltip
                                                      title={hoverText}
                                                      arrow
                                                      placement="right-start"
                                                      enterDelay={350}
                                                      enterNextDelay={350}
                                                      componentsProps={{
                                                        tooltip: { sx: { maxWidth: 420 } },
                                                      }}
                                                    >
                                                      <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                                        {option.label}
                                                      </Box>
                                                    </Tooltip>
                                                  ) : (
                                                    option.label
                                                  )}
                                                </MenuItem>
                                              );
                                            })}
                                          </Field>
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                
                                {/* Additional Details */}
                                <TableCell sx={{ borderRight: '1px solid #cfd8dc', width: '16%' }}>
                                  <Box
                                    sx={{
                                      border: '1px solid #d6dee8',
                                      borderRadius: 1,
                                      p: 1,
                                      backgroundColor: '#fbfdff',
                                      width: '100%',
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    <Field
                                      as={TextField}
                                      name={`document_uploads.document_table.${index}.additionalDetails`}
                                      placeholder="Add comments or notes..."
                                      fullWidth
                                      multiline
                                      minRows={2}
                                      maxRows={4}
                                      size="small"
                                      variant="outlined"
                                      disabled={isFormDisabled}
                                      sx={{ mb: 1 }}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setFieldValue(`document_uploads.document_table.${index}.additionalDetails`, value);
                                        // Sync to comments structure for backward compatibility
                                        if (documentType) {
                                          setFieldValue(`document_uploads.comments.${documentType}`, value);
                                        }
                                      }}
                                    />
                                    {documentType === 'study_design_outline' && (
                                      <Field
                                        as={TextField}
                                        name="study_design_outline_description"
                                        label="Description (if no document uploaded)"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        size="small"
                                        variant="outlined"
                                        disabled={isFormDisabled}
                                        sx={{ mt: 0.5 }}
                                      />
                                    )}
                                    {documentType === 'disease_background_documents' && (
                                      <Field
                                        as={TextField}
                                        name="disease_background_description"
                                        label="Description (if no document uploaded)"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        size="small"
                                        variant="outlined"
                                        disabled={isFormDisabled}
                                        sx={{ mt: 0.5 }}
                                      />
                                    )}
                                    {documentType === 'control_arm_documents' && (
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                        (Optional: Upload documents like labels, primary studies leading to approval of regimens, any studies to be used to benchmark SOC outcomes, etc.)
                                      </Typography>
                                    )}
                                    {documentType === 'disease_background_documents' && (
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                        (Optional: Upload or describe documents to be used to explain the disease background, epidemiology, unmet need, etc.)
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                
                                {/* Extracted Summary – generated on upload, independent of validation */}
                                <TableCell sx={{ borderRight: '1px solid #cfd8dc', width: '24%' }}>
                                  {(() => {
                                    const files =
                                      form.values.document_uploads?.uploaded_files?.[documentType] || [];

                                    const basesForFile = (f) =>
                                      [
                                        ...(f
                                          ? [f.azureBlobName, f.filename, f.originalname, f.name].filter(Boolean)
                                          : []),
                                      ];
                                    const sectionKeysForFile = (f) =>
                                      basesForFile(f).map((b) => `${b}_${documentType}`);

                                    if (files.length === 0) {
                                      return (
                                        <Typography variant="caption" color="textSecondary">
                                          Upload a file to see its summary
                                        </Typography>
                                      );
                                    }

                                    // Render a separate summary box per uploaded file (newest first)
                                    const filesNewestFirst = [...files].reverse();
                                    return (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {filesNewestFirst.map((f, revIdx) => {
                                          const originalIndex = files.length - 1 - revIdx;
                                          const fileLabel =
                                            f?.originalname || f?.name || f?.filename || f?.azureBlobName || `File ${originalIndex + 1}`;

                                          // Prefer Formik-stored summary; fall back to in-memory caches
                                          let summaryText =
                                            f?.contentSummary != null ? String(f.contentSummary) : '';
                                          if (!summaryText) {
                                            for (const sk of sectionKeysForFile(f)) {
                                              if (documentSummaries?.[sk]) {
                                                summaryText = String(documentSummaries[sk] || '');
                                                break;
                                              }
                                              const vSum = fileValidationStatus?.[sk]?.extractedSummary;
                                              if (vSum) {
                                                summaryText = String(vSum || '');
                                                break;
                                              }
                                            }
                                          }

                                          const emptyReason = f?.summaryError ? String(f.summaryError).trim() : '';
                                          const isSummarizing =
                                            Boolean(f?.summaryPending) ||
                                            sectionKeysForFile(f).some((sk) => Boolean(summarizingDocs?.[sk]));

                                          return (
                                            <Box
                                              key={`${f?.azureBlobName || f?.filename || fileLabel}-${originalIndex}`}
                                              sx={{
                                                border: '1px solid #e7edf5',
                                                borderRadius: 1,
                                                p: 1,
                                                backgroundColor: '#fbfdff',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                              }}
                                            >
                                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                {fileLabel}
                                              </Typography>

                                              {isSummarizing ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <CircularProgress size={16} />
                                                  <Typography variant="caption" color="textSecondary">
                                                    Generating summary…
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <TextField
                                                  fullWidth
                                                  multiline
                                                  minRows={6}
                                                  size="small"
                                                  variant="outlined"
                                                  value={summaryText}
                                                  onChange={(e) => {
                                                    const nextVal = e.target.value;
                                                    setFieldValue(
                                                      `document_uploads.uploaded_files.${documentType}.${originalIndex}.contentSummary`,
                                                      nextVal
                                                    );
                                                    setFieldValue(
                                                      `document_uploads.uploaded_files.${documentType}.${originalIndex}.summaryError`,
                                                      ''
                                                    );
                                                  }}
                                                  disabled={isFormDisabled}
                                                  placeholder={emptyReason || 'Summary will appear here after upload'}
                                                  sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-root': { alignItems: 'flex-start', width: '100%' },
                                                    '& .MuiInputBase-inputMultiline': {
                                                      whiteSpace: 'pre-wrap',
                                                      overflowY: 'auto',
                                                      height: 200,
                                                      resize: 'vertical',
                                                    },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    );

                                  })()}
                                </TableCell>

                                {/* Used For (Auto-Populate Sections) */}
                                <TableCell sx={{ borderRight: '1px solid #cfd8dc', width: '20%' }}>
                                  {(() => {
                                    const files =
                                      form.values.document_uploads?.uploaded_files?.[documentType] || [];

                                    if (files.length === 0) {
                                      return (
                                        <Typography variant="caption" color="textSecondary">
                                          Upload a file to map sections
                                        </Typography>
                                      );
                                    }

                                    return (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {[...files].reverse().map((f, revIdx) => {
                                          const originalIndex = files.length - 1 - revIdx;
                                          const fileLabel =
                                            f?.originalname || f?.name || f?.filename || f?.azureBlobName || `File ${originalIndex + 1}`;

                                          const pending = Boolean(f?.intakeSectionsPending);
                                          const sections = Array.isArray(f?.intakeSectionsForAutoPopulate)
                                            ? f.intakeSectionsForAutoPopulate
                                            : [];
                                          const errText = f?.intakeSectionsError
                                            ? String(f.intakeSectionsError).trim()
                                            : '';

                                          return (
                                            <Box
                                              key={`${f?.azureBlobName || f?.filename || fileLabel}-${originalIndex}`}
                                              sx={{
                                                border: '1px solid #e7edf5',
                                                borderRadius: 1,
                                                p: 1,
                                                backgroundColor: '#fbfdff',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                              }}
                                            >
                                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                {fileLabel}
                                              </Typography>

                                              {pending ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <CircularProgress size={16} />
                                                  <Typography variant="caption" color="textSecondary">
                                                    Mapping sections…
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <TextField
                                                  fullWidth
                                                  multiline
                                                  minRows={6}
                                                  size="small"
                                                  variant="outlined"
                                                  value={sections.join('\n')}
                                                  onChange={(e) => {
                                                    const lines = String(e.target.value || '')
                                                      .split('\n')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean);
                                                    setFieldValue(
                                                      `document_uploads.uploaded_files.${documentType}.${originalIndex}.intakeSectionsForAutoPopulate`,
                                                      lines
                                                    );
                                                    setFieldValue(
                                                      `document_uploads.uploaded_files.${documentType}.${originalIndex}.intakeSectionsError`,
                                                      ''
                                                    );
                                                  }}
                                                  disabled={isFormDisabled}
                                                  placeholder={errText || 'Section titles will appear here after summary'}
                                                  sx={{
                                                    width: '100%',
                                                    '& textarea': { whiteSpace: 'pre-wrap' },
                                                    '& .MuiInputBase-root': { alignItems: 'flex-start', width: '100%' },
                                                    '& .MuiInputBase-inputMultiline': {
                                                      height: 200,
                                                      overflowY: 'auto',
                                                      resize: 'vertical',
                                                    },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                
                                {/* Upload */}
                                <TableCell sx={{ borderRight: '1px solid #cfd8dc', width: '16%' }}>
                                  <Box
                                    sx={{
                                      border: '1px dashed #b6c3d1',
                                      borderRadius: 1,
                                      p: 1,
                                      backgroundColor: '#f8fafc',
                                      width: '100%',
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    {documentType && (
                                      <>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          component="label"
                                          startIcon={<CloudUploadIcon fontSize="small" />}
                                          disabled={isFormDisabled}
                                            sx={{ textTransform: 'none' }}
                                        >
                                          {config.multiple ? 'Upload Files' : 'Upload File'}
                                          <input
                                            type="file"
                                            hidden
                                            multiple={config.multiple}
                                            accept={config.accept}
                                            onChange={(e) => {
                                              const files = Array.from(e.target.files || []);
                                              if (!files.length) return;
                                              if (config.multiple) {
                                                files.forEach(file =>
                                                  handleFileUpload(file, documentType, setFieldValue, values)
                                                );
                                              } else {
                                                handleFileUpload(files[0], documentType, setFieldValue, values);
                                              }
                                              // Reset input
                                              e.target.value = '';
                                            }}
                                          />
                                        </Button>
                                          {/* View PDF Button - shows one or multiple PDF files for this document type */}
                                          {(() => {
                                            const files = values.document_uploads?.uploaded_files?.[documentType] || [];
                                            const pdfFiles = files.filter(file => {
                                              const filename = file.azureBlobName || file.filename || file.originalname || file.name;
                                              return filename && filename.toLowerCase().endsWith('.pdf');
                                            });
                                            
                                            if (pdfFiles.length > 0) {
                                              return (
                                                <IconButton
                                                  className="allow-when-locked"
                                                  size="small"
                                                  onClick={() => {
                                                    // Build tabs for all PDF files in this document type
                                                    const tabs = pdfFiles.map((file, index) => {
                                                      const fname = file.azureBlobName || file.filename || file.originalname || file.name;
                                                      const url = API_ENDPOINTS.documents.download(fname);
                                                      const displayName = file.originalname || file.name || fname || `Document ${index + 1}`;
                                                      return { url, name: displayName };
                                                    });
                                                    
                                                    setPdfTabs(tabs);
                                                    setActivePdfIndex(0);
                                                    
                                                    // Initialize viewer with first PDF
                                                    setPdfUrl(tabs[0].url);
                                                    setPdfFileName(tabs[0].name);
                                                    setPageNumber(1);
                                                    setPdfError(null);
                                                    setPdfViewerOpen(true);
                                                  }}
                                                  color={isFormDisabled ? 'default' : 'primary'}
                                                  title="View PDF"
                                                >
                                                  <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </Box>
                                        {renderFileListWithValidation(
                                          values.document_uploads?.uploaded_files?.[documentType] || [],
                                          documentType,
                                          setFieldValue,
                                          isFormDisabled
                                        )}
                                      </>
                                    )}
                                    {!documentType && (
                                      <Typography variant="caption" color="textSecondary">
                                        Select document type first
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                
                                {/* Delete Row Action */}
                                <TableCell sx={{ textAlign: 'center' }}>
                                  <IconButton
                                    size="small"
                                     onClick={() => {
                                      const docTypeToRemove = documentType;
                                      const remainingRows = documentTable.filter((_, i) => i !== index);
                                      remove(index);
                                      if (docTypeToRemove) {
                                        const stillReferenced = remainingRows.some(
                                          (r) => r.documentType === docTypeToRemove
                                        );
                                        if (!stillReferenced) {
                                          setFieldValue(`document_uploads.uploaded_files.${docTypeToRemove}`, []);
                                          clearValidationStatusForDocumentType(docTypeToRemove);
                                        }
                                      }
                                    }}
                                    disabled={isFormDisabled}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* Add Row Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => push({ documentType: '', additionalDetails: '' })}
                        disabled={isFormDisabled}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Row
                      </Button>
                    </Box>
                    </>
                  );
                }}
              </FieldArray>
              
                  {/* Helper text for Study Design Outline */}
                  <Box mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      <strong>Note:</strong> Study Design Outline Document can be a synopsis or a study design PPT, any format is welcome. If not available, provide a brief text or audio description.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
              )}

              {(!hideDocuments || documentsOnly) && (
              <>
              {documentsOnly && !isFormDisabled && (
                <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Source Documents workflow
                  </Typography>
                  <Typography variant="body2" component="div">
                    1. Upload documents in the table above<br />
                    2. Click <strong>Validate Files</strong> once (results are reused)<br />
                    3. <strong>Auto-populate Administrative Info &amp; Trial Schema</strong> below, then save on Administrative Info tab and <strong>Save Trial Schema</strong><br />
                    4. Go to <strong>Protocol Intake Form</strong> → section B to parse intake; Arguments (D) auto-generate; SOA (E) after submit
                  </Typography>
                </Alert>
              )}

              {/* File Validation Status Summary */}
              {!isFormDisabled && validationCompleted && Object.keys(fileValidationStatus).length > 0 && (
                <Alert 
                  severity={allFilesValid ? "success" : "error"} 
                  sx={{ mb: 2, mt: 3 }}
                  icon={allFilesValid ? <CheckCircleIcon /> : <CancelIcon />}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {allFilesValid ? '✓ All Files Validated Successfully' : 'File Validation Issues Detected'}
                  </Typography>
                  <Typography variant="body2">
                    {allFilesValid 
                      ? `All uploaded files passed readability, placement, content, and consistency checks.`
                      : `One or more files failed validation. See green ✓ or red ✗ marks next to each file in the upload column above.`
                    }
                  </Typography>
                </Alert>
              )}

              {/* File Validation and Processing Buttons */}
              {!isFormDisabled && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  color={validationCompleted ? (allFilesValid ? "success" : "error") : "primary"}
                  onClick={() => validateUploadedFiles(values)}
                  // Once every file is valid, lock the button — clicking it again would
                  // just re-run validation from scratch for no reason (files haven't
                  // changed). Re-enabled automatically if a file is added/removed, since
                  // that resets validationCompleted/allFilesValid upstream. The "failed"
                  // state stays clickable — that one is explicitly "Re-validate".
                  disabled={isValidatingFiles || !hasAnyUploadedFiles(values) || (validationCompleted && allFilesValid)}
                  startIcon={isValidatingFiles ? <CircularProgress size={20} /> : validationCompleted ? (allFilesValid ? <CheckCircleIcon /> : <CancelIcon />) : undefined}
                >
                  {isValidatingFiles ? 'Validating Files...' :
                   validationCompleted && allFilesValid ? '✓ All Files Valid' :
                   validationCompleted && !allFilesValid ? '✗ Validation Failed — Re-validate' :
                   'Validate Files'}
                </Button>
              </Box>
              )}
              {isValidatingFiles && pipelineStepLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                  {pipelineStepLabel}
                </Typography>
              )}
              {isValidatingFiles && !pipelineStepLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                  Validating documents — large PDFs can take 2–5 minutes. Please wait; do not click Validate again.
                </Typography>
              )}
              
              {documentsOnly && autoPopulateSucceeded && (
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ✓ Auto-populate complete
                  </Typography>
                  <Typography variant="body2">
                    Administrative Info, Study Registration, and Trial Schema were filled from your documents.
                    Open the <strong>Administrative Info</strong> tab to review and save, then
                    <strong> Protocol Intake Form</strong> → section B to review trial schema before parsing.
                  </Typography>
                </Alert>
              )}

              {pipelineSteps.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Document processing pipeline
                    {pipelineStepLabel ? ` — ${pipelineStepLabel}` : ''}
                  </Typography>
                  {pipelineJobProgress > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {pipelineJobProgress}% complete
                    </Typography>
                  )}
                  {pipelineSteps.map((step) => (
                    <Typography key={step.step} variant="body2" sx={{ mb: 0.25 }}>
                      {step.status === 'complete' ? '✓' : step.status === 'failed' ? '✗' : '…'}{' '}
                      {step.label || step.step}
                    </Typography>
                  ))}
                </Alert>
              )}

              {!isFormDisabled && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  {!allFilesValid && !validationCompleted && (
                    <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1 }}>
                      💡 Click "Validate Files" first to check if all documents are readable
                    </Typography>
                  )}
                  {validationCompleted && !allFilesValid && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                      ⛔ Some files are unreadable. Please fix them before proceeding.
                    </Typography>
                  )}
                  {validationCompleted && allFilesValid && (
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
                      ✓ All files validated and ready for processing!
                    </Typography>
                  )}
                  {!hasAnyUploadedFiles(values) && (
                    <Typography variant="caption" color="error">
                      Please upload at least one source document
                    </Typography>
                  )}
                  {hasAnyUploadedFiles(values) && values.document_uploads?.uploaded_files.study_design_outline.length === 0 && (
                    <Typography variant="caption" color="error">
                      Please upload a Study Design Outline document
                    </Typography>
                  )}
                  {hasAnyUploadedFiles(values) &&
                   values.document_uploads?.uploaded_files.study_design_outline.length > 0 &&
                   !documentsOnly &&
                   (!hasConfiguredTrialPartCount(values.study_overview) || 
                    !values.study_overview?.study_parts_cohorts) && (
                    <Typography variant="caption" color="error">
                      Complete Trial Schema in section B before parsing protocol intake
                    </Typography>
                  )}
                  {documentsOnly && validationCompleted && allFilesValid && (
                    <Typography variant="caption" color="success.main">
                      ✓ Validation cached — use Auto-populate below, then review Administrative Info and Protocol Intake Form tabs
                    </Typography>
                  )}
                </Box>
              )}

              {documentsOnly && !isFormDisabled && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      handleAutoPopulateAdminAndTrialSchema(
                        setFieldValue,
                        values,
                        setValues,
                        getCurrentValues
                      )
                    }
                    disabled={
                      autoPopulatingAdminTrialSchema ||
                      loading ||
                      autoPopulateSucceeded ||
                      !hasAnyUploadedFiles(values) ||
                      (values.document_uploads?.uploaded_files?.study_design_outline || []).length === 0 ||
                      !(
                        (validationCompleted && allFilesValid) ||
                        isDocumentValidationCachePassing(
                          loadDocumentValidationState(
                            currentProject?._id,
                            values.document_uploads,
                            buildValidationFingerprint(values)
                          )
                        )
                      )
                    }
                    startIcon={
                      autoPopulatingAdminTrialSchema ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                  >
                    {autoPopulatingAdminTrialSchema
                      ? pipelineStepLabel || 'Auto-populating…'
                      : autoPopulateSucceeded
                        ? 'Auto-populate complete'
                        : 'Auto-populate Administrative Info & Trial Schema'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 100%', textAlign: 'right' }}>
                    Populates Administrative Info and Trial Schema on their respective tabs. Review and save there before parsing on Protocol Intake Form.
                  </Typography>
                </Box>
              )}
              </>
              )}

              {!documentsOnly && (
              <>
              {/* A. Trial Identifier — PDF section order */}
              <Box
                ref={(el) => {
                  protocolSectionRefs.current['trial-identifier'] = el;
                }}
                sx={{ scrollMarginTop: PROTOCOL_SECTION_SCROLL_MARGIN }}
              >
              <Tooltip
                title="This section captures requirements for study identification details, including study title, protocol number, version information, sponsor details, and other key identifiers used to uniquely reference the study."
                arrow
                placement="top-start"
                enterDelay={450}
                enterNextDelay={450}
              >
                <Typography
                  component="div"
                  sx={M11_SECTION_BANNER_SX}
                >
                  STUDY IDENTIFICATION
                </Typography>
              </Tooltip>
              <Accordion
                expanded={protocolSectionExpanded.trialIdentifier}
                onChange={(_, expanded) =>
                  setProtocolSectionExpanded((prev) => ({ ...prev, trialIdentifier: expanded }))
                }
                sx={{ mb: 1, border: '1px solid #dbe3eb', boxShadow: 'none' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    A. Trial Identifier
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {PROJECT_IDENTITY_SOURCE_HINT}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="A1. Sponsor Protocol Identifier"
                      value={resolveProjectSponsorProtocolId(currentProject)}
                      InputProps={{ readOnly: true }}
                      helperText="From trial creation"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Tooltip
                        title={getHoverInfo('study_identification.preferred_short_title') || ''}
                        arrow
                        placement="top-start"
                        enterDelay={450}
                        enterNextDelay={450}
                        disableHoverListener={!getHoverInfo('study_identification.preferred_short_title')}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          A2. Short Title
                        </Typography>
                      </Tooltip>
                      {!isFormDisabled && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Boolean(values.study_identification?.document_short_title?.trim()) &&
                            values.study_identification?.preferred_short_title !==
                              values.study_identification?.document_short_title && (
                              <Button
                                type="button"
                                variant="text"
                                size="small"
                                onClick={() => handleRevertShortTitle(setFieldValue, values)}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                              >
                                Revert to Original Title
                              </Button>
                            )}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() => handleGenerateOfficialTitle(setFieldValue, values)}
                            disabled={generatingOfficialTitle || loading}
                            startIcon={generatingOfficialTitle ? <CircularProgress size={16} /> : null}
                            sx={{ textTransform: 'none' }}
                          >
                            {generatingOfficialTitle ? 'Generating…' : 'Generate from Intake'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      name="study_identification.preferred_short_title"
                      value={values.study_identification?.preferred_short_title || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isFormDisabled}
                      placeholder="Enter preferred short title for the study"
                      helperText={
                        values.study_identification?.document_short_title?.trim()
                          ? 'Populated from source documents when found during Protocol Intake parse. Edit, regenerate, or revert as needed.'
                          : 'Filled from documents when you parse Protocol Intake, or use Generate from Intake.'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        A3. Official Title
                      </Typography>
                      {!isFormDisabled && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Boolean(values.study_identification?.document_official_title?.trim()) &&
                            values.study_identification?.official_title !==
                              values.study_identification?.document_official_title && (
                              <Button
                                type="button"
                                variant="text"
                                size="small"
                                onClick={() => handleRevertOfficialTitle(setFieldValue, values)}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                              >
                                Revert to Original Title
                              </Button>
                            )}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() => handleGenerateOfficialTitle(setFieldValue, values)}
                            disabled={generatingOfficialTitle || loading}
                            startIcon={generatingOfficialTitle ? <CircularProgress size={16} /> : null}
                            sx={{ textTransform: 'none' }}
                          >
                            {generatingOfficialTitle ? 'Generating…' : 'Generate from Intake'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      name="study_identification.official_title"
                      value={values.study_identification?.official_title || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isFormDisabled}
                      multiline
                      minRows={2}
                      placeholder="Full scientific / protocol title"
                      helperText={
                        values.study_identification?.official_title?.trim()
                          ? 'Populated from source documents when found during Protocol Intake parse. Edit, regenerate, or revert as needed.'
                          : 'Filled from documents when you parse Protocol Intake, or use Generate from Intake.'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="A4. Trial Acronym"
                      value={resolveProjectTrialAcronym(currentProject)}
                      InputProps={{ readOnly: true }}
                      helperText="From trial creation (optional)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="A5. Sponsor's Investigational Product Code(s)"
                      value={initialData?.sponsor_investigational_product_codes || ''}
                      InputProps={{ readOnly: true }}
                      helperText="Set in Administrative Info"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="A6. Non-proprietary Name(s)"
                      value={initialData?.investigational_product_names || ''}
                      InputProps={{ readOnly: true }}
                      helperText="Set in Administrative Info"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="study_identification.date_of_project_creation"
                      label={
                        <Tooltip
                          title={
                            getNonEmptyTextOrFallback(
                              values.ui_hover_texts?.study_identification?.date_of_project_creation,
                              DEFAULT_DATE_OF_PROJECT_CREATION_HOVER
                            )
                          }
                          arrow
                          placement="top-start"
                          enterDelay={450}
                          enterNextDelay={450}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '& .hover-help-edit': { opacity: 0, transition: 'opacity 120ms ease-in-out' },
                              '&:hover .hover-help-edit': {
                                opacity: isFormDisabled || !isAdminUser ? 0 : 1,
                              },
                            }}
                          >
                            <span>A7. Date of Protocol Creation (System Generated)</span>
                            {isAdminUser && (
                              <IconButton
                                className="hover-help-edit"
                                size="small"
                                tabIndex={-1}
                                aria-label="Edit hover help text"
                                disabled={isFormDisabled}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setHoverHelpDraft(
                                    getNonEmptyTextOrFallback(
                                      values.ui_hover_texts?.study_identification?.date_of_project_creation,
                                      DEFAULT_DATE_OF_PROJECT_CREATION_HOVER
                                    )
                                  );
                                  setHoverHelpDialogOpen(true);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            )}
                          </Box>
                        </Tooltip>
                      }
                      type="date"
                      value={values.study_identification?.date_of_project_creation || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={true} // Always disabled as it's system generated
                      helperText="Automatically generated when project is created"
                    />
                  </Grid>
                  <Dialog
                    open={hoverHelpDialogOpen}
                    onClose={() => setHoverHelpDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle>Edit hover help text</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ mb: 1 }}>
                        This text is shown when hovering the “Date of Project Creation” label and is saved with this
                        clinical intake record.
                      </DialogContentText>
                      <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={10}
                        value={hoverHelpDraft}
                        onChange={(e) => setHoverHelpDraft(e.target.value)}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setHoverHelpDialogOpen(false)}>Cancel</Button>
                      <Button
                        variant="contained"
                        disabled={isFormDisabled || !isAdminUser}
                        onClick={() => {
                          if (!isAdminUser) return;
                          setFieldValue(
                            'ui_hover_texts.study_identification.date_of_project_creation',
                            hoverHelpDraft.trim()
                          );
                          setHoverHelpDialogOpen(false);
                        }}
                      >
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="study_identification.comments"
                      label="Additional Notes"
                      value={values.study_identification?.comments || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isFormDisabled}
                      placeholder="Enter any additional notes for this section"
                    />
                  </Grid>
                </Grid>
                </AccordionDetails>
              </Accordion>
              </Box>

              <Box
                ref={(el) => {
                  protocolSectionRefs.current['trial-schema'] = el;
                }}
                sx={{ scrollMarginTop: PROTOCOL_SECTION_SCROLL_MARGIN }}
              >
              <Accordion
                expanded={protocolSectionExpanded.trialSchema}
                onChange={(_, expanded) =>
                  setProtocolSectionExpanded((prev) => ({ ...prev, trialSchema: expanded }))
                }
                sx={{ mb: 1, mt: 0, border: '1px solid #dbe3eb', boxShadow: 'none' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Tooltip
                    title={getHoverInfo('trial_schema_configuration_title') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('trial_schema_configuration_title')}
                  >
                    <Typography component="div" variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                      B. Trial Schema Configuration
                    </Typography>
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Configure trial type, design framework, parts, cohorts, and country-level details. Values
                    auto-populate from Source Documents — review, edit, and save before parsing protocol intake.
                  </Typography>
                  <TrialSchemaConfigurationSection
                    values={values}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    isFormDisabled={isFormDisabled}
                    getHoverInfo={getHoverInfo}
                    onLegacySync={(synced) => {
                      const rebuiltStudyParts = generateDynamicStudyPartsTable(
                        synced.study_overview?.number_of_study_parts_new ||
                          values.study_overview?.number_of_study_parts_new,
                        synced.study_overview?.study_parts_cohorts ||
                          values.study_overview?.study_parts_cohorts,
                        {},
                        values.study_overview?.study_parts || {}
                      );
                      setFieldValue('study_overview.study_parts', rebuiltStudyParts);
                      const rebuiltTreatmentTable = generateDynamicTreatmentTable(
                        synced.study_treatments?.num_regimens ||
                          values.study_treatments?.num_regimens,
                        synced.study_treatments?.regimen_arms ||
                          values.study_treatments?.regimen_arms,
                        {},
                        values.study_treatments?.treatment_table || {},
                        rebuiltStudyParts
                      );
                      setFieldValue('study_treatments.treatment_table', rebuiltTreatmentTable);
                    }}
                  />
                  {!isFormDisabled && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => saveTrialSchemaFieldsToDB(values)}
                        disabled={loading || autoPopulatingAdminTrialSchema || !dirty}
                      >
                        Save Trial Schema
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={() =>
                          handleParseClinicalIntakeFromDocuments(
                            setFieldValue,
                            values,
                            setValues,
                            getCurrentValues
                          )
                        }
                        disabled={
                          loading ||
                          autoPopulatingAdminTrialSchema ||
                          generatingBackgroundInfo ||
                          !hasAnyUploadedFiles(values) ||
                          (values.document_uploads?.uploaded_files?.study_design_outline || []).length === 0 ||
                          !hasConfiguredTrialPartCount(values.study_overview) ||
                          !values.study_overview?.study_parts_cohorts ||
                          intakeParseSucceeded ||
                          !(
                            (validationCompleted && allFilesValid) ||
                            isDocumentValidationCachePassing(
                              loadDocumentValidationState(
                                currentProject?._id,
                                values.document_uploads,
                                buildValidationFingerprint(values)
                              )
                            )
                          )
                        }
                        startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      >
                        {loading
                          ? pipelineStepLabel || 'Parsing protocol intake…'
                          : intakeParseSucceeded
                            ? 'Protocol Intake Parsed'
                            : 'Parse Protocol Intake from Documents'}
                      </Button>
                    </Box>
                  )}
                  {!isFormDisabled && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'right' }}>
                      Auto-populate from Source Documents first. Arguments (D) generate automatically after a successful parse; SOA (E) after submit.
                    </Typography>
                  )}
              </AccordionDetails>
              </Accordion>
              </Box>

              <Box
                ref={(el) => {
                  protocolSectionRefs.current['trial-design'] = el;
                }}
                sx={{ scrollMarginTop: PROTOCOL_SECTION_SCROLL_MARGIN }}
              >
              <Divider sx={{ my: 2 }} />

              {/* Study Overview Section */}
              <Accordion
                expanded={protocolSectionExpanded.trialDesign}
                onChange={(_, expanded) =>
                  setProtocolSectionExpanded((prev) => ({ ...prev, trialDesign: expanded }))
                }
                sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    C. Trial Design
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  M11 Section 3 Objectives &amp; Estimands, Section 4 Trial Design, and Section 5 Trial Population — columns grouped by study part.
                </Typography>
                {/* Dynamic Study Parts and Cohorts Table */}
                <Box sx={{ overflowX: 'auto', mb: 3 }}>
                  {renderDynamicStudyPartsTable(
                    values.study_overview?.study_parts,
                    isFormDisabled,
                    values.study_overview?.study_parts_cohort_names || {},
                    values.study_overview?.study_parts_part_names || {},
                    values.study_overview?.number_of_study_parts_new
                  )}
                </Box>

                <PatientLibrarySection54Panel
                  values={values}
                  setFieldValue={setFieldValue}
                  isFormDisabled={isFormDisabled}
                />

                <TemplateLanguageFieldsTable
                  rows={POPULATION_TEMPLATE_LANGUAGE_ROWS}
                  title="Template Language — Sections 5.4.1 & 5.4.2"
                  showControls={false}
                  hideTemplateColumn
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  isFormDisabled={isFormDisabled}
                  templateLoading={templateLoading}
                  templateData={templateData}
                  templateSelections={templateSelections}
                  templateCheckboxDisabled={templateCheckboxDisabled}
                  templateLlmLoadingField={templateLlmLoadingField}
                  handleTemplateCheckboxChange={handleTemplateCheckboxChange}
                  setValues={setValues}
                  setFieldValue={setFieldValue}
                />

                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={6}
                      name="discontinuation_rules.screen_failure_rescreening_rules"
                      label={
                        <Tooltip
                          title={getHoverInfo('screen_failure_rescreening_rules') || ''}
                          arrow
                          placement="top-start"
                          enterDelay={450}
                          enterNextDelay={450}
                          disableHoverListener={!getHoverInfo('screen_failure_rescreening_rules')}
                        >
                          <span>Screen Failure and Rescreening Rules (M11 Section 5.6)</span>
                        </Tooltip>
                      }
                      value={values.discontinuation_rules?.screen_failure_rescreening_rules || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isFormDisabled}
                    />
                  </Grid>
                </Grid>
                </AccordionDetails>
              </Accordion>

            {/* Study Treatments Section - Reworked */}
            <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                  M11 Section 6 — Trial Intervention
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                {/* Dynamic Treatment Table */}
                {(() => {
                  const shouldShowTable = values.study_treatments?.num_regimens > 0;
                  console.log('Treatment Table rendering - shouldShowTable:', shouldShowTable);
                  console.log('Treatment Table rendering - num_regimens:', values.study_treatments?.num_regimens);
                  console.log('Treatment Table rendering - treatment_table:', values.study_treatments?.treatment_table);
                  console.log('Treatment Table rendering - isFormDisabled:', isFormDisabled);
                  
                  return shouldShowTable && (
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Treatment Table
                      </Typography>
                      <Box sx={{ overflowX: 'auto', mb: 3 }}>
                        {renderDynamicTreatmentTable(
                          values.study_treatments?.treatment_table,
                          isFormDisabled,
                          values,
                          {
                            excludeKeys: [
                              'adverse_events_title',
                              'adverse_events_special_interest',
                              'disease_related_events_outcomes',
                            ],
                          }
                        )}
                      </Box>
                    </Grid>
                  );
                })()}

              </Grid>
              </AccordionDetails>
            </Accordion>

              <Divider sx={{ my: 2 }} />

              {/* Discontinuation Rules Section */}
              <Tooltip
                title={getHoverInfo('discontinuation_withdrawal_title') || ''}
                arrow
                placement="top-start"
                enterDelay={450}
                enterNextDelay={450}
                disableHoverListener={!getHoverInfo('discontinuation_withdrawal_title')}
              >
                {/* <Typography
                  component="div"
                  sx={M11_SECTION_BANNER_SX}
                >
                  DISCONTINUATION OF TRIAL INTERVENTION AND PARTICIPANT WITHDRAWAL
                </Typography> */}
              </Tooltip>
              <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    M11 Section 7 — Participant Discontinuation &amp; Withdrawal
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  M11 Section 7 — template language for discontinuation and withdrawal (PDF order: 7.1.1 → 7.1.2 → 7.1.3 → 7.2 → 7.3 → Additional Notes).
                </Typography>

                <PatientLibrarySection7111Panel
                  values={values}
                  setFieldValue={setFieldValue}
                  isFormDisabled={isFormDisabled}
                />

                <TemporaryDiscontinuationSection712Panel
                  values={values}
                  setFieldValue={setFieldValue}
                  isFormDisabled={isFormDisabled}
                />

                <RechallengeSection713Panel
                  values={values}
                  setFieldValue={setFieldValue}
                  isFormDisabled={isFormDisabled}
                />

                <TemplateLanguageFieldsTable
                  rows={DISCONTINUATION_TEMPLATE_LANGUAGE_ROWS}
                  valuesSection="discontinuation_rules"
                  showControls
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  isFormDisabled={isFormDisabled}
                  templateLoading={templateLoading}
                  templateData={templateData}
                  templateSelections={templateSelections}
                  templateCheckboxDisabled={templateCheckboxDisabled}
                  templateLlmLoadingField={templateLlmLoadingField}
                  handleTemplateCheckboxChange={handleTemplateCheckboxChange}
                  setValues={setValues}
                  setFieldValue={setFieldValue}
                  onClearAll={() => {
                    const confirmed = window.confirm(
                      'This will clear ALL template content from Section 7 fields. Continue?'
                    );
                    if (!confirmed) return;
                    applyTemplateBulk(
                      DISCONTINUATION_TEMPLATE_FIELD_KEYS,
                      'clear',
                      values,
                      setValues,
                      'discontinuation_rules'
                    );
                  }}
                />

                </AccordionDetails>
              </Accordion>
              <Divider sx={{ my: 2 }} />
              {/* Study Assessments and Procedures Section */}
              <Tooltip
                title={getHoverInfo('study_assessments_title') || ''}
                arrow
                placement="top-start"
                enterDelay={450}
                enterNextDelay={450}
                disableHoverListener={!getHoverInfo('study_assessments_title')}
              >
                {/* <Typography
                  component="div"
                  sx={M11_SECTION_BANNER_SX}
                >
                  STUDY ASSESSMENTS
                </Typography> */}
              </Tooltip>
              <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    M11 Section 8 — Study Assessments &amp; Procedures
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                
                {/* Assessments Table */}
                <Box
                  sx={{
                    overflowX: 'auto',
                    mb: 3,
                    '& table.assessments-table td': {
                      height: 'auto !important',
                    },
                    '& table.assessments-table td .MuiFormControl-root .MuiOutlinedInput-root.MuiInputBase-root.MuiInputBase-multiline': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                      alignItems: 'flex-start',
                    },
                    '& table.assessments-table td .MuiFormControl-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                      height: 'auto !important',
                      minHeight: 'unset !important',
                    },
                    '& table.assessments-table td .MuiFormControl-root textarea.MuiInputBase-input.MuiInputBase-inputMultiline': {
                      overflow: 'hidden',
                      resize: 'none',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    },
                  }}
                >
                  {renderStudyAssessmentsTable(
                    values.study_overview?.study_parts,
                    isFormDisabled,
                    values.study_overview?.study_parts_cohort_names || {},
                    values.study_overview?.study_parts_part_names || {},
                    values.study_overview?.number_of_study_parts_new
                  )}
                </Box>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />

              <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {/* <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    M11 Section 9 — Adverse Events
                  </Typography> */}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Standard M11 template text is available for each row. Check the <strong>Template</strong>{' '}
                    box to paste TransCelerate base language, then tailor it with AI using your clinical intake.
                  </Alert>
                  <CptColorLegend />
                  <TemplateLanguageFieldsTable
                    rows={ADVERSE_EVENTS_TEMPLATE_LANGUAGE_ROWS}
                    title="Adverse Events — Template Language"
                    showControls
                    cptColoredPreview
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    isFormDisabled={isFormDisabled}
                    templateLoading={templateLoading}
                    templateData={templateData}
                    templateSelections={templateSelections}
                    templateCheckboxDisabled={templateCheckboxDisabled}
                    templateLlmLoadingField={templateLlmLoadingField}
                    handleTemplateCheckboxChange={handleTemplateCheckboxChange}
                    setValues={setValues}
                    setFieldValue={setFieldValue}
                    onClearAll={() => {
                      const confirmed = window.confirm(
                        'This will clear ALL template content from these Adverse Events template fields. Continue?'
                      );
                      if (!confirmed) return;
                      applyTemplateBulk(
                        ADVERSE_EVENTS_TEMPLATE_FIELD_KEYS,
                        'clear',
                        values,
                        setValues
                      );
                    }}
                  />

                  {values.study_treatments?.num_regimens > 0 && (
                    <Box sx={{ overflowX: 'auto', mb: 2, mt: 2 }}>
                      {renderDynamicTreatmentTable(
                        values.study_treatments?.treatment_table,
                        isFormDisabled,
                        values,
                        {
                          includeOnlyKeys: [
                            'adverse_events_special_interest',
                            'disease_related_events_outcomes',
                          ],
                          hideColumnHeaders: true,
                        }
                      )}
                    </Box>
                  )}

                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />

              {/* Statistical Considerations Section (visual only; rendered from study_parts fields) */}
              <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    M11 Section 10 — Statistical Considerations
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ overflowX: 'auto', mb: 1 }}>
                    {renderStatisticalConsiderationsFromStudyParts(
                      values.study_overview?.study_parts,
                      isFormDisabled,
                      values.study_overview?.study_parts_cohort_names || {},
                      values.study_overview?.study_parts_part_names || {},
                      values.study_overview?.number_of_study_parts_new
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />

              {/* Regulatory and Ethical Requirements Section */}
              <Tooltip
                title={getHoverInfo('regulatory_ethical_requirements_title') || ''}
                arrow
                placement="top-start"
                enterDelay={450}
                enterNextDelay={450}
                disableHoverListener={!getHoverInfo('regulatory_ethical_requirements_title')}
              >
                {/* <Typography
                  component="div"
                  sx={M11_SECTION_BANNER_SX}
                >
                  REGULATORY AND ETHICAL REQUIREMENTS AND TRIAL OVERSIGHT
                </Typography> */}
              </Tooltip>
              <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={M11_ACCORDION_TITLE_SX}>
                    M11 Section11 — Regulatory, Ethical Considerations &amp; Trial Oversight
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                
                {/* Template Controls */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Template Language Options
                    {templateLoading && <CircularProgress size={16} sx={{ ml: 2 }} />}
                  </Typography>
                  
                  {/* Template Status */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Template Status: 
                      {templateLoading ? ' Loading...' : 
                       templateData ? ` Loaded (${Object.keys(templateData).length} fields available)` :
                       ' No templates available'}
                    </Typography>
                    {!templateData && !templateLoading && (
                      <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                        - Checkboxes will be disabled until templates are loaded
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleClearAllTemplates(setValues, values)}
                      disabled={isFormDisabled || templateLoading}
                      sx={{ minWidth: '110px' }}
                    >
                      Clear All Templates
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Check a box once to generate tailored template language with AI. Click an
                      already-checked box again to regenerate.{' '}
                      <strong>Shift+click</strong> a checked box to clear it.
                      <strong> Warning:</strong> Checking a template checkbox will replace any existing content in that field.
                    </Typography>
                  </Box>
                </Box>
                
                <CptColorLegend />
                {/* Regulatory Requirements Table */}
                <Box sx={{ overflowX: 'auto', mb: 3 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
                    <colgroup>
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '55%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          Regulatory and Ethical Requirements
                        </th>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }}>
                          Template
                        </th>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* 11.1 Regulatory and Ethical Considerations */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('regulatory_ethical_considerations') ? (
                            <Tooltip title={getHoverInfo('regulatory_ethical_considerations')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.1 Regulatory and Ethical Considerations
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.1 Regulatory and Ethical Considerations
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('regulatory_ethical_considerations')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.regulatory_ethical_considerations"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe regulatory and ethical considerations for the trial"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.2 Trial Oversight - Title Only */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef',
                          fontWeight: 'bold',
                          verticalAlign: 'top',
                          fontSize: '14px'
                        }}>
                          {getHoverInfo('trial_oversight') ? (
                            <Tooltip title={getHoverInfo('trial_oversight')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <span>11.2 Trial Oversight</span>
                            </Tooltip>
                          ) : (
                            '11.2 Trial Oversight'
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px',
                          backgroundColor: '#e9ecef',
                          verticalAlign: 'top'
                        }}>
                          {/* Empty title row */}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px',
                          backgroundColor: '#e9ecef',
                          verticalAlign: 'top'
                        }}>
                          {/* Empty title row */}
                        </td>
                      </tr>

                      {/* 11.2.1 and 11.2.2 Combined */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          <Box>
                            {getHoverInfo('investigator_responsibilities') ? (
                              <Tooltip title={getHoverInfo('investigator_responsibilities')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  11.2.1 Investigator Responsibilities
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.2.1 Investigator Responsibilities
                              </Typography>
                            )}
                            {getHoverInfo('sponsor_responsibilities') ? (
                              <Tooltip title={getHoverInfo('sponsor_responsibilities')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', marginTop: '8px' }}>
                                  11.2.2 Sponsor Responsibilities
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 'bold', marginTop: '8px' }}>
                                11.2.2 Sponsor Responsibilities
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                            {renderTemplateCheckbox('investigator_responsibilities')}
                            {renderTemplateCheckbox('sponsor_responsibilities')}
                          </Box>
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '120px',
                          position: 'relative'
                        }}>
                          <div style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                            <Field
                              name="regulatory_requirements.investigator_responsibilities"
                              render={({ field }) => (
                                <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Detail investigator responsibilities and obligations"
                              />
                              )}
                            />
                          </div>
                          <div style={{ padding: '8px' }}>
                            <Field
                              name="regulatory_requirements.sponsor_responsibilities"
                              render={({ field }) => (
                                <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Detail sponsor responsibilities and obligations"
                              />
                              )}
                            />
                          </div>
                        </td>
                      </tr>

                      {/* 11.3 Informed Consent Process - Title Only */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef',
                          fontWeight: 'bold',
                          verticalAlign: 'top',
                          fontSize: '14px'
                        }}>
                          {getHoverInfo('informed_consent_process') ? (
                            <Tooltip title={getHoverInfo('informed_consent_process')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <span>11.3 Informed Consent Process</span>
                            </Tooltip>
                          ) : (
                            '11.3 Informed Consent Process'
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px',
                          backgroundColor: '#e9ecef',
                          verticalAlign: 'top'
                        }}>
                          {/* Empty title row */}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px',
                          backgroundColor: '#e9ecef',
                          verticalAlign: 'top'
                        }}>
                          {/* Empty title row */}
                        </td>
                      </tr>

                      {/* 11.3.1 and 11.3.2 Combined */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          <Box>
                            {getHoverInfo('informed_consent_rescreening') ? (
                              <Tooltip title={getHoverInfo('informed_consent_rescreening')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  11.3.1 Informed Consent for Rescreening
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.3.1 Informed Consent for Rescreening
                              </Typography>
                            )}
                            {getHoverInfo('informed_consent_remaining_samples') ? (
                              <Tooltip title={getHoverInfo('informed_consent_remaining_samples')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', marginTop: '8px' }}>
                                  11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 'bold', marginTop: '8px' }}>
                                11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                            {renderTemplateCheckbox('informed_consent_rescreening')}
                            {renderTemplateCheckbox('informed_consent_remaining_samples')}
                          </Box>
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '120px',
                          position: 'relative'
                        }}>
                          <div style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                            <Field
                              name="regulatory_requirements.informed_consent_rescreening"
                              render={({ field }) => (
                                <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe informed consent procedures for rescreening"
                              />
                              )}
                            />
                          </div>
                          <div style={{ padding: '8px' }}>
                            <Field
                              name="regulatory_requirements.informed_consent_remaining_samples"
                              render={({ field }) => (
                                <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe informed consent for use of remaining samples in exploratory research"
                              />
                              )}
                            />
                          </div>
                        </td>
                      </tr>

                      {/* 11.4 Committees */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('committees') ? (
                            <Tooltip title={getHoverInfo('committees')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.4 Committees
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.4 Committees
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('committees')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.committees"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="List committees to be used in the study (e.g. Dose Escalation Committee, Data Monitoring Committee, etc.)"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.5 Insurance and Indemnity */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('insurance_indemnity') ? (
                            <Tooltip title={getHoverInfo('insurance_indemnity')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.5 Insurance and Indemnity
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.5 Insurance and Indemnity
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('insurance_indemnity')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.insurance_indemnity"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe insurance coverage and indemnity arrangements"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.6 Risk-Based Quality Management */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('risk_based_quality_management') ? (
                            <Tooltip title={getHoverInfo('risk_based_quality_management')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.6 Risk-Based Quality Management
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.6 Risk-Based Quality Management
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('risk_based_quality_management')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.risk_based_quality_management"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe risk-based quality management approach"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.7 Data Governance */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('data_governance') ? (
                            <Tooltip title={getHoverInfo('data_governance')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.7 Data Governance
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.7 Data Governance
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('data_governance')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.data_governance"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe data governance framework and procedures"
                              />
                            )}
                          />
                        </td>
                      </tr>
                      {/* 11.8 Data Protection */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('data_protection') ? (
                            <Tooltip title={getHoverInfo('data_protection')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.8 Data Protection
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.8 Data Protection
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('data_protection')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.data_protection"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe data protection measures and compliance"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.9 Source Data */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('source_data') ? (
                            <Tooltip title={getHoverInfo('source_data')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.9 Source Reports
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.9 Source Reports
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('source_data')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.source_data"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe source data requirements and documentation"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.10 Protocol Deviations */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('protocol_deviations') ? (
                            <Tooltip title={getHoverInfo('protocol_deviations')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.10 Protocol Deviations
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.10 Protocol Deviations
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('protocol_deviations')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.protocol_deviations"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe protocol deviation management procedures"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.11 Early Site Closure */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('early_site_closure') ? (
                            <Tooltip title={getHoverInfo('early_site_closure')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.11 Early Site Closure
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.11 Early Site Closure
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('early_site_closure')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.early_site_closure"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe procedures for early site closure"
                              />
                            )}
                          />
                        </td>
                      </tr>

                      {/* 11.12 Data Dissemination */}
                      <tr>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top'
                        }}>
                          {getHoverInfo('data_dissemination') ? (
                            <Tooltip title={getHoverInfo('data_dissemination')} arrow placement="top-start" enterDelay={450} enterNextDelay={450}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                11.12 Data Dissemination
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              11.12 Data Dissemination
                            </Typography>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          {renderTemplateCheckbox('data_dissemination')}
                        </td>
                        <td style={{ 
                          border: '1px solid #ddd', 
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative'
                        }}>
                          <Field
                            name="regulatory_requirements.data_dissemination"
                            render={({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                showCptPreview
                                placeholder="Describe data dissemination policies and procedures"
                              />
                            )}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td style={{
                          border: '1px solid #ddd',
                          padding: '12px',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          verticalAlign: 'top',
                        }}>
                          Additional Notes
                        </td>
                        <td style={{
                          border: '1px solid #ddd',
                          padding: '12px',
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          verticalAlign: 'top',
                        }} />
                        <td style={{
                          border: '1px solid #ddd',
                          padding: '0px',
                          verticalAlign: 'top',
                          height: 'auto',
                          minHeight: '80px',
                          position: 'relative',
                        }}>
                          <Field name="regulatory_requirements.comments">
                            {({ field }) => (
                              <CptTemplateTextField
                                field={field}
                                disabled={isFormDisabled}
                                placeholder="Enter any additional notes for this section"
                              />
                            )}
                          </Field>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />

              {/* Appendices Section */}
              <Box sx={{ mb: 4 }}>
                <Tooltip
                  title={getHoverInfo('list_of_appendices_title') || ''}
                  arrow
                  placement="top-start"
                  enterDelay={450}
                  enterNextDelay={450}
                  disableHoverListener={!getHoverInfo('list_of_appendices_title')}
                >
                  <Typography
                    component="div"
                    sx={{ ...M11_SECTION_BANNER_SX, mb: 1.25 }}
                  >
                    APPENDICES
                  </Typography>
                </Tooltip>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  13. List of Appendices
                </Typography>
                
                {/* Appendices Table */}
                <Box sx={{ overflowX: 'auto', mb: 3 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '38%' }} />
                      <col style={{ width: '40%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          Appendix Title
                        </th>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          Description
                        </th>
                        <th style={{ 
                          border: '1px solid #ddd', 
                          padding: '12px', 
                          backgroundColor: '#e9ecef', 
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          Upload document
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {values.appendices?.map((appendix, index) => (
                        <tr key={index}>
                          <td style={{
                            border: '1px solid #ddd',
                            padding: '0px',
                            verticalAlign: 'top'
                          }}>
                            <Field
                              name={`appendices.${index}.title`}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  variant="outlined"
                                  fullWidth
                                  disabled={isFormDisabled}
                                  placeholder="e.g., 12.1 Clinical Laboratory Tests"
                                  sx={{
                                    width: '100%',
                                    '& fieldset': {
                                      border: 'none'
                                    }
                                  }}
                                />
                              )}
                            />
                            {!isFormDisabled && (
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                startIcon={<DeleteIcon fontSize="small" />}
                                disabled={appendixUploadingIndex != null}
                                onClick={() => handleRemoveAppendix(index, setFieldValue, values)}
                                sx={{ textTransform: 'none', ml: 1 }}
                              >
                                Remove this appendix
                              </Button>
                            )}
                          </td>
                          <td style={{
                            border: '1px solid #ddd',
                            padding: '0px',
                            verticalAlign: 'top'
                          }}>
                            <Field
                              name={`appendices.${index}.description`}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  multiline
                                  variant="outlined"
                                  fullWidth
                                  disabled={isFormDisabled}
                                  placeholder="Describe the appendix content and purpose"
                                  sx={{
                                    width: '100%',
                                    '& fieldset': {
                                      border: 'none'
                                    }
                                  }}
                                />
                              )}
                            />
                          </td>
                          <td style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            verticalAlign: 'top',
                          }}>
                            <Stack spacing={1}>
                              {!isFormDisabled && (
                                <Button
                                  component="label"
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    appendixUploadingIndex === index
                                      ? <CircularProgress size={14} />
                                      : <CloudUploadIcon fontSize="small" />
                                  }
                                  disabled={appendixUploadingIndex != null}
                                >
                                  {appendixUploadingIndex === index ? 'Extracting…' : 'Upload PDF / Word / image'}
                                  <input
                                    hidden
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.bmp,.webp,.tif,.tiff,application/pdf,image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      e.target.value = '';
                                      if (file) {
                                        handleAppendixFileUpload(file, index, setFieldValue, values);
                                      }
                                    }}
                                  />
                                </Button>
                              )}
                              {Array.isArray(appendix?.uploads) && appendix.uploads.length > 0 && (
                                <Stack spacing={0.25}>
                                  {appendix.uploads.map((u, uploadIndex) => (
                                    <Box
                                      key={u?.uploadId || `${u?.name}-${uploadIndex}`}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                        {u?.name || 'file'}
                                      </Typography>
                                      {!isFormDisabled && (
                                        <IconButton
                                          size="small"
                                          aria-label={`Remove ${u?.name || 'file'}`}
                                          disabled={appendixUploadingIndex != null}
                                          onClick={() =>
                                            handleRemoveAppendixUpload(index, uploadIndex, setFieldValue, values)
                                          }
                                        >
                                          <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                              {String(appendix?.contentHtml || '').trim() ? (
                                <Box
                                  sx={{
                                    maxHeight: 140,
                                    overflow: 'auto',
                                    border: '1px solid #eee',
                                    borderRadius: 1,
                                    p: 1,
                                    bgcolor: '#fafafa',
                                    fontSize: 12,
                                    '& img': { maxWidth: '100%', height: 'auto' },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(appendix.contentHtml) }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Uploaded content is appended here (text + images) and used for protocol section 12.
                                </Typography>
                              )}
                            </Stack>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                
                {/* Add/Remove Appendix Buttons */}
                {!isFormDisabled && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        const newAppendices = [
                          ...(values.appendices || []),
                          { title: '', description: '', contentHtml: '', uploads: [] },
                        ];
                        setFieldValue('appendices', newAppendices);
                      }}
                      startIcon={<span>+</span>}
                      size="small"
                    >
                      Add Appendix
                    </Button>
                  </Box>
                )}
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Upload PDF, Word, or images on each row (e.g. 12.1). Content is appended on that row and auto-fills protocol section 12 when that section already exists in Document Authoring.
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              </Box>
              {/* Background Information Section - Moved to bottom */}
               <Box
                ref={(el) => {
                  protocolSectionRefs.current.arguments = el;
                }}
                sx={{ scrollMarginTop: PROTOCOL_SECTION_SCROLL_MARGIN }}
              >
               <Tooltip
                title={getHoverInfo('disease_background_arguments_title') || ''}
                arrow
                placement="top-start"
                enterDelay={450}
                enterNextDelay={450}
                disableHoverListener={!getHoverInfo('disease_background_arguments_title')}
               >
                <Typography
                  component="div"
                  sx={M11_SECTION_BANNER_SX}
                >
                  DISEASE BACKGROUND ARGUMENTS
                </Typography>
               </Tooltip>
              <Accordion
                expanded={protocolSectionExpanded.arguments}
                onChange={(_, expanded) =>
                  setProtocolSectionExpanded((prev) => ({ ...prev, arguments: expanded }))
                }
                sx={{ mb: 2, border: '1px solid #dbe3eb', boxShadow: 'none' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    D. Arguments
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                {!isFormDisabled && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={() => handleAutoGenerateBackgroundInfo(values, setFieldValue, getCurrentValues)}
                      disabled={generatingBackgroundInfo || loading}
                      startIcon={generatingBackgroundInfo ? <CircularProgress size={20} /> : null}
                    >
                      {generatingBackgroundInfo ? 'Generating Arguments…' : 'Generate Arguments from Intake'}
                    </Button>
                  </Box>
                )}
                
                {/* Background Information Generation Progress */}
                {generatingBackgroundInfo && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="info.dark">
                        Generating Background Information...
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="info.dark">
                      {backgroundInfoProgress}
                    </Typography>
                  </Box>
                )}
                
                {backgroundInfoError && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="error.dark">
                      {backgroundInfoError}
                    </Typography>
                  </Box>
                )}
                
                {/* Disease Epidemiology */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    a) Disease Background
                  </Typography>
                  <Tooltip
                    title={getHoverInfo('disease_epidemiology') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('disease_epidemiology')}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        name="background_information.disease_epidemiology"
                        label="Disease Epidemiology (Include prevalence, incidence, demographics, risk factors, and disease burden)"
                        value={values.background_information?.disease_epidemiology || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Reference: Specific I/E for disease condition, Overall primary disease condition, Disease Background Documents"
                        disabled={isFormDisabled} // Disable in view mode or when locked
                      />
                    </Box>
                  </Tooltip>
                  
                  {/* Custom Prompt for Individual Regeneration */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Custom Prompt for Regeneration
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="background_information.custom_prompts.disease_epidemiology"
                      placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                      value={values.background_information?.custom_prompts?.disease_epidemiology || ''}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCustomRegeneration(
                        'disease_epidemiology',
                        values.background_information?.custom_prompts?.disease_epidemiology,
                        setFieldValue
                      )}
                      disabled={isFormDisabled || customRegenerationLoading.disease_epidemiology}
                      startIcon={customRegenerationLoading.disease_epidemiology ? <CircularProgress size={16} /> : null}
                    >
                      {customRegenerationLoading.disease_epidemiology ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                    </Button>
                  </Box>
                </Box>

                {/* Current Standard of Care */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    b) Current Standard of Care and their outcomes
                  </Typography>
                  <Tooltip
                    title={getHoverInfo('current_standard_of_care') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('current_standard_of_care')}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        name="background_information.current_standard_of_care"
                        label="Current Standard of Care (Include approved treatments, guidelines, and clinical practice)"
                        value={values.background_information?.current_standard_of_care || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Reference: Specific I/E for disease condition, Disease Background Documents, Control Arm Description, Control Arm-Related Documents"
                        disabled={isFormDisabled} // Disable in view mode or when locked
                      />
                    </Box>
                  </Tooltip>
                  
                  {/* Custom Prompt for Individual Regeneration */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Custom Prompt for Regeneration
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="background_information.custom_prompts.current_standard_of_care"
                      placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                      value={values.background_information?.custom_prompts?.current_standard_of_care || ''}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCustomRegeneration(
                        'current_standard_of_care',
                        values.background_information?.custom_prompts?.current_standard_of_care,
                        setFieldValue
                      )}
                      disabled={isFormDisabled || customRegenerationLoading.current_standard_of_care}
                      startIcon={customRegenerationLoading.current_standard_of_care ? <CircularProgress size={16} /> : null}
                    >
                      {customRegenerationLoading.current_standard_of_care ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                    </Button>
                  </Box>
                </Box>

                {/* Benefits with Investigational Agent/Regimen */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    c) Benefits with the Investigational Agent/Regimen
                  </Typography>
                  <Tooltip
                    title={getHoverInfo('benefits_investigational') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('benefits_investigational')}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        name="background_information.benefits_investigational"
                        label="Benefits with the Investigational Agent/Regimen (Include potential advantages, mechanism of action, and expected improvements)"
                        value={values.background_information?.benefits_investigational || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Reference: Primary Investigational Product (IP) Sources, Supporting Product Documents, Specific I/E for disease condition, Overall primary disease condition"
                        disabled={isFormDisabled} // Disable in view mode or when locked
                      />
                    </Box>
                  </Tooltip>
                  
                  {/* Custom Prompt for Individual Regeneration */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Custom Prompt for Regeneration
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="background_information.custom_prompts.benefits_investigational"
                      placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                      value={values.background_information?.custom_prompts?.benefits_investigational || ''}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCustomRegeneration(
                        'benefits_investigational',
                        values.background_information?.custom_prompts?.benefits_investigational,
                        setFieldValue
                      )}
                      disabled={isFormDisabled || customRegenerationLoading.benefits_investigational}
                      startIcon={customRegenerationLoading.benefits_investigational ? <CircularProgress size={16} /> : null}
                    >
                      {customRegenerationLoading.benefits_investigational ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                    </Button>
                  </Box>
                </Box>

                {/* Risks with Investigational Agent/Regimen */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    d) Risks with the Investigational Agent/Regimen
                  </Typography>
                  <Tooltip
                    title={getHoverInfo('risks_investigational') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('risks_investigational')}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        name="background_information.risks_investigational"
                        label="Risks with the Investigational Agent/Regimen (Include known safety concerns, potential adverse effects, and risk mitigation strategies)"
                        value={values.background_information?.risks_investigational || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Reference: Primary Investigational Product (IP) Sources, Supporting Product Documents, Specific I/E for disease condition, Overall primary disease condition"
                        disabled={isFormDisabled} // Disable in view mode or when locked
                      />
                    </Box>
                  </Tooltip>
                  
                  {/* Custom Prompt for Individual Regeneration */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Custom Prompt for Regeneration
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="background_information.custom_prompts.risks_investigational"
                      placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                      value={values.background_information?.custom_prompts?.risks_investigational || ''}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCustomRegeneration(
                        'risks_investigational',
                        values.background_information?.custom_prompts?.risks_investigational,
                        setFieldValue
                      )}
                      disabled={isFormDisabled || customRegenerationLoading.risks_investigational}
                      startIcon={customRegenerationLoading.risks_investigational ? <CircularProgress size={16} /> : null}
                    >
                      {customRegenerationLoading.risks_investigational ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                    </Button>
                  </Box>
                </Box>

                {/* Purpose of the Trial */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    e) Purpose of the Trial
                  </Typography>
                  <Tooltip
                    title={getHoverInfo('purpose_of_trial') || ''}
                    arrow
                    placement="top-start"
                    enterDelay={450}
                    enterNextDelay={450}
                    disableHoverListener={!getHoverInfo('purpose_of_trial')}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        name="background_information.purpose_of_trial"
                        label="Purpose of the Trial (Include scientific justification, clinical need, and expected outcomes)"
                        value={values.background_information?.purpose_of_trial || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Reference: Study Overview, Disease Indication, Study Type, Study Design, Primary Endpoint"
                        disabled={isFormDisabled}
                      />
                    </Box>
                  </Tooltip>
                  
                  {/* Custom Prompt for Individual Regeneration */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Custom Prompt for Regeneration
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      name="background_information.custom_prompts.purpose_of_trial"
                      placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                      value={values.background_information?.custom_prompts?.purpose_of_trial || ''}
                      onChange={handleChange}
                      disabled={isFormDisabled}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCustomRegeneration(
                        'purpose_of_trial',
                        values.background_information?.custom_prompts?.purpose_of_trial,
                        setFieldValue
                      )}
                      disabled={isFormDisabled || customRegenerationLoading.purpose_of_trial}
                      startIcon={customRegenerationLoading.purpose_of_trial ? <CircularProgress size={16} /> : null}
                    >
                      {customRegenerationLoading.purpose_of_trial ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                    </Button>
                  </Box>
                </Box>

                {/* Rationale Field Relevance Loading Indicator */}
                {determiningRationaleRelevance && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Analyzing trial design to determine relevant rationale sections...
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Rationale Fields Status Indicator */}
                {!determiningRationaleRelevance && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {Object.values(rationaleFieldRelevance).filter(Boolean).length > 0 
                        ? `Showing ${Object.values(rationaleFieldRelevance).filter(Boolean).length} relevant rationale sections based on your trial design`
                        : 'No specific rationale sections identified for your trial design - showing standard sections only'
                      }
                    </Typography>
                  </Box>
                )}

                {/* Rationale for Estimands */}
                {rationaleFieldRelevance.rationale_for_estimands && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      f) Rationale for Estimands
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_estimands') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_estimands')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_estimands"
                          label="Rationale for Estimands (Include statistical considerations, clinical relevance, and regulatory requirements)"
                          value={values.background_information?.rationale_for_estimands || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Primary Endpoint, Secondary Endpoints, Study Population, Study Design"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_estimands"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_estimands || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_estimands',
                          values.background_information?.custom_prompts?.rationale_for_estimands,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_estimands}
                        startIcon={customRegenerationLoading.rationale_for_estimands ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_estimands ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Interventional Type */}
                {rationaleFieldRelevance.rationale_for_interventional_type && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      g) Rationale for Interventional Type
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_interventional_type') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_interventional_type')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_interventional_type"
                          label="Rationale for Interventional Type (Include scientific justification, regulatory considerations, and clinical benefits)"
                          value={values.background_information?.rationale_for_interventional_type || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Study Type, Investigational Product, Control Regimen, Therapeutic Area"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_interventional_type"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_interventional_type || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_interventional_type',
                          values.background_information?.custom_prompts?.rationale_for_interventional_type,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_interventional_type}
                        startIcon={customRegenerationLoading.rationale_for_interventional_type ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_interventional_type ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Control Method */}
                {rationaleFieldRelevance.rationale_for_control_method && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      h) Rationale for Control Method
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_control_method') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_control_method')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_control_method"
                          label="Rationale for Control Method (Include ethical considerations, scientific validity, and regulatory acceptability)"
                          value={values.background_information?.rationale_for_control_method || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Control Regimen, Control Method, Study Design, Primary Endpoint"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_control_method"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_control_method || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_control_method',
                          values.background_information?.custom_prompts?.rationale_for_control_method,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_control_method}
                        startIcon={customRegenerationLoading.rationale_for_control_method ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_control_method ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Trial Duration */}
                {rationaleFieldRelevance.rationale_for_trial_duration && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      i) Rationale for Trial Duration
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_trial_duration') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_trial_duration')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_trial_duration"
                          label="Rationale for Trial Duration (Include clinical considerations, endpoint assessment timing, and regulatory requirements)"
                          value={values.background_information?.rationale_for_trial_duration || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Study Duration, Follow-up Period, Primary Endpoint, Study Population"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_trial_duration"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_trial_duration || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_trial_duration',
                          values.background_information?.custom_prompts?.rationale_for_trial_duration,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_trial_duration}
                        startIcon={customRegenerationLoading.rationale_for_trial_duration ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_trial_duration ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Adaptive or Novel Trial Design */}
                {rationaleFieldRelevance.rationale_for_adaptive_design && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      j) Rationale for Adaptive or Novel Trial Design
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_adaptive_design') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_adaptive_design')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_adaptive_design"
                          label="Rationale for Adaptive or Novel Trial Design (Include statistical considerations, operational benefits, and regulatory guidance compliance)"
                          value={values.background_information?.rationale_for_adaptive_design || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Study Design, Study Type, Primary Endpoint, Study Population"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_adaptive_design"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_adaptive_design || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_adaptive_design',
                          values.background_information?.custom_prompts?.rationale_for_adaptive_design,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_adaptive_design}
                        startIcon={customRegenerationLoading.rationale_for_adaptive_design ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_adaptive_design ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Interim Analysis */}
                {rationaleFieldRelevance.rationale_for_interim_analysis && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      k) Rationale for Interim Analysis
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_interim_analysis') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_interim_analysis')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_interim_analysis"
                          label="Rationale for Interim Analysis (Include statistical stopping rules, ethical considerations, and regulatory requirements)"
                          value={values.background_information?.rationale_for_interim_analysis || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Study Design, Primary Endpoint, Study Duration, Safety Considerations"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_interim_analysis"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_interim_analysis || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_interim_analysis',
                          values.background_information?.custom_prompts?.rationale_for_interim_analysis,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_interim_analysis}
                        startIcon={customRegenerationLoading.rationale_for_interim_analysis ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_interim_analysis ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Rationale for Other Trial Design Aspects */}
                {rationaleFieldRelevance.rationale_for_other_design_aspects && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      l) Rationale for Other Trial Design Aspects
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_other_design_aspects') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_other_design_aspects')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_other_design_aspects"
                          label="Rationale for Other Trial Design Aspects (Include randomization methods, blinding procedures, and any special considerations)"
                          value={values.background_information?.rationale_for_other_design_aspects || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Study Design, Study Type, Therapeutic Area, Study Population"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_other_design_aspects"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_other_design_aspects || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_other_design_aspects',
                          values.background_information?.custom_prompts?.rationale_for_other_design_aspects,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_other_design_aspects}
                        startIcon={customRegenerationLoading.rationale_for_other_design_aspects ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_other_design_aspects ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}
                {/* Rationale for Trial Population */}
                {rationaleFieldRelevance.rationale_for_trial_population && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      m) Rationale for Trial Population
                    </Typography>
                    <Tooltip
                      title={getHoverInfo('rationale_for_trial_population') || ''}
                      arrow
                      placement="top-start"
                      enterDelay={450}
                      enterNextDelay={450}
                      disableHoverListener={!getHoverInfo('rationale_for_trial_population')}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={20}
                          name="background_information.rationale_for_trial_population"
                          label="Rationale for Trial Population (Include clinical relevance, safety considerations, and regulatory guidance compliance)"
                          value={values.background_information?.rationale_for_trial_population || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Reference: Target Population, Inclusion Criteria, Exclusion Criteria, Disease Indication"
                          disabled={isFormDisabled}
                        />
                      </Box>
                    </Tooltip>
                    
                    {/* Custom Prompt for Individual Regeneration */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Custom Prompt for Regeneration
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        name="background_information.custom_prompts.rationale_for_trial_population"
                        placeholder="Enter a custom prompt to regenerate this section. Leave empty to use the default prompt."
                        value={values.background_information?.custom_prompts?.rationale_for_trial_population || ''}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCustomRegeneration(
                          'rationale_for_trial_population',
                          values.background_information?.custom_prompts?.rationale_for_trial_population,
                          setFieldValue
                        )}
                        disabled={isFormDisabled || customRegenerationLoading.rationale_for_trial_population}
                        startIcon={customRegenerationLoading.rationale_for_trial_population ? <CircularProgress size={16} /> : null}
                      >
                        {customRegenerationLoading.rationale_for_trial_population ? 'Regenerating...' : 'Regenerate with Custom Prompt'}
                      </Button>
                    </Box>
                  </Box>
                )}
                </AccordionDetails>
              </Accordion>
              </Box>

              {/* Submit Button — moved above the SOA section: the Protocol Intake
                  Form must be submitted before SOA (authored from the submitted
                  intake data) can be opened; see the "Open SOA tab" button's
                  disabled={!formSubmitted} below. */}
              {!isFormDisabled && !formLocked && ( // Hide in view mode or when locked
                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  {documentsOnly ? (
                    null
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || isSubmitting || !dirty}
                      onClick={() => {console.log('Submit button clicked')}}
                    >
                      Submit
                    </Button>
                  )}
                </Box>
              )}

              {/* E. Schedule of Activities — authored in SOA tab */}
              <Box
                ref={(el) => {
                  protocolSectionRefs.current.soa = el;
                }}
                sx={{ scrollMarginTop: PROTOCOL_SECTION_SCROLL_MARGIN }}
              >
                <Paper sx={{ p: 3, mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    E. Schedule of Activities (SOA)
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }} color="text.secondary">
                    The Schedule of Activities is now authored in the dedicated <strong>SOA</strong> protocol tab
                    using the chat-based Assessment Schedule. Build and publish the SOA there; Document Editor
                    section 1.3 will use the published version.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        const params = new URLSearchParams(location.search);
                        params.set('protocolSubTab', 'soa');
                        if (currentProject?._id) params.set('projectId', currentProject._id);
                        navigate({ search: params.toString() });
                      }}
                      // Locked (grey, unclickable) until the Protocol Intake Form has actually
                      // been submitted — SOA is authored from the submitted intake data, so
                      // opening it earlier would have nothing to work from. Once formSubmitted
                      // is true (including for an already-locked/submitted form reloaded later)
                      // it stays enabled — MUI's own disabled styling handles the grey state.
                      disabled={!formSubmitted}
                    >
                      Open SOA tab
                    </Button>
                    {formSubmitted && (
                      <Button
                        variant="outlined"
                        color="success"
                        onClick={handleNextToDocumentEditor}
                        // Same rationale: greyed out but still navigable when locked.
                        sx={
                          isFormDisabled
                            ? {
                                color: (theme) => `${theme.palette.text.disabled} !important`,
                                borderColor: (theme) => `${theme.palette.action.disabled} !important`,
                                '&:hover': {
                                  borderColor: (theme) => `${theme.palette.action.disabled} !important`,
                                },
                              }
                            : undefined
                        }
                      >
                        Next: Go to Document Editor
                      </Button>
                    )}
                  </Stack>
                  {!formSubmitted && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Submit the protocol intake form first, then open the SOA tab to author the schedule.
                    </Typography>
                  )}
                </Paper>
              </Box>

              </>
              )}

              {/* Locked Form Message */}
              {formLocked && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    🔒 This form has been submitted and is locked. No further changes can be made.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Form>
          </>
        );
        }}
            </Formik>

      {/* General feedback (validation, auto-populate, uploads, etc.) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={8000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Shown only after explicit form submit — not draft saves or parse */}
      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccess(false)} 
          severity="success"
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {formLocked
            ? 'Form submitted successfully! The form is now locked.'
            : 'Form submitted successfully! Open the SOA tab to author and publish the Schedule of Activities.'}
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Document Parsing Warnings Dialog */}
      <Dialog 
        open={showParsingWarnings} 
        onClose={() => setShowParsingWarnings(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#fff3cd', color: '#856404' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Typography variant="h6" component="span">
              {allFilesValid ? '✓ File Validation Passed' : ' File Validation Failed - Processing Blocked'}
            </Typography>
            {isValidatingFiles && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>⛔ Automatic processing has been stopped.</strong>
            <br />
            {parsingWarningsState.length} file(s) could not be parsed or contained no readable text. 
            ALL files must be readable before processing can begin.
          </Alert>
          
          {/* Show ALL files with their validation status */}
          {Object.keys(fileValidationStatus).length > 0 && (
            <>
              <DialogContentText sx={{ mb: 2, fontWeight: 'bold' }}>
                File Validation Status (All Uploaded Files):
              </DialogContentText>
              
              <List>
                {Object.entries(fileValidationStatus).map(([filename, status], index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      border: `2px solid ${status.isValid ? '#4caf50' : '#f44336'}`, 
                      borderRadius: 1, 
                      mb: 1,
                      backgroundColor: status.isValid ? '#f1f8f4' : '#ffebee'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: status.isValid ? '#2e7d32' : '#c62828',
                              fontWeight: 'bold'
                            }}
                          >
                            {status.isValid ? '✓' : '✗'}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {filename}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color={status.isValid ? 'success.main' : 'error'} 
                          sx={{ mt: 0.5 }}
                        >
                          <strong>{status.isValid ? 'Status:' : 'Error:'}</strong> {status.reason}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          
          {/* Also show just the failed files for quick reference */}
          {parsingWarningsState.length > 0 && (
            <>
              <DialogContentText sx={{ mb: 2, mt: 3, fontWeight: 'bold', color: 'error.main' }}>
                ⚠️ Files That MUST Be Fixed ({parsingWarningsState.length}):
              </DialogContentText>
              
              <List>
                {parsingWarningsState.map((warning, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      border: '2px solid #f44336', 
                      borderRadius: 1, 
                      mb: 1,
                      backgroundColor: '#ffebee'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: 'error.main' }}>
                          ✗ {warning.filename}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          <strong>Reason:</strong> {warning.reason}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Suggestions:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>For scanned PDFs: Try using OCR software to convert them to text-searchable PDFs</li>
              <li>For password-protected files: Remove the password and re-upload</li>
              <li>For unsupported formats: Convert to PDF, DOCX, or TXT format</li>
              <li>For corrupted files: Try re-downloading and re-uploading the original document</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowParsingWarnings(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
          </>
        );
      })()}
      
      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfViewerOpen}
        onClose={() => {
          setPdfViewerOpen(false);
          setPdfUrl(null);
          setPdfFileName('');
          setPdfTabs([]);
          setActivePdfIndex(0);
          setPageNumber(1);
          setNumPages(0);
          setPdfError(null);
          setPdfDoc(null);
          setRenderedPages([]);
          canvasRefs.current = [];
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '95vh',
            maxHeight: '95vh',
            width: '95vw',
            maxWidth: '1400px'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, mb: pdfTabs.length > 1 ? 1 : 0 }}>
              {pdfFileName || 'PDF Viewer'}
            </Typography>
            {pdfTabs.length > 1 && (
              <Tabs
                value={activePdfIndex}
                onChange={(_, newIndex) => {
                  setActivePdfIndex(newIndex);
                  const tab = pdfTabs[newIndex];
                  if (tab) {
                    setPdfUrl(tab.url);
                    setPdfFileName(tab.name);
                    setPageNumber(1);
                    setPdfError(null);
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, paddingY: 0.5 } }}
              >
                {pdfTabs.map((tab, idx) => (
                  <Tab key={idx} label={tab.name || `Document ${idx + 1}`} />
                ))}
              </Tabs>
            )}
          </Box>
          <IconButton
            onClick={() => {
              setPdfViewerOpen(false);
              setPdfUrl(null);
              setPdfFileName('');
              setPdfTabs([]);
              setActivePdfIndex(0);
              setPageNumber(1);
              setNumPages(0);
              setPdfDoc(null);
              setPdfError(null);
              setPdfLoading(false);
              setRenderedPages([]);
              canvasRefs.current = [];
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          overflow: 'auto',
          minHeight: '70vh',
          width: '100%'
        }}>
          {pdfError && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {pdfError}
            </Alert>
          )}
          
          {pdfUrl && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              {pdfLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {numPages > 0 && !pdfLoading && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {numPages} page{numPages !== 1 ? 's' : ''} - Scroll to view all pages
                  </Typography>
                  
                  <Box sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    pb: 2
                  }}>
                    {Array.from({ length: numPages }, (_, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1,
                          width: '100%',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          p: 2,
                          mb: 1
                        }}
                      >
                        <Box sx={{ 
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderRadius: 1,
                          p: 2
                        }}>
                          <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary' }}>
                            Page {index + 1} of {numPages}
                          </Typography>
                          <canvas 
                            ref={(el) => {
                              if (el) {
                                canvasRefs.current[index] = el;
                              }
                            }} 
                            style={{ 
                              display: 'block', 
                              maxWidth: '100%',
                              height: 'auto',
                              width: 'auto'
                            }} 
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              // Download currently active PDF tab
              const currentTab = pdfTabs.length > 0 ? pdfTabs[activePdfIndex] : null;
              const downloadLink = currentTab?.url || pdfUrl;
              if (downloadLink) {
                window.open(downloadLink, '_blank');
              }
            }}
            variant="outlined"
          >
            Download PDF
          </Button>
          <Button
            onClick={() => {
              setPdfViewerOpen(false);
              setPdfUrl(null);
              setPdfFileName('');
              setPageNumber(1);
              setNumPages(0);
              setPdfError(null);
              setPdfDoc(null);
              setRenderedPages([]);
              canvasRefs.current = [];
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

       {/* Pre-Parse Options Dialog (shown before parsing/auto-populate) */}
      <Dialog
        open={preParseOptionsOpen}
        onClose={() => {
          setPreParseOptionsOpen(false);
          preParseArgsRef.current = null;
          preParseBypassOnceRef.current = false;
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Choose optional sub-fields to include</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These options control which extra sub-rows will be shown and populated when we parse your documents.
            You can change them later (while unlocked), but selecting them now ensures the first auto-populate includes them.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Study Parts: Lifestyle Restrictions sub-fields
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.meals)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, meals: e.target.checked }))
                      }
                    />
                  }
                  label="Meals and Dietary Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.caffeine)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, caffeine: e.target.checked }))
                      }
                    />
                  }
                  label="Caffeine, Alcohol, Tobacco, and Other Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.physical)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, physical: e.target.checked }))
                      }
                    />
                  }
                  label="Physical Activity Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.other)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, other: e.target.checked }))
                      }
                    />
                  }
                  label="Other Activity Restrictions"
                />
              </FormGroup>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Treatment Table: Prep/Storage/Accountability sub-fields
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.preparation)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          preparation: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Preparation"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.storageHandling)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          storageHandling: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Storage and Handling"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.accountability)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          accountability: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Accountability"
                />
              </FormGroup>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Rescue Medication sub-fields (stored inside Rescue Medication)
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(rescueMedicationEnabledSubfields.backgroundTrialIntervention)}
                      onChange={(e) =>
                        setRescueMedicationEnabledSubfields((prev) => ({
                          ...prev,
                          backgroundTrialIntervention: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Background Trial Intervention"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(rescueMedicationEnabledSubfields.otherNonInvestigationalIntervention)}
                      onChange={(e) =>
                        setRescueMedicationEnabledSubfields((prev) => ({
                          ...prev,
                          otherNonInvestigationalIntervention: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Other Noninvestigational Trial Intervention"
                />
              </FormGroup>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Analyses sub-fields (stored inside main field)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Primary objective
              </Typography>
              <FormGroup sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.handling_of_data)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Data"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_missing_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Missing Data"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, sensitivity_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Sensitivity Analysis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.supplementary_analysis)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, supplementary_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Supplementary Analysis"
                />
              </FormGroup>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Secondary objective
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.handling_of_data)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Data"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_missing_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Missing Data"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, sensitivity_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Sensitivity Analysis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.supplementary_analysis)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, supplementary_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Supplementary Analysis"
                />
              </FormGroup>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreParseOptionsOpen(false);
              preParseArgsRef.current = null;
              preParseBypassOnceRef.current = false;
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const args = preParseArgsRef.current;
              setPreParseOptionsOpen(false);
              preParseArgsRef.current = null;
              if (!args) return;
              preParseBypassOnceRef.current = true;
              await handleParseClinicalIntakeFromDocuments(args.setFieldValue, args.values, args.setValues, args.getCurrentValues);
            }}
          >
            Continue & Parse Intake
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assessment Guidance Dialog */}
      <Dialog
        open={assessmentHintDialog.open}
        onClose={() => setAssessmentHintDialog({ open: false, title: '', content: '', fieldKey: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{assessmentHintDialog.title || 'Assessment Guidance'}</DialogTitle>
        <DialogContent dividers>
          {assessmentHintDialog.fieldKey === 'lifestyle_restrictions' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessmentHintDialog.content}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Optional sub-fields to show in the intake form
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.meals)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, meals: e.target.checked }))
                      }
                    />
                  }
                  label="Meals and Dietary Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.caffeine)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, caffeine: e.target.checked }))
                      }
                    />
                  }
                  label="Caffeine, Alcohol, Tobacco, and Other Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.physical)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, physical: e.target.checked }))
                      }
                    />
                  }
                  label="Physical Activity Restrictions"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(lifestyleRestrictionEnabledSubfields.other)}
                      onChange={(e) =>
                        setLifestyleRestrictionEnabledSubfields((prev) => ({ ...prev, other: e.target.checked }))
                      }
                    />
                  }
                  label="Other Activity Restrictions"
                />
              </FormGroup>
            </Box>
          ) : assessmentHintDialog.fieldKey === 'preparation_storage_handling_accountability' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessmentHintDialog.content}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Optional sub-fields to show in the treatment table
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.preparation)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          preparation: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Preparation of Investigational Trial Intervention"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.storageHandling)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          storageHandling: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Storage and Handling of Investigational Trial Intervention"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(investigationalInterventionHandlingEnabledSubfields.accountability)}
                      onChange={(e) =>
                        setInvestigationalInterventionHandlingEnabledSubfields((prev) => ({
                          ...prev,
                          accountability: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Accountability of Investigational Trial Intervention"
                />
              </FormGroup>
            </Box>
          ) : assessmentHintDialog.fieldKey === 'rescue_medication' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessmentHintDialog.content}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Optional sub-fields to show under Rescue Medication (stored inside Rescue Medication)
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(rescueMedicationEnabledSubfields.backgroundTrialIntervention)}
                      onChange={(e) =>
                        setRescueMedicationEnabledSubfields((prev) => ({
                          ...prev,
                          backgroundTrialIntervention: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Background Trial Intervention"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(rescueMedicationEnabledSubfields.otherNonInvestigationalIntervention)}
                      onChange={(e) =>
                        setRescueMedicationEnabledSubfields((prev) => ({
                          ...prev,
                          otherNonInvestigationalIntervention: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Other Noninvestigational Trial Intervention"
                />
              </FormGroup>
            </Box>
          ) : assessmentHintDialog.fieldKey === 'analyses_primary_objective' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessmentHintDialog.content}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Optional sub-fields to show under Analyses Supporting Primary Objective (stored inside the main field)
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.handling_of_data)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Data in Relation to Primary Estimands"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_missing_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Missing Data in Relation to Primary Estimands"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, sensitivity_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Sensitivity Analysis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(primaryObjectiveAnalysisEnabledSubfields.supplementary_analysis)}
                      onChange={(e) =>
                        setPrimaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, supplementary_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Supplementary Analysis"
                />
              </FormGroup>
            </Box>
          ) : assessmentHintDialog.fieldKey === 'analyses_secondary_objective' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assessmentHintDialog.content}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Optional sub-fields to show under Analyses Supporting Secondary Objective (stored inside the main field)
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.handling_of_data)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Data in Relation to Secondary Estimands"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.handling_of_missing_data)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, handling_of_missing_data: e.target.checked }))
                      }
                    />
                  }
                  label="Handling of Missing Data in Relation to Secondary Estimands"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.sensitivity_analysis)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, sensitivity_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Sensitivity Analysis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isViewMode || formLocked}
                      checked={Boolean(secondaryObjectiveAnalysisEnabledSubfields.supplementary_analysis)}
                      onChange={(e) =>
                        setSecondaryObjectiveAnalysisEnabledSubfields((prev) => ({ ...prev, supplementary_analysis: e.target.checked }))
                      }
                    />
                  }
                  label="Supplementary Analysis"
                />
              </FormGroup>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {assessmentHintDialog.content}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssessmentHintDialog({ open: false, title: '', content: '', fieldKey: '' })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Scroll to Top Button */}
      <ScrollToTop />
    </Box>
  );
};

export default ClinicalIntakeForm;